import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import u, { useState, useEffect, useCallback, createContext, useContext, useMemo, forwardRef, useRef, useReducer, useImperativeHandle, Fragment as Fragment$1, PureComponent, createRef, useId, Component, createElement } from "react";
import { Popover, Button, Card, Spinner, Tooltip, Link, ButtonGroup, Dropdown, Label, Description, ProgressBar, Chip, ScrollShadow, ListBox, ComboBox, Input, TextField, Select, Checkbox, Collection, EmptyState, InputGroup, CloseButton, FieldError, Modal, SearchField, Skeleton as Skeleton$1, Accordion, Avatar, Breadcrumbs, BreadcrumbsItem, DateRangePicker as DateRangePicker$1, DateField, RangeCalendar, Radio, cn, RadioGroup, useTheme, Badge, ListBoxItem, Switch, Drawer as Drawer$1, DatePicker, Calendar, TimeField, NumberField, Alert, ToggleButtonGroup, ToggleButton } from "@heroui/react";
import { useCopyToClipboard, useMediaQuery, useDeepCompareMemoize, useDeepCompareEffect, useList, useDebounce } from "@livefree/react";
import { toISO, DateTime, SystemZone, fromISO, convert, objectReject, isArray, isFunction } from "@livefree/common";
import { toCalendarDateTime, Time, parseDateTime, CalendarDateTime, fromDate, getLocalTimeZone, today, parseDate, parseTime } from "@internationalized/date";
import { ItemCard } from "@heroui-pro/react";
import { useMapsLibrary, Marker, Map as Map$1, useMap } from "@vis.gl/react-google-maps";
import { CopyToClipboard as CopyToClipboard$1 } from "@livefree/react-ui";
import { useFilter } from "@react-aria/i18n";
import { Link as Link$1, useNavigate, isRouteErrorResponse } from "react-router";
import { createPortal } from "react-dom";
import { RiInformationLine } from "@remixicon/react";
import { Resizable } from "@heroui-pro/react/resizable";
const V3_BUTTON_VARIANTS = [
  "primary",
  "secondary",
  "tertiary",
  "outline",
  "ghost",
  "danger",
  "danger-soft"
];
const toButtonVariant = (color, variant) => {
  if (V3_BUTTON_VARIANTS.includes(variant)) return variant;
  if (color === "danger") {
    return variant === "flat" || variant === "light" ? "danger-soft" : "danger";
  }
  if (variant === "ghost") return "ghost";
  if (variant === "bordered" || variant === "faded") return "secondary";
  if (variant === "light" || variant === "flat") return "tertiary";
  if (color === "secondary") return "secondary";
  if (color === "default") return "secondary";
  return "primary";
};
const toChipColor = (color) => {
  if (color === "primary" || color === "secondary") return "accent";
  if (["default", "accent", "success", "warning", "danger"].includes(color)) return color;
  return "default";
};
const toChipVariant = (variant) => {
  switch (variant) {
    case "bordered":
    case "faded":
      return "secondary";
    case "light":
      return "soft";
    case "flat":
      return "tertiary";
    case "primary":
    case "secondary":
    case "tertiary":
    case "soft":
      return variant;
    default:
      return "primary";
  }
};
const ConfirmButton = ({
  onConfirm,
  isDisabled,
  color,
  variant,
  confirmColor,
  body,
  children,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return /* @__PURE__ */ jsxs(Popover, { isOpen, onOpenChange: (open) => setIsOpen(open), children: [
    /* @__PURE__ */ jsx(
      Button,
      {
        variant: toButtonVariant(color, variant),
        ...rest,
        isDisabled: isDisabled || isOpen,
        children
      }
    ),
    /* @__PURE__ */ jsx(Popover.Content, { placement: "top", offset: 10, className: "w-75", children: /* @__PURE__ */ jsxs(Popover.Dialog, { children: [
      /* @__PURE__ */ jsx(Popover.Arrow, {}),
      /* @__PURE__ */ jsxs(Card, { className: "w-full shadow-none rounded-none", children: [
        /* @__PURE__ */ jsx(Card.Header, { children: /* @__PURE__ */ jsx(Card.Title, { children: "Confirm" }) }),
        /* @__PURE__ */ jsxs(Card.Content, { children: [
          body && /* @__PURE__ */ jsx("div", { children: body }),
          !body && /* @__PURE__ */ jsx("p", { className: "text-muted", children: "Are you sure?" })
        ] }),
        /* @__PURE__ */ jsxs(Card.Footer, { className: "flex justify-end gap-2", children: [
          /* @__PURE__ */ jsxs(Button, { variant: "secondary", size: "sm", onPress: () => setIsOpen(false), children: [
            /* @__PURE__ */ jsx("i", { className: "ri-close-circle-line" }),
            " Cancel"
          ] }),
          /* @__PURE__ */ jsxs(
            Button,
            {
              variant: toButtonVariant(confirmColor || "danger"),
              size: "sm",
              onPress: () => {
                setIsOpen(false);
                onConfirm();
              },
              children: [
                /* @__PURE__ */ jsx("i", { className: "ri-check-line" }),
                " Confirm"
              ]
            }
          )
        ] })
      ] })
    ] }) })
  ] });
};
const ConfirmableButton = ({
  withConfirm = true,
  iconClass,
  isIconOnly,
  isLoading,
  label,
  color,
  variant,
  confirmColor,
  onPress,
  startContent,
  endContent,
  ...rest
}) => {
  const content = /* @__PURE__ */ jsxs(Fragment, { children: [
    isLoading ? /* @__PURE__ */ jsx(Spinner, { size: "sm", color: "current" }) : /* @__PURE__ */ jsxs(Fragment, { children: [
      startContent,
      iconClass && /* @__PURE__ */ jsx("i", { className: iconClass })
    ] }),
    !isIconOnly && /* @__PURE__ */ jsx("span", { className: "relative top-px", children: label }),
    endContent
  ] });
  return withConfirm ? /* @__PURE__ */ jsx(
    ConfirmButton,
    {
      color,
      variant,
      confirmColor,
      onConfirm: () => onPress && onPress(),
      isIconOnly,
      isPending: isLoading,
      ...rest,
      children: content
    }
  ) : /* @__PURE__ */ jsx(
    Button,
    {
      variant: toButtonVariant(color, variant),
      isPending: isLoading,
      ...rest,
      onPress: () => onPress && onPress(),
      children: content
    }
  );
};
const ArchiveButton = ({ ...rest }) => /* @__PURE__ */ jsx(
  ConfirmableButton,
  {
    variant: "danger-soft",
    iconClass: "ri-archive-line",
    label: "Archive",
    withConfirm: true,
    ...rest
  }
);
const CopyToClipboard = ({ text }) => {
  const [, copyToClipboard] = useCopyToClipboard();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timeout = setTimeout(() => {
      setCopied(false);
    }, 2e3);
    return () => clearTimeout(timeout);
  }, [copied]);
  const handleCopy = useCallback(() => {
    copyToClipboard(text);
    setCopied(true);
  }, [text, copyToClipboard]);
  return /* @__PURE__ */ jsxs(Tooltip, { isOpen: copied, children: [
    /* @__PURE__ */ jsx(Tooltip.Trigger, { children: /* @__PURE__ */ jsx(Link, { className: "cursor-pointer", onPress: handleCopy, children: /* @__PURE__ */ jsx("i", { className: "ri-file-copy-line" }) }) }),
    /* @__PURE__ */ jsx(Tooltip.Content, { placement: "top", children: "Copied!" })
  ] });
};
const MultiActionButton = ({
  selectedKey,
  options,
  onPress,
  dropdownProps = {},
  isLoading = false,
  triggerOnSelect = false,
  onSelectionChange,
  emptyContent = "",
  isIconOnly,
  icon,
  size,
  ...rest
}) => {
  const [selectedOption, setSelectedOption] = useState(() => /* @__PURE__ */ new Set([selectedKey]));
  useEffect(() => {
    setSelectedOption(/* @__PURE__ */ new Set([selectedKey]));
  }, [selectedKey]);
  const selectedOptionValue = Array.from(selectedOption)[0];
  return /* @__PURE__ */ jsxs(ButtonGroup, { ...rest, children: [
    isIconOnly ? /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
      /* @__PURE__ */ jsx(
        Button,
        {
          className: "rounded-e-none",
          size,
          isIconOnly: true,
          onPress: () => onPress && onPress(selectedOptionValue),
          isPending: isLoading,
          children: icon ?? (options[selectedOptionValue]?.iconClass && /* @__PURE__ */ jsx("i", { className: options[selectedOptionValue].iconClass }))
        }
      ),
      /* @__PURE__ */ jsx(Tooltip.Content, { placement: "end", children: options[selectedOptionValue]?.label ?? emptyContent })
    ] }) : /* @__PURE__ */ jsxs(
      Button,
      {
        size,
        onPress: () => onPress && onPress(selectedOptionValue),
        isPending: isLoading,
        children: [
          options[selectedOptionValue]?.iconClass && /* @__PURE__ */ jsx("i", { className: options[selectedOptionValue].iconClass }),
          /* @__PURE__ */ jsx("span", { className: "relative top-px", children: options[selectedOptionValue]?.label ?? emptyContent })
        ]
      }
    ),
    /* @__PURE__ */ jsxs(Dropdown, { children: [
      /* @__PURE__ */ jsxs(Button, { size, isIconOnly: true, children: [
        /* @__PURE__ */ jsx(ButtonGroup.Separator, {}),
        /* @__PURE__ */ jsx("i", { className: "ri-arrow-down-s-line" })
      ] }),
      /* @__PURE__ */ jsx(Dropdown.Popover, { placement: "bottom end", className: "max-w-75", children: /* @__PURE__ */ jsx(
        Dropdown.Menu,
        {
          disallowEmptySelection: true,
          selectionMode: "single",
          ...dropdownProps,
          onSelectionChange: (option) => {
            if (triggerOnSelect) {
              onPress && onPress(Array.from(option)[0]);
            }
            onSelectionChange && onSelectionChange(Array.from(option)[0]);
            setSelectedOption(option);
          },
          selectedKeys: selectedOption,
          children: Object.entries(options).map(([k2, v2]) => /* @__PURE__ */ jsxs(Dropdown.Item, { id: k2, textValue: v2.label || k2, children: [
            v2.iconClass && /* @__PURE__ */ jsx("i", { className: v2.iconClass }),
            /* @__PURE__ */ jsx(Label, { children: v2.label || "" }),
            v2.description && /* @__PURE__ */ jsx(Description, { children: v2.description })
          ] }, k2))
        }
      ) })
    ] })
  ] });
};
const DefaultLayoutContext = createContext();
const SidebarModeContext = createContext("rail");
const useSidebarMode = () => useContext(SidebarModeContext);
function useSidebarExpanded() {
  const { isOpen = true } = useContext(DefaultLayoutContext) ?? {};
  const mode = useSidebarMode();
  const md = useMediaQuery("(min-width: 768px)");
  return mode === "drawer" ? true : md ? isOpen : false;
}
function useDefaultLayoutContext(defaultLang) {
  const context = useContext(DefaultLayoutContext);
  const { setDefaults } = context;
  useEffect(() => {
    setDefaults(defaultLang);
  }, []);
  return context;
}
function DefaultLayoutContextProvider({ children }) {
  const [isOpen, setIsOpen] = useState(true);
  const [mobileIsOpen, setMobileIsOpen] = useState(false);
  const [lang, setLang] = useState();
  const setDefaults = useCallback(
    (defaultLang) => {
      defaultLang && setLang(defaultLang);
    },
    [setLang]
  );
  const context = useMemo(
    () => ({
      isOpen,
      setIsOpen,
      mobileIsOpen,
      setMobileIsOpen,
      lang,
      setLang,
      setDefaults
    }),
    [isOpen, mobileIsOpen, lang, setIsOpen, setMobileIsOpen, setLang, setDefaults]
  );
  return /* @__PURE__ */ jsx(DefaultLayoutContext.Provider, { value: context, children });
}
function withProvider(WrappedComponent) {
  function Wrapped(props) {
    return /* @__PURE__ */ jsx(DefaultLayoutContextProvider, { children: /* @__PURE__ */ jsx(WrappedComponent, { ...props }) });
  }
  return Wrapped;
}
const CreateItemButton = ({ baseUrl, items, handlers, wrapperClassName, ...rest }) => {
  const openCheck = useSidebarExpanded();
  const mapItems = (x3) => x3.map((item) => item.iconClass ? item : { ...item, iconClass: "ri-add-line" }).reduce((prev, next) => ({ ...prev, [next.key]: next }), {});
  const [options, setOptions] = useState(() => mapItems(items));
  useEffect(() => {
    setOptions(mapItems(items));
  }, [items]);
  const [selectedKey, setSelectedKey] = useState({});
  useEffect(() => {
    const route = window.location.pathname;
    let match = route;
    if (baseUrl !== "/" && route.indexOf(baseUrl) >= 0) {
      match = route.slice(baseUrl.length);
    }
    const selected = Object.values(options).find((x3) => match.startsWith(x3.route));
    if (selected) {
      setSelectedKey({ value: selected.key, ...selected });
    } else {
      setSelectedKey({});
    }
  }, [baseUrl, window.location.pathname, options]);
  return window.location.pathname.startsWith(baseUrl) && Object.keys(options).length > 0 && /* @__PURE__ */ jsx("div", { className: wrapperClassName, children: /* @__PURE__ */ jsx(
    MultiActionButton,
    {
      size: openCheck ? "md" : "sm",
      isIconOnly: !openCheck,
      icon: "+",
      color: "primary",
      options,
      fullWidth: true,
      triggerOnSelect: true,
      emptyContent: "Create New",
      ...rest,
      selectedKey: selectedKey.value,
      onSelectionChange: (key) => setSelectedKey({ value: key }),
      onPress: (itemKey) => {
        if (handlers && handlers[itemKey] && typeof handlers[itemKey] === "function") {
          handlers[itemKey]({ ...options[itemKey], ...selectedKey.props });
        }
      }
    }
  ) });
};
const DeleteButton = ({ ...rest }) => /* @__PURE__ */ jsx(
  ConfirmableButton,
  {
    variant: "danger",
    iconClass: "ri-delete-bin-line",
    label: "Delete",
    withConfirm: true,
    ...rest
  }
);
const ModifyButton = ({ ...rest }) => /* @__PURE__ */ jsx(
  ConfirmableButton,
  {
    variant: "primary",
    iconClass: "ri-pencil-line",
    label: "Edit",
    withConfirm: false,
    ...rest
  }
);
const NewButton = forwardRef(({ label = "New", isIconOnly, variant, ...rest }, ref) => /* @__PURE__ */ jsxs(Button, { variant: "secondary", className: "text-success", ref, isIconOnly, ...rest, children: [
  /* @__PURE__ */ jsx("i", { className: "ri-add-fill" }),
  " ",
  !isIconOnly && label
] }));
const ReloadButton = ({
  isLoading,
  isIconOnly,
  isDisabled = false,
  label = "Reload",
  color = "primary",
  variant = "secondary",
  ...rest
}) => {
  return /* @__PURE__ */ jsxs(
    Button,
    {
      size: "sm",
      variant: toButtonVariant(color, variant),
      isPending: isLoading,
      isIconOnly,
      ...rest,
      isDisabled: isLoading || isDisabled,
      children: [
        isLoading ? /* @__PURE__ */ jsx(Spinner, { size: "sm", color: "current" }) : /* @__PURE__ */ jsx("i", { className: "text-sm ri-loop-right-line" }),
        !isIconOnly && label
      ]
    }
  );
};
const SaveButton = ({ ...rest }) => /* @__PURE__ */ jsx(
  ConfirmableButton,
  {
    variant: "primary",
    iconClass: "ri-save-line",
    label: "Save",
    withConfirm: false,
    ...rest
  }
);
function getDefaultExportFromCjs(x3) {
  return x3 && x3.__esModule && Object.prototype.hasOwnProperty.call(x3, "default") ? x3["default"] : x3;
}
var propTypes = { exports: {} };
var reactIs = { exports: {} };
var reactIs_production_min = {};
/** @license React v16.13.1
 * react-is.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var hasRequiredReactIs_production_min;
function requireReactIs_production_min() {
  if (hasRequiredReactIs_production_min) return reactIs_production_min;
  hasRequiredReactIs_production_min = 1;
  var b2 = "function" === typeof Symbol && Symbol.for, c = b2 ? Symbol.for("react.element") : 60103, d = b2 ? Symbol.for("react.portal") : 60106, e = b2 ? Symbol.for("react.fragment") : 60107, f = b2 ? Symbol.for("react.strict_mode") : 60108, g = b2 ? Symbol.for("react.profiler") : 60114, h = b2 ? Symbol.for("react.provider") : 60109, k2 = b2 ? Symbol.for("react.context") : 60110, l = b2 ? Symbol.for("react.async_mode") : 60111, m2 = b2 ? Symbol.for("react.concurrent_mode") : 60111, n = b2 ? Symbol.for("react.forward_ref") : 60112, p = b2 ? Symbol.for("react.suspense") : 60113, q = b2 ? Symbol.for("react.suspense_list") : 60120, r2 = b2 ? Symbol.for("react.memo") : 60115, t = b2 ? Symbol.for("react.lazy") : 60116, v2 = b2 ? Symbol.for("react.block") : 60121, w = b2 ? Symbol.for("react.fundamental") : 60117, x3 = b2 ? Symbol.for("react.responder") : 60118, y = b2 ? Symbol.for("react.scope") : 60119;
  function z(a) {
    if ("object" === typeof a && null !== a) {
      var u2 = a.$$typeof;
      switch (u2) {
        case c:
          switch (a = a.type, a) {
            case l:
            case m2:
            case e:
            case g:
            case f:
            case p:
              return a;
            default:
              switch (a = a && a.$$typeof, a) {
                case k2:
                case n:
                case t:
                case r2:
                case h:
                  return a;
                default:
                  return u2;
              }
          }
        case d:
          return u2;
      }
    }
  }
  function A(a) {
    return z(a) === m2;
  }
  reactIs_production_min.AsyncMode = l;
  reactIs_production_min.ConcurrentMode = m2;
  reactIs_production_min.ContextConsumer = k2;
  reactIs_production_min.ContextProvider = h;
  reactIs_production_min.Element = c;
  reactIs_production_min.ForwardRef = n;
  reactIs_production_min.Fragment = e;
  reactIs_production_min.Lazy = t;
  reactIs_production_min.Memo = r2;
  reactIs_production_min.Portal = d;
  reactIs_production_min.Profiler = g;
  reactIs_production_min.StrictMode = f;
  reactIs_production_min.Suspense = p;
  reactIs_production_min.isAsyncMode = function(a) {
    return A(a) || z(a) === l;
  };
  reactIs_production_min.isConcurrentMode = A;
  reactIs_production_min.isContextConsumer = function(a) {
    return z(a) === k2;
  };
  reactIs_production_min.isContextProvider = function(a) {
    return z(a) === h;
  };
  reactIs_production_min.isElement = function(a) {
    return "object" === typeof a && null !== a && a.$$typeof === c;
  };
  reactIs_production_min.isForwardRef = function(a) {
    return z(a) === n;
  };
  reactIs_production_min.isFragment = function(a) {
    return z(a) === e;
  };
  reactIs_production_min.isLazy = function(a) {
    return z(a) === t;
  };
  reactIs_production_min.isMemo = function(a) {
    return z(a) === r2;
  };
  reactIs_production_min.isPortal = function(a) {
    return z(a) === d;
  };
  reactIs_production_min.isProfiler = function(a) {
    return z(a) === g;
  };
  reactIs_production_min.isStrictMode = function(a) {
    return z(a) === f;
  };
  reactIs_production_min.isSuspense = function(a) {
    return z(a) === p;
  };
  reactIs_production_min.isValidElementType = function(a) {
    return "string" === typeof a || "function" === typeof a || a === e || a === m2 || a === g || a === f || a === p || a === q || "object" === typeof a && null !== a && (a.$$typeof === t || a.$$typeof === r2 || a.$$typeof === h || a.$$typeof === k2 || a.$$typeof === n || a.$$typeof === w || a.$$typeof === x3 || a.$$typeof === y || a.$$typeof === v2);
  };
  reactIs_production_min.typeOf = z;
  return reactIs_production_min;
}
var reactIs_development = {};
/** @license React v16.13.1
 * react-is.development.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var hasRequiredReactIs_development;
function requireReactIs_development() {
  if (hasRequiredReactIs_development) return reactIs_development;
  hasRequiredReactIs_development = 1;
  if (process.env.NODE_ENV !== "production") {
    (function() {
      var hasSymbol = typeof Symbol === "function" && Symbol.for;
      var REACT_ELEMENT_TYPE = hasSymbol ? Symbol.for("react.element") : 60103;
      var REACT_PORTAL_TYPE = hasSymbol ? Symbol.for("react.portal") : 60106;
      var REACT_FRAGMENT_TYPE = hasSymbol ? Symbol.for("react.fragment") : 60107;
      var REACT_STRICT_MODE_TYPE = hasSymbol ? Symbol.for("react.strict_mode") : 60108;
      var REACT_PROFILER_TYPE = hasSymbol ? Symbol.for("react.profiler") : 60114;
      var REACT_PROVIDER_TYPE = hasSymbol ? Symbol.for("react.provider") : 60109;
      var REACT_CONTEXT_TYPE = hasSymbol ? Symbol.for("react.context") : 60110;
      var REACT_ASYNC_MODE_TYPE = hasSymbol ? Symbol.for("react.async_mode") : 60111;
      var REACT_CONCURRENT_MODE_TYPE = hasSymbol ? Symbol.for("react.concurrent_mode") : 60111;
      var REACT_FORWARD_REF_TYPE = hasSymbol ? Symbol.for("react.forward_ref") : 60112;
      var REACT_SUSPENSE_TYPE = hasSymbol ? Symbol.for("react.suspense") : 60113;
      var REACT_SUSPENSE_LIST_TYPE = hasSymbol ? Symbol.for("react.suspense_list") : 60120;
      var REACT_MEMO_TYPE = hasSymbol ? Symbol.for("react.memo") : 60115;
      var REACT_LAZY_TYPE = hasSymbol ? Symbol.for("react.lazy") : 60116;
      var REACT_BLOCK_TYPE = hasSymbol ? Symbol.for("react.block") : 60121;
      var REACT_FUNDAMENTAL_TYPE = hasSymbol ? Symbol.for("react.fundamental") : 60117;
      var REACT_RESPONDER_TYPE = hasSymbol ? Symbol.for("react.responder") : 60118;
      var REACT_SCOPE_TYPE = hasSymbol ? Symbol.for("react.scope") : 60119;
      function isValidElementType(type) {
        return typeof type === "string" || typeof type === "function" || // Note: its typeof might be other than 'symbol' or 'number' if it's a polyfill.
        type === REACT_FRAGMENT_TYPE || type === REACT_CONCURRENT_MODE_TYPE || type === REACT_PROFILER_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || typeof type === "object" && type !== null && (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_FUNDAMENTAL_TYPE || type.$$typeof === REACT_RESPONDER_TYPE || type.$$typeof === REACT_SCOPE_TYPE || type.$$typeof === REACT_BLOCK_TYPE);
      }
      function typeOf(object) {
        if (typeof object === "object" && object !== null) {
          var $$typeof = object.$$typeof;
          switch ($$typeof) {
            case REACT_ELEMENT_TYPE:
              var type = object.type;
              switch (type) {
                case REACT_ASYNC_MODE_TYPE:
                case REACT_CONCURRENT_MODE_TYPE:
                case REACT_FRAGMENT_TYPE:
                case REACT_PROFILER_TYPE:
                case REACT_STRICT_MODE_TYPE:
                case REACT_SUSPENSE_TYPE:
                  return type;
                default:
                  var $$typeofType = type && type.$$typeof;
                  switch ($$typeofType) {
                    case REACT_CONTEXT_TYPE:
                    case REACT_FORWARD_REF_TYPE:
                    case REACT_LAZY_TYPE:
                    case REACT_MEMO_TYPE:
                    case REACT_PROVIDER_TYPE:
                      return $$typeofType;
                    default:
                      return $$typeof;
                  }
              }
            case REACT_PORTAL_TYPE:
              return $$typeof;
          }
        }
        return void 0;
      }
      var AsyncMode = REACT_ASYNC_MODE_TYPE;
      var ConcurrentMode = REACT_CONCURRENT_MODE_TYPE;
      var ContextConsumer = REACT_CONTEXT_TYPE;
      var ContextProvider = REACT_PROVIDER_TYPE;
      var Element = REACT_ELEMENT_TYPE;
      var ForwardRef = REACT_FORWARD_REF_TYPE;
      var Fragment2 = REACT_FRAGMENT_TYPE;
      var Lazy = REACT_LAZY_TYPE;
      var Memo = REACT_MEMO_TYPE;
      var Portal = REACT_PORTAL_TYPE;
      var Profiler = REACT_PROFILER_TYPE;
      var StrictMode = REACT_STRICT_MODE_TYPE;
      var Suspense = REACT_SUSPENSE_TYPE;
      var hasWarnedAboutDeprecatedIsAsyncMode = false;
      function isAsyncMode(object) {
        {
          if (!hasWarnedAboutDeprecatedIsAsyncMode) {
            hasWarnedAboutDeprecatedIsAsyncMode = true;
            console["warn"]("The ReactIs.isAsyncMode() alias has been deprecated, and will be removed in React 17+. Update your code to use ReactIs.isConcurrentMode() instead. It has the exact same API.");
          }
        }
        return isConcurrentMode(object) || typeOf(object) === REACT_ASYNC_MODE_TYPE;
      }
      function isConcurrentMode(object) {
        return typeOf(object) === REACT_CONCURRENT_MODE_TYPE;
      }
      function isContextConsumer(object) {
        return typeOf(object) === REACT_CONTEXT_TYPE;
      }
      function isContextProvider(object) {
        return typeOf(object) === REACT_PROVIDER_TYPE;
      }
      function isElement(object) {
        return typeof object === "object" && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
      }
      function isForwardRef(object) {
        return typeOf(object) === REACT_FORWARD_REF_TYPE;
      }
      function isFragment(object) {
        return typeOf(object) === REACT_FRAGMENT_TYPE;
      }
      function isLazy(object) {
        return typeOf(object) === REACT_LAZY_TYPE;
      }
      function isMemo(object) {
        return typeOf(object) === REACT_MEMO_TYPE;
      }
      function isPortal(object) {
        return typeOf(object) === REACT_PORTAL_TYPE;
      }
      function isProfiler(object) {
        return typeOf(object) === REACT_PROFILER_TYPE;
      }
      function isStrictMode(object) {
        return typeOf(object) === REACT_STRICT_MODE_TYPE;
      }
      function isSuspense(object) {
        return typeOf(object) === REACT_SUSPENSE_TYPE;
      }
      reactIs_development.AsyncMode = AsyncMode;
      reactIs_development.ConcurrentMode = ConcurrentMode;
      reactIs_development.ContextConsumer = ContextConsumer;
      reactIs_development.ContextProvider = ContextProvider;
      reactIs_development.Element = Element;
      reactIs_development.ForwardRef = ForwardRef;
      reactIs_development.Fragment = Fragment2;
      reactIs_development.Lazy = Lazy;
      reactIs_development.Memo = Memo;
      reactIs_development.Portal = Portal;
      reactIs_development.Profiler = Profiler;
      reactIs_development.StrictMode = StrictMode;
      reactIs_development.Suspense = Suspense;
      reactIs_development.isAsyncMode = isAsyncMode;
      reactIs_development.isConcurrentMode = isConcurrentMode;
      reactIs_development.isContextConsumer = isContextConsumer;
      reactIs_development.isContextProvider = isContextProvider;
      reactIs_development.isElement = isElement;
      reactIs_development.isForwardRef = isForwardRef;
      reactIs_development.isFragment = isFragment;
      reactIs_development.isLazy = isLazy;
      reactIs_development.isMemo = isMemo;
      reactIs_development.isPortal = isPortal;
      reactIs_development.isProfiler = isProfiler;
      reactIs_development.isStrictMode = isStrictMode;
      reactIs_development.isSuspense = isSuspense;
      reactIs_development.isValidElementType = isValidElementType;
      reactIs_development.typeOf = typeOf;
    })();
  }
  return reactIs_development;
}
var hasRequiredReactIs;
function requireReactIs() {
  if (hasRequiredReactIs) return reactIs.exports;
  hasRequiredReactIs = 1;
  if (process.env.NODE_ENV === "production") {
    reactIs.exports = requireReactIs_production_min();
  } else {
    reactIs.exports = requireReactIs_development();
  }
  return reactIs.exports;
}
/*
object-assign
(c) Sindre Sorhus
@license MIT
*/
var objectAssign;
var hasRequiredObjectAssign;
function requireObjectAssign() {
  if (hasRequiredObjectAssign) return objectAssign;
  hasRequiredObjectAssign = 1;
  var getOwnPropertySymbols = Object.getOwnPropertySymbols;
  var hasOwnProperty = Object.prototype.hasOwnProperty;
  var propIsEnumerable = Object.prototype.propertyIsEnumerable;
  function toObject(val) {
    if (val === null || val === void 0) {
      throw new TypeError("Object.assign cannot be called with null or undefined");
    }
    return Object(val);
  }
  function shouldUseNative() {
    try {
      if (!Object.assign) {
        return false;
      }
      var test1 = new String("abc");
      test1[5] = "de";
      if (Object.getOwnPropertyNames(test1)[0] === "5") {
        return false;
      }
      var test2 = {};
      for (var i = 0; i < 10; i++) {
        test2["_" + String.fromCharCode(i)] = i;
      }
      var order2 = Object.getOwnPropertyNames(test2).map(function(n) {
        return test2[n];
      });
      if (order2.join("") !== "0123456789") {
        return false;
      }
      var test3 = {};
      "abcdefghijklmnopqrst".split("").forEach(function(letter) {
        test3[letter] = letter;
      });
      if (Object.keys(Object.assign({}, test3)).join("") !== "abcdefghijklmnopqrst") {
        return false;
      }
      return true;
    } catch (err) {
      return false;
    }
  }
  objectAssign = shouldUseNative() ? Object.assign : function(target, source) {
    var from;
    var to = toObject(target);
    var symbols;
    for (var s = 1; s < arguments.length; s++) {
      from = Object(arguments[s]);
      for (var key in from) {
        if (hasOwnProperty.call(from, key)) {
          to[key] = from[key];
        }
      }
      if (getOwnPropertySymbols) {
        symbols = getOwnPropertySymbols(from);
        for (var i = 0; i < symbols.length; i++) {
          if (propIsEnumerable.call(from, symbols[i])) {
            to[symbols[i]] = from[symbols[i]];
          }
        }
      }
    }
    return to;
  };
  return objectAssign;
}
var ReactPropTypesSecret_1;
var hasRequiredReactPropTypesSecret;
function requireReactPropTypesSecret() {
  if (hasRequiredReactPropTypesSecret) return ReactPropTypesSecret_1;
  hasRequiredReactPropTypesSecret = 1;
  var ReactPropTypesSecret = "SECRET_DO_NOT_PASS_THIS_OR_YOU_WILL_BE_FIRED";
  ReactPropTypesSecret_1 = ReactPropTypesSecret;
  return ReactPropTypesSecret_1;
}
var has;
var hasRequiredHas;
function requireHas() {
  if (hasRequiredHas) return has;
  hasRequiredHas = 1;
  has = Function.call.bind(Object.prototype.hasOwnProperty);
  return has;
}
var checkPropTypes_1;
var hasRequiredCheckPropTypes;
function requireCheckPropTypes() {
  if (hasRequiredCheckPropTypes) return checkPropTypes_1;
  hasRequiredCheckPropTypes = 1;
  var printWarning = function() {
  };
  if (process.env.NODE_ENV !== "production") {
    var ReactPropTypesSecret = /* @__PURE__ */ requireReactPropTypesSecret();
    var loggedTypeFailures = {};
    var has2 = /* @__PURE__ */ requireHas();
    printWarning = function(text) {
      var message = "Warning: " + text;
      if (typeof console !== "undefined") {
        console.error(message);
      }
      try {
        throw new Error(message);
      } catch (x3) {
      }
    };
  }
  function checkPropTypes(typeSpecs, values, location, componentName, getStack) {
    if (process.env.NODE_ENV !== "production") {
      for (var typeSpecName in typeSpecs) {
        if (has2(typeSpecs, typeSpecName)) {
          var error;
          try {
            if (typeof typeSpecs[typeSpecName] !== "function") {
              var err = Error(
                (componentName || "React class") + ": " + location + " type `" + typeSpecName + "` is invalid; it must be a function, usually from the `prop-types` package, but received `" + typeof typeSpecs[typeSpecName] + "`.This often happens because of typos such as `PropTypes.function` instead of `PropTypes.func`."
              );
              err.name = "Invariant Violation";
              throw err;
            }
            error = typeSpecs[typeSpecName](values, typeSpecName, componentName, location, null, ReactPropTypesSecret);
          } catch (ex) {
            error = ex;
          }
          if (error && !(error instanceof Error)) {
            printWarning(
              (componentName || "React class") + ": type specification of " + location + " `" + typeSpecName + "` is invalid; the type checker function must return `null` or an `Error` but returned a " + typeof error + ". You may have forgotten to pass an argument to the type checker creator (arrayOf, instanceOf, objectOf, oneOf, oneOfType, and shape all require an argument)."
            );
          }
          if (error instanceof Error && !(error.message in loggedTypeFailures)) {
            loggedTypeFailures[error.message] = true;
            var stack = getStack ? getStack() : "";
            printWarning(
              "Failed " + location + " type: " + error.message + (stack != null ? stack : "")
            );
          }
        }
      }
    }
  }
  checkPropTypes.resetWarningCache = function() {
    if (process.env.NODE_ENV !== "production") {
      loggedTypeFailures = {};
    }
  };
  checkPropTypes_1 = checkPropTypes;
  return checkPropTypes_1;
}
var factoryWithTypeCheckers;
var hasRequiredFactoryWithTypeCheckers;
function requireFactoryWithTypeCheckers() {
  if (hasRequiredFactoryWithTypeCheckers) return factoryWithTypeCheckers;
  hasRequiredFactoryWithTypeCheckers = 1;
  var ReactIs = requireReactIs();
  var assign = requireObjectAssign();
  var ReactPropTypesSecret = /* @__PURE__ */ requireReactPropTypesSecret();
  var has2 = /* @__PURE__ */ requireHas();
  var checkPropTypes = /* @__PURE__ */ requireCheckPropTypes();
  var printWarning = function() {
  };
  if (process.env.NODE_ENV !== "production") {
    printWarning = function(text) {
      var message = "Warning: " + text;
      if (typeof console !== "undefined") {
        console.error(message);
      }
      try {
        throw new Error(message);
      } catch (x3) {
      }
    };
  }
  function emptyFunctionThatReturnsNull() {
    return null;
  }
  factoryWithTypeCheckers = function(isValidElement, throwOnDirectAccess) {
    var ITERATOR_SYMBOL = typeof Symbol === "function" && Symbol.iterator;
    var FAUX_ITERATOR_SYMBOL = "@@iterator";
    function getIteratorFn(maybeIterable) {
      var iteratorFn = maybeIterable && (ITERATOR_SYMBOL && maybeIterable[ITERATOR_SYMBOL] || maybeIterable[FAUX_ITERATOR_SYMBOL]);
      if (typeof iteratorFn === "function") {
        return iteratorFn;
      }
    }
    var ANONYMOUS = "<<anonymous>>";
    var ReactPropTypes = {
      array: createPrimitiveTypeChecker("array"),
      bigint: createPrimitiveTypeChecker("bigint"),
      bool: createPrimitiveTypeChecker("boolean"),
      func: createPrimitiveTypeChecker("function"),
      number: createPrimitiveTypeChecker("number"),
      object: createPrimitiveTypeChecker("object"),
      string: createPrimitiveTypeChecker("string"),
      symbol: createPrimitiveTypeChecker("symbol"),
      any: createAnyTypeChecker(),
      arrayOf: createArrayOfTypeChecker,
      element: createElementTypeChecker(),
      elementType: createElementTypeTypeChecker(),
      instanceOf: createInstanceTypeChecker,
      node: createNodeChecker(),
      objectOf: createObjectOfTypeChecker,
      oneOf: createEnumTypeChecker,
      oneOfType: createUnionTypeChecker,
      shape: createShapeTypeChecker,
      exact: createStrictShapeTypeChecker
    };
    function is(x3, y) {
      if (x3 === y) {
        return x3 !== 0 || 1 / x3 === 1 / y;
      } else {
        return x3 !== x3 && y !== y;
      }
    }
    function PropTypeError(message, data) {
      this.message = message;
      this.data = data && typeof data === "object" ? data : {};
      this.stack = "";
    }
    PropTypeError.prototype = Error.prototype;
    function createChainableTypeChecker(validate) {
      if (process.env.NODE_ENV !== "production") {
        var manualPropTypeCallCache = {};
        var manualPropTypeWarningCount = 0;
      }
      function checkType(isRequired, props, propName, componentName, location, propFullName, secret) {
        componentName = componentName || ANONYMOUS;
        propFullName = propFullName || propName;
        if (secret !== ReactPropTypesSecret) {
          if (throwOnDirectAccess) {
            var err = new Error(
              "Calling PropTypes validators directly is not supported by the `prop-types` package. Use `PropTypes.checkPropTypes()` to call them. Read more at http://fb.me/use-check-prop-types"
            );
            err.name = "Invariant Violation";
            throw err;
          } else if (process.env.NODE_ENV !== "production" && typeof console !== "undefined") {
            var cacheKey = componentName + ":" + propName;
            if (!manualPropTypeCallCache[cacheKey] && // Avoid spamming the console because they are often not actionable except for lib authors
            manualPropTypeWarningCount < 3) {
              printWarning(
                "You are manually calling a React.PropTypes validation function for the `" + propFullName + "` prop on `" + componentName + "`. This is deprecated and will throw in the standalone `prop-types` package. You may be seeing this warning due to a third-party PropTypes library. See https://fb.me/react-warning-dont-call-proptypes for details."
              );
              manualPropTypeCallCache[cacheKey] = true;
              manualPropTypeWarningCount++;
            }
          }
        }
        if (props[propName] == null) {
          if (isRequired) {
            if (props[propName] === null) {
              return new PropTypeError("The " + location + " `" + propFullName + "` is marked as required " + ("in `" + componentName + "`, but its value is `null`."));
            }
            return new PropTypeError("The " + location + " `" + propFullName + "` is marked as required in " + ("`" + componentName + "`, but its value is `undefined`."));
          }
          return null;
        } else {
          return validate(props, propName, componentName, location, propFullName);
        }
      }
      var chainedCheckType = checkType.bind(null, false);
      chainedCheckType.isRequired = checkType.bind(null, true);
      return chainedCheckType;
    }
    function createPrimitiveTypeChecker(expectedType) {
      function validate(props, propName, componentName, location, propFullName, secret) {
        var propValue = props[propName];
        var propType = getPropType(propValue);
        if (propType !== expectedType) {
          var preciseType = getPreciseType(propValue);
          return new PropTypeError(
            "Invalid " + location + " `" + propFullName + "` of type " + ("`" + preciseType + "` supplied to `" + componentName + "`, expected ") + ("`" + expectedType + "`."),
            { expectedType }
          );
        }
        return null;
      }
      return createChainableTypeChecker(validate);
    }
    function createAnyTypeChecker() {
      return createChainableTypeChecker(emptyFunctionThatReturnsNull);
    }
    function createArrayOfTypeChecker(typeChecker) {
      function validate(props, propName, componentName, location, propFullName) {
        if (typeof typeChecker !== "function") {
          return new PropTypeError("Property `" + propFullName + "` of component `" + componentName + "` has invalid PropType notation inside arrayOf.");
        }
        var propValue = props[propName];
        if (!Array.isArray(propValue)) {
          var propType = getPropType(propValue);
          return new PropTypeError("Invalid " + location + " `" + propFullName + "` of type " + ("`" + propType + "` supplied to `" + componentName + "`, expected an array."));
        }
        for (var i = 0; i < propValue.length; i++) {
          var error = typeChecker(propValue, i, componentName, location, propFullName + "[" + i + "]", ReactPropTypesSecret);
          if (error instanceof Error) {
            return error;
          }
        }
        return null;
      }
      return createChainableTypeChecker(validate);
    }
    function createElementTypeChecker() {
      function validate(props, propName, componentName, location, propFullName) {
        var propValue = props[propName];
        if (!isValidElement(propValue)) {
          var propType = getPropType(propValue);
          return new PropTypeError("Invalid " + location + " `" + propFullName + "` of type " + ("`" + propType + "` supplied to `" + componentName + "`, expected a single ReactElement."));
        }
        return null;
      }
      return createChainableTypeChecker(validate);
    }
    function createElementTypeTypeChecker() {
      function validate(props, propName, componentName, location, propFullName) {
        var propValue = props[propName];
        if (!ReactIs.isValidElementType(propValue)) {
          var propType = getPropType(propValue);
          return new PropTypeError("Invalid " + location + " `" + propFullName + "` of type " + ("`" + propType + "` supplied to `" + componentName + "`, expected a single ReactElement type."));
        }
        return null;
      }
      return createChainableTypeChecker(validate);
    }
    function createInstanceTypeChecker(expectedClass) {
      function validate(props, propName, componentName, location, propFullName) {
        if (!(props[propName] instanceof expectedClass)) {
          var expectedClassName = expectedClass.name || ANONYMOUS;
          var actualClassName = getClassName(props[propName]);
          return new PropTypeError("Invalid " + location + " `" + propFullName + "` of type " + ("`" + actualClassName + "` supplied to `" + componentName + "`, expected ") + ("instance of `" + expectedClassName + "`."));
        }
        return null;
      }
      return createChainableTypeChecker(validate);
    }
    function createEnumTypeChecker(expectedValues) {
      if (!Array.isArray(expectedValues)) {
        if (process.env.NODE_ENV !== "production") {
          if (arguments.length > 1) {
            printWarning(
              "Invalid arguments supplied to oneOf, expected an array, got " + arguments.length + " arguments. A common mistake is to write oneOf(x, y, z) instead of oneOf([x, y, z])."
            );
          } else {
            printWarning("Invalid argument supplied to oneOf, expected an array.");
          }
        }
        return emptyFunctionThatReturnsNull;
      }
      function validate(props, propName, componentName, location, propFullName) {
        var propValue = props[propName];
        for (var i = 0; i < expectedValues.length; i++) {
          if (is(propValue, expectedValues[i])) {
            return null;
          }
        }
        var valuesString = JSON.stringify(expectedValues, function replacer(key, value) {
          var type = getPreciseType(value);
          if (type === "symbol") {
            return String(value);
          }
          return value;
        });
        return new PropTypeError("Invalid " + location + " `" + propFullName + "` of value `" + String(propValue) + "` " + ("supplied to `" + componentName + "`, expected one of " + valuesString + "."));
      }
      return createChainableTypeChecker(validate);
    }
    function createObjectOfTypeChecker(typeChecker) {
      function validate(props, propName, componentName, location, propFullName) {
        if (typeof typeChecker !== "function") {
          return new PropTypeError("Property `" + propFullName + "` of component `" + componentName + "` has invalid PropType notation inside objectOf.");
        }
        var propValue = props[propName];
        var propType = getPropType(propValue);
        if (propType !== "object") {
          return new PropTypeError("Invalid " + location + " `" + propFullName + "` of type " + ("`" + propType + "` supplied to `" + componentName + "`, expected an object."));
        }
        for (var key in propValue) {
          if (has2(propValue, key)) {
            var error = typeChecker(propValue, key, componentName, location, propFullName + "." + key, ReactPropTypesSecret);
            if (error instanceof Error) {
              return error;
            }
          }
        }
        return null;
      }
      return createChainableTypeChecker(validate);
    }
    function createUnionTypeChecker(arrayOfTypeCheckers) {
      if (!Array.isArray(arrayOfTypeCheckers)) {
        process.env.NODE_ENV !== "production" ? printWarning("Invalid argument supplied to oneOfType, expected an instance of array.") : void 0;
        return emptyFunctionThatReturnsNull;
      }
      for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
        var checker = arrayOfTypeCheckers[i];
        if (typeof checker !== "function") {
          printWarning(
            "Invalid argument supplied to oneOfType. Expected an array of check functions, but received " + getPostfixForTypeWarning(checker) + " at index " + i + "."
          );
          return emptyFunctionThatReturnsNull;
        }
      }
      function validate(props, propName, componentName, location, propFullName) {
        var expectedTypes = [];
        for (var i2 = 0; i2 < arrayOfTypeCheckers.length; i2++) {
          var checker2 = arrayOfTypeCheckers[i2];
          var checkerResult = checker2(props, propName, componentName, location, propFullName, ReactPropTypesSecret);
          if (checkerResult == null) {
            return null;
          }
          if (checkerResult.data && has2(checkerResult.data, "expectedType")) {
            expectedTypes.push(checkerResult.data.expectedType);
          }
        }
        var expectedTypesMessage = expectedTypes.length > 0 ? ", expected one of type [" + expectedTypes.join(", ") + "]" : "";
        return new PropTypeError("Invalid " + location + " `" + propFullName + "` supplied to " + ("`" + componentName + "`" + expectedTypesMessage + "."));
      }
      return createChainableTypeChecker(validate);
    }
    function createNodeChecker() {
      function validate(props, propName, componentName, location, propFullName) {
        if (!isNode(props[propName])) {
          return new PropTypeError("Invalid " + location + " `" + propFullName + "` supplied to " + ("`" + componentName + "`, expected a ReactNode."));
        }
        return null;
      }
      return createChainableTypeChecker(validate);
    }
    function invalidValidatorError(componentName, location, propFullName, key, type) {
      return new PropTypeError(
        (componentName || "React class") + ": " + location + " type `" + propFullName + "." + key + "` is invalid; it must be a function, usually from the `prop-types` package, but received `" + type + "`."
      );
    }
    function createShapeTypeChecker(shapeTypes) {
      function validate(props, propName, componentName, location, propFullName) {
        var propValue = props[propName];
        var propType = getPropType(propValue);
        if (propType !== "object") {
          return new PropTypeError("Invalid " + location + " `" + propFullName + "` of type `" + propType + "` " + ("supplied to `" + componentName + "`, expected `object`."));
        }
        for (var key in shapeTypes) {
          var checker = shapeTypes[key];
          if (typeof checker !== "function") {
            return invalidValidatorError(componentName, location, propFullName, key, getPreciseType(checker));
          }
          var error = checker(propValue, key, componentName, location, propFullName + "." + key, ReactPropTypesSecret);
          if (error) {
            return error;
          }
        }
        return null;
      }
      return createChainableTypeChecker(validate);
    }
    function createStrictShapeTypeChecker(shapeTypes) {
      function validate(props, propName, componentName, location, propFullName) {
        var propValue = props[propName];
        var propType = getPropType(propValue);
        if (propType !== "object") {
          return new PropTypeError("Invalid " + location + " `" + propFullName + "` of type `" + propType + "` " + ("supplied to `" + componentName + "`, expected `object`."));
        }
        var allKeys = assign({}, props[propName], shapeTypes);
        for (var key in allKeys) {
          var checker = shapeTypes[key];
          if (has2(shapeTypes, key) && typeof checker !== "function") {
            return invalidValidatorError(componentName, location, propFullName, key, getPreciseType(checker));
          }
          if (!checker) {
            return new PropTypeError(
              "Invalid " + location + " `" + propFullName + "` key `" + key + "` supplied to `" + componentName + "`.\nBad object: " + JSON.stringify(props[propName], null, "  ") + "\nValid keys: " + JSON.stringify(Object.keys(shapeTypes), null, "  ")
            );
          }
          var error = checker(propValue, key, componentName, location, propFullName + "." + key, ReactPropTypesSecret);
          if (error) {
            return error;
          }
        }
        return null;
      }
      return createChainableTypeChecker(validate);
    }
    function isNode(propValue) {
      switch (typeof propValue) {
        case "number":
        case "string":
        case "undefined":
          return true;
        case "boolean":
          return !propValue;
        case "object":
          if (Array.isArray(propValue)) {
            return propValue.every(isNode);
          }
          if (propValue === null || isValidElement(propValue)) {
            return true;
          }
          var iteratorFn = getIteratorFn(propValue);
          if (iteratorFn) {
            var iterator = iteratorFn.call(propValue);
            var step;
            if (iteratorFn !== propValue.entries) {
              while (!(step = iterator.next()).done) {
                if (!isNode(step.value)) {
                  return false;
                }
              }
            } else {
              while (!(step = iterator.next()).done) {
                var entry = step.value;
                if (entry) {
                  if (!isNode(entry[1])) {
                    return false;
                  }
                }
              }
            }
          } else {
            return false;
          }
          return true;
        default:
          return false;
      }
    }
    function isSymbol(propType, propValue) {
      if (propType === "symbol") {
        return true;
      }
      if (!propValue) {
        return false;
      }
      if (propValue["@@toStringTag"] === "Symbol") {
        return true;
      }
      if (typeof Symbol === "function" && propValue instanceof Symbol) {
        return true;
      }
      return false;
    }
    function getPropType(propValue) {
      var propType = typeof propValue;
      if (Array.isArray(propValue)) {
        return "array";
      }
      if (propValue instanceof RegExp) {
        return "object";
      }
      if (isSymbol(propType, propValue)) {
        return "symbol";
      }
      return propType;
    }
    function getPreciseType(propValue) {
      if (typeof propValue === "undefined" || propValue === null) {
        return "" + propValue;
      }
      var propType = getPropType(propValue);
      if (propType === "object") {
        if (propValue instanceof Date) {
          return "date";
        } else if (propValue instanceof RegExp) {
          return "regexp";
        }
      }
      return propType;
    }
    function getPostfixForTypeWarning(value) {
      var type = getPreciseType(value);
      switch (type) {
        case "array":
        case "object":
          return "an " + type;
        case "boolean":
        case "date":
        case "regexp":
          return "a " + type;
        default:
          return type;
      }
    }
    function getClassName(propValue) {
      if (!propValue.constructor || !propValue.constructor.name) {
        return ANONYMOUS;
      }
      return propValue.constructor.name;
    }
    ReactPropTypes.checkPropTypes = checkPropTypes;
    ReactPropTypes.resetWarningCache = checkPropTypes.resetWarningCache;
    ReactPropTypes.PropTypes = ReactPropTypes;
    return ReactPropTypes;
  };
  return factoryWithTypeCheckers;
}
var factoryWithThrowingShims;
var hasRequiredFactoryWithThrowingShims;
function requireFactoryWithThrowingShims() {
  if (hasRequiredFactoryWithThrowingShims) return factoryWithThrowingShims;
  hasRequiredFactoryWithThrowingShims = 1;
  var ReactPropTypesSecret = /* @__PURE__ */ requireReactPropTypesSecret();
  function emptyFunction() {
  }
  function emptyFunctionWithReset() {
  }
  emptyFunctionWithReset.resetWarningCache = emptyFunction;
  factoryWithThrowingShims = function() {
    function shim(props, propName, componentName, location, propFullName, secret) {
      if (secret === ReactPropTypesSecret) {
        return;
      }
      var err = new Error(
        "Calling PropTypes validators directly is not supported by the `prop-types` package. Use PropTypes.checkPropTypes() to call them. Read more at http://fb.me/use-check-prop-types"
      );
      err.name = "Invariant Violation";
      throw err;
    }
    shim.isRequired = shim;
    function getShim() {
      return shim;
    }
    var ReactPropTypes = {
      array: shim,
      bigint: shim,
      bool: shim,
      func: shim,
      number: shim,
      object: shim,
      string: shim,
      symbol: shim,
      any: shim,
      arrayOf: getShim,
      element: shim,
      elementType: shim,
      instanceOf: getShim,
      node: shim,
      objectOf: getShim,
      oneOf: getShim,
      oneOfType: getShim,
      shape: getShim,
      exact: getShim,
      checkPropTypes: emptyFunctionWithReset,
      resetWarningCache: emptyFunction
    };
    ReactPropTypes.PropTypes = ReactPropTypes;
    return ReactPropTypes;
  };
  return factoryWithThrowingShims;
}
var hasRequiredPropTypes;
function requirePropTypes() {
  if (hasRequiredPropTypes) return propTypes.exports;
  hasRequiredPropTypes = 1;
  if (process.env.NODE_ENV !== "production") {
    var ReactIs = requireReactIs();
    var throwOnDirectAccess = true;
    propTypes.exports = /* @__PURE__ */ requireFactoryWithTypeCheckers()(ReactIs.isElement, throwOnDirectAccess);
  } else {
    propTypes.exports = /* @__PURE__ */ requireFactoryWithThrowingShims()();
  }
  return propTypes.exports;
}
var propTypesExports = /* @__PURE__ */ requirePropTypes();
const PropTypes = /* @__PURE__ */ getDefaultExportFromCjs(propTypesExports);
function __awaiter(thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function(resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function(resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}
typeof SuppressedError === "function" ? SuppressedError : function(error, suppressed, message) {
  var e = new Error(message);
  return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};
const COMMON_MIME_TYPES = /* @__PURE__ */ new Map([
  // https://github.com/guzzle/psr7/blob/2d9260799e713f1c475d3c5fdc3d6561ff7441b2/src/MimeType.php
  ["1km", "application/vnd.1000minds.decision-model+xml"],
  ["3dml", "text/vnd.in3d.3dml"],
  ["3ds", "image/x-3ds"],
  ["3g2", "video/3gpp2"],
  ["3gp", "video/3gp"],
  ["3gpp", "video/3gpp"],
  ["3mf", "model/3mf"],
  ["7z", "application/x-7z-compressed"],
  ["7zip", "application/x-7z-compressed"],
  ["123", "application/vnd.lotus-1-2-3"],
  ["aab", "application/x-authorware-bin"],
  ["aac", "audio/x-acc"],
  ["aam", "application/x-authorware-map"],
  ["aas", "application/x-authorware-seg"],
  ["abw", "application/x-abiword"],
  ["ac", "application/vnd.nokia.n-gage.ac+xml"],
  ["ac3", "audio/ac3"],
  ["acc", "application/vnd.americandynamics.acc"],
  ["ace", "application/x-ace-compressed"],
  ["acu", "application/vnd.acucobol"],
  ["acutc", "application/vnd.acucorp"],
  ["adp", "audio/adpcm"],
  ["aep", "application/vnd.audiograph"],
  ["afm", "application/x-font-type1"],
  ["afp", "application/vnd.ibm.modcap"],
  ["ahead", "application/vnd.ahead.space"],
  ["ai", "application/pdf"],
  ["aif", "audio/x-aiff"],
  ["aifc", "audio/x-aiff"],
  ["aiff", "audio/x-aiff"],
  ["air", "application/vnd.adobe.air-application-installer-package+zip"],
  ["ait", "application/vnd.dvb.ait"],
  ["ami", "application/vnd.amiga.ami"],
  ["amr", "audio/amr"],
  ["apk", "application/vnd.android.package-archive"],
  ["apng", "image/apng"],
  ["appcache", "text/cache-manifest"],
  ["application", "application/x-ms-application"],
  ["apr", "application/vnd.lotus-approach"],
  ["arc", "application/x-freearc"],
  ["arj", "application/x-arj"],
  ["asc", "application/pgp-signature"],
  ["asf", "video/x-ms-asf"],
  ["asm", "text/x-asm"],
  ["aso", "application/vnd.accpac.simply.aso"],
  ["asx", "video/x-ms-asf"],
  ["atc", "application/vnd.acucorp"],
  ["atom", "application/atom+xml"],
  ["atomcat", "application/atomcat+xml"],
  ["atomdeleted", "application/atomdeleted+xml"],
  ["atomsvc", "application/atomsvc+xml"],
  ["atx", "application/vnd.antix.game-component"],
  ["au", "audio/x-au"],
  ["avi", "video/x-msvideo"],
  ["avif", "image/avif"],
  ["aw", "application/applixware"],
  ["azf", "application/vnd.airzip.filesecure.azf"],
  ["azs", "application/vnd.airzip.filesecure.azs"],
  ["azv", "image/vnd.airzip.accelerator.azv"],
  ["azw", "application/vnd.amazon.ebook"],
  ["b16", "image/vnd.pco.b16"],
  ["bat", "application/x-msdownload"],
  ["bcpio", "application/x-bcpio"],
  ["bdf", "application/x-font-bdf"],
  ["bdm", "application/vnd.syncml.dm+wbxml"],
  ["bdoc", "application/x-bdoc"],
  ["bed", "application/vnd.realvnc.bed"],
  ["bh2", "application/vnd.fujitsu.oasysprs"],
  ["bin", "application/octet-stream"],
  ["blb", "application/x-blorb"],
  ["blorb", "application/x-blorb"],
  ["bmi", "application/vnd.bmi"],
  ["bmml", "application/vnd.balsamiq.bmml+xml"],
  ["bmp", "image/bmp"],
  ["book", "application/vnd.framemaker"],
  ["box", "application/vnd.previewsystems.box"],
  ["boz", "application/x-bzip2"],
  ["bpk", "application/octet-stream"],
  ["bpmn", "application/octet-stream"],
  ["bsp", "model/vnd.valve.source.compiled-map"],
  ["btif", "image/prs.btif"],
  ["buffer", "application/octet-stream"],
  ["bz", "application/x-bzip"],
  ["bz2", "application/x-bzip2"],
  ["c", "text/x-c"],
  ["c4d", "application/vnd.clonk.c4group"],
  ["c4f", "application/vnd.clonk.c4group"],
  ["c4g", "application/vnd.clonk.c4group"],
  ["c4p", "application/vnd.clonk.c4group"],
  ["c4u", "application/vnd.clonk.c4group"],
  ["c11amc", "application/vnd.cluetrust.cartomobile-config"],
  ["c11amz", "application/vnd.cluetrust.cartomobile-config-pkg"],
  ["cab", "application/vnd.ms-cab-compressed"],
  ["caf", "audio/x-caf"],
  ["cap", "application/vnd.tcpdump.pcap"],
  ["car", "application/vnd.curl.car"],
  ["cat", "application/vnd.ms-pki.seccat"],
  ["cb7", "application/x-cbr"],
  ["cba", "application/x-cbr"],
  ["cbr", "application/x-cbr"],
  ["cbt", "application/x-cbr"],
  ["cbz", "application/x-cbr"],
  ["cc", "text/x-c"],
  ["cco", "application/x-cocoa"],
  ["cct", "application/x-director"],
  ["ccxml", "application/ccxml+xml"],
  ["cdbcmsg", "application/vnd.contact.cmsg"],
  ["cda", "application/x-cdf"],
  ["cdf", "application/x-netcdf"],
  ["cdfx", "application/cdfx+xml"],
  ["cdkey", "application/vnd.mediastation.cdkey"],
  ["cdmia", "application/cdmi-capability"],
  ["cdmic", "application/cdmi-container"],
  ["cdmid", "application/cdmi-domain"],
  ["cdmio", "application/cdmi-object"],
  ["cdmiq", "application/cdmi-queue"],
  ["cdr", "application/cdr"],
  ["cdx", "chemical/x-cdx"],
  ["cdxml", "application/vnd.chemdraw+xml"],
  ["cdy", "application/vnd.cinderella"],
  ["cer", "application/pkix-cert"],
  ["cfs", "application/x-cfs-compressed"],
  ["cgm", "image/cgm"],
  ["chat", "application/x-chat"],
  ["chm", "application/vnd.ms-htmlhelp"],
  ["chrt", "application/vnd.kde.kchart"],
  ["cif", "chemical/x-cif"],
  ["cii", "application/vnd.anser-web-certificate-issue-initiation"],
  ["cil", "application/vnd.ms-artgalry"],
  ["cjs", "application/node"],
  ["cla", "application/vnd.claymore"],
  ["class", "application/octet-stream"],
  ["clkk", "application/vnd.crick.clicker.keyboard"],
  ["clkp", "application/vnd.crick.clicker.palette"],
  ["clkt", "application/vnd.crick.clicker.template"],
  ["clkw", "application/vnd.crick.clicker.wordbank"],
  ["clkx", "application/vnd.crick.clicker"],
  ["clp", "application/x-msclip"],
  ["cmc", "application/vnd.cosmocaller"],
  ["cmdf", "chemical/x-cmdf"],
  ["cml", "chemical/x-cml"],
  ["cmp", "application/vnd.yellowriver-custom-menu"],
  ["cmx", "image/x-cmx"],
  ["cod", "application/vnd.rim.cod"],
  ["coffee", "text/coffeescript"],
  ["com", "application/x-msdownload"],
  ["conf", "text/plain"],
  ["cpio", "application/x-cpio"],
  ["cpp", "text/x-c"],
  ["cpt", "application/mac-compactpro"],
  ["crd", "application/x-mscardfile"],
  ["crl", "application/pkix-crl"],
  ["crt", "application/x-x509-ca-cert"],
  ["crx", "application/x-chrome-extension"],
  ["cryptonote", "application/vnd.rig.cryptonote"],
  ["csh", "application/x-csh"],
  ["csl", "application/vnd.citationstyles.style+xml"],
  ["csml", "chemical/x-csml"],
  ["csp", "application/vnd.commonspace"],
  ["csr", "application/octet-stream"],
  ["css", "text/css"],
  ["cst", "application/x-director"],
  ["csv", "text/csv"],
  ["cu", "application/cu-seeme"],
  ["curl", "text/vnd.curl"],
  ["cww", "application/prs.cww"],
  ["cxt", "application/x-director"],
  ["cxx", "text/x-c"],
  ["dae", "model/vnd.collada+xml"],
  ["daf", "application/vnd.mobius.daf"],
  ["dart", "application/vnd.dart"],
  ["dataless", "application/vnd.fdsn.seed"],
  ["davmount", "application/davmount+xml"],
  ["dbf", "application/vnd.dbf"],
  ["dbk", "application/docbook+xml"],
  ["dcr", "application/x-director"],
  ["dcurl", "text/vnd.curl.dcurl"],
  ["dd2", "application/vnd.oma.dd2+xml"],
  ["ddd", "application/vnd.fujixerox.ddd"],
  ["ddf", "application/vnd.syncml.dmddf+xml"],
  ["dds", "image/vnd.ms-dds"],
  ["deb", "application/x-debian-package"],
  ["def", "text/plain"],
  ["deploy", "application/octet-stream"],
  ["der", "application/x-x509-ca-cert"],
  ["dfac", "application/vnd.dreamfactory"],
  ["dgc", "application/x-dgc-compressed"],
  ["dic", "text/x-c"],
  ["dir", "application/x-director"],
  ["dis", "application/vnd.mobius.dis"],
  ["disposition-notification", "message/disposition-notification"],
  ["dist", "application/octet-stream"],
  ["distz", "application/octet-stream"],
  ["djv", "image/vnd.djvu"],
  ["djvu", "image/vnd.djvu"],
  ["dll", "application/octet-stream"],
  ["dmg", "application/x-apple-diskimage"],
  ["dmn", "application/octet-stream"],
  ["dmp", "application/vnd.tcpdump.pcap"],
  ["dms", "application/octet-stream"],
  ["dna", "application/vnd.dna"],
  ["doc", "application/msword"],
  ["docm", "application/vnd.ms-word.template.macroEnabled.12"],
  ["docx", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
  ["dot", "application/msword"],
  ["dotm", "application/vnd.ms-word.template.macroEnabled.12"],
  ["dotx", "application/vnd.openxmlformats-officedocument.wordprocessingml.template"],
  ["dp", "application/vnd.osgi.dp"],
  ["dpg", "application/vnd.dpgraph"],
  ["dra", "audio/vnd.dra"],
  ["drle", "image/dicom-rle"],
  ["dsc", "text/prs.lines.tag"],
  ["dssc", "application/dssc+der"],
  ["dtb", "application/x-dtbook+xml"],
  ["dtd", "application/xml-dtd"],
  ["dts", "audio/vnd.dts"],
  ["dtshd", "audio/vnd.dts.hd"],
  ["dump", "application/octet-stream"],
  ["dvb", "video/vnd.dvb.file"],
  ["dvi", "application/x-dvi"],
  ["dwd", "application/atsc-dwd+xml"],
  ["dwf", "model/vnd.dwf"],
  ["dwg", "image/vnd.dwg"],
  ["dxf", "image/vnd.dxf"],
  ["dxp", "application/vnd.spotfire.dxp"],
  ["dxr", "application/x-director"],
  ["ear", "application/java-archive"],
  ["ecelp4800", "audio/vnd.nuera.ecelp4800"],
  ["ecelp7470", "audio/vnd.nuera.ecelp7470"],
  ["ecelp9600", "audio/vnd.nuera.ecelp9600"],
  ["ecma", "application/ecmascript"],
  ["edm", "application/vnd.novadigm.edm"],
  ["edx", "application/vnd.novadigm.edx"],
  ["efif", "application/vnd.picsel"],
  ["ei6", "application/vnd.pg.osasli"],
  ["elc", "application/octet-stream"],
  ["emf", "image/emf"],
  ["eml", "message/rfc822"],
  ["emma", "application/emma+xml"],
  ["emotionml", "application/emotionml+xml"],
  ["emz", "application/x-msmetafile"],
  ["eol", "audio/vnd.digital-winds"],
  ["eot", "application/vnd.ms-fontobject"],
  ["eps", "application/postscript"],
  ["epub", "application/epub+zip"],
  ["es", "application/ecmascript"],
  ["es3", "application/vnd.eszigno3+xml"],
  ["esa", "application/vnd.osgi.subsystem"],
  ["esf", "application/vnd.epson.esf"],
  ["et3", "application/vnd.eszigno3+xml"],
  ["etx", "text/x-setext"],
  ["eva", "application/x-eva"],
  ["evy", "application/x-envoy"],
  ["exe", "application/octet-stream"],
  ["exi", "application/exi"],
  ["exp", "application/express"],
  ["exr", "image/aces"],
  ["ext", "application/vnd.novadigm.ext"],
  ["ez", "application/andrew-inset"],
  ["ez2", "application/vnd.ezpix-album"],
  ["ez3", "application/vnd.ezpix-package"],
  ["f", "text/x-fortran"],
  ["f4v", "video/mp4"],
  ["f77", "text/x-fortran"],
  ["f90", "text/x-fortran"],
  ["fbs", "image/vnd.fastbidsheet"],
  ["fcdt", "application/vnd.adobe.formscentral.fcdt"],
  ["fcs", "application/vnd.isac.fcs"],
  ["fdf", "application/vnd.fdf"],
  ["fdt", "application/fdt+xml"],
  ["fe_launch", "application/vnd.denovo.fcselayout-link"],
  ["fg5", "application/vnd.fujitsu.oasysgp"],
  ["fgd", "application/x-director"],
  ["fh", "image/x-freehand"],
  ["fh4", "image/x-freehand"],
  ["fh5", "image/x-freehand"],
  ["fh7", "image/x-freehand"],
  ["fhc", "image/x-freehand"],
  ["fig", "application/x-xfig"],
  ["fits", "image/fits"],
  ["flac", "audio/x-flac"],
  ["fli", "video/x-fli"],
  ["flo", "application/vnd.micrografx.flo"],
  ["flv", "video/x-flv"],
  ["flw", "application/vnd.kde.kivio"],
  ["flx", "text/vnd.fmi.flexstor"],
  ["fly", "text/vnd.fly"],
  ["fm", "application/vnd.framemaker"],
  ["fnc", "application/vnd.frogans.fnc"],
  ["fo", "application/vnd.software602.filler.form+xml"],
  ["for", "text/x-fortran"],
  ["fpx", "image/vnd.fpx"],
  ["frame", "application/vnd.framemaker"],
  ["fsc", "application/vnd.fsc.weblaunch"],
  ["fst", "image/vnd.fst"],
  ["ftc", "application/vnd.fluxtime.clip"],
  ["fti", "application/vnd.anser-web-funds-transfer-initiation"],
  ["fvt", "video/vnd.fvt"],
  ["fxp", "application/vnd.adobe.fxp"],
  ["fxpl", "application/vnd.adobe.fxp"],
  ["fzs", "application/vnd.fuzzysheet"],
  ["g2w", "application/vnd.geoplan"],
  ["g3", "image/g3fax"],
  ["g3w", "application/vnd.geospace"],
  ["gac", "application/vnd.groove-account"],
  ["gam", "application/x-tads"],
  ["gbr", "application/rpki-ghostbusters"],
  ["gca", "application/x-gca-compressed"],
  ["gdl", "model/vnd.gdl"],
  ["gdoc", "application/vnd.google-apps.document"],
  ["geo", "application/vnd.dynageo"],
  ["geojson", "application/geo+json"],
  ["gex", "application/vnd.geometry-explorer"],
  ["ggb", "application/vnd.geogebra.file"],
  ["ggt", "application/vnd.geogebra.tool"],
  ["ghf", "application/vnd.groove-help"],
  ["gif", "image/gif"],
  ["gim", "application/vnd.groove-identity-message"],
  ["glb", "model/gltf-binary"],
  ["gltf", "model/gltf+json"],
  ["gml", "application/gml+xml"],
  ["gmx", "application/vnd.gmx"],
  ["gnumeric", "application/x-gnumeric"],
  ["gpg", "application/gpg-keys"],
  ["gph", "application/vnd.flographit"],
  ["gpx", "application/gpx+xml"],
  ["gqf", "application/vnd.grafeq"],
  ["gqs", "application/vnd.grafeq"],
  ["gram", "application/srgs"],
  ["gramps", "application/x-gramps-xml"],
  ["gre", "application/vnd.geometry-explorer"],
  ["grv", "application/vnd.groove-injector"],
  ["grxml", "application/srgs+xml"],
  ["gsf", "application/x-font-ghostscript"],
  ["gsheet", "application/vnd.google-apps.spreadsheet"],
  ["gslides", "application/vnd.google-apps.presentation"],
  ["gtar", "application/x-gtar"],
  ["gtm", "application/vnd.groove-tool-message"],
  ["gtw", "model/vnd.gtw"],
  ["gv", "text/vnd.graphviz"],
  ["gxf", "application/gxf"],
  ["gxt", "application/vnd.geonext"],
  ["gz", "application/gzip"],
  ["gzip", "application/gzip"],
  ["h", "text/x-c"],
  ["h261", "video/h261"],
  ["h263", "video/h263"],
  ["h264", "video/h264"],
  ["hal", "application/vnd.hal+xml"],
  ["hbci", "application/vnd.hbci"],
  ["hbs", "text/x-handlebars-template"],
  ["hdd", "application/x-virtualbox-hdd"],
  ["hdf", "application/x-hdf"],
  ["heic", "image/heic"],
  ["heics", "image/heic-sequence"],
  ["heif", "image/heif"],
  ["heifs", "image/heif-sequence"],
  ["hej2", "image/hej2k"],
  ["held", "application/atsc-held+xml"],
  ["hh", "text/x-c"],
  ["hjson", "application/hjson"],
  ["hlp", "application/winhlp"],
  ["hpgl", "application/vnd.hp-hpgl"],
  ["hpid", "application/vnd.hp-hpid"],
  ["hps", "application/vnd.hp-hps"],
  ["hqx", "application/mac-binhex40"],
  ["hsj2", "image/hsj2"],
  ["htc", "text/x-component"],
  ["htke", "application/vnd.kenameaapp"],
  ["htm", "text/html"],
  ["html", "text/html"],
  ["hvd", "application/vnd.yamaha.hv-dic"],
  ["hvp", "application/vnd.yamaha.hv-voice"],
  ["hvs", "application/vnd.yamaha.hv-script"],
  ["i2g", "application/vnd.intergeo"],
  ["icc", "application/vnd.iccprofile"],
  ["ice", "x-conference/x-cooltalk"],
  ["icm", "application/vnd.iccprofile"],
  ["ico", "image/x-icon"],
  ["ics", "text/calendar"],
  ["ief", "image/ief"],
  ["ifb", "text/calendar"],
  ["ifm", "application/vnd.shana.informed.formdata"],
  ["iges", "model/iges"],
  ["igl", "application/vnd.igloader"],
  ["igm", "application/vnd.insors.igm"],
  ["igs", "model/iges"],
  ["igx", "application/vnd.micrografx.igx"],
  ["iif", "application/vnd.shana.informed.interchange"],
  ["img", "application/octet-stream"],
  ["imp", "application/vnd.accpac.simply.imp"],
  ["ims", "application/vnd.ms-ims"],
  ["in", "text/plain"],
  ["ini", "text/plain"],
  ["ink", "application/inkml+xml"],
  ["inkml", "application/inkml+xml"],
  ["install", "application/x-install-instructions"],
  ["iota", "application/vnd.astraea-software.iota"],
  ["ipfix", "application/ipfix"],
  ["ipk", "application/vnd.shana.informed.package"],
  ["irm", "application/vnd.ibm.rights-management"],
  ["irp", "application/vnd.irepository.package+xml"],
  ["iso", "application/x-iso9660-image"],
  ["itp", "application/vnd.shana.informed.formtemplate"],
  ["its", "application/its+xml"],
  ["ivp", "application/vnd.immervision-ivp"],
  ["ivu", "application/vnd.immervision-ivu"],
  ["jad", "text/vnd.sun.j2me.app-descriptor"],
  ["jade", "text/jade"],
  ["jam", "application/vnd.jam"],
  ["jar", "application/java-archive"],
  ["jardiff", "application/x-java-archive-diff"],
  ["java", "text/x-java-source"],
  ["jhc", "image/jphc"],
  ["jisp", "application/vnd.jisp"],
  ["jls", "image/jls"],
  ["jlt", "application/vnd.hp-jlyt"],
  ["jng", "image/x-jng"],
  ["jnlp", "application/x-java-jnlp-file"],
  ["joda", "application/vnd.joost.joda-archive"],
  ["jp2", "image/jp2"],
  ["jpe", "image/jpeg"],
  ["jpeg", "image/jpeg"],
  ["jpf", "image/jpx"],
  ["jpg", "image/jpeg"],
  ["jpg2", "image/jp2"],
  ["jpgm", "video/jpm"],
  ["jpgv", "video/jpeg"],
  ["jph", "image/jph"],
  ["jpm", "video/jpm"],
  ["jpx", "image/jpx"],
  ["js", "application/javascript"],
  ["json", "application/json"],
  ["json5", "application/json5"],
  ["jsonld", "application/ld+json"],
  // https://jsonlines.org/
  ["jsonl", "application/jsonl"],
  ["jsonml", "application/jsonml+json"],
  ["jsx", "text/jsx"],
  ["jxr", "image/jxr"],
  ["jxra", "image/jxra"],
  ["jxrs", "image/jxrs"],
  ["jxs", "image/jxs"],
  ["jxsc", "image/jxsc"],
  ["jxsi", "image/jxsi"],
  ["jxss", "image/jxss"],
  ["kar", "audio/midi"],
  ["karbon", "application/vnd.kde.karbon"],
  ["kdb", "application/octet-stream"],
  ["kdbx", "application/x-keepass2"],
  ["key", "application/x-iwork-keynote-sffkey"],
  ["kfo", "application/vnd.kde.kformula"],
  ["kia", "application/vnd.kidspiration"],
  ["kml", "application/vnd.google-earth.kml+xml"],
  ["kmz", "application/vnd.google-earth.kmz"],
  ["kne", "application/vnd.kinar"],
  ["knp", "application/vnd.kinar"],
  ["kon", "application/vnd.kde.kontour"],
  ["kpr", "application/vnd.kde.kpresenter"],
  ["kpt", "application/vnd.kde.kpresenter"],
  ["kpxx", "application/vnd.ds-keypoint"],
  ["ksp", "application/vnd.kde.kspread"],
  ["ktr", "application/vnd.kahootz"],
  ["ktx", "image/ktx"],
  ["ktx2", "image/ktx2"],
  ["ktz", "application/vnd.kahootz"],
  ["kwd", "application/vnd.kde.kword"],
  ["kwt", "application/vnd.kde.kword"],
  ["lasxml", "application/vnd.las.las+xml"],
  ["latex", "application/x-latex"],
  ["lbd", "application/vnd.llamagraphics.life-balance.desktop"],
  ["lbe", "application/vnd.llamagraphics.life-balance.exchange+xml"],
  ["les", "application/vnd.hhe.lesson-player"],
  ["less", "text/less"],
  ["lgr", "application/lgr+xml"],
  ["lha", "application/octet-stream"],
  ["link66", "application/vnd.route66.link66+xml"],
  ["list", "text/plain"],
  ["list3820", "application/vnd.ibm.modcap"],
  ["listafp", "application/vnd.ibm.modcap"],
  ["litcoffee", "text/coffeescript"],
  ["lnk", "application/x-ms-shortcut"],
  ["log", "text/plain"],
  ["lostxml", "application/lost+xml"],
  ["lrf", "application/octet-stream"],
  ["lrm", "application/vnd.ms-lrm"],
  ["ltf", "application/vnd.frogans.ltf"],
  ["lua", "text/x-lua"],
  ["luac", "application/x-lua-bytecode"],
  ["lvp", "audio/vnd.lucent.voice"],
  ["lwp", "application/vnd.lotus-wordpro"],
  ["lzh", "application/octet-stream"],
  ["m1v", "video/mpeg"],
  ["m2a", "audio/mpeg"],
  ["m2v", "video/mpeg"],
  ["m3a", "audio/mpeg"],
  ["m3u", "text/plain"],
  ["m3u8", "application/vnd.apple.mpegurl"],
  ["m4a", "audio/x-m4a"],
  ["m4p", "application/mp4"],
  ["m4s", "video/iso.segment"],
  ["m4u", "application/vnd.mpegurl"],
  ["m4v", "video/x-m4v"],
  ["m13", "application/x-msmediaview"],
  ["m14", "application/x-msmediaview"],
  ["m21", "application/mp21"],
  ["ma", "application/mathematica"],
  ["mads", "application/mads+xml"],
  ["maei", "application/mmt-aei+xml"],
  ["mag", "application/vnd.ecowin.chart"],
  ["maker", "application/vnd.framemaker"],
  ["man", "text/troff"],
  ["manifest", "text/cache-manifest"],
  ["map", "application/json"],
  ["mar", "application/octet-stream"],
  ["markdown", "text/markdown"],
  ["mathml", "application/mathml+xml"],
  ["mb", "application/mathematica"],
  ["mbk", "application/vnd.mobius.mbk"],
  ["mbox", "application/mbox"],
  ["mc1", "application/vnd.medcalcdata"],
  ["mcd", "application/vnd.mcd"],
  ["mcurl", "text/vnd.curl.mcurl"],
  ["md", "text/markdown"],
  ["mdb", "application/x-msaccess"],
  ["mdi", "image/vnd.ms-modi"],
  ["mdx", "text/mdx"],
  ["me", "text/troff"],
  ["mesh", "model/mesh"],
  ["meta4", "application/metalink4+xml"],
  ["metalink", "application/metalink+xml"],
  ["mets", "application/mets+xml"],
  ["mfm", "application/vnd.mfmp"],
  ["mft", "application/rpki-manifest"],
  ["mgp", "application/vnd.osgeo.mapguide.package"],
  ["mgz", "application/vnd.proteus.magazine"],
  ["mid", "audio/midi"],
  ["midi", "audio/midi"],
  ["mie", "application/x-mie"],
  ["mif", "application/vnd.mif"],
  ["mime", "message/rfc822"],
  ["mj2", "video/mj2"],
  ["mjp2", "video/mj2"],
  ["mjs", "application/javascript"],
  ["mk3d", "video/x-matroska"],
  ["mka", "audio/x-matroska"],
  ["mkd", "text/x-markdown"],
  ["mks", "video/x-matroska"],
  ["mkv", "video/x-matroska"],
  ["mlp", "application/vnd.dolby.mlp"],
  ["mmd", "application/vnd.chipnuts.karaoke-mmd"],
  ["mmf", "application/vnd.smaf"],
  ["mml", "text/mathml"],
  ["mmr", "image/vnd.fujixerox.edmics-mmr"],
  ["mng", "video/x-mng"],
  ["mny", "application/x-msmoney"],
  ["mobi", "application/x-mobipocket-ebook"],
  ["mods", "application/mods+xml"],
  ["mov", "video/quicktime"],
  ["movie", "video/x-sgi-movie"],
  ["mp2", "audio/mpeg"],
  ["mp2a", "audio/mpeg"],
  ["mp3", "audio/mpeg"],
  ["mp4", "video/mp4"],
  ["mp4a", "audio/mp4"],
  ["mp4s", "application/mp4"],
  ["mp4v", "video/mp4"],
  ["mp21", "application/mp21"],
  ["mpc", "application/vnd.mophun.certificate"],
  ["mpd", "application/dash+xml"],
  ["mpe", "video/mpeg"],
  ["mpeg", "video/mpeg"],
  ["mpg", "video/mpeg"],
  ["mpg4", "video/mp4"],
  ["mpga", "audio/mpeg"],
  ["mpkg", "application/vnd.apple.installer+xml"],
  ["mpm", "application/vnd.blueice.multipass"],
  ["mpn", "application/vnd.mophun.application"],
  ["mpp", "application/vnd.ms-project"],
  ["mpt", "application/vnd.ms-project"],
  ["mpy", "application/vnd.ibm.minipay"],
  ["mqy", "application/vnd.mobius.mqy"],
  ["mrc", "application/marc"],
  ["mrcx", "application/marcxml+xml"],
  ["ms", "text/troff"],
  ["mscml", "application/mediaservercontrol+xml"],
  ["mseed", "application/vnd.fdsn.mseed"],
  ["mseq", "application/vnd.mseq"],
  ["msf", "application/vnd.epson.msf"],
  ["msg", "application/vnd.ms-outlook"],
  ["msh", "model/mesh"],
  ["msi", "application/x-msdownload"],
  ["msl", "application/vnd.mobius.msl"],
  ["msm", "application/octet-stream"],
  ["msp", "application/octet-stream"],
  ["msty", "application/vnd.muvee.style"],
  ["mtl", "model/mtl"],
  ["mts", "model/vnd.mts"],
  ["mus", "application/vnd.musician"],
  ["musd", "application/mmt-usd+xml"],
  ["musicxml", "application/vnd.recordare.musicxml+xml"],
  ["mvb", "application/x-msmediaview"],
  ["mvt", "application/vnd.mapbox-vector-tile"],
  ["mwf", "application/vnd.mfer"],
  ["mxf", "application/mxf"],
  ["mxl", "application/vnd.recordare.musicxml"],
  ["mxmf", "audio/mobile-xmf"],
  ["mxml", "application/xv+xml"],
  ["mxs", "application/vnd.triscape.mxs"],
  ["mxu", "video/vnd.mpegurl"],
  ["n-gage", "application/vnd.nokia.n-gage.symbian.install"],
  ["n3", "text/n3"],
  ["nb", "application/mathematica"],
  ["nbp", "application/vnd.wolfram.player"],
  ["nc", "application/x-netcdf"],
  ["ncx", "application/x-dtbncx+xml"],
  ["nfo", "text/x-nfo"],
  ["ngdat", "application/vnd.nokia.n-gage.data"],
  ["nitf", "application/vnd.nitf"],
  ["nlu", "application/vnd.neurolanguage.nlu"],
  ["nml", "application/vnd.enliven"],
  ["nnd", "application/vnd.noblenet-directory"],
  ["nns", "application/vnd.noblenet-sealer"],
  ["nnw", "application/vnd.noblenet-web"],
  ["npx", "image/vnd.net-fpx"],
  ["nq", "application/n-quads"],
  ["nsc", "application/x-conference"],
  ["nsf", "application/vnd.lotus-notes"],
  ["nt", "application/n-triples"],
  ["ntf", "application/vnd.nitf"],
  ["numbers", "application/x-iwork-numbers-sffnumbers"],
  ["nzb", "application/x-nzb"],
  ["oa2", "application/vnd.fujitsu.oasys2"],
  ["oa3", "application/vnd.fujitsu.oasys3"],
  ["oas", "application/vnd.fujitsu.oasys"],
  ["obd", "application/x-msbinder"],
  ["obgx", "application/vnd.openblox.game+xml"],
  ["obj", "model/obj"],
  ["oda", "application/oda"],
  ["odb", "application/vnd.oasis.opendocument.database"],
  ["odc", "application/vnd.oasis.opendocument.chart"],
  ["odf", "application/vnd.oasis.opendocument.formula"],
  ["odft", "application/vnd.oasis.opendocument.formula-template"],
  ["odg", "application/vnd.oasis.opendocument.graphics"],
  ["odi", "application/vnd.oasis.opendocument.image"],
  ["odm", "application/vnd.oasis.opendocument.text-master"],
  ["odp", "application/vnd.oasis.opendocument.presentation"],
  ["ods", "application/vnd.oasis.opendocument.spreadsheet"],
  ["odt", "application/vnd.oasis.opendocument.text"],
  ["oga", "audio/ogg"],
  ["ogex", "model/vnd.opengex"],
  ["ogg", "audio/ogg"],
  ["ogv", "video/ogg"],
  ["ogx", "application/ogg"],
  ["omdoc", "application/omdoc+xml"],
  ["onepkg", "application/onenote"],
  ["onetmp", "application/onenote"],
  ["onetoc", "application/onenote"],
  ["onetoc2", "application/onenote"],
  ["opf", "application/oebps-package+xml"],
  ["opml", "text/x-opml"],
  ["oprc", "application/vnd.palm"],
  ["opus", "audio/ogg"],
  ["org", "text/x-org"],
  ["osf", "application/vnd.yamaha.openscoreformat"],
  ["osfpvg", "application/vnd.yamaha.openscoreformat.osfpvg+xml"],
  ["osm", "application/vnd.openstreetmap.data+xml"],
  ["otc", "application/vnd.oasis.opendocument.chart-template"],
  ["otf", "font/otf"],
  ["otg", "application/vnd.oasis.opendocument.graphics-template"],
  ["oth", "application/vnd.oasis.opendocument.text-web"],
  ["oti", "application/vnd.oasis.opendocument.image-template"],
  ["otp", "application/vnd.oasis.opendocument.presentation-template"],
  ["ots", "application/vnd.oasis.opendocument.spreadsheet-template"],
  ["ott", "application/vnd.oasis.opendocument.text-template"],
  ["ova", "application/x-virtualbox-ova"],
  ["ovf", "application/x-virtualbox-ovf"],
  ["owl", "application/rdf+xml"],
  ["oxps", "application/oxps"],
  ["oxt", "application/vnd.openofficeorg.extension"],
  ["p", "text/x-pascal"],
  ["p7a", "application/x-pkcs7-signature"],
  ["p7b", "application/x-pkcs7-certificates"],
  ["p7c", "application/pkcs7-mime"],
  ["p7m", "application/pkcs7-mime"],
  ["p7r", "application/x-pkcs7-certreqresp"],
  ["p7s", "application/pkcs7-signature"],
  ["p8", "application/pkcs8"],
  ["p10", "application/x-pkcs10"],
  ["p12", "application/x-pkcs12"],
  ["pac", "application/x-ns-proxy-autoconfig"],
  ["pages", "application/x-iwork-pages-sffpages"],
  ["pas", "text/x-pascal"],
  ["paw", "application/vnd.pawaafile"],
  ["pbd", "application/vnd.powerbuilder6"],
  ["pbm", "image/x-portable-bitmap"],
  ["pcap", "application/vnd.tcpdump.pcap"],
  ["pcf", "application/x-font-pcf"],
  ["pcl", "application/vnd.hp-pcl"],
  ["pclxl", "application/vnd.hp-pclxl"],
  ["pct", "image/x-pict"],
  ["pcurl", "application/vnd.curl.pcurl"],
  ["pcx", "image/x-pcx"],
  ["pdb", "application/x-pilot"],
  ["pde", "text/x-processing"],
  ["pdf", "application/pdf"],
  ["pem", "application/x-x509-user-cert"],
  ["pfa", "application/x-font-type1"],
  ["pfb", "application/x-font-type1"],
  ["pfm", "application/x-font-type1"],
  ["pfr", "application/font-tdpfr"],
  ["pfx", "application/x-pkcs12"],
  ["pgm", "image/x-portable-graymap"],
  ["pgn", "application/x-chess-pgn"],
  ["pgp", "application/pgp"],
  ["php", "application/x-httpd-php"],
  ["php3", "application/x-httpd-php"],
  ["php4", "application/x-httpd-php"],
  ["phps", "application/x-httpd-php-source"],
  ["phtml", "application/x-httpd-php"],
  ["pic", "image/x-pict"],
  ["pkg", "application/octet-stream"],
  ["pki", "application/pkixcmp"],
  ["pkipath", "application/pkix-pkipath"],
  ["pkpass", "application/vnd.apple.pkpass"],
  ["pl", "application/x-perl"],
  ["plb", "application/vnd.3gpp.pic-bw-large"],
  ["plc", "application/vnd.mobius.plc"],
  ["plf", "application/vnd.pocketlearn"],
  ["pls", "application/pls+xml"],
  ["pm", "application/x-perl"],
  ["pml", "application/vnd.ctc-posml"],
  ["png", "image/png"],
  ["pnm", "image/x-portable-anymap"],
  ["portpkg", "application/vnd.macports.portpkg"],
  ["pot", "application/vnd.ms-powerpoint"],
  ["potm", "application/vnd.ms-powerpoint.presentation.macroEnabled.12"],
  ["potx", "application/vnd.openxmlformats-officedocument.presentationml.template"],
  ["ppa", "application/vnd.ms-powerpoint"],
  ["ppam", "application/vnd.ms-powerpoint.addin.macroEnabled.12"],
  ["ppd", "application/vnd.cups-ppd"],
  ["ppm", "image/x-portable-pixmap"],
  ["pps", "application/vnd.ms-powerpoint"],
  ["ppsm", "application/vnd.ms-powerpoint.slideshow.macroEnabled.12"],
  ["ppsx", "application/vnd.openxmlformats-officedocument.presentationml.slideshow"],
  ["ppt", "application/powerpoint"],
  ["pptm", "application/vnd.ms-powerpoint.presentation.macroEnabled.12"],
  ["pptx", "application/vnd.openxmlformats-officedocument.presentationml.presentation"],
  ["pqa", "application/vnd.palm"],
  ["prc", "application/x-pilot"],
  ["pre", "application/vnd.lotus-freelance"],
  ["prf", "application/pics-rules"],
  ["provx", "application/provenance+xml"],
  ["ps", "application/postscript"],
  ["psb", "application/vnd.3gpp.pic-bw-small"],
  ["psd", "application/x-photoshop"],
  ["psf", "application/x-font-linux-psf"],
  ["pskcxml", "application/pskc+xml"],
  ["pti", "image/prs.pti"],
  ["ptid", "application/vnd.pvi.ptid1"],
  ["pub", "application/x-mspublisher"],
  ["pvb", "application/vnd.3gpp.pic-bw-var"],
  ["pwn", "application/vnd.3m.post-it-notes"],
  ["pya", "audio/vnd.ms-playready.media.pya"],
  ["pyv", "video/vnd.ms-playready.media.pyv"],
  ["qam", "application/vnd.epson.quickanime"],
  ["qbo", "application/vnd.intu.qbo"],
  ["qfx", "application/vnd.intu.qfx"],
  ["qps", "application/vnd.publishare-delta-tree"],
  ["qt", "video/quicktime"],
  ["qwd", "application/vnd.quark.quarkxpress"],
  ["qwt", "application/vnd.quark.quarkxpress"],
  ["qxb", "application/vnd.quark.quarkxpress"],
  ["qxd", "application/vnd.quark.quarkxpress"],
  ["qxl", "application/vnd.quark.quarkxpress"],
  ["qxt", "application/vnd.quark.quarkxpress"],
  ["ra", "audio/x-realaudio"],
  ["ram", "audio/x-pn-realaudio"],
  ["raml", "application/raml+yaml"],
  ["rapd", "application/route-apd+xml"],
  ["rar", "application/x-rar"],
  ["ras", "image/x-cmu-raster"],
  ["rcprofile", "application/vnd.ipunplugged.rcprofile"],
  ["rdf", "application/rdf+xml"],
  ["rdz", "application/vnd.data-vision.rdz"],
  ["relo", "application/p2p-overlay+xml"],
  ["rep", "application/vnd.businessobjects"],
  ["res", "application/x-dtbresource+xml"],
  ["rgb", "image/x-rgb"],
  ["rif", "application/reginfo+xml"],
  ["rip", "audio/vnd.rip"],
  ["ris", "application/x-research-info-systems"],
  ["rl", "application/resource-lists+xml"],
  ["rlc", "image/vnd.fujixerox.edmics-rlc"],
  ["rld", "application/resource-lists-diff+xml"],
  ["rm", "audio/x-pn-realaudio"],
  ["rmi", "audio/midi"],
  ["rmp", "audio/x-pn-realaudio-plugin"],
  ["rms", "application/vnd.jcp.javame.midlet-rms"],
  ["rmvb", "application/vnd.rn-realmedia-vbr"],
  ["rnc", "application/relax-ng-compact-syntax"],
  ["rng", "application/xml"],
  ["roa", "application/rpki-roa"],
  ["roff", "text/troff"],
  ["rp9", "application/vnd.cloanto.rp9"],
  ["rpm", "audio/x-pn-realaudio-plugin"],
  ["rpss", "application/vnd.nokia.radio-presets"],
  ["rpst", "application/vnd.nokia.radio-preset"],
  ["rq", "application/sparql-query"],
  ["rs", "application/rls-services+xml"],
  ["rsa", "application/x-pkcs7"],
  ["rsat", "application/atsc-rsat+xml"],
  ["rsd", "application/rsd+xml"],
  ["rsheet", "application/urc-ressheet+xml"],
  ["rss", "application/rss+xml"],
  ["rtf", "text/rtf"],
  ["rtx", "text/richtext"],
  ["run", "application/x-makeself"],
  ["rusd", "application/route-usd+xml"],
  ["rv", "video/vnd.rn-realvideo"],
  ["s", "text/x-asm"],
  ["s3m", "audio/s3m"],
  ["saf", "application/vnd.yamaha.smaf-audio"],
  ["sass", "text/x-sass"],
  ["sbml", "application/sbml+xml"],
  ["sc", "application/vnd.ibm.secure-container"],
  ["scd", "application/x-msschedule"],
  ["scm", "application/vnd.lotus-screencam"],
  ["scq", "application/scvp-cv-request"],
  ["scs", "application/scvp-cv-response"],
  ["scss", "text/x-scss"],
  ["scurl", "text/vnd.curl.scurl"],
  ["sda", "application/vnd.stardivision.draw"],
  ["sdc", "application/vnd.stardivision.calc"],
  ["sdd", "application/vnd.stardivision.impress"],
  ["sdkd", "application/vnd.solent.sdkm+xml"],
  ["sdkm", "application/vnd.solent.sdkm+xml"],
  ["sdp", "application/sdp"],
  ["sdw", "application/vnd.stardivision.writer"],
  ["sea", "application/octet-stream"],
  ["see", "application/vnd.seemail"],
  ["seed", "application/vnd.fdsn.seed"],
  ["sema", "application/vnd.sema"],
  ["semd", "application/vnd.semd"],
  ["semf", "application/vnd.semf"],
  ["senmlx", "application/senml+xml"],
  ["sensmlx", "application/sensml+xml"],
  ["ser", "application/java-serialized-object"],
  ["setpay", "application/set-payment-initiation"],
  ["setreg", "application/set-registration-initiation"],
  ["sfd-hdstx", "application/vnd.hydrostatix.sof-data"],
  ["sfs", "application/vnd.spotfire.sfs"],
  ["sfv", "text/x-sfv"],
  ["sgi", "image/sgi"],
  ["sgl", "application/vnd.stardivision.writer-global"],
  ["sgm", "text/sgml"],
  ["sgml", "text/sgml"],
  ["sh", "application/x-sh"],
  ["shar", "application/x-shar"],
  ["shex", "text/shex"],
  ["shf", "application/shf+xml"],
  ["shtml", "text/html"],
  ["sid", "image/x-mrsid-image"],
  ["sieve", "application/sieve"],
  ["sig", "application/pgp-signature"],
  ["sil", "audio/silk"],
  ["silo", "model/mesh"],
  ["sis", "application/vnd.symbian.install"],
  ["sisx", "application/vnd.symbian.install"],
  ["sit", "application/x-stuffit"],
  ["sitx", "application/x-stuffitx"],
  ["siv", "application/sieve"],
  ["skd", "application/vnd.koan"],
  ["skm", "application/vnd.koan"],
  ["skp", "application/vnd.koan"],
  ["skt", "application/vnd.koan"],
  ["sldm", "application/vnd.ms-powerpoint.slide.macroenabled.12"],
  ["sldx", "application/vnd.openxmlformats-officedocument.presentationml.slide"],
  ["slim", "text/slim"],
  ["slm", "text/slim"],
  ["sls", "application/route-s-tsid+xml"],
  ["slt", "application/vnd.epson.salt"],
  ["sm", "application/vnd.stepmania.stepchart"],
  ["smf", "application/vnd.stardivision.math"],
  ["smi", "application/smil"],
  ["smil", "application/smil"],
  ["smv", "video/x-smv"],
  ["smzip", "application/vnd.stepmania.package"],
  ["snd", "audio/basic"],
  ["snf", "application/x-font-snf"],
  ["so", "application/octet-stream"],
  ["spc", "application/x-pkcs7-certificates"],
  ["spdx", "text/spdx"],
  ["spf", "application/vnd.yamaha.smaf-phrase"],
  ["spl", "application/x-futuresplash"],
  ["spot", "text/vnd.in3d.spot"],
  ["spp", "application/scvp-vp-response"],
  ["spq", "application/scvp-vp-request"],
  ["spx", "audio/ogg"],
  ["sql", "application/x-sql"],
  ["src", "application/x-wais-source"],
  ["srt", "application/x-subrip"],
  ["sru", "application/sru+xml"],
  ["srx", "application/sparql-results+xml"],
  ["ssdl", "application/ssdl+xml"],
  ["sse", "application/vnd.kodak-descriptor"],
  ["ssf", "application/vnd.epson.ssf"],
  ["ssml", "application/ssml+xml"],
  ["sst", "application/octet-stream"],
  ["st", "application/vnd.sailingtracker.track"],
  ["stc", "application/vnd.sun.xml.calc.template"],
  ["std", "application/vnd.sun.xml.draw.template"],
  ["stf", "application/vnd.wt.stf"],
  ["sti", "application/vnd.sun.xml.impress.template"],
  ["stk", "application/hyperstudio"],
  ["stl", "model/stl"],
  ["stpx", "model/step+xml"],
  ["stpxz", "model/step-xml+zip"],
  ["stpz", "model/step+zip"],
  ["str", "application/vnd.pg.format"],
  ["stw", "application/vnd.sun.xml.writer.template"],
  ["styl", "text/stylus"],
  ["stylus", "text/stylus"],
  ["sub", "text/vnd.dvb.subtitle"],
  ["sus", "application/vnd.sus-calendar"],
  ["susp", "application/vnd.sus-calendar"],
  ["sv4cpio", "application/x-sv4cpio"],
  ["sv4crc", "application/x-sv4crc"],
  ["svc", "application/vnd.dvb.service"],
  ["svd", "application/vnd.svd"],
  ["svg", "image/svg+xml"],
  ["svgz", "image/svg+xml"],
  ["swa", "application/x-director"],
  ["swf", "application/x-shockwave-flash"],
  ["swi", "application/vnd.aristanetworks.swi"],
  ["swidtag", "application/swid+xml"],
  ["sxc", "application/vnd.sun.xml.calc"],
  ["sxd", "application/vnd.sun.xml.draw"],
  ["sxg", "application/vnd.sun.xml.writer.global"],
  ["sxi", "application/vnd.sun.xml.impress"],
  ["sxm", "application/vnd.sun.xml.math"],
  ["sxw", "application/vnd.sun.xml.writer"],
  ["t", "text/troff"],
  ["t3", "application/x-t3vm-image"],
  ["t38", "image/t38"],
  ["taglet", "application/vnd.mynfc"],
  ["tao", "application/vnd.tao.intent-module-archive"],
  ["tap", "image/vnd.tencent.tap"],
  ["tar", "application/x-tar"],
  ["tcap", "application/vnd.3gpp2.tcap"],
  ["tcl", "application/x-tcl"],
  ["td", "application/urc-targetdesc+xml"],
  ["teacher", "application/vnd.smart.teacher"],
  ["tei", "application/tei+xml"],
  ["teicorpus", "application/tei+xml"],
  ["tex", "application/x-tex"],
  ["texi", "application/x-texinfo"],
  ["texinfo", "application/x-texinfo"],
  ["text", "text/plain"],
  ["tfi", "application/thraud+xml"],
  ["tfm", "application/x-tex-tfm"],
  ["tfx", "image/tiff-fx"],
  ["tga", "image/x-tga"],
  ["tgz", "application/x-tar"],
  ["thmx", "application/vnd.ms-officetheme"],
  ["tif", "image/tiff"],
  ["tiff", "image/tiff"],
  ["tk", "application/x-tcl"],
  ["tmo", "application/vnd.tmobile-livetv"],
  ["toml", "application/toml"],
  ["torrent", "application/x-bittorrent"],
  ["tpl", "application/vnd.groove-tool-template"],
  ["tpt", "application/vnd.trid.tpt"],
  ["tr", "text/troff"],
  ["tra", "application/vnd.trueapp"],
  ["trig", "application/trig"],
  ["trm", "application/x-msterminal"],
  ["ts", "video/mp2t"],
  ["tsd", "application/timestamped-data"],
  ["tsv", "text/tab-separated-values"],
  ["ttc", "font/collection"],
  ["ttf", "font/ttf"],
  ["ttl", "text/turtle"],
  ["ttml", "application/ttml+xml"],
  ["twd", "application/vnd.simtech-mindmapper"],
  ["twds", "application/vnd.simtech-mindmapper"],
  ["txd", "application/vnd.genomatix.tuxedo"],
  ["txf", "application/vnd.mobius.txf"],
  ["txt", "text/plain"],
  ["u8dsn", "message/global-delivery-status"],
  ["u8hdr", "message/global-headers"],
  ["u8mdn", "message/global-disposition-notification"],
  ["u8msg", "message/global"],
  ["u32", "application/x-authorware-bin"],
  ["ubj", "application/ubjson"],
  ["udeb", "application/x-debian-package"],
  ["ufd", "application/vnd.ufdl"],
  ["ufdl", "application/vnd.ufdl"],
  ["ulx", "application/x-glulx"],
  ["umj", "application/vnd.umajin"],
  ["unityweb", "application/vnd.unity"],
  ["uoml", "application/vnd.uoml+xml"],
  ["uri", "text/uri-list"],
  ["uris", "text/uri-list"],
  ["urls", "text/uri-list"],
  ["usdz", "model/vnd.usdz+zip"],
  ["ustar", "application/x-ustar"],
  ["utz", "application/vnd.uiq.theme"],
  ["uu", "text/x-uuencode"],
  ["uva", "audio/vnd.dece.audio"],
  ["uvd", "application/vnd.dece.data"],
  ["uvf", "application/vnd.dece.data"],
  ["uvg", "image/vnd.dece.graphic"],
  ["uvh", "video/vnd.dece.hd"],
  ["uvi", "image/vnd.dece.graphic"],
  ["uvm", "video/vnd.dece.mobile"],
  ["uvp", "video/vnd.dece.pd"],
  ["uvs", "video/vnd.dece.sd"],
  ["uvt", "application/vnd.dece.ttml+xml"],
  ["uvu", "video/vnd.uvvu.mp4"],
  ["uvv", "video/vnd.dece.video"],
  ["uvva", "audio/vnd.dece.audio"],
  ["uvvd", "application/vnd.dece.data"],
  ["uvvf", "application/vnd.dece.data"],
  ["uvvg", "image/vnd.dece.graphic"],
  ["uvvh", "video/vnd.dece.hd"],
  ["uvvi", "image/vnd.dece.graphic"],
  ["uvvm", "video/vnd.dece.mobile"],
  ["uvvp", "video/vnd.dece.pd"],
  ["uvvs", "video/vnd.dece.sd"],
  ["uvvt", "application/vnd.dece.ttml+xml"],
  ["uvvu", "video/vnd.uvvu.mp4"],
  ["uvvv", "video/vnd.dece.video"],
  ["uvvx", "application/vnd.dece.unspecified"],
  ["uvvz", "application/vnd.dece.zip"],
  ["uvx", "application/vnd.dece.unspecified"],
  ["uvz", "application/vnd.dece.zip"],
  ["vbox", "application/x-virtualbox-vbox"],
  ["vbox-extpack", "application/x-virtualbox-vbox-extpack"],
  ["vcard", "text/vcard"],
  ["vcd", "application/x-cdlink"],
  ["vcf", "text/x-vcard"],
  ["vcg", "application/vnd.groove-vcard"],
  ["vcs", "text/x-vcalendar"],
  ["vcx", "application/vnd.vcx"],
  ["vdi", "application/x-virtualbox-vdi"],
  ["vds", "model/vnd.sap.vds"],
  ["vhd", "application/x-virtualbox-vhd"],
  ["vis", "application/vnd.visionary"],
  ["viv", "video/vnd.vivo"],
  ["vlc", "application/videolan"],
  ["vmdk", "application/x-virtualbox-vmdk"],
  ["vob", "video/x-ms-vob"],
  ["vor", "application/vnd.stardivision.writer"],
  ["vox", "application/x-authorware-bin"],
  ["vrml", "model/vrml"],
  ["vsd", "application/vnd.visio"],
  ["vsf", "application/vnd.vsf"],
  ["vss", "application/vnd.visio"],
  ["vst", "application/vnd.visio"],
  ["vsw", "application/vnd.visio"],
  ["vtf", "image/vnd.valve.source.texture"],
  ["vtt", "text/vtt"],
  ["vtu", "model/vnd.vtu"],
  ["vxml", "application/voicexml+xml"],
  ["w3d", "application/x-director"],
  ["wad", "application/x-doom"],
  ["wadl", "application/vnd.sun.wadl+xml"],
  ["war", "application/java-archive"],
  ["wasm", "application/wasm"],
  ["wav", "audio/x-wav"],
  ["wax", "audio/x-ms-wax"],
  ["wbmp", "image/vnd.wap.wbmp"],
  ["wbs", "application/vnd.criticaltools.wbs+xml"],
  ["wbxml", "application/wbxml"],
  ["wcm", "application/vnd.ms-works"],
  ["wdb", "application/vnd.ms-works"],
  ["wdp", "image/vnd.ms-photo"],
  ["weba", "audio/webm"],
  ["webapp", "application/x-web-app-manifest+json"],
  ["webm", "video/webm"],
  ["webmanifest", "application/manifest+json"],
  ["webp", "image/webp"],
  ["wg", "application/vnd.pmi.widget"],
  ["wgt", "application/widget"],
  ["wks", "application/vnd.ms-works"],
  ["wm", "video/x-ms-wm"],
  ["wma", "audio/x-ms-wma"],
  ["wmd", "application/x-ms-wmd"],
  ["wmf", "image/wmf"],
  ["wml", "text/vnd.wap.wml"],
  ["wmlc", "application/wmlc"],
  ["wmls", "text/vnd.wap.wmlscript"],
  ["wmlsc", "application/vnd.wap.wmlscriptc"],
  ["wmv", "video/x-ms-wmv"],
  ["wmx", "video/x-ms-wmx"],
  ["wmz", "application/x-msmetafile"],
  ["woff", "font/woff"],
  ["woff2", "font/woff2"],
  ["word", "application/msword"],
  ["wpd", "application/vnd.wordperfect"],
  ["wpl", "application/vnd.ms-wpl"],
  ["wps", "application/vnd.ms-works"],
  ["wqd", "application/vnd.wqd"],
  ["wri", "application/x-mswrite"],
  ["wrl", "model/vrml"],
  ["wsc", "message/vnd.wfa.wsc"],
  ["wsdl", "application/wsdl+xml"],
  ["wspolicy", "application/wspolicy+xml"],
  ["wtb", "application/vnd.webturbo"],
  ["wvx", "video/x-ms-wvx"],
  ["x3d", "model/x3d+xml"],
  ["x3db", "model/x3d+fastinfoset"],
  ["x3dbz", "model/x3d+binary"],
  ["x3dv", "model/x3d-vrml"],
  ["x3dvz", "model/x3d+vrml"],
  ["x3dz", "model/x3d+xml"],
  ["x32", "application/x-authorware-bin"],
  ["x_b", "model/vnd.parasolid.transmit.binary"],
  ["x_t", "model/vnd.parasolid.transmit.text"],
  ["xaml", "application/xaml+xml"],
  ["xap", "application/x-silverlight-app"],
  ["xar", "application/vnd.xara"],
  ["xav", "application/xcap-att+xml"],
  ["xbap", "application/x-ms-xbap"],
  ["xbd", "application/vnd.fujixerox.docuworks.binder"],
  ["xbm", "image/x-xbitmap"],
  ["xca", "application/xcap-caps+xml"],
  ["xcs", "application/calendar+xml"],
  ["xdf", "application/xcap-diff+xml"],
  ["xdm", "application/vnd.syncml.dm+xml"],
  ["xdp", "application/vnd.adobe.xdp+xml"],
  ["xdssc", "application/dssc+xml"],
  ["xdw", "application/vnd.fujixerox.docuworks"],
  ["xel", "application/xcap-el+xml"],
  ["xenc", "application/xenc+xml"],
  ["xer", "application/patch-ops-error+xml"],
  ["xfdf", "application/vnd.adobe.xfdf"],
  ["xfdl", "application/vnd.xfdl"],
  ["xht", "application/xhtml+xml"],
  ["xhtml", "application/xhtml+xml"],
  ["xhvml", "application/xv+xml"],
  ["xif", "image/vnd.xiff"],
  ["xl", "application/excel"],
  ["xla", "application/vnd.ms-excel"],
  ["xlam", "application/vnd.ms-excel.addin.macroEnabled.12"],
  ["xlc", "application/vnd.ms-excel"],
  ["xlf", "application/xliff+xml"],
  ["xlm", "application/vnd.ms-excel"],
  ["xls", "application/vnd.ms-excel"],
  ["xlsb", "application/vnd.ms-excel.sheet.binary.macroEnabled.12"],
  ["xlsm", "application/vnd.ms-excel.sheet.macroEnabled.12"],
  ["xlsx", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
  ["xlt", "application/vnd.ms-excel"],
  ["xltm", "application/vnd.ms-excel.template.macroEnabled.12"],
  ["xltx", "application/vnd.openxmlformats-officedocument.spreadsheetml.template"],
  ["xlw", "application/vnd.ms-excel"],
  ["xm", "audio/xm"],
  ["xml", "application/xml"],
  ["xns", "application/xcap-ns+xml"],
  ["xo", "application/vnd.olpc-sugar"],
  ["xop", "application/xop+xml"],
  ["xpi", "application/x-xpinstall"],
  ["xpl", "application/xproc+xml"],
  ["xpm", "image/x-xpixmap"],
  ["xpr", "application/vnd.is-xpr"],
  ["xps", "application/vnd.ms-xpsdocument"],
  ["xpw", "application/vnd.intercon.formnet"],
  ["xpx", "application/vnd.intercon.formnet"],
  ["xsd", "application/xml"],
  ["xsl", "application/xml"],
  ["xslt", "application/xslt+xml"],
  ["xsm", "application/vnd.syncml+xml"],
  ["xspf", "application/xspf+xml"],
  ["xul", "application/vnd.mozilla.xul+xml"],
  ["xvm", "application/xv+xml"],
  ["xvml", "application/xv+xml"],
  ["xwd", "image/x-xwindowdump"],
  ["xyz", "chemical/x-xyz"],
  ["xz", "application/x-xz"],
  ["yaml", "text/yaml"],
  ["yang", "application/yang"],
  ["yin", "application/yin+xml"],
  ["yml", "text/yaml"],
  ["ymp", "text/x-suse-ymp"],
  ["z", "application/x-compress"],
  ["z1", "application/x-zmachine"],
  ["z2", "application/x-zmachine"],
  ["z3", "application/x-zmachine"],
  ["z4", "application/x-zmachine"],
  ["z5", "application/x-zmachine"],
  ["z6", "application/x-zmachine"],
  ["z7", "application/x-zmachine"],
  ["z8", "application/x-zmachine"],
  ["zaz", "application/vnd.zzazz.deck+xml"],
  ["zip", "application/zip"],
  ["zir", "application/vnd.zul"],
  ["zirz", "application/vnd.zul"],
  ["zmm", "application/vnd.handheld-entertainment+xml"],
  ["zsh", "text/x-scriptzsh"]
]);
function toFileWithPath(file, path, h) {
  const f = withMimeType(file);
  const { webkitRelativePath } = file;
  const p = typeof path === "string" ? path : typeof webkitRelativePath === "string" && webkitRelativePath.length > 0 ? webkitRelativePath : `./${file.name}`;
  if (typeof f.path !== "string") {
    setObjProp(f, "path", p);
  }
  setObjProp(f, "relativePath", p);
  return f;
}
function withMimeType(file) {
  const { name } = file;
  const hasExtension = name && name.lastIndexOf(".") !== -1;
  if (hasExtension && !file.type) {
    const ext = name.split(".").pop().toLowerCase();
    const type = COMMON_MIME_TYPES.get(ext);
    if (type) {
      Object.defineProperty(file, "type", {
        value: type,
        writable: false,
        configurable: false,
        enumerable: true
      });
    }
  }
  return file;
}
function setObjProp(f, key, value) {
  Object.defineProperty(f, key, {
    value,
    writable: false,
    configurable: false,
    enumerable: true
  });
}
const FILES_TO_IGNORE = [
  // Thumbnail cache files for macOS and Windows
  ".DS_Store",
  // macOs
  "Thumbs.db"
  // Windows
];
function fromEvent(evt) {
  return __awaiter(this, void 0, void 0, function* () {
    if (isObject(evt) && isDataTransfer(evt.dataTransfer)) {
      return getDataTransferFiles(evt.dataTransfer, evt.type);
    } else if (isChangeEvt(evt)) {
      return getInputFiles(evt);
    } else if (Array.isArray(evt) && evt.every((item) => "getFile" in item && typeof item.getFile === "function")) {
      return getFsHandleFiles(evt);
    }
    return [];
  });
}
function isDataTransfer(value) {
  return isObject(value);
}
function isChangeEvt(value) {
  return isObject(value) && isObject(value.target);
}
function isObject(v2) {
  return typeof v2 === "object" && v2 !== null;
}
function getInputFiles(evt) {
  return fromList(evt.target.files).map((file) => toFileWithPath(file));
}
function getFsHandleFiles(handles) {
  return __awaiter(this, void 0, void 0, function* () {
    const files = yield Promise.all(handles.map((h) => h.getFile()));
    return files.map((file) => toFileWithPath(file));
  });
}
function getDataTransferFiles(dt, type) {
  return __awaiter(this, void 0, void 0, function* () {
    if (dt.items) {
      const items = fromList(dt.items).filter((item) => item.kind === "file");
      if (type !== "drop") {
        return items;
      }
      const files = yield Promise.all(items.map(toFilePromises));
      return noIgnoredFiles(flatten(files));
    }
    return noIgnoredFiles(fromList(dt.files).map((file) => toFileWithPath(file)));
  });
}
function noIgnoredFiles(files) {
  return files.filter((file) => FILES_TO_IGNORE.indexOf(file.name) === -1);
}
function fromList(items) {
  if (items === null) {
    return [];
  }
  const files = [];
  for (let i = 0; i < items.length; i++) {
    const file = items[i];
    files.push(file);
  }
  return files;
}
function toFilePromises(item) {
  if (typeof item.webkitGetAsEntry !== "function") {
    return fromDataTransferItem(item);
  }
  const entry = item.webkitGetAsEntry();
  if (entry && entry.isDirectory) {
    return fromDirEntry(entry);
  }
  return fromDataTransferItem(item, entry);
}
function flatten(items) {
  return items.reduce((acc, files) => [
    ...acc,
    ...Array.isArray(files) ? flatten(files) : [files]
  ], []);
}
function fromDataTransferItem(item, entry) {
  return __awaiter(this, void 0, void 0, function* () {
    var _a;
    if (globalThis.isSecureContext && typeof item.getAsFileSystemHandle === "function") {
      const h = yield item.getAsFileSystemHandle();
      if (h === null) {
        throw new Error(`${item} is not a File`);
      }
      if (h !== void 0) {
        const file2 = yield h.getFile();
        file2.handle = h;
        return toFileWithPath(file2);
      }
    }
    const file = item.getAsFile();
    if (!file) {
      throw new Error(`${item} is not a File`);
    }
    const fwp = toFileWithPath(file, (_a = entry === null || entry === void 0 ? void 0 : entry.fullPath) !== null && _a !== void 0 ? _a : void 0);
    return fwp;
  });
}
function fromEntry(entry) {
  return __awaiter(this, void 0, void 0, function* () {
    return entry.isDirectory ? fromDirEntry(entry) : fromFileEntry(entry);
  });
}
function fromDirEntry(entry) {
  const reader = entry.createReader();
  return new Promise((resolve, reject) => {
    const entries = [];
    function readEntries() {
      reader.readEntries((batch) => __awaiter(this, void 0, void 0, function* () {
        if (!batch.length) {
          try {
            const files = yield Promise.all(entries);
            resolve(files);
          } catch (err) {
            reject(err);
          }
        } else {
          const items = Promise.all(batch.map(fromEntry));
          entries.push(items);
          readEntries();
        }
      }), (err) => {
        reject(err);
      });
    }
    readEntries();
  });
}
function fromFileEntry(entry) {
  return __awaiter(this, void 0, void 0, function* () {
    return new Promise((resolve, reject) => {
      entry.file((file) => {
        const fwp = toFileWithPath(file, entry.fullPath);
        resolve(fwp);
      }, (err) => {
        reject(err);
      });
    });
  });
}
var es = {};
var hasRequiredEs;
function requireEs() {
  if (hasRequiredEs) return es;
  hasRequiredEs = 1;
  es.__esModule = true;
  es.default = function(file, acceptedFiles) {
    if (file && acceptedFiles) {
      var acceptedFilesArray = Array.isArray(acceptedFiles) ? acceptedFiles : acceptedFiles.split(",");
      if (acceptedFilesArray.length === 0) {
        return true;
      }
      var fileName = file.name || "";
      var mimeType = (file.type || "").toLowerCase();
      var baseMimeType = mimeType.replace(/\/.*$/, "");
      return acceptedFilesArray.some(function(type) {
        var validType = type.trim().toLowerCase();
        if (validType.charAt(0) === ".") {
          return fileName.toLowerCase().endsWith(validType);
        } else if (validType.endsWith("/*")) {
          return baseMimeType === validType.replace(/\/.*$/, "");
        }
        return mimeType === validType;
      });
    }
    return true;
  };
  return es;
}
var esExports = requireEs();
const _accepts = /* @__PURE__ */ getDefaultExportFromCjs(esExports);
function _toConsumableArray$1(arr) {
  return _arrayWithoutHoles$1(arr) || _iterableToArray$1(arr) || _unsupportedIterableToArray$1(arr) || _nonIterableSpread$1();
}
function _nonIterableSpread$1() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _iterableToArray$1(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _arrayWithoutHoles$1(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray$1(arr);
}
function ownKeys$1(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function(sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread$1(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$1(Object(source), true).forEach(function(key) {
      _defineProperty$1(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$1(Object(source)).forEach(function(key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty$1(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, { value, enumerable: true, configurable: true, writable: true });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _slicedToArray$1(arr, i) {
  return _arrayWithHoles$1(arr) || _iterableToArrayLimit$1(arr, i) || _unsupportedIterableToArray$1(arr, i) || _nonIterableRest$1();
}
function _nonIterableRest$1() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$1(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$1(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen);
}
function _arrayLikeToArray$1(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$1(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$1(arr) {
  if (Array.isArray(arr)) return arr;
}
var accepts = typeof _accepts === "function" ? _accepts : _accepts.default;
var FILE_INVALID_TYPE = "file-invalid-type";
var FILE_TOO_LARGE = "file-too-large";
var FILE_TOO_SMALL = "file-too-small";
var TOO_MANY_FILES = "too-many-files";
var getInvalidTypeRejectionErr = function getInvalidTypeRejectionErr2() {
  var accept = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : "";
  var acceptArr = accept.split(",");
  var msg = acceptArr.length > 1 ? "one of ".concat(acceptArr.join(", ")) : acceptArr[0];
  return {
    code: FILE_INVALID_TYPE,
    message: "File type must be ".concat(msg)
  };
};
var getTooLargeRejectionErr = function getTooLargeRejectionErr2(maxSize) {
  return {
    code: FILE_TOO_LARGE,
    message: "File is larger than ".concat(maxSize, " ").concat(maxSize === 1 ? "byte" : "bytes")
  };
};
var getTooSmallRejectionErr = function getTooSmallRejectionErr2(minSize) {
  return {
    code: FILE_TOO_SMALL,
    message: "File is smaller than ".concat(minSize, " ").concat(minSize === 1 ? "byte" : "bytes")
  };
};
var TOO_MANY_FILES_REJECTION = {
  code: TOO_MANY_FILES,
  message: "Too many files"
};
function isDataTransferItemWithEmptyType(file) {
  return file.type === "" && typeof file.getAsFile === "function";
}
function fileAccepted(file, accept) {
  var isAcceptable = file.type === "application/x-moz-file" || accepts(file, accept) || isDataTransferItemWithEmptyType(file);
  return [isAcceptable, isAcceptable ? null : getInvalidTypeRejectionErr(accept)];
}
function fileMatchSize(file, minSize, maxSize) {
  if (isDefined(file.size)) {
    if (isDefined(minSize) && isDefined(maxSize)) {
      if (file.size > maxSize) return [false, getTooLargeRejectionErr(maxSize)];
      if (file.size < minSize) return [false, getTooSmallRejectionErr(minSize)];
    } else if (isDefined(minSize) && file.size < minSize) return [false, getTooSmallRejectionErr(minSize)];
    else if (isDefined(maxSize) && file.size > maxSize) return [false, getTooLargeRejectionErr(maxSize)];
  }
  return [true, null];
}
function isDefined(value) {
  return value !== void 0 && value !== null;
}
function allFilesAccepted(_ref) {
  var files = _ref.files, accept = _ref.accept, minSize = _ref.minSize, maxSize = _ref.maxSize, multiple = _ref.multiple, maxFiles = _ref.maxFiles, validator = _ref.validator;
  if (!multiple && files.length > 1 || multiple && maxFiles >= 1 && files.length > maxFiles) {
    return false;
  }
  return files.every(function(file) {
    var _fileAccepted = fileAccepted(file, accept), _fileAccepted2 = _slicedToArray$1(_fileAccepted, 1), accepted = _fileAccepted2[0];
    var _fileMatchSize = fileMatchSize(file, minSize, maxSize), _fileMatchSize2 = _slicedToArray$1(_fileMatchSize, 1), sizeMatch = _fileMatchSize2[0];
    var customErrors = validator ? validator(file) : null;
    return accepted && sizeMatch && !customErrors;
  });
}
function isPropagationStopped(event) {
  if (typeof event.isPropagationStopped === "function") {
    return event.isPropagationStopped();
  } else if (typeof event.cancelBubble !== "undefined") {
    return event.cancelBubble;
  }
  return false;
}
function isEvtWithFiles(event) {
  if (!event.dataTransfer) {
    return !!event.target && !!event.target.files;
  }
  return Array.prototype.some.call(event.dataTransfer.types, function(type) {
    return type === "Files" || type === "application/x-moz-file";
  });
}
function onDocumentDragOver(event) {
  event.preventDefault();
}
function isIe(userAgent) {
  return userAgent.indexOf("MSIE") !== -1 || userAgent.indexOf("Trident/") !== -1;
}
function isEdge(userAgent) {
  return userAgent.indexOf("Edge/") !== -1;
}
function isIeOrEdge() {
  var userAgent = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : window.navigator.userAgent;
  return isIe(userAgent) || isEdge(userAgent);
}
function composeEventHandlers() {
  for (var _len = arguments.length, fns = new Array(_len), _key = 0; _key < _len; _key++) {
    fns[_key] = arguments[_key];
  }
  return function(event) {
    for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
      args[_key2 - 1] = arguments[_key2];
    }
    return fns.some(function(fn) {
      if (!isPropagationStopped(event) && fn) {
        fn.apply(void 0, [event].concat(args));
      }
      return isPropagationStopped(event);
    });
  };
}
function canUseFileSystemAccessAPI() {
  return "showOpenFilePicker" in window;
}
function pickerOptionsFromAccept(accept) {
  if (isDefined(accept)) {
    var acceptForPicker = Object.entries(accept).filter(function(_ref2) {
      var _ref3 = _slicedToArray$1(_ref2, 2), mimeType = _ref3[0], ext = _ref3[1];
      var ok = true;
      if (!isMIMEType(mimeType)) {
        console.warn('Skipped "'.concat(mimeType, '" because it is not a valid MIME type. Check https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types for a list of valid MIME types.'));
        ok = false;
      }
      if (!Array.isArray(ext) || !ext.every(isExt)) {
        console.warn('Skipped "'.concat(mimeType, '" because an invalid file extension was provided.'));
        ok = false;
      }
      return ok;
    }).reduce(function(agg, _ref4) {
      var _ref5 = _slicedToArray$1(_ref4, 2), mimeType = _ref5[0], ext = _ref5[1];
      return _objectSpread$1(_objectSpread$1({}, agg), {}, _defineProperty$1({}, mimeType, ext));
    }, {});
    return [{
      // description is required due to https://crbug.com/1264708
      description: "Files",
      accept: acceptForPicker
    }];
  }
  return accept;
}
function acceptPropAsAcceptAttr(accept) {
  if (isDefined(accept)) {
    return Object.entries(accept).reduce(function(a, _ref6) {
      var _ref7 = _slicedToArray$1(_ref6, 2), mimeType = _ref7[0], ext = _ref7[1];
      return [].concat(_toConsumableArray$1(a), [mimeType], _toConsumableArray$1(ext));
    }, []).filter(function(v2) {
      return isMIMEType(v2) || isExt(v2);
    }).join(",");
  }
  return void 0;
}
function isAbort(v2) {
  return v2 instanceof DOMException && (v2.name === "AbortError" || v2.code === v2.ABORT_ERR);
}
function isSecurityError(v2) {
  return v2 instanceof DOMException && (v2.name === "SecurityError" || v2.code === v2.SECURITY_ERR);
}
function isMIMEType(v2) {
  return v2 === "audio/*" || v2 === "video/*" || v2 === "image/*" || v2 === "text/*" || v2 === "application/*" || /\w+\/[-+.\w]+/g.test(v2);
}
function isExt(v2) {
  return /^.*\.[\w]+$/.test(v2);
}
var _excluded = ["children"], _excluded2 = ["open"], _excluded3 = ["refKey", "role", "onKeyDown", "onFocus", "onBlur", "onClick", "onDragEnter", "onDragOver", "onDragLeave", "onDrop"], _excluded4 = ["refKey", "onChange", "onClick"];
function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}
function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function(sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), true).forEach(function(key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function(key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, { value, enumerable: true, configurable: true, writable: true });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
var Dropzone$1 = /* @__PURE__ */ forwardRef(function(_ref, ref) {
  var children = _ref.children, params = _objectWithoutProperties(_ref, _excluded);
  var _useDropzone = useDropzone(params), open = _useDropzone.open, props = _objectWithoutProperties(_useDropzone, _excluded2);
  useImperativeHandle(ref, function() {
    return {
      open
    };
  }, [open]);
  return /* @__PURE__ */ u.createElement(Fragment$1, null, children(_objectSpread(_objectSpread({}, props), {}, {
    open
  })));
});
Dropzone$1.displayName = "Dropzone";
var defaultProps = {
  disabled: false,
  getFilesFromEvent: fromEvent,
  maxSize: Infinity,
  minSize: 0,
  multiple: true,
  maxFiles: 0,
  preventDropOnDocument: true,
  noClick: false,
  noKeyboard: false,
  noDrag: false,
  noDragEventsBubbling: false,
  validator: null,
  useFsAccessApi: false,
  autoFocus: false
};
Dropzone$1.defaultProps = defaultProps;
Dropzone$1.propTypes = {
  /**
   * Render function that exposes the dropzone state and prop getter fns
   *
   * @param {object} params
   * @param {Function} params.getRootProps Returns the props you should apply to the root drop container you render
   * @param {Function} params.getInputProps Returns the props you should apply to hidden file input you render
   * @param {Function} params.open Open the native file selection dialog
   * @param {boolean} params.isFocused Dropzone area is in focus
   * @param {boolean} params.isFileDialogActive File dialog is opened
   * @param {boolean} params.isDragActive Active drag is in progress
   * @param {boolean} params.isDragAccept Dragged files are accepted
   * @param {boolean} params.isDragReject Some dragged files are rejected
   * @param {boolean} params.isDragGlobal Files are being dragged anywhere on the document
   * @param {File[]} params.acceptedFiles Accepted files
   * @param {FileRejection[]} params.fileRejections Rejected files and why they were rejected
   */
  children: PropTypes.func,
  /**
   * Set accepted file types.
   * Checkout https://developer.mozilla.org/en-US/docs/Web/API/window/showOpenFilePicker types option for more information.
   * Keep in mind that mime type determination is not reliable across platforms. CSV files,
   * for example, are reported as text/plain under macOS but as application/vnd.ms-excel under
   * Windows. In some cases there might not be a mime type set at all (https://github.com/react-dropzone/react-dropzone/issues/276).
   */
  accept: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  /**
   * Allow drag 'n' drop (or selection from the file dialog) of multiple files
   */
  multiple: PropTypes.bool,
  /**
   * If false, allow dropped items to take over the current browser window
   */
  preventDropOnDocument: PropTypes.bool,
  /**
   * If true, disables click to open the native file selection dialog
   */
  noClick: PropTypes.bool,
  /**
   * If true, disables SPACE/ENTER to open the native file selection dialog.
   * Note that it also stops tracking the focus state.
   */
  noKeyboard: PropTypes.bool,
  /**
   * If true, disables drag 'n' drop
   */
  noDrag: PropTypes.bool,
  /**
   * If true, stops drag event propagation to parents
   */
  noDragEventsBubbling: PropTypes.bool,
  /**
   * Minimum file size (in bytes)
   */
  minSize: PropTypes.number,
  /**
   * Maximum file size (in bytes)
   */
  maxSize: PropTypes.number,
  /**
   * Maximum accepted number of files
   * The default value is 0 which means there is no limitation to how many files are accepted.
   */
  maxFiles: PropTypes.number,
  /**
   * Enable/disable the dropzone
   */
  disabled: PropTypes.bool,
  /**
   * Use this to provide a custom file aggregator
   *
   * @param {(DragEvent|Event|Array<FileSystemFileHandle>)} event A drag event or input change event (if files were selected via the file dialog)
   */
  getFilesFromEvent: PropTypes.func,
  /**
   * Cb for when closing the file dialog with no selection
   */
  onFileDialogCancel: PropTypes.func,
  /**
   * Cb for when opening the file dialog
   */
  onFileDialogOpen: PropTypes.func,
  /**
   * Set to true to use the https://developer.mozilla.org/en-US/docs/Web/API/File_System_Access_API
   * to open the file picker instead of using an `<input type="file">` click event.
   */
  useFsAccessApi: PropTypes.bool,
  /**
   * Set to true to focus the root element on render
   */
  autoFocus: PropTypes.bool,
  /**
   * Cb for when the `dragenter` event occurs.
   *
   * @param {DragEvent} event
   */
  onDragEnter: PropTypes.func,
  /**
   * Cb for when the `dragleave` event occurs
   *
   * @param {DragEvent} event
   */
  onDragLeave: PropTypes.func,
  /**
   * Cb for when the `dragover` event occurs
   *
   * @param {DragEvent} event
   */
  onDragOver: PropTypes.func,
  /**
   * Cb for when the `drop` event occurs.
   * Note that this callback is invoked after the `getFilesFromEvent` callback is done.
   *
   * Files are accepted or rejected based on the `accept`, `multiple`, `minSize` and `maxSize` props.
   * `accept` must be a valid [MIME type](http://www.iana.org/assignments/media-types/media-types.xhtml) according to [input element specification](https://www.w3.org/wiki/HTML/Elements/input/file) or a valid file extension.
   * If `multiple` is set to false and additional files are dropped,
   * all files besides the first will be rejected.
   * Any file which does not have a size in the [`minSize`, `maxSize`] range, will be rejected as well.
   *
   * Note that the `onDrop` callback will always be invoked regardless if the dropped files were accepted or rejected.
   * If you'd like to react to a specific scenario, use the `onDropAccepted`/`onDropRejected` props.
   *
   * `onDrop` will provide you with an array of [File](https://developer.mozilla.org/en-US/docs/Web/API/File) objects which you can then process and send to a server.
   * For example, with [SuperAgent](https://github.com/visionmedia/superagent) as a http/ajax library:
   *
   * ```js
   * function onDrop(acceptedFiles) {
   *   const req = request.post('/upload')
   *   acceptedFiles.forEach(file => {
   *     req.attach(file.name, file)
   *   })
   *   req.end(callback)
   * }
   * ```
   *
   * @param {File[]} acceptedFiles
   * @param {FileRejection[]} fileRejections
   * @param {(DragEvent|Event)} event A drag event or input change event (if files were selected via the file dialog)
   */
  onDrop: PropTypes.func,
  /**
   * Cb for when the `drop` event occurs.
   * Note that if no files are accepted, this callback is not invoked.
   *
   * @param {File[]} files
   * @param {(DragEvent|Event)} event
   */
  onDropAccepted: PropTypes.func,
  /**
   * Cb for when the `drop` event occurs.
   * Note that if no files are rejected, this callback is not invoked.
   *
   * @param {FileRejection[]} fileRejections
   * @param {(DragEvent|Event)} event
   */
  onDropRejected: PropTypes.func,
  /**
   * Cb for when there's some error from any of the promises.
   *
   * @param {Error} error
   */
  onError: PropTypes.func,
  /**
   * Custom validation function. It must return null if there's no errors.
   * @param {File} file
   * @returns {FileError|FileError[]|null}
   */
  validator: PropTypes.func
};
var initialState$1 = {
  isFocused: false,
  isFileDialogActive: false,
  isDragActive: false,
  isDragAccept: false,
  isDragReject: false,
  isDragGlobal: false,
  acceptedFiles: [],
  fileRejections: []
};
function useDropzone() {
  var props = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
  var _defaultProps$props = _objectSpread(_objectSpread({}, defaultProps), props), accept = _defaultProps$props.accept, disabled = _defaultProps$props.disabled, getFilesFromEvent = _defaultProps$props.getFilesFromEvent, maxSize = _defaultProps$props.maxSize, minSize = _defaultProps$props.minSize, multiple = _defaultProps$props.multiple, maxFiles = _defaultProps$props.maxFiles, onDragEnter = _defaultProps$props.onDragEnter, onDragLeave = _defaultProps$props.onDragLeave, onDragOver = _defaultProps$props.onDragOver, onDrop = _defaultProps$props.onDrop, onDropAccepted = _defaultProps$props.onDropAccepted, onDropRejected = _defaultProps$props.onDropRejected, onFileDialogCancel = _defaultProps$props.onFileDialogCancel, onFileDialogOpen = _defaultProps$props.onFileDialogOpen, useFsAccessApi = _defaultProps$props.useFsAccessApi, autoFocus = _defaultProps$props.autoFocus, preventDropOnDocument = _defaultProps$props.preventDropOnDocument, noClick = _defaultProps$props.noClick, noKeyboard = _defaultProps$props.noKeyboard, noDrag = _defaultProps$props.noDrag, noDragEventsBubbling = _defaultProps$props.noDragEventsBubbling, onError = _defaultProps$props.onError, validator = _defaultProps$props.validator;
  var acceptAttr = useMemo(function() {
    return acceptPropAsAcceptAttr(accept);
  }, [accept]);
  var pickerTypes = useMemo(function() {
    return pickerOptionsFromAccept(accept);
  }, [accept]);
  var onFileDialogOpenCb = useMemo(function() {
    return typeof onFileDialogOpen === "function" ? onFileDialogOpen : noop$1;
  }, [onFileDialogOpen]);
  var onFileDialogCancelCb = useMemo(function() {
    return typeof onFileDialogCancel === "function" ? onFileDialogCancel : noop$1;
  }, [onFileDialogCancel]);
  var rootRef = useRef(null);
  var inputRef = useRef(null);
  var _useReducer = useReducer(reducer$2, initialState$1), _useReducer2 = _slicedToArray(_useReducer, 2), state = _useReducer2[0], dispatch = _useReducer2[1];
  var isFocused = state.isFocused, isFileDialogActive = state.isFileDialogActive;
  var fsAccessApiWorksRef = useRef(typeof window !== "undefined" && window.isSecureContext && useFsAccessApi && canUseFileSystemAccessAPI());
  var onWindowFocus = function onWindowFocus2() {
    if (!fsAccessApiWorksRef.current && isFileDialogActive) {
      setTimeout(function() {
        if (inputRef.current) {
          var files = inputRef.current.files;
          if (!files.length) {
            dispatch({
              type: "closeDialog"
            });
            onFileDialogCancelCb();
          }
        }
      }, 300);
    }
  };
  useEffect(function() {
    window.addEventListener("focus", onWindowFocus, false);
    return function() {
      window.removeEventListener("focus", onWindowFocus, false);
    };
  }, [inputRef, isFileDialogActive, onFileDialogCancelCb, fsAccessApiWorksRef]);
  var dragTargetsRef = useRef([]);
  var globalDragTargetsRef = useRef([]);
  var onDocumentDrop = function onDocumentDrop2(event) {
    if (rootRef.current && rootRef.current.contains(event.target)) {
      return;
    }
    event.preventDefault();
    dragTargetsRef.current = [];
  };
  useEffect(function() {
    if (preventDropOnDocument) {
      document.addEventListener("dragover", onDocumentDragOver, false);
      document.addEventListener("drop", onDocumentDrop, false);
    }
    return function() {
      if (preventDropOnDocument) {
        document.removeEventListener("dragover", onDocumentDragOver);
        document.removeEventListener("drop", onDocumentDrop);
      }
    };
  }, [rootRef, preventDropOnDocument]);
  useEffect(function() {
    var onDocumentDragEnter = function onDocumentDragEnter2(event) {
      globalDragTargetsRef.current = [].concat(_toConsumableArray(globalDragTargetsRef.current), [event.target]);
      if (isEvtWithFiles(event)) {
        dispatch({
          isDragGlobal: true,
          type: "setDragGlobal"
        });
      }
    };
    var onDocumentDragLeave = function onDocumentDragLeave2(event) {
      globalDragTargetsRef.current = globalDragTargetsRef.current.filter(function(el) {
        return el !== event.target && el !== null;
      });
      if (globalDragTargetsRef.current.length > 0) {
        return;
      }
      dispatch({
        isDragGlobal: false,
        type: "setDragGlobal"
      });
    };
    var onDocumentDragEnd = function onDocumentDragEnd2() {
      globalDragTargetsRef.current = [];
      dispatch({
        isDragGlobal: false,
        type: "setDragGlobal"
      });
    };
    var onDocumentDropGlobal = function onDocumentDropGlobal2() {
      globalDragTargetsRef.current = [];
      dispatch({
        isDragGlobal: false,
        type: "setDragGlobal"
      });
    };
    document.addEventListener("dragenter", onDocumentDragEnter, false);
    document.addEventListener("dragleave", onDocumentDragLeave, false);
    document.addEventListener("dragend", onDocumentDragEnd, false);
    document.addEventListener("drop", onDocumentDropGlobal, false);
    return function() {
      document.removeEventListener("dragenter", onDocumentDragEnter);
      document.removeEventListener("dragleave", onDocumentDragLeave);
      document.removeEventListener("dragend", onDocumentDragEnd);
      document.removeEventListener("drop", onDocumentDropGlobal);
    };
  }, [rootRef]);
  useEffect(function() {
    if (!disabled && autoFocus && rootRef.current) {
      rootRef.current.focus();
    }
    return function() {
    };
  }, [rootRef, autoFocus, disabled]);
  var onErrCb = useCallback(function(e) {
    if (onError) {
      onError(e);
    } else {
      console.error(e);
    }
  }, [onError]);
  var onDragEnterCb = useCallback(function(event) {
    event.preventDefault();
    event.persist();
    stopPropagation(event);
    dragTargetsRef.current = [].concat(_toConsumableArray(dragTargetsRef.current), [event.target]);
    if (isEvtWithFiles(event)) {
      Promise.resolve(getFilesFromEvent(event)).then(function(files) {
        if (isPropagationStopped(event) && !noDragEventsBubbling) {
          return;
        }
        var fileCount = files.length;
        var isDragAccept = fileCount > 0 && allFilesAccepted({
          files,
          accept: acceptAttr,
          minSize,
          maxSize,
          multiple,
          maxFiles,
          validator
        });
        var isDragReject = fileCount > 0 && !isDragAccept;
        dispatch({
          isDragAccept,
          isDragReject,
          isDragActive: true,
          type: "setDraggedFiles"
        });
        if (onDragEnter) {
          onDragEnter(event);
        }
      }).catch(function(e) {
        return onErrCb(e);
      });
    }
  }, [getFilesFromEvent, onDragEnter, onErrCb, noDragEventsBubbling, acceptAttr, minSize, maxSize, multiple, maxFiles, validator]);
  var onDragOverCb = useCallback(function(event) {
    event.preventDefault();
    event.persist();
    stopPropagation(event);
    var hasFiles = isEvtWithFiles(event);
    if (hasFiles && event.dataTransfer) {
      try {
        event.dataTransfer.dropEffect = "copy";
      } catch (_unused) {
      }
    }
    if (hasFiles && onDragOver) {
      onDragOver(event);
    }
    return false;
  }, [onDragOver, noDragEventsBubbling]);
  var onDragLeaveCb = useCallback(function(event) {
    event.preventDefault();
    event.persist();
    stopPropagation(event);
    var targets = dragTargetsRef.current.filter(function(target) {
      return rootRef.current && rootRef.current.contains(target);
    });
    var targetIdx = targets.indexOf(event.target);
    if (targetIdx !== -1) {
      targets.splice(targetIdx, 1);
    }
    dragTargetsRef.current = targets;
    if (targets.length > 0) {
      return;
    }
    dispatch({
      type: "setDraggedFiles",
      isDragActive: false,
      isDragAccept: false,
      isDragReject: false
    });
    if (isEvtWithFiles(event) && onDragLeave) {
      onDragLeave(event);
    }
  }, [rootRef, onDragLeave, noDragEventsBubbling]);
  var setFiles = useCallback(function(files, event) {
    var acceptedFiles = [];
    var fileRejections = [];
    files.forEach(function(file) {
      var _fileAccepted = fileAccepted(file, acceptAttr), _fileAccepted2 = _slicedToArray(_fileAccepted, 2), accepted = _fileAccepted2[0], acceptError = _fileAccepted2[1];
      var _fileMatchSize = fileMatchSize(file, minSize, maxSize), _fileMatchSize2 = _slicedToArray(_fileMatchSize, 2), sizeMatch = _fileMatchSize2[0], sizeError = _fileMatchSize2[1];
      var customErrors = validator ? validator(file) : null;
      if (accepted && sizeMatch && !customErrors) {
        acceptedFiles.push(file);
      } else {
        var errors = [acceptError, sizeError];
        if (customErrors) {
          errors = errors.concat(customErrors);
        }
        fileRejections.push({
          file,
          errors: errors.filter(function(e) {
            return e;
          })
        });
      }
    });
    if (!multiple && acceptedFiles.length > 1 || multiple && maxFiles >= 1 && acceptedFiles.length > maxFiles) {
      acceptedFiles.forEach(function(file) {
        fileRejections.push({
          file,
          errors: [TOO_MANY_FILES_REJECTION]
        });
      });
      acceptedFiles.splice(0);
    }
    dispatch({
      acceptedFiles,
      fileRejections,
      isDragReject: fileRejections.length > 0,
      type: "setFiles"
    });
    if (onDrop) {
      onDrop(acceptedFiles, fileRejections, event);
    }
    if (fileRejections.length > 0 && onDropRejected) {
      onDropRejected(fileRejections, event);
    }
    if (acceptedFiles.length > 0 && onDropAccepted) {
      onDropAccepted(acceptedFiles, event);
    }
  }, [dispatch, multiple, acceptAttr, minSize, maxSize, maxFiles, onDrop, onDropAccepted, onDropRejected, validator]);
  var onDropCb = useCallback(function(event) {
    event.preventDefault();
    event.persist();
    stopPropagation(event);
    dragTargetsRef.current = [];
    if (isEvtWithFiles(event)) {
      Promise.resolve(getFilesFromEvent(event)).then(function(files) {
        if (isPropagationStopped(event) && !noDragEventsBubbling) {
          return;
        }
        setFiles(files, event);
      }).catch(function(e) {
        return onErrCb(e);
      });
    }
    dispatch({
      type: "reset"
    });
  }, [getFilesFromEvent, setFiles, onErrCb, noDragEventsBubbling]);
  var openFileDialog = useCallback(function() {
    if (fsAccessApiWorksRef.current) {
      dispatch({
        type: "openDialog"
      });
      onFileDialogOpenCb();
      var opts = {
        multiple,
        types: pickerTypes
      };
      window.showOpenFilePicker(opts).then(function(handles) {
        return getFilesFromEvent(handles);
      }).then(function(files) {
        setFiles(files, null);
        dispatch({
          type: "closeDialog"
        });
      }).catch(function(e) {
        if (isAbort(e)) {
          onFileDialogCancelCb(e);
          dispatch({
            type: "closeDialog"
          });
        } else if (isSecurityError(e)) {
          fsAccessApiWorksRef.current = false;
          if (inputRef.current) {
            inputRef.current.value = null;
            inputRef.current.click();
          } else {
            onErrCb(new Error("Cannot open the file picker because the https://developer.mozilla.org/en-US/docs/Web/API/File_System_Access_API is not supported and no <input> was provided."));
          }
        } else {
          onErrCb(e);
        }
      });
      return;
    }
    if (inputRef.current) {
      dispatch({
        type: "openDialog"
      });
      onFileDialogOpenCb();
      inputRef.current.value = null;
      inputRef.current.click();
    }
  }, [dispatch, onFileDialogOpenCb, onFileDialogCancelCb, useFsAccessApi, setFiles, onErrCb, pickerTypes, multiple]);
  var onKeyDownCb = useCallback(function(event) {
    if (!rootRef.current || !rootRef.current.isEqualNode(event.target)) {
      return;
    }
    if (event.key === " " || event.key === "Enter" || event.keyCode === 32 || event.keyCode === 13) {
      event.preventDefault();
      openFileDialog();
    }
  }, [rootRef, openFileDialog]);
  var onFocusCb = useCallback(function() {
    dispatch({
      type: "focus"
    });
  }, []);
  var onBlurCb = useCallback(function() {
    dispatch({
      type: "blur"
    });
  }, []);
  var onClickCb = useCallback(function() {
    if (noClick) {
      return;
    }
    if (isIeOrEdge()) {
      setTimeout(openFileDialog, 0);
    } else {
      openFileDialog();
    }
  }, [noClick, openFileDialog]);
  var composeHandler = function composeHandler2(fn) {
    return disabled ? null : fn;
  };
  var composeKeyboardHandler = function composeKeyboardHandler2(fn) {
    return noKeyboard ? null : composeHandler(fn);
  };
  var composeDragHandler = function composeDragHandler2(fn) {
    return noDrag ? null : composeHandler(fn);
  };
  var stopPropagation = function stopPropagation2(event) {
    if (noDragEventsBubbling) {
      event.stopPropagation();
    }
  };
  var getRootProps = useMemo(function() {
    return function() {
      var _ref2 = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {}, _ref2$refKey = _ref2.refKey, refKey = _ref2$refKey === void 0 ? "ref" : _ref2$refKey, role = _ref2.role, onKeyDown = _ref2.onKeyDown, onFocus = _ref2.onFocus, onBlur = _ref2.onBlur, onClick = _ref2.onClick, onDragEnter2 = _ref2.onDragEnter, onDragOver2 = _ref2.onDragOver, onDragLeave2 = _ref2.onDragLeave, onDrop2 = _ref2.onDrop, rest = _objectWithoutProperties(_ref2, _excluded3);
      return _objectSpread(_objectSpread(_defineProperty({
        onKeyDown: composeKeyboardHandler(composeEventHandlers(onKeyDown, onKeyDownCb)),
        onFocus: composeKeyboardHandler(composeEventHandlers(onFocus, onFocusCb)),
        onBlur: composeKeyboardHandler(composeEventHandlers(onBlur, onBlurCb)),
        onClick: composeHandler(composeEventHandlers(onClick, onClickCb)),
        onDragEnter: composeDragHandler(composeEventHandlers(onDragEnter2, onDragEnterCb)),
        onDragOver: composeDragHandler(composeEventHandlers(onDragOver2, onDragOverCb)),
        onDragLeave: composeDragHandler(composeEventHandlers(onDragLeave2, onDragLeaveCb)),
        onDrop: composeDragHandler(composeEventHandlers(onDrop2, onDropCb)),
        role: typeof role === "string" && role !== "" ? role : "presentation"
      }, refKey, rootRef), !disabled && !noKeyboard ? {
        tabIndex: 0
      } : {}), rest);
    };
  }, [rootRef, onKeyDownCb, onFocusCb, onBlurCb, onClickCb, onDragEnterCb, onDragOverCb, onDragLeaveCb, onDropCb, noKeyboard, noDrag, disabled]);
  var onInputElementClick = useCallback(function(event) {
    event.stopPropagation();
  }, []);
  var getInputProps = useMemo(function() {
    return function() {
      var _ref3 = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {}, _ref3$refKey = _ref3.refKey, refKey = _ref3$refKey === void 0 ? "ref" : _ref3$refKey, onChange = _ref3.onChange, onClick = _ref3.onClick, rest = _objectWithoutProperties(_ref3, _excluded4);
      var inputProps = _defineProperty({
        accept: acceptAttr,
        multiple,
        type: "file",
        style: {
          border: 0,
          clip: "rect(0, 0, 0, 0)",
          clipPath: "inset(50%)",
          height: "1px",
          margin: "0 -1px -1px 0",
          overflow: "hidden",
          padding: 0,
          position: "absolute",
          width: "1px",
          whiteSpace: "nowrap"
        },
        onChange: composeHandler(composeEventHandlers(onChange, onDropCb)),
        onClick: composeHandler(composeEventHandlers(onClick, onInputElementClick)),
        tabIndex: -1
      }, refKey, inputRef);
      return _objectSpread(_objectSpread({}, inputProps), rest);
    };
  }, [inputRef, accept, multiple, onDropCb, disabled]);
  return _objectSpread(_objectSpread({}, state), {}, {
    isFocused: isFocused && !disabled,
    getRootProps,
    getInputProps,
    rootRef,
    inputRef,
    open: composeHandler(openFileDialog)
  });
}
function reducer$2(state, action) {
  switch (action.type) {
    case "focus":
      return _objectSpread(_objectSpread({}, state), {}, {
        isFocused: true
      });
    case "blur":
      return _objectSpread(_objectSpread({}, state), {}, {
        isFocused: false
      });
    case "openDialog":
      return _objectSpread(_objectSpread({}, initialState$1), {}, {
        isFileDialogActive: true
      });
    case "closeDialog":
      return _objectSpread(_objectSpread({}, state), {}, {
        isFileDialogActive: false
      });
    case "setDraggedFiles":
      return _objectSpread(_objectSpread({}, state), {}, {
        isDragActive: action.isDragActive,
        isDragAccept: action.isDragAccept,
        isDragReject: action.isDragReject
      });
    case "setFiles":
      return _objectSpread(_objectSpread({}, state), {}, {
        acceptedFiles: action.acceptedFiles,
        fileRejections: action.fileRejections,
        isDragReject: action.isDragReject
      });
    case "setDragGlobal":
      return _objectSpread(_objectSpread({}, state), {}, {
        isDragGlobal: action.isDragGlobal
      });
    case "reset":
      return _objectSpread({}, initialState$1);
    default:
      return state;
  }
}
function noop$1() {
}
const classes = (classNames) => Object.entries(classNames).filter((x3) => x3[1]).map((x3) => x3[0]).join(" ");
const classObject = (classNames) => classNames.split(/\s/).reduce((prev, curr) => {
  if (curr) {
    prev[curr] = true;
  }
  return prev;
}, {});
const DateRangeTypes = {
  Today: 1,
  Yesterday: 2,
  WeekToDate: 3,
  LastWeek: 4,
  MonthToDate: 5,
  LastMonth: 6,
  Last30: 7,
  Last3Months: 8,
  Last6Months: 9,
  YearToDate: 10
};
const DateRangeTypeLabels = {
  1: "Today",
  2: "Yesterday",
  3: "Week to date",
  4: "Last week",
  5: "Month to date",
  6: "Last month",
  7: "Last 30 days",
  8: "Last 3 months",
  9: "Last 6 months",
  10: "Year to date"
};
const DateRangeSettings = {
  [DateRangeTypes.Today]: {
    unit: "day"
  },
  [DateRangeTypes.Yesterday]: {
    unit: "day",
    offset_start: { days: -1 },
    offset_end: { days: -1 }
  },
  [DateRangeTypes.WeekToDate]: {
    unit: "week"
  },
  [DateRangeTypes.LastWeek]: {
    unit: "week",
    offset_start: { weeks: -1 },
    offset_end: { weeks: -1, days: -1 }
  },
  [DateRangeTypes.MonthToDate]: {
    unit: "month"
  },
  [DateRangeTypes.LastMonth]: {
    unit: "month",
    offset_start: { months: -1 },
    offset_end: { months: -1 }
  },
  [DateRangeTypes.Last30]: {
    unit: "days",
    offset_start: { days: -30 }
  },
  [DateRangeTypes.YearToDate]: {
    unit: "year"
  },
  [DateRangeTypes.Last3Months]: {
    unit: "months",
    offset_start: { months: -3 },
    offset_end: { months: -1 }
  },
  [DateRangeTypes.Last6Months]: {
    unit: "months",
    offset_start: { months: -6 },
    offset_end: { months: -1 }
  }
};
const buildDateRangeForType = ({
  type = DateRangeTypes.MonthToDate,
  from = DateTime.local(),
  sourceTZ = void 0,
  destinationTZ = "utc"
}) => {
  const { unit, offset_start, offset_end } = DateRangeSettings[type];
  const startOfRange = offset_start ? from.startOf(unit).plus(offset_start).startOf(unit) : from.startOf(unit);
  const endOfRange = offset_end ? from.endOf(unit).plus(offset_end).endOf(unit) : from.endOf(unit);
  return [
    toISO(
      startOfRange.set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0
      }),
      destinationTZ,
      sourceTZ
    ),
    toISO(
      endOfRange.set({
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 999
      }),
      destinationTZ,
      sourceTZ
    )
  ];
};
const convertISOtoCalendarDateTime = (dt, defaultValue, sourceTZ, destinationTZ) => dt ? Array.isArray(dt) ? dt.map(
  (x3) => parseDateTime(
    toISO(
      fromISO(x3, sourceTZ),
      destinationTZ || SystemZone.instance.name,
      sourceTZ || "utc"
    )
  )
) : parseDateTime(
  toISO(
    fromISO(dt, sourceTZ),
    destinationTZ || SystemZone.instance.name,
    sourceTZ || "utc"
  )
) : defaultValue;
const convertCalendarDateRange = (range, sourceTZ, destinationTZ) => {
  if (!Array.isArray(range)) {
    throw new Error("range should be an array of CalendarDate");
  }
  let [start, end] = range;
  start = toCalendarDateTime(start);
  end = toCalendarDateTime(end, new Time(23, 59, 59, 999));
  return [
    convertISOtoCalendarDateTime(
      start.toString(),
      void 0,
      sourceTZ || SystemZone.instance.name,
      destinationTZ || "utc"
    ),
    convertISOtoCalendarDateTime(
      end.toString(),
      void 0,
      sourceTZ || SystemZone.instance.name,
      destinationTZ || "utc"
    )
  ];
};
const formatPhone = (value) => {
  if (!value) return value;
  const raw = String(value);
  let digits2 = raw.replace(/\D/g, "");
  if (digits2.length === 11 && digits2.startsWith("1")) digits2 = digits2.slice(1);
  if (digits2.length !== 10) return raw;
  return `(${digits2.slice(0, 3)}) ${digits2.slice(3, 6)}-${digits2.slice(6)}`;
};
const keyPressedHandler = (key, handler) => {
  return (e) => {
    if (e.key.toLowerCase() === key.toLowerCase()) {
      e.preventDefault();
      handler();
    }
  };
};
const cardNumberMask = "0000-0000-0000-0000";
const cardDateMask = "00/00";
const cardCvvMask = "000";
const internationalPhoneRegex = /^$|^\+?\d{11,15}$/;
const numberWithDecimalsRegex = /^(\d+)?(\.\d*)?$/;
const masks = {
  phone: {
    mask: "(000) 000-0000",
    placeholder: "(123) 456-7890"
  },
  internationalPhone: {
    mask: "+00000000000[0000]",
    placeholder: "+1 (234) 567-8900"
  },
  cardNumberMask,
  cardDateMask,
  cardCvvMask,
  internationalPhoneRegex,
  numberWithDecimalsRegex
};
const noop = () => {
};
const echo = (x3) => x3;
const startsWith = (str, prefix) => str.toLowerCase().startsWith(prefix.toLowerCase());
const getBase64 = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split("base64,")[1];
      resolve(base64);
    };
    reader.readAsDataURL(file);
  });
};
const iconByType = {
  "text/xml": "ri-file-code-line",
  "text/plain": "ri-file-text-line",
  "application/pdf": "ri-file-pdf-line",
  "application/json": "ri-braces-line",
  "application/msword": "ri-file-word-line",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "ri-file-word-2-line",
  "application/vnd.ms-excel": "ri-file-excel-line",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "ri-file-excel-2-line",
  "application/vnd.ms-powerpoint": "ri-file-ppt-line",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "ri-file-ppt-2-line",
  "application/zip": "ri-file-zip-line",
  "application/x-rar-compressed": "ri-file-zip-line",
  "application/x-tar": "ri-file-zip-line",
  "application/x-7z-compressed": "ri-file-zip-line",
  "application/x-gzip": "ri-file-zip-line",
  "application/x-bzip2": "ri-file-zip-line",
  "image/jpeg": "ri-file-image-line",
  "image/png": "ri-file-image-line",
  "image/gif": "ri-file-image-line",
  "image/bmp": "ri-file-image-line",
  "image/webp": "ri-file-image-line",
  "image/svg+xml": "ri-file-image-line",
  "image/tiff": "ri-file-image-line",
  "image/vnd.microsoft.icon": "ri-file-image-line",
  "image/vnd.adobe.photoshop": "ri-file-image-line",
  "image/x-icon": "ri-file-image-line",
  "image/x-photoshop": "ri-file-image-line",
  "image/x-tga": "ri-file-image-line",
  "image/x-xcf": "ri-file-image-line",
  "image/x-pcx": "ri-file-image-line",
  "video/mp4": "ri-file-video-line",
  "video/quicktime": "ri-file-video-line",
  "video/x-msvideo": "ri-file-video-line",
  "video/x-flv": "ri-file-video-line",
  "video/x-m4v": "ri-file-video-line",
  "video/x-ms-wmv": "ri-file-video-line",
  "audio/mpeg": "ri-file-music-line"
};
const getChildren = (children) => {
  let _children = u.Children.toArray(children);
  if (_children.length === 1 && u.isValidElement(_children[0]) && _children[0].type === u.Fragment) {
    _children = _children[0].children;
  }
  return _children ?? [];
};
const _matchesType = (element, type) => {
  return element && typeof element === "object" && element.type && element.type.displayName === type.displayName;
};
const getElementByType = (children, type) => getChildren(children).find((x3) => _matchesType(x3, type));
const getElementsByType = (children, type) => getChildren(children).filter((x3) => x3.type === type).map((x3) => u.createElement(x3));
const copyStatics = (source, target) => {
  const base = ["length", "name", "prototyp"];
  Object.getOwnPropertyNames(source).filter((x3) => !base.includes(x3)).forEach((x3) => target[x3] = source[x3]);
  return target;
};
const createChildRenderer = (displayName) => {
  const render = ({ children }) => {
    return children ?? null;
  };
  render.displayName = displayName;
  return render;
};
const createRenderer = (displayName, render) => {
  render.displayName = displayName;
  return render;
};
const reactUtils = {
  getChildren,
  getElementByType,
  getElementsByType,
  copyStatics,
  createChildRenderer,
  createRenderer
};
const convertWithRemainder = (value, sourceUnits, targetUnits) => {
  const converted = Math.floor(convert(value, sourceUnits, targetUnits));
  const remainder = value - convert(converted, targetUnits, sourceUnits);
  return [converted, remainder];
};
const convertToUnits = (value = 0, sourceUnits) => {
  const [weeks, rw] = convertWithRemainder(value, sourceUnits, "w");
  const [days, rd] = convertWithRemainder(rw, sourceUnits, "d");
  const [hours, rh] = convertWithRemainder(rd, sourceUnits, "h");
  const [minutes, rm] = convertWithRemainder(rh, sourceUnits, "m");
  const [seconds, rs] = convertWithRemainder(rm, sourceUnits, "s");
  const [milliseconds] = convertWithRemainder(rs, sourceUnits, "ms");
  return { weeks, days, hours, minutes, seconds, milliseconds };
};
const TimeUnitPriority = {
  w: 1,
  week: 1,
  d: 2,
  day: 2,
  h: 3,
  hour: 3,
  m: 4,
  minute: 4,
  s: 5,
  second: 5,
  ms: 6,
  millisecond: 6
};
const internationalPhoneOptions = {
  validationMessage: "Invalid phone number",
  required: false,
  requiredMessage: "Phone # is required"
};
const internationalPhone = (options) => {
  const merged = { ...internationalPhoneOptions, ...options };
  return (validator) => validator.string().min(12, merged.validationMessage).required(merged.required, merged.requiredMessage);
};
const cardNumberOptions = {
  validationMessage: "Invalid card number",
  required: false,
  requiredMessage: "Card number is required"
};
const cardNumber = (options) => {
  const merged = { ...cardNumberOptions, ...options };
  return (validator) => validator.string().min(11, merged.validationMessage).required(merged.required, merged.requiredMessage);
};
const cardDateOptions = {
  validationMessage: "Invalid date",
  required: false,
  requiredMessage: "Expiration date is required"
};
const cardDate = (options) => {
  const merged = { ...cardDateOptions, ...options };
  return (validator) => validator.string().min(3, merged.validationMessage).required(merged.required, merged.requiredMessage);
};
const cardCvvOptions = {
  validationMessage: "Invalid CVV",
  required: false,
  requiredMessage: "CVV is required"
};
const cardCvv = (options) => {
  const merged = { ...cardCvvOptions, ...options };
  return (validator) => validator.string().min(2, merged.validationMessage).required(merged.required, merged.requiredMessage);
};
const emailOptions = {
  validationMessage: "Invalid email",
  required: false,
  requiredMessage: "Email is required"
};
const email = (options) => {
  const merged = { ...emailOptions, ...objectReject(options, (x3) => x3 === null || x3 === void 0) };
  return (validator) => {
    validator = validator.string().email(merged.validationMessage);
    return merged.required ? validator.required(merged.requiredMessage) : validator;
  };
};
let MAX_DATE = /* @__PURE__ */ new Date(864e13);
let MIN_DATE = /* @__PURE__ */ new Date(-864e13);
const dateOptions = {
  validationMessage: "Invalid date",
  required: false,
  requiredMessage: "Date is required"
};
const anyDate = (options) => {
  const merged = { ...dateOptions };
  return (validator) => validator.date().min(MIN_DATE, merged.validationMessage).max(MAX_DATE, merged.validationMessage).typeError(merged.validationMessage).required(merged.required, merged.requiredMessage);
};
const maxDate = (max, options) => {
  const merged = { ...dateOptions };
  return (validator) => validator.date().min(MIN_DATE, merged.validationMessage).max(max, merged.validationMessage).typeError(merged.validationMessage).required(merged.required, merged.requiredMessage);
};
const minDate = (min, options) => {
  const merged = { ...dateOptions };
  return (validator) => validator.date().min(min, merged.validationMessage).max(MAX_DATE, merged.validationMessage).typeError(merged.validationMessage).required(merged.required, merged.requiredMessage);
};
const minMaxDate = (min, max, options) => {
  const merged = { ...dateOptions };
  return (validator) => validator.date().min(min, merged.validationMessage).max(max, merged.validationMessage).typeError(merged.validationMessage).required(merged.required, merged.requiredMessage);
};
const anyDateRange = (options) => {
  const merged = { ...dateOptions };
  return (validator) => validator.array().length(2).nullable().of(
    validator.date().min(MIN_DATE, merged.validationMessage).max(MAX_DATE, merged.validationMessage).typeError(merged.validationMessage).required(merged.required, merged.requiredMessage)
  ).required(merged.required, merged.requiredMessage);
};
const timePattern = /^([01]\d|2[0-3]):([0-5]\d)(:([0-5]\d))?$/;
const anyTimeRange = (options) => {
  const merged = { ...timeOptions };
  return (validator) => validator.array().length(2).nullable().of(
    validator.string().matches(timePattern, merged.validationMessage).nullable().required(merged.requiredMessage)
  ).required(merged.required, merged.requiredMessage);
};
const timeOptions = {
  validationMessage: "Invalid time",
  required: false,
  requiredMessage: "Time is required"
};
const time = (options) => {
  const merged = { ...timeOptions };
  return (validator) => validator.string().matches(timePattern, merged.validationMessage).nullable().required(merged.requiredMessage);
};
const address = (validator) => validator.object().shape({
  address1: validator.string().required("Street address is required"),
  city: validator.string().required("City name is required"),
  state: validator.string().required("State is required").length(2, "State should be 2 characters"),
  postalCode: validator.string().required("Zip Code is required")
});
const validators = {
  internationalPhone,
  cardNumber,
  cardDate,
  cardCvv,
  email,
  anyDate,
  maxDate,
  minDate,
  minMaxDate,
  anyDateRange,
  time,
  anyTimeRange,
  address
};
const defaultMonitors = {
  save: { label: "Saving...", color: "success", executing: false },
  remove: { label: "Removing...", color: "danger", executing: false },
  removeAll: { label: "Removing all...", color: "danger", executing: false },
  download: { label: "Downloading...", color: "accent", executing: false }
};
const mergerMonitors = (defaultMonitor, monitors) => {
  return {
    ...defaultMonitor,
    ...monitors,
    ...Object.keys(defaultMonitors).reduce((acc, key) => {
      if (!monitors[key]) return acc;
      acc[key] = { ...defaultMonitors[key], ...monitors[key] };
      return acc;
    }, {})
  };
};
const Attachments = ({
  attachments,
  transformOut = echo,
  onSave,
  onRemove,
  onRemoveAll,
  onDownload,
  monitors = defaultMonitors,
  classScrollShadowName = "h-200"
}) => {
  monitors = mergerMonitors(defaultMonitors, monitors);
  const [files, setFiles] = useState(attachments || []);
  const action = useDeepCompareMemoize(Object.values(monitors).find((x3) => x3.executing));
  const handleSave = async (localFiles) => {
    const filesWithBase64 = await Promise.all(
      localFiles.map(transformOut).map(async (file) => ({
        ...file,
        data: await getBase64(file.data)
      }))
    );
    onSave && onSave(filesWithBase64);
  };
  const handleAdd = (sources) => {
    const duplicatedFiles = sources.filter(
      (newFile) => files.some((prevFile) => prevFile.name.toLowerCase() === newFile.name.toLowerCase())
    );
    let newSources = [];
    if (duplicatedFiles.length === 0)
      newSources = sources.map((file) => ({
        name: file.name,
        type: file.type,
        data: file
      }));
    else
      newSources = sources.filter(
        (newFile) => !duplicatedFiles.some(
          (duplicatedFile) => duplicatedFile.name.toLowerCase() === newFile.name.toLowerCase()
        )
      ).map((file) => ({
        name: file.name,
        type: file.type,
        data: file
      }));
    newSources.length > 0 && handleSave(newSources);
  };
  const handleRemove = (attachment) => {
    onRemove && onRemove(attachment);
  };
  const handleRemoveAll = () => {
    onRemoveAll && onRemoveAll();
  };
  const handleDownload = (attachment) => {
    onDownload && onDownload(attachment);
  };
  const onDrop = useCallback(
    (acceptedFiles) => {
      handleAdd(acceptedFiles);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [useDeepCompareMemoize(files)]
  );
  const { getRootProps, getInputProps, open } = useDropzone({ onDrop });
  useDeepCompareEffect(() => {
    if (Array.isArray(attachments)) setFiles(attachments);
  }, attachments);
  return /* @__PURE__ */ jsxs("div", { className: `flex flex-col ${classScrollShadowName}`, children: [
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: "flex flex-row justify-between items-center gap-2 border",
        style: { borderTopLeftRadius: "10px", borderTopRightRadius: "10px" },
        children: [
          /* @__PURE__ */ jsxs("div", { className: "flex flex-row gap-2", children: [
            /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
              /* @__PURE__ */ jsx(Button, { isIconOnly: true, variant: "tertiary", onPress: open, children: /* @__PURE__ */ jsx("i", { className: "text-lg text-accent ri-attachment-line" }) }),
              /* @__PURE__ */ jsx(Tooltip.Content, { placement: "top", children: "Choose files to upload" })
            ] }),
            /* @__PURE__ */ jsx(
              ConfirmableButton,
              {
                isIconOnly: true,
                variant: "light",
                color: "danger",
                iconClass: "text-lg ri-delete-bin-line",
                onConfirm: handleRemoveAll,
                isDisabled: files.length === 0,
                body: /* @__PURE__ */ jsx("p", { className: "text-muted", children: "Are you sure you want to remove all attachments?" })
              }
            )
          ] }),
          action?.executing ? /* @__PURE__ */ jsxs(
            ProgressBar,
            {
              isIndeterminate: true,
              className: "max-w-md",
              size: "sm",
              "aria-label": action.label,
              color: action.color === "primary" ? "accent" : action.color,
              children: [
                /* @__PURE__ */ jsx(Label, { children: action.label }),
                /* @__PURE__ */ jsx(ProgressBar.Track, { children: /* @__PURE__ */ jsx(ProgressBar.Fill, {}) })
              ]
            }
          ) : /* @__PURE__ */ jsx("div", { className: "flex text-xs text-muted gab-2", children: /* @__PURE__ */ jsx(Chip, { color: "accent", variant: "soft", size: "sm", children: files.length > 0 ? files.length === 1 ? "1 File" : `${files.length} Files` : "No Files" }) })
        ]
      }
    ),
    /* @__PURE__ */ jsx(ScrollShadow, { className: classScrollShadowName, hideScrollBar: true, children: /* @__PURE__ */ jsxs(
      "div",
      {
        className: `flex flex-col gap-2 ${files.length === 0 ? "justify-center h-[750px]" : ""} text-center border`,
        style: { borderBottomLeftRadius: "10px", borderBottomRightRadius: "10px" },
        ...getRootProps(),
        children: [
          /* @__PURE__ */ jsx("input", { ...getInputProps() }),
          files.length === 0 && /* @__PURE__ */ jsxs("div", { style: { cursor: "pointer" }, children: [
            /* @__PURE__ */ jsx("i", { className: "text-6xl ri-attachment-line" }),
            /* @__PURE__ */ jsx("div", { children: "Drag and Drop Files Here" })
          ] }),
          files.length > 0 && /* @__PURE__ */ jsx(ListBox, { "aria-label": "attachments", "aria-labelledby": "attachments", children: files.sort((a, b2) => a.name.localeCompare(b2.name)).map((attachment, index) => /* @__PURE__ */ jsxs(
            ListBox.Item,
            {
              id: `attachment-${index}`,
              textValue: attachment.name,
              "aria-label": attachment.name,
              className: "group flex justify-between items-center",
              children: [
                /* @__PURE__ */ jsxs("div", { className: "flex flex-row gap-2 items-center", children: [
                  /* @__PURE__ */ jsx(Chip, { color: "accent", variant: "soft", size: "sm", children: iconByType[attachment.type] ? /* @__PURE__ */ jsx("i", { className: `text-lg ${iconByType[attachment.type]}` }) : /* @__PURE__ */ jsx("i", { className: "text-lg ri-file-line" }) }),
                  /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1 items-start", children: [
                    /* @__PURE__ */ jsx("div", { className: "text-sm text-muted", children: attachment.name.length >= 30 ? `${attachment.name.substring(0, 30)}...` : attachment.name }),
                    /* @__PURE__ */ jsx("div", { className: "text-xs text-muted", children: attachment.type })
                  ] })
                ] }),
                /* @__PURE__ */ jsxs("div", { className: "flex flex-row gap-2", children: [
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      isIconOnly: true,
                      variant: "tertiary",
                      onPress: () => handleDownload(attachment),
                      className: "invisible group-hover:visible",
                      children: /* @__PURE__ */ jsx("i", { className: "text-lg ri-download-cloud-line" })
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    DeleteButton,
                    {
                      isIconOnly: true,
                      variant: "light",
                      color: "danger",
                      onPress: () => handleRemove(attachment)
                    }
                  )
                ] })
              ]
            },
            `attachment-${index}`
          )) })
        ]
      }
    ) })
  ] });
};
const DetailCard = ({ title, action, children, className = "" }) => /* @__PURE__ */ jsxs(
  "div",
  {
    className: `overflow-hidden rounded-[10px] border border-[var(--border-default-c)] bg-[var(--surface)] ${className}`,
    children: [
      title && /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between border-b border-[var(--border-subtle)] px-5 py-2.5", children: [
        /* @__PURE__ */ jsx("span", { className: "text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--fg-3)]", children: title }),
        action && /* @__PURE__ */ jsx("div", { className: "flex items-center gap-1.5", children: action })
      ] }),
      children
    ]
  }
);
const FieldRow = ({
  label,
  value,
  children,
  last,
  labelWidth = "90px",
  px = "px-4",
  className = ""
}) => /* @__PURE__ */ jsxs(
  ItemCard,
  {
    variant: "transparent",
    className: `gap-3 rounded-none ${px} py-2.5 ${last ? "" : "border-b border-[var(--border-subtle)]"} ${className}`,
    children: [
      /* @__PURE__ */ jsx(ItemCard.Content, { className: "flex-none", style: { minWidth: labelWidth }, children: /* @__PURE__ */ jsx(ItemCard.Title, { className: "text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--fg-3)]", children: label }) }),
      /* @__PURE__ */ jsx(ItemCard.Action, { className: "min-w-0 flex-1 pl-0 text-[13.5px] text-[var(--foreground)]", children: value ?? children ?? "—" })
    ]
  }
);
const toCoords = (address2) => Number.isFinite(address2?.lat) && Number.isFinite(address2?.lon) ? { lat: address2.lat, lng: address2.lon } : null;
function GoogleMap({
  address: address2,
  defaultLat = 42.8940358,
  defaultLng = -112.4793466,
  defaultZoom = 15,
  classNames = {},
  children
}) {
  const places = useMapsLibrary("places");
  const [resolved, setResolved] = useState(() => toCoords(address2));
  const [camera, setCamera] = useState({
    zoom: defaultZoom,
    center: toCoords(address2) ?? { lat: defaultLat, lng: defaultLng }
  });
  useEffect(() => {
    const coords = toCoords(address2);
    if (coords) {
      setResolved(coords);
      setCamera({ zoom: defaultZoom, center: coords });
      return;
    }
    if (!places || !address2?.address1) return;
    let stale = false;
    const query = [address2.address1, address2.city, address2.state, address2.postalCode].filter(Boolean).join(", ");
    const service = new places.PlacesService(document.createElement("div"));
    service.findPlaceFromQuery({ query, fields: ["geometry"] }, (results) => {
      const location = results?.[0]?.geometry?.location;
      if (stale || !location) return;
      const found = { lat: location.lat(), lng: location.lng() };
      setResolved(found);
      setCamera({ zoom: defaultZoom, center: found });
    });
    return () => {
      stale = true;
    };
  }, [places, address2]);
  const centerMarker = useMemo(() => {
    return /* @__PURE__ */ jsx(Marker, { position: resolved ?? { lat: defaultLat, lng: defaultLng } });
  }, [resolved]);
  return /* @__PURE__ */ jsx("div", { className: classNames.wrapper ?? "w-full h-[500px]", children: /* @__PURE__ */ jsxs(
    Map$1,
    {
      className: classNames.map ?? "",
      onCameraChanged: (newCamera) => setCamera(newCamera.detail),
      ...camera,
      children: [
        centerMarker,
        children
      ]
    }
  ) });
}
const GoogleMapCircle = ({
  center,
  radius,
  strokeColor = "#000000",
  strokeOpacity = 0.6,
  strokeWeight = 1,
  fillColor = "#ffffff",
  fillOpacity = 0.2
}) => {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const circle = new window.google.maps.Circle({
      strokeColor,
      strokeOpacity,
      strokeWeight,
      fillColor,
      fillOpacity,
      map,
      center,
      radius
    });
    return () => circle.setMap(null);
  }, [map, center, radius]);
  return null;
};
const GoogleMapMarker = ({ position, icon, ...rest }) => /* @__PURE__ */ jsx(Marker, { position, icon, ...rest });
const ICON_BTN_VARIANT = {
  default: "border border-[var(--border-default-c)] bg-[var(--surface)] text-[var(--fg-1)] hover:bg-[var(--nav-hover-bg)]",
  danger: "border border-[var(--border-default-c)] bg-transparent text-[var(--lf-danger-500)] hover:bg-[var(--pill-danger-bg)]"
};
const ICON_BTN_SIZE = {
  md: "h-[34px] w-[34px] text-[14px]",
  sm: "h-7 w-7 text-[13px]"
};
const IconButton = ({ icon, variant = "default", size = "md", label, className = "", ...rest }) => /* @__PURE__ */ jsx(
  "button",
  {
    type: "button",
    "aria-label": label,
    title: label,
    className: `inline-flex flex-none items-center justify-center rounded-md transition-colors ${ICON_BTN_SIZE[size] ?? ICON_BTN_SIZE.md} ${ICON_BTN_VARIANT[variant] ?? ICON_BTN_VARIANT.default} ${className}`,
    ...rest,
    children: /* @__PURE__ */ jsx("i", { className: icon })
  }
);
const KeyDisplay = ({ keyValue, maxLength, dir = "start" }) => /* @__PURE__ */ jsxs("div", { className: "flex gap-1 items-center", children: [
  /* @__PURE__ */ jsx("code", { className: "text-xs text-accent bg-accent-soft px-1.5 py-0.5 rounded-sm", children: maxLength > 0 && maxLength < keyValue.length ? /* @__PURE__ */ jsxs(Fragment, { children: [
    dir === "start" && `${keyValue.slice(0, maxLength)}...`,
    dir === "end" && `...${keyValue.slice(-maxLength)}`
  ] }) : /* @__PURE__ */ jsx(Fragment, { children: keyValue }) }),
  /* @__PURE__ */ jsx(CopyToClipboard$1, { text: keyValue })
] });
const LockedValue = ({ icon, mono = false, note = "cannot be changed", children }) => /* @__PURE__ */ jsxs(
  "div",
  {
    className: `inline-flex h-9 items-center gap-2 rounded-md border border-[var(--border-subtle)] bg-[var(--bg-app)] px-3 text-[13px] text-[var(--fg-2)] ${mono ? "font-mono" : ""}`,
    children: [
      icon && /* @__PURE__ */ jsx("i", { className: icon }),
      children,
      /* @__PURE__ */ jsxs("span", { className: "ml-2 font-sans text-[var(--fg-3)]", children: [
        "· ",
        note
      ] })
    ]
  }
);
const PILL_TONES = {
  active: "bg-[var(--pill-success-bg)] text-[var(--pill-success-fg)]",
  warn: "bg-[var(--pill-warning-bg)] text-[var(--pill-warning-fg)]",
  danger: "bg-[var(--pill-danger-bg)] text-[var(--pill-danger-fg)]",
  info: "bg-[var(--pill-info-bg)] text-[var(--pill-info-fg)]",
  neutral: "bg-[var(--pill-neutral-bg)] text-[var(--pill-neutral-fg)]",
  // CSS-token vocabulary aliases (so 'success'/'warning' resolve too).
  success: "bg-[var(--pill-success-bg)] text-[var(--pill-success-fg)]",
  warning: "bg-[var(--pill-warning-bg)] text-[var(--pill-warning-fg)]"
};
const Pill = ({ tone = "neutral", className = "", children }) => /* @__PURE__ */ jsx(
  "span",
  {
    className: `inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold leading-4 tracking-[0.01em] ${PILL_TONES[tone] ?? PILL_TONES.neutral} ${className}`,
    children
  }
);
const Autocomplete = (props) => {
  const {
    name,
    value,
    onChange,
    items,
    children,
    label,
    placeholder,
    extractValue = (val) => val.toString(),
    extractDisplayValue = (val) => val.toString(),
    transformIn = (val) => val.toString(),
    transformOut = (val) => val,
    ...rest
  } = props;
  const toKey = (val) => val != null && val !== "" ? transformIn(val) : null;
  const findItem = (key) => (items ?? []).find((option) => extractValue(option) === key);
  const displayOf = (key) => {
    const item = findItem(key);
    return item ? extractDisplayValue(item) : "";
  };
  const [fieldState, setFieldState] = useState(() => ({
    selectedKey: toKey(value),
    inputValue: displayOf(toKey(value)),
    items: items ?? []
  }));
  useEffect(() => {
    const key = toKey(value);
    setFieldState((prev) => ({
      selectedKey: key,
      inputValue: key != null ? displayOf(key) || prev.inputValue : "",
      items: items ?? []
    }));
  }, [value, items]);
  const { contains } = useFilter({ sensitivity: "base" });
  const onSelectionChange = (key) => {
    if (key == null) return;
    setFieldState((prev) => ({
      selectedKey: key,
      inputValue: displayOf(key),
      items: items ?? []
    }));
    onChange(transformOut(key.toString()));
  };
  const onInputChange = (inputValue) => {
    setFieldState((prev) => ({
      ...prev,
      inputValue: inputValue ?? "",
      items: (items ?? []).filter((item) => contains(extractDisplayValue(item), inputValue ?? ""))
    }));
  };
  const onOpenChange = (isOpen, menuTrigger) => {
    if (isOpen && menuTrigger === "manual") {
      setFieldState((prev) => ({ ...prev, items: items ?? [] }));
    }
    if (!isOpen) {
      setFieldState((prev) => ({
        ...prev,
        inputValue: prev.selectedKey != null ? displayOf(prev.selectedKey) : "",
        items: items ?? []
      }));
    }
  };
  return /* @__PURE__ */ jsxs(
    ComboBox,
    {
      name,
      id: name,
      allowsCustomValue: true,
      selectedKey: fieldState.selectedKey,
      fullWidth: true,
      ...rest,
      inputValue: fieldState.inputValue,
      onInputChange,
      onOpenChange,
      onSelectionChange,
      children: [
        label && /* @__PURE__ */ jsx(Label, { children: label }),
        /* @__PURE__ */ jsxs(ComboBox.InputGroup, { children: [
          /* @__PURE__ */ jsx(Input, { variant: "secondary", placeholder }),
          /* @__PURE__ */ jsx(ComboBox.Trigger, {})
        ] }),
        /* @__PURE__ */ jsx(ComboBox.Popover, { children: /* @__PURE__ */ jsx(ListBox, { items: fieldState.items, children }) })
      ]
    },
    name
  );
};
const InputField$2 = ({ value, onChange, ...rest }) => {
  const [input, setInput] = useState(value);
  useEffect(() => {
    setInput(value);
  }, [value]);
  return /* @__PURE__ */ jsx(
    Input,
    {
      ...rest,
      value: input,
      onChange: (e) => setInput(e.target.value),
      onBlur: () => onChange(input)
    }
  );
};
const ArrayDynamicField = ({
  value,
  onChange,
  label = "List",
  withAutocomplete,
  defaultItems
}) => {
  const [list, { push, removeAt, updateAt, set }] = useList(value ?? []);
  const handleOnChange = (index, item) => {
    updateAt(index, item);
  };
  const handleOnDelete = (index) => {
    removeAt(index);
  };
  useEffect(() => {
    onChange(list);
  }, [list]);
  useDeepCompareEffect(() => {
    set(value ?? []);
  }, value);
  const items = useDeepCompareMemoize(defaultItems ?? []);
  const disabledItems = useMemo(
    () => items.filter((x3) => list.includes(x3.value)).map((x3) => x3.value),
    [items, list]
  );
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs("h4", { className: "text-gray ms-1 mb-2", children: [
      " ",
      label,
      " "
    ] }),
    /* @__PURE__ */ jsxs(ScrollShadow, { size: 100, className: "flex flex-col gap-2", children: [
      list.map((item, index) => /* @__PURE__ */ jsxs("div", { className: "w-full flex items-center gap-2", children: [
        withAutocomplete ? /* @__PURE__ */ jsx(
          Autocomplete,
          {
            "aria-label": label,
            name: item,
            value: item,
            items,
            disabledKeys: disabledItems,
            extractValue: (x3) => x3.value,
            extractDisplayValue: (x3) => x3.label,
            onChange: (value2) => {
              console.log("val", value2);
              handleOnChange(index, value2);
            },
            children: (x3) => /* @__PURE__ */ jsx(ListBox.Item, { id: x3.value, textValue: x3.label, children: x3.label })
          }
        ) : /* @__PURE__ */ jsx(
          InputField$2,
          {
            className: "w-full my-1",
            name: index.toString(),
            value: item,
            variant: "secondary",
            onChange: (value2) => handleOnChange(index, value2)
          }
        ),
        /* @__PURE__ */ jsx(
          Button,
          {
            isIconOnly: true,
            variant: "danger",
            "aria-label": "add",
            onPress: () => {
              handleOnDelete(index);
            },
            children: "-"
          }
        )
      ] }, `tfdl_${index}`)),
      /* @__PURE__ */ jsx("div", { className: "w-full flex justify-center", children: /* @__PURE__ */ jsx(
        Button,
        {
          isIconOnly: true,
          variant: "secondary",
          "aria-label": "add",
          isDisabled: list.some((x3) => x3 === ""),
          onPress: () => push(""),
          children: /* @__PURE__ */ jsx("i", { className: "ri-add-line" })
        }
      ) })
    ] })
  ] });
};
function Grid({
  children,
  cols,
  rows,
  sm,
  md,
  lg,
  xl,
  xxl,
  align,
  justify,
  className = ""
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: classes({
        "grid w-full h-full": true,
        "items-start": align === "start",
        "items-end": align === "end",
        "items-center": align === "center",
        "items-baseline": align === "baseline",
        "items-stretch": align === "stretch",
        "justify-normal": justify === "normal",
        "justify-start": justify === "start",
        "justify-end": justify === "end",
        "justify-center": justify === "center",
        "justify-between": justify === "between",
        "justify-around": justify === "around",
        "justify-evenly": justify === "evenly",
        "justify-stretch": justify === "stretch",
        "grid-cols-1": cols === 1,
        "grid-cols-2": cols === 2,
        "grid-cols-3": cols === 3,
        "grid-cols-4": cols === 4,
        "grid-cols-5": cols === 5,
        "grid-cols-6": cols === 6,
        "grid-cols-7": cols === 7,
        "grid-cols-8": cols === 8,
        "grid-cols-9": cols === 9,
        "grid-cols-10": cols === 10,
        "grid-cols-11": cols === 11,
        "grid-cols-12": cols === 12,
        "grid-flow-col": rows && !cols,
        "grid-rows-1": rows === 1 && !cols,
        "grid-rows-2": rows === 2 && !cols,
        "grid-rows-3": rows === 3 && !cols,
        "grid-rows-4": rows === 4 && !cols,
        "grid-rows-5": rows === 5 && !cols,
        "grid-rows-6": rows === 6 && !cols,
        "sm:grid-cols-1": cols && sm === 1,
        "sm:grid-cols-2": cols && sm === 2,
        "sm:grid-cols-3": cols && sm === 3,
        "sm:grid-cols-4": cols && sm === 4,
        "sm:grid-cols-5": cols && sm === 5,
        "sm:grid-cols-6": cols && sm === 6,
        "sm:grid-cols-7": cols && sm === 7,
        "sm:grid-cols-8": cols && sm === 8,
        "sm:grid-cols-9": cols && sm === 9,
        "sm:grid-cols-10": cols && sm === 10,
        "sm:grid-cols-11": cols && sm === 11,
        "sm:grid-cols-12": cols && sm === 12,
        "md:grid-cols-1": cols && md === 1,
        "md:grid-cols-2": cols && md === 2,
        "md:grid-cols-3": cols && md === 3,
        "md:grid-cols-4": cols && md === 4,
        "md:grid-cols-5": cols && md === 5,
        "md:grid-cols-6": cols && md === 6,
        "md:grid-cols-7": cols && md === 7,
        "md:grid-cols-8": cols && md === 8,
        "md:grid-cols-9": cols && md === 9,
        "md:grid-cols-10": cols && md === 10,
        "md:grid-cols-11": cols && md === 11,
        "md:grid-cols-12": cols && md === 12,
        "lg:grid-cols-1": cols && lg === 1,
        "lg:grid-cols-2": cols && lg === 2,
        "lg:grid-cols-3": cols && lg === 3,
        "lg:grid-cols-4": cols && lg === 4,
        "lg:grid-cols-5": cols && lg === 5,
        "lg:grid-cols-6": cols && lg === 6,
        "lg:grid-cols-7": cols && lg === 7,
        "lg:grid-cols-8": cols && lg === 8,
        "lg:grid-cols-9": cols && lg === 9,
        "lg:grid-cols-10": cols && lg === 10,
        "lg:grid-cols-11": cols && lg === 11,
        "lg:grid-cols-12": cols && lg === 12,
        "xl:grid-cols-1": cols && xl === 1,
        "xl:grid-cols-2": cols && xl === 2,
        "xl:grid-cols-3": cols && xl === 3,
        "xl:grid-cols-4": cols && xl === 4,
        "xl:grid-cols-5": cols && xl === 5,
        "xl:grid-cols-6": cols && xl === 6,
        "xl:grid-cols-7": cols && xl === 7,
        "xl:grid-cols-8": cols && xl === 8,
        "xl:grid-cols-9": cols && xl === 9,
        "xl:grid-cols-10": cols && xl === 10,
        "xl:grid-cols-11": cols && xl === 11,
        "xl:grid-cols-12": cols && xl === 12,
        "2xl:grid-cols-1": cols && xxl === 1,
        "2xl:grid-cols-2": cols && xxl === 2,
        "2xl:grid-cols-3": cols && xxl === 3,
        "2xl:grid-cols-4": cols && xxl === 4,
        "2xl:grid-cols-5": cols && xxl === 5,
        "2xl:grid-cols-6": cols && xxl === 6,
        "2xl:grid-cols-7": cols && xxl === 7,
        "2xl:grid-cols-8": cols && xxl === 8,
        "2xl:grid-cols-9": cols && xxl === 9,
        "2xl:grid-cols-10": cols && xxl === 10,
        "2xl:grid-cols-11": cols && xxl === 11,
        "2xl:grid-cols-12": cols && xxl === 12,
        "sm:grid-rows-1": rows && sm === 1,
        "sm:grid-rows-2": rows && sm === 2,
        "sm:grid-rows-3": rows && sm === 3,
        "sm:grid-rows-4": rows && sm === 4,
        "sm:grid-rows-5": rows && sm === 5,
        "sm:grid-rows-6": rows && sm === 6,
        "md:grid-rows-1": rows && md === 1,
        "md:grid-rows-2": rows && md === 2,
        "md:grid-rows-3": rows && md === 3,
        "md:grid-rows-4": rows && md === 4,
        "md:grid-rows-5": rows && md === 5,
        "md:grid-rows-6": rows && md === 6,
        "lg:grid-rows-1": rows && lg === 1,
        "lg:grid-rows-2": rows && lg === 2,
        "lg:grid-rows-3": rows && lg === 3,
        "lg:grid-rows-4": rows && lg === 4,
        "lg:grid-rows-5": rows && lg === 5,
        "lg:grid-rows-6": rows && lg === 6,
        "xl:grid-rows-1": rows && xl === 1,
        "xl:grid-rows-2": rows && xl === 2,
        "xl:grid-rows-3": rows && xl === 3,
        "xl:grid-rows-4": rows && xl === 4,
        "xl:grid-rows-5": rows && xl === 5,
        "xl:grid-rows-6": rows && xl === 6,
        "2xl:grid-rows-1": rows && xxl === 1,
        "2xl:grid-rows-2": rows && xxl === 2,
        "2xl:grid-rows-3": rows && xxl === 3,
        "2xl:grid-rows-4": rows && xxl === 4,
        "2xl:grid-rows-5": rows && xxl === 5,
        "2xl:grid-rows-6": rows && xxl === 6,
        ...classObject(className)
      }),
      children
    }
  );
}
function Item({
  children,
  rowSpan,
  rowStart,
  rowEnd,
  colSpan,
  colStart,
  colEnd,
  sm,
  md,
  lg,
  xl,
  xxl,
  className = ""
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: classes({
        "col-span-1": colSpan === 1,
        "col-span-2": colSpan === 2,
        "col-span-3": colSpan === 3,
        "col-span-4": colSpan === 4,
        "col-span-5": colSpan === 5,
        "col-span-6": colSpan === 6,
        "col-span-7": colSpan === 7,
        "col-span-8": colSpan === 8,
        "col-span-9": colSpan === 9,
        "col-span-10": colSpan === 10,
        "col-span-11": colSpan === 11,
        "col-span-12": colSpan === 12,
        "col-start-1": colStart === 1,
        "col-start-2": colStart === 2,
        "col-start-3": colStart === 3,
        "col-start-4": colStart === 4,
        "col-start-5": colStart === 5,
        "col-start-6": colStart === 6,
        "col-start-7": colStart === 7,
        "col-start-8": colStart === 8,
        "col-start-9": colStart === 9,
        "col-start-10": colStart === 10,
        "col-start-11": colStart === 11,
        "col-start-12": colStart === 12,
        "col-end-1": colEnd === 1,
        "col-end-2": colEnd === 2,
        "col-end-3": colEnd === 3,
        "col-end-4": colEnd === 4,
        "col-end-5": colEnd === 5,
        "col-end-6": colEnd === 6,
        "col-end-7": colEnd === 7,
        "col-end-8": colEnd === 8,
        "col-end-9": colEnd === 9,
        "col-end-10": colEnd === 10,
        "col-end-11": colEnd === 11,
        "col-end-12": colEnd === 12,
        "row-span-1": rowSpan === 1,
        "row-span-2": rowSpan === 2,
        "row-span-3": rowSpan === 3,
        "row-span-4": rowSpan === 4,
        "row-span-5": rowSpan === 5,
        "row-span-6": rowSpan === 6,
        "row-start-1": rowStart === 1,
        "row-start-2": rowStart === 2,
        "row-start-3": rowStart === 3,
        "row-start-4": rowStart === 4,
        "row-start-5": rowStart === 5,
        "row-start-6": rowStart === 6,
        "row-end-1": rowEnd === 1,
        "row-end-2": rowEnd === 2,
        "row-end-3": rowEnd === 3,
        "row-end-4": rowEnd === 4,
        "row-end-5": rowEnd === 5,
        "row-end-6": rowEnd === 6,
        "sm:col-span-1": colSpan && sm === 1,
        "sm:col-span-2": colSpan && sm === 2,
        "sm:col-span-3": colSpan && sm === 3,
        "sm:col-span-4": colSpan && sm === 4,
        "sm:col-span-5": colSpan && sm === 5,
        "sm:col-span-6": colSpan && sm === 6,
        "sm:col-span-7": colSpan && sm === 7,
        "sm:col-span-8": colSpan && sm === 8,
        "sm:col-span-9": colSpan && sm === 9,
        "sm:col-span-10": colSpan && sm === 10,
        "sm:col-span-11": colSpan && sm === 11,
        "sm:col-span-12": colSpan && sm === 12,
        "md:col-span-1": colSpan && md === 1,
        "md:col-span-2": colSpan && md === 2,
        "md:col-span-3": colSpan && md === 3,
        "md:col-span-4": colSpan && md === 4,
        "md:col-span-5": colSpan && md === 5,
        "md:col-span-6": colSpan && md === 6,
        "md:col-span-7": colSpan && md === 7,
        "md:col-span-8": colSpan && md === 8,
        "md:col-span-9": colSpan && md === 9,
        "md:col-span-10": colSpan && md === 10,
        "md:col-span-11": colSpan && md === 11,
        "md:col-span-12": colSpan && md === 12,
        "lg:col-span-1": colSpan && lg === 1,
        "lg:col-span-2": colSpan && lg === 2,
        "lg:col-span-3": colSpan && lg === 3,
        "lg:col-span-4": colSpan && lg === 4,
        "lg:col-span-5": colSpan && lg === 5,
        "lg:col-span-6": colSpan && lg === 6,
        "lg:col-span-7": colSpan && lg === 7,
        "lg:col-span-8": colSpan && lg === 8,
        "lg:col-span-9": colSpan && lg === 9,
        "lg:col-span-10": colSpan && lg === 10,
        "lg:col-span-11": colSpan && lg === 11,
        "lg:col-span-12": colSpan && lg === 12,
        "xl:col-span-1": colSpan && xl === 1,
        "xl:col-span-2": colSpan && xl === 2,
        "xl:col-span-3": colSpan && xl === 3,
        "xl:col-span-4": colSpan && xl === 4,
        "xl:col-span-5": colSpan && xl === 5,
        "xl:col-span-6": colSpan && xl === 6,
        "xl:col-span-7": colSpan && xl === 7,
        "xl:col-span-8": colSpan && xl === 8,
        "xl:col-span-9": colSpan && xl === 9,
        "xl:col-span-10": colSpan && xl === 10,
        "xl:col-span-11": colSpan && xl === 11,
        "xl:col-span-12": colSpan && xl === 12,
        "2xl:col-span-1": colSpan && xxl === 1,
        "2xl:col-span-2": colSpan && xxl === 2,
        "2xl:col-span-3": colSpan && xxl === 3,
        "2xl:col-span-4": colSpan && xxl === 4,
        "2xl:col-span-5": colSpan && xxl === 5,
        "2xl:col-span-6": colSpan && xxl === 6,
        "2xl:col-span-7": colSpan && xxl === 7,
        "2xl:col-span-8": colSpan && xxl === 8,
        "2xl:col-span-9": colSpan && xxl === 9,
        "2xl:col-span-10": colSpan && xxl === 10,
        "2xl:col-span-11": colSpan && xxl === 11,
        "2xl:col-span-12": colSpan && xxl === 12,
        "sm:row-span-1": rowSpan && sm === 1,
        "sm:row-span-2": rowSpan && sm === 2,
        "sm:row-span-3": rowSpan && sm === 3,
        "sm:row-span-4": rowSpan && sm === 4,
        "sm:row-span-5": rowSpan && sm === 5,
        "sm:row-span-6": rowSpan && sm === 6,
        "md:row-span-1": rowSpan && md === 1,
        "md:row-span-2": rowSpan && md === 2,
        "md:row-span-3": rowSpan && md === 3,
        "md:row-span-4": rowSpan && md === 4,
        "md:row-span-5": rowSpan && md === 5,
        "md:row-span-6": rowSpan && md === 6,
        "lg:row-span-1": rowSpan && lg === 1,
        "lg:row-span-2": rowSpan && lg === 2,
        "lg:row-span-3": rowSpan && lg === 3,
        "lg:row-span-4": rowSpan && lg === 4,
        "lg:row-span-5": rowSpan && lg === 5,
        "lg:row-span-6": rowSpan && lg === 6,
        "xl:row-span-1": rowSpan && xl === 1,
        "xl:row-span-2": rowSpan && xl === 2,
        "xl:row-span-3": rowSpan && xl === 3,
        "xl:row-span-4": rowSpan && xl === 4,
        "xl:row-span-5": rowSpan && xl === 5,
        "xl:row-span-6": rowSpan && xl === 6,
        "2xl:row-span-1": rowSpan && xxl === 1,
        "2xl:row-span-2": rowSpan && xxl === 2,
        "2xl:row-span-3": rowSpan && xxl === 3,
        "2xl:row-span-4": rowSpan && xxl === 4,
        "2xl:row-span-5": rowSpan && xxl === 5,
        "2xl:row-span-6": rowSpan && xxl === 6,
        ...classObject(className)
      }),
      children
    }
  );
}
Grid.Item = Item;
const ACCOUNT_TYPES = [
  { id: "Checking", name: "Checking" },
  { id: "Savings", name: "Savings" }
];
const BankAccountInput = ({ value = {}, onChange, locked = false, isInvalid = false }) => {
  const change = (field, fieldValue) => onChange?.({ ...value, [field]: fieldValue });
  return /* @__PURE__ */ jsxs(Grid, { cols: 2, className: "gap-3.5", children: [
    /* @__PURE__ */ jsxs(TextField, { variant: "secondary", isDisabled: locked, isInvalid, children: [
      /* @__PURE__ */ jsx(Label, { children: "Bank name" }),
      /* @__PURE__ */ jsx(
        Input,
        {
          value: value.bankName ?? "",
          onChange: (evt) => change("bankName", evt.target.value)
        }
      )
    ] }),
    /* @__PURE__ */ jsxs(
      Select,
      {
        variant: "secondary",
        "aria-label": "Account type",
        value: value.accountType ?? "Checking",
        onChange: (key) => key && change("accountType", key),
        isDisabled: locked,
        children: [
          /* @__PURE__ */ jsx(Label, { children: "Account type" }),
          /* @__PURE__ */ jsxs(Select.Trigger, { children: [
            /* @__PURE__ */ jsx(Select.Value, {}),
            /* @__PURE__ */ jsx(Select.Indicator, {})
          ] }),
          /* @__PURE__ */ jsx(Select.Popover, { children: /* @__PURE__ */ jsx(ListBox, { items: ACCOUNT_TYPES, children: (item) => /* @__PURE__ */ jsx(ListBox.Item, { id: item.id, children: item.name }, item.id) }) })
        ]
      }
    ),
    locked ? /* @__PURE__ */ jsxs(Grid.Item, { className: "self-end", children: [
      /* @__PURE__ */ jsx(Label, { children: "Bank account" }),
      /* @__PURE__ */ jsx(LockedValue, { mono: true, children: "••••" })
    ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs(TextField, { variant: "secondary", isRequired: true, isInvalid, children: [
        /* @__PURE__ */ jsx(Label, { children: "Routing number" }),
        /* @__PURE__ */ jsx(
          Input,
          {
            inputMode: "numeric",
            placeholder: "000000000",
            value: value.routingNumber ?? "",
            onChange: (evt) => change("routingNumber", evt.target.value)
          }
        )
      ] }),
      /* @__PURE__ */ jsxs(TextField, { variant: "secondary", isRequired: true, isInvalid, children: [
        /* @__PURE__ */ jsx(Label, { children: "Account number" }),
        /* @__PURE__ */ jsx(
          Input,
          {
            inputMode: "numeric",
            value: value.accountNumber ?? "",
            onChange: (evt) => change("accountNumber", evt.target.value)
          }
        )
      ] })
    ] })
  ] });
};
const digits = (s) => (s || "").replace(/\D/g, "");
const formatCardNumber = (s) => digits(s).slice(0, 19).replace(/(.{4})/g, "$1 ").trim();
const CardInput = ({
  value = {},
  onChange,
  locked = false,
  maskedNumber,
  isInvalid = false,
  isDisabled = false
}) => {
  const change = (field, fieldValue) => onChange?.({ ...value, [field]: fieldValue });
  return /* @__PURE__ */ jsxs(Grid, { cols: 3, className: "gap-3.5", children: [
    /* @__PURE__ */ jsx(Grid.Item, { colSpan: 3, children: locked ? /* @__PURE__ */ jsx(LockedValue, { icon: "ri-bank-card-line", mono: true, children: maskedNumber || "••••" }) : /* @__PURE__ */ jsxs(TextField, { variant: "secondary", isRequired: true, isInvalid, isDisabled, children: [
      /* @__PURE__ */ jsx(Label, { children: "Card number" }),
      /* @__PURE__ */ jsx(
        Input,
        {
          inputMode: "numeric",
          autoComplete: "cc-number",
          placeholder: "0000 0000 0000 0000",
          value: formatCardNumber(value.cardNumber),
          onChange: (evt) => change("cardNumber", digits(evt.target.value).slice(0, 19))
        }
      )
    ] }) }),
    /* @__PURE__ */ jsxs(TextField, { variant: "secondary", isRequired: true, isInvalid, isDisabled, children: [
      /* @__PURE__ */ jsx(Label, { children: "Exp. month" }),
      /* @__PURE__ */ jsx(
        Input,
        {
          placeholder: "MM",
          value: value.expirationMonth ?? "",
          onChange: (evt) => change("expirationMonth", evt.target.value)
        }
      )
    ] }),
    /* @__PURE__ */ jsxs(TextField, { variant: "secondary", isRequired: true, isInvalid, isDisabled, children: [
      /* @__PURE__ */ jsx(Label, { children: "Exp. year" }),
      /* @__PURE__ */ jsx(
        Input,
        {
          placeholder: "YYYY",
          value: value.expirationYear ?? "",
          onChange: (evt) => change("expirationYear", evt.target.value)
        }
      )
    ] }),
    !locked && /* @__PURE__ */ jsxs(TextField, { variant: "secondary", isRequired: true, isInvalid, isDisabled, children: [
      /* @__PURE__ */ jsx(Label, { children: "CVV" }),
      /* @__PURE__ */ jsx(
        Input,
        {
          inputMode: "numeric",
          autoComplete: "cc-csc",
          value: value.cvv ?? "",
          onChange: (evt) => change("cvv", digits(evt.target.value).slice(0, 4))
        }
      )
    ] })
  ] });
};
function r(e) {
  var t, f, n = "";
  if ("string" == typeof e || "number" == typeof e) n += e;
  else if ("object" == typeof e) if (Array.isArray(e)) {
    var o = e.length;
    for (t = 0; t < o; t++) e[t] && (f = r(e[t])) && (n && (n += " "), n += f);
  } else for (f in e) e[f] && (n && (n += " "), n += f);
  return n;
}
function clsx() {
  for (var e, t, f = 0, n = "", o = arguments.length; f < o; f++) (e = arguments[f]) && (t = r(e)) && (n && (n += " "), n += t);
  return n;
}
const CheckboxCard = ({
  title,
  description,
  icon,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsxs(
    Checkbox,
    {
      ...props,
      classNames: {
        base: clsx(
          "inline-flex flex-row w-full max-w-full m-0",
          "p-[10px_14px] rounded-[var(--radius-md,8px)] border",
          "data-[selected=true]:bg-[var(--bg-surface,var(--surface))] data-[selected=false]:bg-[var(--bg-sunken-c,var(--surface-hover))]",
          "data-[selected=true]:border-[var(--border-default-c,var(--border))] data-[selected=false]:border-[var(--border-subtle-c,var(--border-subtle))]",
          "hover:bg-[var(--bg-surface,var(--surface))] transition-colors cursor-pointer items-start justify-start gap-3",
          className
        ),
        label: "w-full"
      },
      children: [
        /* @__PURE__ */ jsx(Checkbox.Control, { children: /* @__PURE__ */ jsx(Checkbox.Indicator, {}) }),
        /* @__PURE__ */ jsxs(Checkbox.Content, { className: "w-full flex-1 min-w-0 flex items-center gap-3", children: [
          icon && /* @__PURE__ */ jsx("span", { className: "flex-none flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm,4px)] bg-[var(--bg-input,var(--border-subtle))] group-data-[selected=true]:text-[var(--fg-1,var(--foreground))] group-data-[selected=false]:text-[var(--fg-3,var(--muted))]", children: /* @__PURE__ */ jsx("i", { className: clsx(icon, "text-[15px]") }) }),
          /* @__PURE__ */ jsxs("div", { className: "flex flex-col min-w-0", children: [
            /* @__PURE__ */ jsx("span", { className: "text-[13.5px] font-semibold group-data-[selected=true]:text-[var(--fg-1,var(--foreground))] group-data-[selected=false]:text-[var(--fg-2,var(--muted))]", children: title }),
            description && /* @__PURE__ */ jsx("span", { className: "text-[12px] text-[var(--fg-2,var(--muted))] mt-[1px]", children: description })
          ] })
        ] })
      ]
    }
  );
};
const InputField$1 = ({ item, onChange }) => {
  const [key, setKey] = useState(item.key);
  const [value, setValue] = useState(item.value);
  const handleChanged = () => {
    onChange({ key, value });
  };
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col w-full", children: [
    /* @__PURE__ */ jsxs(TextField, { name: item.key, value: key, onChange: setKey, onBlur: handleChanged, children: [
      /* @__PURE__ */ jsx(Label, { children: "Key" }),
      /* @__PURE__ */ jsx(Input, { variant: "secondary" })
    ] }),
    /* @__PURE__ */ jsxs(
      TextField,
      {
        name: `${item.key}_value`,
        value,
        onChange: setValue,
        onBlur: handleChanged,
        children: [
          /* @__PURE__ */ jsx(Label, { children: "Value" }),
          /* @__PURE__ */ jsx(Input, { variant: "secondary" })
        ]
      }
    )
  ] });
};
const DictionaryDynamicField = ({ value, label, onChange }) => {
  const [list, { push, removeAt, updateAt, set }] = useList(
    Object.entries(value).map(([k2, v2]) => ({ key: k2, value: v2 }))
  );
  const handleAddProperty = (key, value2) => {
    push({ key, value: value2 });
  };
  const handleDeleteProperty = (index) => {
    removeAt(index);
  };
  const handleOnChange = (index, item) => {
    updateAt(index, item);
  };
  useEffect(() => {
    const transformed = list.reduce((prev, curr) => {
      prev[curr.key] = curr.value;
      return prev;
    }, {});
    onChange(transformed);
  }, [list]);
  useDeepCompareEffect(() => {
    set(Object.entries(value).map(([k2, v2]) => ({ key: k2, value: v2 })));
  }, [value]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("h4", { children: label || "List" }),
    /* @__PURE__ */ jsx(ScrollShadow, { size: 100, children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2 items-center", children: [
      list.map((item, index) => {
        return /* @__PURE__ */ jsxs("div", { className: "flex flex-row gap-2 items-end", children: [
          /* @__PURE__ */ jsx(
            InputField$1,
            {
              item,
              variant: "secondary",
              onChange: (val) => handleOnChange(index, val)
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              isIconOnly: true,
              variant: "danger",
              "aria-label": "add",
              onPress: () => handleDeleteProperty(index),
              children: "-"
            }
          )
        ] }, `ddf_${item.key}`);
      }),
      /* @__PURE__ */ jsx(
        Button,
        {
          isIconOnly: true,
          variant: "secondary",
          "aria-label": "add",
          isDisabled: list.some((x3) => x3.key === ""),
          onPress: () => handleAddProperty("", ""),
          children: "+"
        }
      )
    ] }) })
  ] });
};
const ErrorListChip = ({ errors = [] }) => {
  const count = errors.length;
  if (count === 0) return null;
  return /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
    /* @__PURE__ */ jsxs(Button, { variant: "danger-soft", size: "sm", children: [
      count,
      " ",
      count === 1 ? "Error" : "Errors"
    ] }),
    /* @__PURE__ */ jsxs(Tooltip.Content, { showArrow: true, shouldFlip: true, children: [
      /* @__PURE__ */ jsx(Tooltip.Arrow, {}),
      /* @__PURE__ */ jsx("div", { className: "px-1 py-2", children: errors.map((message, i) => /* @__PURE__ */ jsxs("div", { className: "text-xs text-danger", children: [
        "• ",
        message
      ] }, i)) })
    ] })
  ] });
};
const formatAddress = (address2) => {
  if (!address2 || typeof address2 !== "object") return "";
  return [
    address2.address1,
    address2.city,
    [address2.state, address2.postalCode].filter(Boolean).join(" ")
  ].filter(Boolean).join(", ");
};
const GoogleAutocomplete = ({
  onSelect,
  value,
  placeholder,
  label,
  startContent,
  ...rest
}) => {
  const places = useMapsLibrary("places");
  const [sessionToken, setSessionToken] = useState();
  const [placesService, setPlacesService] = useState();
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [predictionResults, setPredictionResults] = useState([]);
  const [inputValue, setInputValue] = useState(() => formatAddress(value));
  useEffect(() => {
    const display = formatAddress(value);
    if (display) setInputValue(display);
  }, [value]);
  useEffect(() => {
    if (!places) return;
    setPlacesService(new places.PlacesService(document.getElementById("googleDiv")));
    setAutocompleteService(new places.AutocompleteService());
    setSessionToken(new places.AutocompleteSessionToken());
    return () => {
      setPlacesService(null);
      setAutocompleteService(null);
    };
  }, [places]);
  const fetchPredictions = useCallback(
    async (inputValue2) => {
      if (!autocompleteService || !placesService || !inputValue2) {
        setPredictionResults([]);
        return;
      }
      const request = { input: inputValue2, types: ["address"], sessionToken };
      const response = await autocompleteService.getPlacePredictions(request);
      setPredictionResults(response.predictions);
    },
    [autocompleteService, sessionToken, placesService]
  );
  const onInputChange = useCallback(
    (value2) => {
      setInputValue(value2);
      fetchPredictions(value2);
    },
    [fetchPredictions]
  );
  const handlePress = useCallback(
    (item) => {
      setInputValue(item.description);
      setPredictionResults([item]);
      placesService.getDetails({ placeId: item.place_id }, (_address) => {
        const extract = (components, part, format) => components.find((x3) => x3.types.includes(part))?.[format] || "";
        const { address_components, geometry } = _address;
        if (address_components === void 0) return;
        const address2 = {
          address1: `${extract(
            address_components,
            "street_number",
            "long_name"
          )} ${extract(address_components, "route", "long_name")}`,
          address2: "",
          address3: "",
          city: extract(address_components, "locality", "long_name"),
          state: extract(address_components, "administrative_area_level_1", "short_name"),
          postalCode: extract(address_components, "postal_code", "short_name"),
          country: extract(address_components, "country", "short_name"),
          lat: geometry.location.lat(),
          lon: geometry.location.lng()
        };
        onSelect(address2);
      });
    },
    [placesService, onSelect]
  );
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(
      ComboBox,
      {
        inputValue,
        onInputChange,
        allowsCustomValue: true,
        allowsEmptyCollection: true,
        defaultFilter: () => true,
        "aria-label": "Address lookup",
        ...rest,
        children: [
          label && /* @__PURE__ */ jsx(Label, { children: label }),
          /* @__PURE__ */ jsxs(ComboBox.InputGroup, { children: [
            startContent && /* @__PURE__ */ jsx("span", { className: "absolute left-3 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center text-[var(--fg-3)] pointer-events-none", children: startContent }),
            /* @__PURE__ */ jsx(
              Input,
              {
                variant: "secondary",
                "aria-label": placeholder ?? "Address lookup",
                placeholder,
                className: startContent ? "pl-9" : void 0
              }
            ),
            /* @__PURE__ */ jsx(ComboBox.Trigger, {})
          ] }),
          /* @__PURE__ */ jsx(ComboBox.Popover, { children: /* @__PURE__ */ jsx(ListBox, { renderEmptyState: () => /* @__PURE__ */ jsx(EmptyState, { children: "No matching addresses" }), children: /* @__PURE__ */ jsx(Collection, { items: predictionResults, children: (item) => /* @__PURE__ */ jsx(
            ListBox.Item,
            {
              id: item.place_id,
              textValue: item.description,
              onAction: () => handlePress(item),
              children: item.description
            }
          ) }) }) })
        ]
      }
    ),
    /* @__PURE__ */ jsx("div", { hidden: true, id: "googleDiv" })
  ] });
};
var _ = Object.defineProperty;
var $ = (a, h, e) => h in a ? _(a, h, { enumerable: true, configurable: true, writable: true, value: e }) : a[h] = e;
var m = (a, h, e) => $(a, typeof h != "symbol" ? h + "" : h, e);
const E = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  unit: "px"
}, b = (a, h, e) => Math.min(Math.max(a, h), e), H = (...a) => a.filter((h) => h && typeof h == "string").join(" "), X = (a, h) => a === h || a.width === h.width && a.height === h.height && a.x === h.x && a.y === h.y && a.unit === h.unit;
function v(a, h, e) {
  return a.unit === "%" ? { ...E, ...a, unit: "%" } : {
    unit: "%",
    x: a.x ? a.x / h * 100 : 0,
    y: a.y ? a.y / e * 100 : 0,
    width: a.width ? a.width / h * 100 : 0,
    height: a.height ? a.height / e * 100 : 0
  };
}
function D(a, h, e) {
  return a.unit ? a.unit === "px" ? { ...E, ...a, unit: "px" } : {
    unit: "px",
    x: a.x ? a.x * h / 100 : 0,
    y: a.y ? a.y * e / 100 : 0,
    width: a.width ? a.width * h / 100 : 0,
    height: a.height ? a.height * e / 100 : 0
  } : { ...E, ...a, unit: "px" };
}
function k(a, h, e, n, t, d = 0, r2 = 0, o = n, w = t) {
  const i = { ...a };
  let s = Math.min(d, n), c = Math.min(r2, t), g = Math.min(o, n), p = Math.min(w, t);
  h && (h > 1 ? (s = r2 ? r2 * h : s, c = s / h, g = o * h) : (c = d ? d / h : c, s = c * h, p = w / h)), i.y < 0 && (i.height = Math.max(i.height + i.y, c), i.y = 0), i.x < 0 && (i.width = Math.max(i.width + i.x, s), i.x = 0);
  const l = n - (i.x + i.width);
  l < 0 && (i.x = Math.min(i.x, n - s), i.width += l);
  const C = t - (i.y + i.height);
  if (C < 0 && (i.y = Math.min(i.y, t - c), i.height += C), i.width < s && ((e === "sw" || e == "nw") && (i.x -= s - i.width), i.width = s), i.height < c && ((e === "nw" || e == "ne") && (i.y -= c - i.height), i.height = c), i.width > g && ((e === "sw" || e == "nw") && (i.x -= g - i.width), i.width = g), i.height > p && ((e === "nw" || e == "ne") && (i.y -= p - i.height), i.height = p), h) {
    const y = i.width / i.height;
    if (y < h) {
      const f = Math.max(i.width / h, c);
      (e === "nw" || e == "ne") && (i.y -= f - i.height), i.height = f;
    } else if (y > h) {
      const f = Math.max(i.height * h, s);
      (e === "sw" || e == "nw") && (i.x -= f - i.width), i.width = f;
    }
  }
  return i;
}
function I(a, h, e, n) {
  const t = { ...a };
  return h === "ArrowLeft" ? n === "nw" ? (t.x -= e, t.y -= e, t.width += e, t.height += e) : n === "w" ? (t.x -= e, t.width += e) : n === "sw" ? (t.x -= e, t.width += e, t.height += e) : n === "ne" ? (t.y += e, t.width -= e, t.height -= e) : n === "e" ? t.width -= e : n === "se" && (t.width -= e, t.height -= e) : h === "ArrowRight" && (n === "nw" ? (t.x += e, t.y += e, t.width -= e, t.height -= e) : n === "w" ? (t.x += e, t.width -= e) : n === "sw" ? (t.x += e, t.width -= e, t.height -= e) : n === "ne" ? (t.y -= e, t.width += e, t.height += e) : n === "e" ? t.width += e : n === "se" && (t.width += e, t.height += e)), h === "ArrowUp" ? n === "nw" ? (t.x -= e, t.y -= e, t.width += e, t.height += e) : n === "n" ? (t.y -= e, t.height += e) : n === "ne" ? (t.y -= e, t.width += e, t.height += e) : n === "sw" ? (t.x += e, t.width -= e, t.height -= e) : n === "s" ? t.height -= e : n === "se" && (t.width -= e, t.height -= e) : h === "ArrowDown" && (n === "nw" ? (t.x += e, t.y += e, t.width -= e, t.height -= e) : n === "n" ? (t.y += e, t.height -= e) : n === "ne" ? (t.y += e, t.width -= e, t.height -= e) : n === "sw" ? (t.x -= e, t.width += e, t.height += e) : n === "s" ? t.height += e : n === "se" && (t.width += e, t.height += e)), t;
}
const M = { capture: true, passive: false };
let N = 0;
const x = class x2 extends PureComponent {
  constructor() {
    super(...arguments);
    m(this, "docMoveBound", false);
    m(this, "mouseDownOnCrop", false);
    m(this, "dragStarted", false);
    m(this, "evData", {
      startClientX: 0,
      startClientY: 0,
      startCropX: 0,
      startCropY: 0,
      clientX: 0,
      clientY: 0,
      isResize: true
    });
    m(this, "componentRef", createRef());
    m(this, "mediaRef", createRef());
    m(this, "resizeObserver");
    m(this, "initChangeCalled", false);
    m(this, "instanceId", `rc-${N++}`);
    m(this, "state", {
      cropIsActive: false,
      newCropIsBeingDrawn: false
    });
    m(this, "onCropPointerDown", (e) => {
      const { crop: n, disabled: t } = this.props, d = this.getBox();
      if (!n)
        return;
      const r2 = D(n, d.width, d.height);
      if (t)
        return;
      e.cancelable && e.preventDefault(), this.bindDocMove(), this.componentRef.current.focus({ preventScroll: true });
      const o = e.target.dataset.ord, w = !!o;
      let i = e.clientX, s = e.clientY, c = r2.x, g = r2.y;
      if (o) {
        const p = e.clientX - d.x, l = e.clientY - d.y;
        let C = 0, y = 0;
        o === "ne" || o == "e" ? (C = p - (r2.x + r2.width), y = l - r2.y, c = r2.x, g = r2.y + r2.height) : o === "se" || o === "s" ? (C = p - (r2.x + r2.width), y = l - (r2.y + r2.height), c = r2.x, g = r2.y) : o === "sw" || o == "w" ? (C = p - r2.x, y = l - (r2.y + r2.height), c = r2.x + r2.width, g = r2.y) : (o === "nw" || o == "n") && (C = p - r2.x, y = l - r2.y, c = r2.x + r2.width, g = r2.y + r2.height), i = c + d.x + C, s = g + d.y + y;
      }
      this.evData = {
        startClientX: i,
        startClientY: s,
        startCropX: c,
        startCropY: g,
        clientX: e.clientX,
        clientY: e.clientY,
        isResize: w,
        ord: o
      }, this.mouseDownOnCrop = true, this.setState({ cropIsActive: true });
    });
    m(this, "onComponentPointerDown", (e) => {
      const { crop: n, disabled: t, locked: d, keepSelection: r2, onChange: o } = this.props, w = this.getBox();
      if (t || d || r2 && n)
        return;
      e.cancelable && e.preventDefault(), this.bindDocMove(), this.componentRef.current.focus({ preventScroll: true });
      const i = e.clientX - w.x, s = e.clientY - w.y, c = {
        unit: "px",
        x: i,
        y: s,
        width: 0,
        height: 0
      };
      this.evData = {
        startClientX: e.clientX,
        startClientY: e.clientY,
        startCropX: i,
        startCropY: s,
        clientX: e.clientX,
        clientY: e.clientY,
        isResize: true
      }, this.mouseDownOnCrop = true, o(D(c, w.width, w.height), v(c, w.width, w.height)), this.setState({ cropIsActive: true, newCropIsBeingDrawn: true });
    });
    m(this, "onDocPointerMove", (e) => {
      const { crop: n, disabled: t, onChange: d, onDragStart: r2 } = this.props, o = this.getBox();
      if (t || !n || !this.mouseDownOnCrop)
        return;
      e.cancelable && e.preventDefault(), this.dragStarted || (this.dragStarted = true, r2 && r2(e));
      const { evData: w } = this;
      w.clientX = e.clientX, w.clientY = e.clientY;
      let i;
      w.isResize ? i = this.resizeCrop() : i = this.dragCrop(), X(n, i) || d(
        D(i, o.width, o.height),
        v(i, o.width, o.height)
      );
    });
    m(this, "onComponentKeyDown", (e) => {
      const { crop: n, disabled: t, onChange: d, onComplete: r2 } = this.props;
      if (t)
        return;
      const o = e.key;
      let w = false;
      if (!n)
        return;
      const i = this.getBox(), s = this.makePixelCrop(i), g = (navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey) ? x2.nudgeStepLarge : e.shiftKey ? x2.nudgeStepMedium : x2.nudgeStep;
      if (o === "ArrowLeft" ? (s.x -= g, w = true) : o === "ArrowRight" ? (s.x += g, w = true) : o === "ArrowUp" ? (s.y -= g, w = true) : o === "ArrowDown" && (s.y += g, w = true), w) {
        e.cancelable && e.preventDefault(), s.x = b(s.x, 0, i.width - s.width), s.y = b(s.y, 0, i.height - s.height);
        const p = D(s, i.width, i.height), l = v(s, i.width, i.height);
        d(p, l), r2 && r2(p, l);
      }
    });
    m(this, "onHandlerKeyDown", (e, n) => {
      const {
        aspect: t = 0,
        crop: d,
        disabled: r2,
        minWidth: o = 0,
        minHeight: w = 0,
        maxWidth: i,
        maxHeight: s,
        onChange: c,
        onComplete: g
      } = this.props, p = this.getBox();
      if (r2 || !d)
        return;
      if (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "ArrowLeft" || e.key === "ArrowRight")
        e.stopPropagation(), e.preventDefault();
      else
        return;
      const C = (navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey) ? x2.nudgeStepLarge : e.shiftKey ? x2.nudgeStepMedium : x2.nudgeStep, y = D(d, p.width, p.height), f = I(y, e.key, C, n), R = k(
        f,
        t,
        n,
        p.width,
        p.height,
        o,
        w,
        i,
        s
      );
      if (!X(d, R)) {
        const Y = v(R, p.width, p.height);
        c(R, Y), g && g(R, Y);
      }
    });
    m(this, "onDocPointerDone", (e) => {
      const { crop: n, disabled: t, onComplete: d, onDragEnd: r2 } = this.props, o = this.getBox();
      this.unbindDocMove(), !(t || !n) && this.mouseDownOnCrop && (this.mouseDownOnCrop = false, this.dragStarted = false, r2 && r2(e), d && d(D(n, o.width, o.height), v(n, o.width, o.height)), this.setState({ cropIsActive: false, newCropIsBeingDrawn: false }));
    });
    m(this, "onDragFocus", () => {
      var e;
      (e = this.componentRef.current) == null || e.scrollTo(0, 0);
    });
  }
  get document() {
    return document;
  }
  // We unfortunately get the bounding box every time as x+y changes
  // due to scrolling.
  getBox() {
    const e = this.mediaRef.current;
    if (!e)
      return { x: 0, y: 0, width: 0, height: 0 };
    const { x: n, y: t, width: d, height: r2 } = e.getBoundingClientRect();
    return { x: n, y: t, width: d, height: r2 };
  }
  componentDidUpdate(e) {
    const { crop: n, onComplete: t } = this.props;
    if (t && !e.crop && n) {
      const { width: d, height: r2 } = this.getBox();
      d && r2 && t(D(n, d, r2), v(n, d, r2));
    }
  }
  componentWillUnmount() {
    this.resizeObserver && this.resizeObserver.disconnect(), this.unbindDocMove();
  }
  bindDocMove() {
    this.docMoveBound || (this.document.addEventListener("pointermove", this.onDocPointerMove, M), this.document.addEventListener("pointerup", this.onDocPointerDone, M), this.document.addEventListener("pointercancel", this.onDocPointerDone, M), this.docMoveBound = true);
  }
  unbindDocMove() {
    this.docMoveBound && (this.document.removeEventListener("pointermove", this.onDocPointerMove, M), this.document.removeEventListener("pointerup", this.onDocPointerDone, M), this.document.removeEventListener("pointercancel", this.onDocPointerDone, M), this.docMoveBound = false);
  }
  getCropStyle() {
    const { crop: e } = this.props;
    if (e)
      return {
        top: `${e.y}${e.unit}`,
        left: `${e.x}${e.unit}`,
        width: `${e.width}${e.unit}`,
        height: `${e.height}${e.unit}`
      };
  }
  dragCrop() {
    const { evData: e } = this, n = this.getBox(), t = this.makePixelCrop(n), d = e.clientX - e.startClientX, r2 = e.clientY - e.startClientY;
    return t.x = b(e.startCropX + d, 0, n.width - t.width), t.y = b(e.startCropY + r2, 0, n.height - t.height), t;
  }
  getPointRegion(e, n, t, d) {
    const { evData: r2 } = this, o = r2.clientX - e.x, w = r2.clientY - e.y;
    let i;
    d && n ? i = n === "nw" || n === "n" || n === "ne" : i = w < r2.startCropY;
    let s;
    return t && n ? s = n === "nw" || n === "w" || n === "sw" : s = o < r2.startCropX, s ? i ? "nw" : "sw" : i ? "ne" : "se";
  }
  resolveMinDimensions(e, n, t = 0, d = 0) {
    const r2 = Math.min(t, e.width), o = Math.min(d, e.height);
    return !n || !r2 && !o ? [r2, o] : n > 1 ? r2 ? [r2, r2 / n] : [o * n, o] : o ? [o * n, o] : [r2, r2 / n];
  }
  resizeCrop() {
    const { evData: e } = this, { aspect: n = 0, maxWidth: t, maxHeight: d } = this.props, r2 = this.getBox(), [o, w] = this.resolveMinDimensions(r2, n, this.props.minWidth, this.props.minHeight);
    let i = this.makePixelCrop(r2);
    const s = this.getPointRegion(r2, e.ord, o, w), c = e.ord || s;
    let g = e.clientX - e.startClientX, p = e.clientY - e.startClientY;
    (o && c === "nw" || c === "w" || c === "sw") && (g = Math.min(g, -o)), (w && c === "nw" || c === "n" || c === "ne") && (p = Math.min(p, -w));
    const l = {
      unit: "px",
      x: 0,
      y: 0,
      width: 0,
      height: 0
    };
    s === "ne" ? (l.x = e.startCropX, l.width = g, n ? (l.height = l.width / n, l.y = e.startCropY - l.height) : (l.height = Math.abs(p), l.y = e.startCropY - l.height)) : s === "se" ? (l.x = e.startCropX, l.y = e.startCropY, l.width = g, n ? l.height = l.width / n : l.height = p) : s === "sw" ? (l.x = e.startCropX + g, l.y = e.startCropY, l.width = Math.abs(g), n ? l.height = l.width / n : l.height = p) : s === "nw" && (l.x = e.startCropX + g, l.width = Math.abs(g), n ? (l.height = l.width / n, l.y = e.startCropY - l.height) : (l.height = Math.abs(p), l.y = e.startCropY + p));
    const C = k(
      l,
      n,
      s,
      r2.width,
      r2.height,
      o,
      w,
      t,
      d
    );
    return n || x2.xyOrds.indexOf(c) > -1 ? i = C : x2.xOrds.indexOf(c) > -1 ? (i.x = C.x, i.width = C.width) : x2.yOrds.indexOf(c) > -1 && (i.y = C.y, i.height = C.height), i.x = b(i.x, 0, r2.width - i.width), i.y = b(i.y, 0, r2.height - i.height), i;
  }
  renderCropSelection() {
    const {
      ariaLabels: e = x2.defaultProps.ariaLabels,
      disabled: n,
      locked: t,
      renderSelectionAddon: d,
      ruleOfThirds: r2,
      crop: o
    } = this.props, w = this.getCropStyle();
    if (o)
      return /* @__PURE__ */ u.createElement(
        "div",
        {
          style: w,
          className: "ReactCrop__crop-selection",
          onPointerDown: this.onCropPointerDown,
          "aria-label": e.cropArea,
          tabIndex: 0,
          onKeyDown: this.onComponentKeyDown,
          role: "group"
        },
        !n && !t && /* @__PURE__ */ u.createElement("div", { className: "ReactCrop__drag-elements", onFocus: this.onDragFocus }, /* @__PURE__ */ u.createElement("div", { className: "ReactCrop__drag-bar ord-n", "data-ord": "n" }), /* @__PURE__ */ u.createElement("div", { className: "ReactCrop__drag-bar ord-e", "data-ord": "e" }), /* @__PURE__ */ u.createElement("div", { className: "ReactCrop__drag-bar ord-s", "data-ord": "s" }), /* @__PURE__ */ u.createElement("div", { className: "ReactCrop__drag-bar ord-w", "data-ord": "w" }), /* @__PURE__ */ u.createElement(
          "div",
          {
            className: "ReactCrop__drag-handle ord-nw",
            "data-ord": "nw",
            tabIndex: 0,
            "aria-label": e.nwDragHandle,
            onKeyDown: (i) => this.onHandlerKeyDown(i, "nw"),
            role: "button"
          }
        ), /* @__PURE__ */ u.createElement(
          "div",
          {
            className: "ReactCrop__drag-handle ord-n",
            "data-ord": "n",
            tabIndex: 0,
            "aria-label": e.nDragHandle,
            onKeyDown: (i) => this.onHandlerKeyDown(i, "n"),
            role: "button"
          }
        ), /* @__PURE__ */ u.createElement(
          "div",
          {
            className: "ReactCrop__drag-handle ord-ne",
            "data-ord": "ne",
            tabIndex: 0,
            "aria-label": e.neDragHandle,
            onKeyDown: (i) => this.onHandlerKeyDown(i, "ne"),
            role: "button"
          }
        ), /* @__PURE__ */ u.createElement(
          "div",
          {
            className: "ReactCrop__drag-handle ord-e",
            "data-ord": "e",
            tabIndex: 0,
            "aria-label": e.eDragHandle,
            onKeyDown: (i) => this.onHandlerKeyDown(i, "e"),
            role: "button"
          }
        ), /* @__PURE__ */ u.createElement(
          "div",
          {
            className: "ReactCrop__drag-handle ord-se",
            "data-ord": "se",
            tabIndex: 0,
            "aria-label": e.seDragHandle,
            onKeyDown: (i) => this.onHandlerKeyDown(i, "se"),
            role: "button"
          }
        ), /* @__PURE__ */ u.createElement(
          "div",
          {
            className: "ReactCrop__drag-handle ord-s",
            "data-ord": "s",
            tabIndex: 0,
            "aria-label": e.sDragHandle,
            onKeyDown: (i) => this.onHandlerKeyDown(i, "s"),
            role: "button"
          }
        ), /* @__PURE__ */ u.createElement(
          "div",
          {
            className: "ReactCrop__drag-handle ord-sw",
            "data-ord": "sw",
            tabIndex: 0,
            "aria-label": e.swDragHandle,
            onKeyDown: (i) => this.onHandlerKeyDown(i, "sw"),
            role: "button"
          }
        ), /* @__PURE__ */ u.createElement(
          "div",
          {
            className: "ReactCrop__drag-handle ord-w",
            "data-ord": "w",
            tabIndex: 0,
            "aria-label": e.wDragHandle,
            onKeyDown: (i) => this.onHandlerKeyDown(i, "w"),
            role: "button"
          }
        )),
        d && /* @__PURE__ */ u.createElement("div", { className: "ReactCrop__selection-addon", onPointerDown: (i) => i.stopPropagation() }, d(this.state)),
        r2 && /* @__PURE__ */ u.createElement(u.Fragment, null, /* @__PURE__ */ u.createElement("div", { className: "ReactCrop__rule-of-thirds-hz" }), /* @__PURE__ */ u.createElement("div", { className: "ReactCrop__rule-of-thirds-vt" }))
      );
  }
  makePixelCrop(e) {
    const n = { ...E, ...this.props.crop || {} };
    return D(n, e.width, e.height);
  }
  render() {
    const { aspect: e, children: n, circularCrop: t, className: d, crop: r2, disabled: o, locked: w, style: i, ruleOfThirds: s } = this.props, { cropIsActive: c, newCropIsBeingDrawn: g } = this.state, p = r2 ? this.renderCropSelection() : null, l = H(
      "ReactCrop",
      d,
      c && "ReactCrop--active",
      o && "ReactCrop--disabled",
      w && "ReactCrop--locked",
      g && "ReactCrop--new-crop",
      r2 && e && "ReactCrop--fixed-aspect",
      r2 && t && "ReactCrop--circular-crop",
      r2 && s && "ReactCrop--rule-of-thirds",
      !this.dragStarted && r2 && !r2.width && !r2.height && "ReactCrop--invisible-crop",
      t && "ReactCrop--no-animate"
    );
    return /* @__PURE__ */ u.createElement("div", { ref: this.componentRef, className: l, style: i }, /* @__PURE__ */ u.createElement("div", { ref: this.mediaRef, className: "ReactCrop__child-wrapper", onPointerDown: this.onComponentPointerDown }, n), r2 ? /* @__PURE__ */ u.createElement("svg", { className: "ReactCrop__crop-mask", width: "100%", height: "100%" }, /* @__PURE__ */ u.createElement("defs", null, /* @__PURE__ */ u.createElement("mask", { id: `hole-${this.instanceId}` }, /* @__PURE__ */ u.createElement("rect", { width: "100%", height: "100%", fill: "white" }), t ? /* @__PURE__ */ u.createElement(
      "ellipse",
      {
        cx: `${r2.x + r2.width / 2}${r2.unit}`,
        cy: `${r2.y + r2.height / 2}${r2.unit}`,
        rx: `${r2.width / 2}${r2.unit}`,
        ry: `${r2.height / 2}${r2.unit}`,
        fill: "black"
      }
    ) : /* @__PURE__ */ u.createElement(
      "rect",
      {
        x: `${r2.x}${r2.unit}`,
        y: `${r2.y}${r2.unit}`,
        width: `${r2.width}${r2.unit}`,
        height: `${r2.height}${r2.unit}`,
        fill: "black"
      }
    ))), /* @__PURE__ */ u.createElement("rect", { fill: "black", fillOpacity: 0.5, width: "100%", height: "100%", mask: `url(#hole-${this.instanceId})` })) : void 0, p);
  }
};
m(x, "xOrds", ["e", "w"]), m(x, "yOrds", ["n", "s"]), m(x, "xyOrds", ["nw", "ne", "se", "sw"]), m(x, "nudgeStep", 1), m(x, "nudgeStepMedium", 10), m(x, "nudgeStepLarge", 100), m(x, "defaultProps", {
  ariaLabels: {
    cropArea: "Use the arrow keys to move the crop selection area",
    nwDragHandle: "Use the arrow keys to move the north west drag handle to change the crop selection area",
    nDragHandle: "Use the up and down arrow keys to move the north drag handle to change the crop selection area",
    neDragHandle: "Use the arrow keys to move the north east drag handle to change the crop selection area",
    eDragHandle: "Use the up and down arrow keys to move the east drag handle to change the crop selection area",
    seDragHandle: "Use the arrow keys to move the south east drag handle to change the crop selection area",
    sDragHandle: "Use the up and down arrow keys to move the south drag handle to change the crop selection area",
    swDragHandle: "Use the arrow keys to move the south west drag handle to change the crop selection area",
    wDragHandle: "Use the up and down arrow keys to move the west drag handle to change the crop selection area"
  }
});
let S = x;
const Preview = ({ children, image }) => {
  return typeof children === "function" ? children(image) : children;
};
const DefaultDropzone = ({ onDrop, accept, maxSize, isEditing }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: accept === "image/*" ? { "image/jpeg": [], "image/png": [] } : accept,
    onDrop,
    disabled: isEditing,
    maxSize,
    multiple: false
  });
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ...getRootProps({
        className: `flex flex-col items-center justify-center gap-2 dropzone ${isDragActive ? "active" : ""} cursor-${isEditing ? "default" : "pointer"}`,
        onClick: isEditing ? (e) => e.preventDefault() : void 0
      }),
      children: [
        /* @__PURE__ */ jsx("input", { ...getInputProps() }),
        /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center w-full h-24 transition-colors border-2 border-gray-300 border-dashed dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600", children: /* @__PURE__ */ jsx("i", { className: "text-4xl ri-drag-drop-line" }) }),
        /* @__PURE__ */ jsx("div", { className: "self-start", children: /* @__PURE__ */ jsx("p", { className: "text-tiny text-foreground-400", children: "Drag-and-drop an image or click to select an image" }) })
      ]
    }
  );
};
const ImageInput = ({
  name,
  label,
  defaultValue = null,
  onChange,
  accept = "image/*",
  maxSize = 5 * 1024 * 1024,
  children,
  hasDropzone = false
}) => {
  const [image, setImage] = useState(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [isEditing, setIsEditing] = useState(false);
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const processFile = useCallback(
    (file) => {
      if (!file?.type?.startsWith("image/")) throw new Error("The file is not an image");
      if (file.size > maxSize) throw new Error("The file is too large");
      return URL.createObjectURL(file);
    },
    [maxSize]
  );
  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      try {
        const previewUrl = processFile(file);
        setImage(previewUrl);
        onChange?.(file);
      } catch (error) {
        console.error(error.message);
      }
    },
    [onChange, processFile]
  );
  const handleSaveEdit = () => {
    previewCanvasRef.current?.toBlob((blob) => {
      const fileUrl = URL.createObjectURL(blob);
      setImage(fileUrl);
      setIsEditing(false);
      onChange?.(blob);
    }, "image/png");
  };
  const handleRemoveImage = () => {
    setImage(null);
    setCrop(void 0);
    setIsEditing(false);
    onChange?.(null);
  };
  useEffect(() => {
    if (completedCrop?.width && completedCrop?.height && imgRef.current && previewCanvasRef.current) {
      canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop);
    }
  }, [completedCrop]);
  useEffect(() => {
    if (!defaultValue) {
      setImage(null);
    } else if (defaultValue instanceof Blob) {
      const reader = new FileReader();
      reader.onload = (e) => setImage(e.target.result);
      reader.readAsDataURL(defaultValue);
    } else if (typeof defaultValue === "string") {
      setImage(defaultValue);
    }
  }, [defaultValue]);
  const getSubComponent = (type, props = {}) => {
    const childrenArray = u.Children.toArray(children);
    const child = childrenArray.find((child2) => child2.type === type);
    if (child) {
      return u.cloneElement(child, props);
    }
    if (type === ImageInput.Preview) {
      return /* @__PURE__ */ jsx(Preview, { ...props, children: (image2) => /* @__PURE__ */ jsx("img", { src: image2, alt: label, className: "object-contain w-full h-full" }) });
    }
    if (type === ImageInput.Dropzone) {
      return /* @__PURE__ */ jsx(DefaultDropzone, { ...props });
    }
    return null;
  };
  return /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-center image-input-container", children: image ? /* @__PURE__ */ jsx("div", { className: "relative cursor-pointer group", children: isEditing ? /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("div", { className: "flex justify-center items-center w-[300px] h-[300px]", children: /* @__PURE__ */ jsx(S, { crop, onChange: setCrop, onComplete: setCompletedCrop, children: /* @__PURE__ */ jsx(
      "img",
      {
        ref: imgRef,
        src: image,
        alt: "Upload preview",
        className: "object-contain w-full h-full"
      }
    ) }) }),
    /* @__PURE__ */ jsx("canvas", { style: { display: "none" }, ref: previewCanvasRef }),
    /* @__PURE__ */ jsxs("div", { className: "absolute flex gap-2 top-2 right-2", children: [
      /* @__PURE__ */ jsx(SaveButton, { isIconOnly: true, type: "button", onPress: handleSaveEdit }),
      /* @__PURE__ */ jsx(
        DeleteButton,
        {
          isIconOnly: true,
          withConfirm: false,
          type: "button",
          onPress: handleRemoveImage
        }
      )
    ] })
  ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Preview, { image, children: getSubComponent(ImageInput.Preview)?.props.children || /* @__PURE__ */ jsx("img", { src: image, alt: name, className: "object-contain" }) }),
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "absolute inset-0 flex items-center justify-center gap-2 transition-opacity opacity-0 bg-black/50 group-hover:opacity-100",
        onClick: () => setIsEditing(true),
        children: /* @__PURE__ */ jsx("i", { className: "text-4xl text-gray-400 ri-pencil-line" })
      }
    )
  ] }) }) : hasDropzone ? getSubComponent(ImageInput.Dropzone, { onDrop, accept, maxSize, isEditing }) : /* @__PURE__ */ jsx(
    "input",
    {
      type: "file",
      accept,
      onChange: (e) => onDrop(e.target.files),
      className: "traditional-input"
    }
  ) });
};
const Dropzone = ({ children, isEditing, onDrop, accept, maxSize }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: accept === "image/*" ? { "image/jpeg": [], "image/png": [] } : accept,
    onDrop,
    disabled: isEditing,
    maxSize,
    multiple: false
  });
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ...getRootProps({
        className: `dropzone ${isDragActive ? "active" : ""} cursor-${isEditing ? "default" : "pointer"}`,
        onClick: isEditing ? (e) => e.preventDefault() : void 0
      }),
      children: [
        /* @__PURE__ */ jsx("input", { ...getInputProps() }),
        children
      ]
    }
  );
};
ImageInput.Dropzone = Dropzone;
ImageInput.Preview = Preview;
const canvasPreview = (img, canvas, crop = null) => {
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No 2d context");
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  if (crop && crop.width && crop.height) {
    canvas.width = crop.width;
    canvas.height = crop.height;
    const scaleX = img.naturalWidth / img.width;
    const scaleY = img.naturalHeight / img.height;
    const cropX = crop.x * scaleX;
    const cropY = crop.y * scaleY;
    const cropWidth = crop.width * scaleX;
    const cropHeight = crop.height * scaleY;
    ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, crop.width, crop.height);
  } else {
    canvas.width = img.width;
    canvas.height = img.height;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  }
};
const DATA = [
  ["AF", "Afghanistan", "93"],
  ["AL", "Albania", "355"],
  ["DZ", "Algeria", "213"],
  ["AD", "Andorra", "376"],
  ["AO", "Angola", "244"],
  ["AG", "Antigua and Barbuda", "1"],
  ["AR", "Argentina", "54"],
  ["AM", "Armenia", "374"],
  ["AU", "Australia", "61"],
  ["AT", "Austria", "43"],
  ["AZ", "Azerbaijan", "994"],
  ["BS", "Bahamas", "1"],
  ["BH", "Bahrain", "973"],
  ["BD", "Bangladesh", "880"],
  ["BB", "Barbados", "1"],
  ["BY", "Belarus", "375"],
  ["BE", "Belgium", "32"],
  ["BZ", "Belize", "501"],
  ["BJ", "Benin", "229"],
  ["BT", "Bhutan", "975"],
  ["BO", "Bolivia", "591"],
  ["BA", "Bosnia and Herzegovina", "387"],
  ["BW", "Botswana", "267"],
  ["BR", "Brazil", "55"],
  ["BN", "Brunei", "673"],
  ["BG", "Bulgaria", "359"],
  ["BF", "Burkina Faso", "226"],
  ["BI", "Burundi", "257"],
  ["KH", "Cambodia", "855"],
  ["CM", "Cameroon", "237"],
  ["CA", "Canada", "1"],
  ["CV", "Cape Verde", "238"],
  ["CF", "Central African Republic", "236"],
  ["TD", "Chad", "235"],
  ["CL", "Chile", "56"],
  ["CN", "China", "86"],
  ["CO", "Colombia", "57"],
  ["KM", "Comoros", "269"],
  ["CG", "Congo", "242"],
  ["CD", "Congo (DRC)", "243"],
  ["CR", "Costa Rica", "506"],
  ["CI", "Côte d'Ivoire", "225"],
  ["HR", "Croatia", "385"],
  ["CU", "Cuba", "53"],
  ["CY", "Cyprus", "357"],
  ["CZ", "Czechia", "420"],
  ["DK", "Denmark", "45"],
  ["DJ", "Djibouti", "253"],
  ["DM", "Dominica", "1"],
  ["DO", "Dominican Republic", "1"],
  ["EC", "Ecuador", "593"],
  ["EG", "Egypt", "20"],
  ["SV", "El Salvador", "503"],
  ["GQ", "Equatorial Guinea", "240"],
  ["ER", "Eritrea", "291"],
  ["EE", "Estonia", "372"],
  ["SZ", "Eswatini", "268"],
  ["ET", "Ethiopia", "251"],
  ["FJ", "Fiji", "679"],
  ["FI", "Finland", "358"],
  ["FR", "France", "33"],
  ["GA", "Gabon", "241"],
  ["GM", "Gambia", "220"],
  ["GE", "Georgia", "995"],
  ["DE", "Germany", "49"],
  ["GH", "Ghana", "233"],
  ["GR", "Greece", "30"],
  ["GD", "Grenada", "1"],
  ["GT", "Guatemala", "502"],
  ["GN", "Guinea", "224"],
  ["GW", "Guinea-Bissau", "245"],
  ["GY", "Guyana", "592"],
  ["HT", "Haiti", "509"],
  ["HN", "Honduras", "504"],
  ["HK", "Hong Kong", "852"],
  ["HU", "Hungary", "36"],
  ["IS", "Iceland", "354"],
  ["IN", "India", "91"],
  ["ID", "Indonesia", "62"],
  ["IR", "Iran", "98"],
  ["IQ", "Iraq", "964"],
  ["IE", "Ireland", "353"],
  ["IL", "Israel", "972"],
  ["IT", "Italy", "39"],
  ["JM", "Jamaica", "1"],
  ["JP", "Japan", "81"],
  ["JO", "Jordan", "962"],
  ["KZ", "Kazakhstan", "7"],
  ["KE", "Kenya", "254"],
  ["KI", "Kiribati", "686"],
  ["KW", "Kuwait", "965"],
  ["KG", "Kyrgyzstan", "996"],
  ["LA", "Laos", "856"],
  ["LV", "Latvia", "371"],
  ["LB", "Lebanon", "961"],
  ["LS", "Lesotho", "266"],
  ["LR", "Liberia", "231"],
  ["LY", "Libya", "218"],
  ["LI", "Liechtenstein", "423"],
  ["LT", "Lithuania", "370"],
  ["LU", "Luxembourg", "352"],
  ["MO", "Macau", "853"],
  ["MG", "Madagascar", "261"],
  ["MW", "Malawi", "265"],
  ["MY", "Malaysia", "60"],
  ["MV", "Maldives", "960"],
  ["ML", "Mali", "223"],
  ["MT", "Malta", "356"],
  ["MH", "Marshall Islands", "692"],
  ["MR", "Mauritania", "222"],
  ["MU", "Mauritius", "230"],
  ["MX", "Mexico", "52"],
  ["FM", "Micronesia", "691"],
  ["MD", "Moldova", "373"],
  ["MC", "Monaco", "377"],
  ["MN", "Mongolia", "976"],
  ["ME", "Montenegro", "382"],
  ["MA", "Morocco", "212"],
  ["MZ", "Mozambique", "258"],
  ["MM", "Myanmar", "95"],
  ["NA", "Namibia", "264"],
  ["NR", "Nauru", "674"],
  ["NP", "Nepal", "977"],
  ["NL", "Netherlands", "31"],
  ["NZ", "New Zealand", "64"],
  ["NI", "Nicaragua", "505"],
  ["NE", "Niger", "227"],
  ["NG", "Nigeria", "234"],
  ["KP", "North Korea", "850"],
  ["MK", "North Macedonia", "389"],
  ["NO", "Norway", "47"],
  ["OM", "Oman", "968"],
  ["PK", "Pakistan", "92"],
  ["PW", "Palau", "680"],
  ["PS", "Palestine", "970"],
  ["PA", "Panama", "507"],
  ["PG", "Papua New Guinea", "675"],
  ["PY", "Paraguay", "595"],
  ["PE", "Peru", "51"],
  ["PH", "Philippines", "63"],
  ["PL", "Poland", "48"],
  ["PT", "Portugal", "351"],
  ["PR", "Puerto Rico", "1"],
  ["QA", "Qatar", "974"],
  ["RO", "Romania", "40"],
  ["RU", "Russia", "7"],
  ["RW", "Rwanda", "250"],
  ["KN", "Saint Kitts and Nevis", "1"],
  ["LC", "Saint Lucia", "1"],
  ["VC", "Saint Vincent and the Grenadines", "1"],
  ["WS", "Samoa", "685"],
  ["SM", "San Marino", "378"],
  ["ST", "São Tomé and Príncipe", "239"],
  ["SA", "Saudi Arabia", "966"],
  ["SN", "Senegal", "221"],
  ["RS", "Serbia", "381"],
  ["SC", "Seychelles", "248"],
  ["SL", "Sierra Leone", "232"],
  ["SG", "Singapore", "65"],
  ["SK", "Slovakia", "421"],
  ["SI", "Slovenia", "386"],
  ["SB", "Solomon Islands", "677"],
  ["SO", "Somalia", "252"],
  ["ZA", "South Africa", "27"],
  ["KR", "South Korea", "82"],
  ["SS", "South Sudan", "211"],
  ["ES", "Spain", "34"],
  ["LK", "Sri Lanka", "94"],
  ["SD", "Sudan", "249"],
  ["SR", "Suriname", "597"],
  ["SE", "Sweden", "46"],
  ["CH", "Switzerland", "41"],
  ["SY", "Syria", "963"],
  ["TW", "Taiwan", "886"],
  ["TJ", "Tajikistan", "992"],
  ["TZ", "Tanzania", "255"],
  ["TH", "Thailand", "66"],
  ["TL", "Timor-Leste", "670"],
  ["TG", "Togo", "228"],
  ["TO", "Tonga", "676"],
  ["TT", "Trinidad and Tobago", "1"],
  ["TN", "Tunisia", "216"],
  ["TR", "Turkey", "90"],
  ["TM", "Turkmenistan", "993"],
  ["TV", "Tuvalu", "688"],
  ["UG", "Uganda", "256"],
  ["UA", "Ukraine", "380"],
  ["AE", "United Arab Emirates", "971"],
  ["GB", "United Kingdom", "44"],
  ["US", "United States", "1"],
  ["UY", "Uruguay", "598"],
  ["UZ", "Uzbekistan", "998"],
  ["VU", "Vanuatu", "678"],
  ["VA", "Vatican City", "39"],
  ["VE", "Venezuela", "58"],
  ["VN", "Vietnam", "84"],
  ["YE", "Yemen", "967"],
  ["ZM", "Zambia", "260"],
  ["ZW", "Zimbabwe", "263"]
];
const flagEmoji = (code) => code.toUpperCase().replace(/./g, (character) => String.fromCodePoint(127462 + character.charCodeAt(0) - 65));
const DEFAULT_COUNTRY = "US";
const COUNTRIES = DATA.map(([code, name, dialCode]) => ({
  code,
  name,
  dialCode,
  flag: flagEmoji(code)
}));
const BY_CODE = new Map(COUNTRIES.map((country) => [country.code, country]));
const findCountry = (code) => BY_CODE.get(code);
const findCountryByDigits = (digits2) => {
  let best;
  for (const country of COUNTRIES) {
    if (digits2.startsWith(country.dialCode)) {
      if (!best || country.dialCode.length > best.dialCode.length) best = country;
    }
  }
  if (best?.dialCode === "1") return BY_CODE.get("US");
  return best;
};
const onlyDigits = (value) => String(value ?? "").replace(/\D/g, "");
const readValue = (arg) => typeof arg === "string" ? arg : arg?.target?.value ?? "";
const nationalOf = (value, country) => {
  const digits2 = onlyDigits(value);
  return country && digits2.startsWith(country.dialCode) ? digits2.slice(country.dialCode.length) : digits2;
};
const CountryPicker = ({ countryCode, onChange, isDisabled }) => /* @__PURE__ */ jsxs(
  Select,
  {
    "aria-label": "Country calling code",
    value: countryCode,
    onChange: (key) => key && onChange(key),
    isDisabled,
    className: "shrink-0",
    children: [
      /* @__PURE__ */ jsxs(Select.Trigger, { className: "flex h-auto items-center border-0 bg-transparent pl-1 pr-6 shadow-none", children: [
        /* @__PURE__ */ jsx(Select.Value, { children: ({ defaultChildren, isPlaceholder, state }) => {
          const selected = findCountry(state.selectedItems?.[0]?.key);
          return isPlaceholder || !selected ? defaultChildren : /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1 text-sm leading-none", children: [
            /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "text-base leading-none", children: selected.flag }),
            "+",
            selected.dialCode
          ] });
        } }),
        /* @__PURE__ */ jsx(Select.Indicator, {})
      ] }),
      /* @__PURE__ */ jsx(Select.Popover, { className: "w-[300px]", children: /* @__PURE__ */ jsx(ListBox, { children: COUNTRIES.map((country) => /* @__PURE__ */ jsxs(
        ListBox.Item,
        {
          id: country.code,
          textValue: country.name,
          className: "flex items-center gap-2",
          children: [
            /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: country.flag }),
            /* @__PURE__ */ jsx("span", { className: "flex-1", children: country.name }),
            /* @__PURE__ */ jsxs("span", { className: "text-muted", children: [
              "+",
              country.dialCode
            ] }),
            /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
          ]
        },
        country.code
      )) }) })
    ]
  }
);
const PhoneInput = ({
  value = "",
  onChange,
  international = true,
  defaultCountry = DEFAULT_COUNTRY,
  label,
  labelAction,
  "aria-label": ariaLabel,
  id,
  isRequired = false,
  isInvalid = false,
  placeholder = "Phone number",
  isDisabled = false,
  className = ""
}) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const [countryCode, setCountryCode] = useState(() => {
    const digits2 = onlyDigits(value);
    if (international && digits2) {
      const matched = findCountryByDigits(digits2);
      if (matched) return matched.code;
    }
    return findCountry(international ? defaultCountry : "US")?.code ?? "US";
  });
  const country = findCountry(countryCode) ?? COUNTRIES[0];
  const national = nationalOf(value, country);
  const emit = (dialCode, digits2) => onChange?.(digits2 ? `+${dialCode}${digits2}` : "");
  const handleInput = (raw) => {
    if (raw.trim().startsWith("+")) {
      const digits2 = onlyDigits(raw);
      const matched = findCountryByDigits(digits2);
      if (matched) {
        setCountryCode(matched.code);
        emit(matched.dialCode, digits2.slice(matched.dialCode.length));
      } else {
        onChange?.(digits2 ? `+${digits2}` : "");
      }
      return;
    }
    emit(country.dialCode, onlyDigits(raw));
  };
  const handleCountry = (code) => {
    const next = findCountry(code);
    if (!next) return;
    setCountryCode(next.code);
    emit(next.dialCode, national);
  };
  const group = /* @__PURE__ */ jsxs(InputGroup, { fullWidth: true, className: `bg-surface-secondary ${className}`, isInvalid, children: [
    /* @__PURE__ */ jsx(InputGroup.Prefix, { className: "shrink-0 pr-0", children: international ? /* @__PURE__ */ jsx(
      CountryPicker,
      {
        countryCode,
        onChange: handleCountry,
        isDisabled
      }
    ) : /* @__PURE__ */ jsxs("span", { className: "text-muted flex items-center gap-1 px-1 text-sm whitespace-nowrap", children: [
      /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "text-base leading-none", children: country.flag }),
      "+",
      country.dialCode
    ] }) }),
    /* @__PURE__ */ jsx(
      InputGroup.Input,
      {
        id: inputId,
        className: "min-w-0",
        type: "tel",
        inputMode: "numeric",
        autoComplete: "tel-national",
        "aria-label": label ? void 0 : ariaLabel ?? "Phone number",
        placeholder,
        value: national,
        disabled: isDisabled,
        onChange: (arg) => handleInput(readValue(arg))
      }
    )
  ] });
  if (!label) return group;
  const labelElement = /* @__PURE__ */ jsx(Label, { htmlFor: inputId, isRequired, isDisabled, isInvalid, children: label });
  return /* @__PURE__ */ jsxs("div", { className: "flex w-full flex-col gap-1", children: [
    labelAction ? /* @__PURE__ */ jsxs("div", { "data-slot": "label-row", className: "flex flex-wrap items-center justify-between gap-x-2", children: [
      labelElement,
      labelAction
    ] }) : labelElement,
    group
  ] });
};
const TagsList = ({ tags, onClose }) => {
  return /* @__PURE__ */ jsx("div", { className: "flex flex-row items-center gap-1", children: tags.filter((tag) => !!tag?.name && tag?.name !== "").map((tag, index) => /* @__PURE__ */ jsxs(Chip, { color: tag.editing ? "warning" : "accent", size: "sm", children: [
    /* @__PURE__ */ jsx("span", { className: "font-bold", children: tag.name }),
    /* @__PURE__ */ jsx(CloseButton, { "aria-label": "Remove tag", onPress: () => onClose(tag) })
  ] }, `tag-${index}`)) });
};
const InputField = ({ value, onChange, ...rest }) => {
  const [state, setState] = useState(value);
  return /* @__PURE__ */ jsx(
    Input,
    {
      ...rest,
      value,
      onChange: (e) => setState(e.target.value),
      onBlur: () => onChange(state),
      onKeyUp: (e) => {
        if (e.key === "Enter") {
          onChange(state);
        }
      }
    }
  );
};
const TagInput = ({ availableTags, withAutocomplete, onChange, children }) => {
  const items = useMemo(
    () => (availableTags ?? []).map((x3) => ({ value: x3.name, label: x3.name })),
    [availableTags]
  );
  return /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-1", children: withAutocomplete ? /* @__PURE__ */ jsx(
    Autocomplete,
    {
      name: "tag",
      placeholder: "Tags",
      "aria-label": "Tags",
      items,
      value: "",
      allowsCustomValue: false,
      extractValue: (x3) => x3.value,
      extractDisplayValue: (x3) => x3.label,
      onChange: (val) => onChange(val),
      children: (item) => /* @__PURE__ */ jsx(ListBox.Item, { id: item.value, textValue: item.label, children: item.label })
    }
  ) : /* @__PURE__ */ jsx(
    InputField,
    {
      className: "w-full my-1",
      name: "tag",
      onChange: (val) => onChange(val),
      placeholder: "Tag Name"
    }
  ) });
};
const TagsField = ({
  defaultValue = [],
  onChange,
  withAutocomplete,
  availableTags = []
}) => {
  const convertNames = useCallback((t) => (t ?? []).map((x3) => ({ name: x3, editing: false })), []);
  const [isOpen, setIsOpen] = useState(false);
  const [available, { push: pushAvailable, removeAt: removeAvailableAt, set: setAvailable }] = useList(convertNames(availableTags.filter((x3) => !defaultValue.includes(x3))));
  const [tags, { push: pushTag, removeAt: removeTagAt, set: setTags }] = useList(
    convertNames(defaultValue)
  );
  const handleChange = (tag) => {
    if (tag && tag !== "" && !tags.find((x3) => x3.name === tag)) {
      pushTag({ name: tag, editing: true });
    }
    const index = available.findIndex((x3) => x3.name === tag);
    if (index > -1) {
      removeAvailableAt(index);
    }
    setIsOpen(false);
  };
  const handleRemove = (tag) => {
    const index = tags.indexOf(tag);
    if (index > -1) {
      removeTagAt(index);
    }
    pushAvailable(tag);
  };
  useDeepCompareEffect(() => {
    onChange(tags.map((x3) => x3.name));
  }, [tags]);
  useDeepCompareEffect(() => {
    setAvailable(convertNames(availableTags.filter((x3) => !defaultValue.includes(x3))));
  }, [availableTags, defaultValue]);
  useDeepCompareEffect(() => {
    setTags(convertNames(defaultValue));
  }, [defaultValue]);
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-1", children: [
    /* @__PURE__ */ jsxs(
      Popover,
      {
        isOpen,
        onOpenChange: (open) => {
          setIsOpen(open);
        },
        children: [
          /* @__PURE__ */ jsx(Button, { isIconOnly: true, size: "sm", variant: "tertiary", "aria-label": "Add tag", children: /* @__PURE__ */ jsx("i", { className: "text-3xl ri-add-line" }) }),
          /* @__PURE__ */ jsx(Popover.Content, { placement: "bottom", offset: 10, children: /* @__PURE__ */ jsxs(Popover.Dialog, { children: [
            /* @__PURE__ */ jsx(Popover.Arrow, {}),
            /* @__PURE__ */ jsx("div", { className: "flex flex-row items-center gap-2", children: /* @__PURE__ */ jsx(
              TagInput,
              {
                availableTags: available,
                withAutocomplete,
                onChange: handleChange
              }
            ) })
          ] }) })
        ]
      }
    ),
    /* @__PURE__ */ jsx(TagsList, { tags, onClose: handleRemove })
  ] });
};
const DEFAULT_MAX_MATCHES = 100;
const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || void 0;
  } catch {
    return void 0;
  }
};
const allTimeZones = () => {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return [browserTimeZone(), "UTC"].filter(Boolean);
  }
};
const normalize$1 = (zone) => typeof zone === "string" ? { id: zone, label: zone } : zone;
const browserTimeZoneIn = (zones) => {
  const zone = browserTimeZone();
  return zone && (zones ?? allTimeZones()).map(normalize$1).some((option) => option.id === zone) ? zone : void 0;
};
const TimeZoneComboBox = ({
  value,
  onChange,
  zones,
  label = "Time zone",
  defaultToBrowserTimeZone = true,
  maxMatches = DEFAULT_MAX_MATCHES,
  placeholder = "Search time zones",
  emptyContent = "No matching time zones",
  isDisabled,
  isInvalid,
  errorMessage,
  className,
  ...rest
}) => {
  const { contains } = useFilter({ sensitivity: "base" });
  const listed = useMemo(() => (zones ?? allTimeZones()).map(normalize$1), [zones]);
  const options = useMemo(() => {
    return value && !listed.some((zone) => zone.id === value) ? [...listed, { id: value, label: value }] : listed;
  }, [listed, value]);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    if (value || !defaultToBrowserTimeZone || isDisabled) return;
    const zone = browserTimeZoneIn(listed);
    if (zone) onChangeRef.current?.(zone);
  }, [value, defaultToBrowserTimeZone, isDisabled, listed]);
  const selectedLabel = useMemo(
    () => options.find((zone) => zone.id === value)?.label ?? "",
    [options, value]
  );
  const [inputValue, setInputValue] = useState(selectedLabel);
  useEffect(() => setInputValue(selectedLabel), [selectedLabel]);
  const items = useMemo(() => {
    const query = inputValue === selectedLabel ? "" : inputValue;
    return options.filter((zone) => contains(zone.label, query) || contains(zone.id, query)).slice(0, maxMatches);
  }, [options, inputValue, selectedLabel, maxMatches, contains]);
  return /* @__PURE__ */ jsxs(
    ComboBox,
    {
      selectedKey: value ?? null,
      inputValue,
      defaultFilter: () => true,
      allowsEmptyCollection: true,
      onInputChange: setInputValue,
      onSelectionChange: (key) => key && onChange(String(key)),
      isDisabled,
      isInvalid,
      className,
      ...rest,
      children: [
        label && /* @__PURE__ */ jsx(Label, { children: label }),
        /* @__PURE__ */ jsxs(ComboBox.InputGroup, { children: [
          /* @__PURE__ */ jsx(Input, { variant: "secondary", placeholder }),
          /* @__PURE__ */ jsx(ComboBox.Trigger, {})
        ] }),
        isInvalid && errorMessage && /* @__PURE__ */ jsx(FieldError, { children: errorMessage }),
        /* @__PURE__ */ jsx(ComboBox.Popover, { children: /* @__PURE__ */ jsx(ListBox, { items, renderEmptyState: () => emptyContent, children: (zone) => /* @__PURE__ */ jsxs(ListBox.Item, { id: zone.id, textValue: zone.label, children: [
          zone.label,
          /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
        ] }) }) })
      ]
    }
  );
};
const COMMON_TIME_ZONES = Object.freeze([
  { id: "America/New_York", label: "(UTC−5/−4) Eastern" },
  { id: "America/Chicago", label: "(UTC−6/−5) Central" },
  { id: "America/Denver", label: "(UTC−7/−6) Mountain" },
  { id: "America/Los_Angeles", label: "(UTC−8/−7) Pacific" },
  { id: "America/Phoenix", label: "(UTC−7) Arizona — no DST" },
  { id: "America/Anchorage", label: "(UTC−9/−8) Alaska" },
  { id: "Pacific/Honolulu", label: "(UTC−10) Hawaii" }
]);
const USTimeZoneComboBox = (props) => /* @__PURE__ */ jsx(TimeZoneComboBox, { ...props, zones: COMMON_TIME_ZONES });
const ResourceActions = ({
  onDelete,
  onArchive,
  onCancel,
  onSave,
  deleteSize = "sm",
  archiveSize = "sm",
  cancelSize = "sm",
  saveSize = "sm",
  isLoading,
  isDeleteEnabled = true,
  isArchiveEnabled = true,
  isCancelEnabled = true,
  isSaveEnabled = true,
  saveLabel = "Save",
  cancelLabel = "Cancel",
  deleteLabel = "Delete",
  archiveLabel = "Archive",
  confirmOnCancel = false,
  errors = [],
  children
}) => {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-row w-full gap-1", children: [
    onDelete && /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
      /* @__PURE__ */ jsx(
        DeleteButton,
        {
          onPress: onDelete,
          isIconOnly: true,
          size: deleteSize,
          isDisabled: !isDeleteEnabled || isLoading,
          label: deleteLabel
        }
      ),
      /* @__PURE__ */ jsx(Tooltip.Content, { children: "Delete" })
    ] }),
    onArchive && /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
      /* @__PURE__ */ jsx(
        ArchiveButton,
        {
          onPress: onArchive,
          isIconOnly: true,
          size: archiveSize,
          isDisabled: !isArchiveEnabled || isLoading,
          label: archiveLabel
        }
      ),
      /* @__PURE__ */ jsx(Tooltip.Content, { children: "Archive" })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex grow flex-row items-center justify-end", children: [
      /* @__PURE__ */ jsx("div", { className: "grow", children }),
      /* @__PURE__ */ jsx(ErrorListChip, { errors })
    ] }),
    onCancel && /* @__PURE__ */ jsx(
      ConfirmableButton,
      {
        variant: "tertiary",
        onPress: onCancel,
        size: cancelSize,
        isDisabled: !isCancelEnabled || isLoading,
        withConfirm: confirmOnCancel,
        label: cancelLabel
      }
    ),
    /* @__PURE__ */ jsx(
      SaveButton,
      {
        onPress: onSave,
        size: saveSize,
        isDisabled: !isSaveEnabled,
        isLoading,
        label: saveLabel
      }
    )
  ] });
};
const defaultItemValue = (item) => item?.id;
const defaultItemText = (item) => item?.name ?? "";
const defaultMinLengthMessage = (minLength) => `Type at least ${minLength} character${minLength === 1 ? "" : "s"} to search.`;
const SearchComboBox = ({
  items = null,
  isSearching = false,
  onSearch,
  onSelect,
  children,
  getItemValue = defaultItemValue,
  getItemText = defaultItemText,
  minLength = 2,
  delay = 300,
  defaultInputValue = "",
  defaultValue = null,
  label,
  placeholder = "Search...",
  description,
  errorMessage,
  emptyMessage = "No results found.",
  searchingMessage = "Searching...",
  formatMinLengthMessage = defaultMinLengthMessage,
  name,
  isClearable = false,
  isDisabled = false,
  isRequired = false,
  isInvalid = false,
  autoFocus = false,
  fullWidth = true,
  className
}) => {
  const [term, setTerm] = useState(defaultInputValue);
  const lastSearched = useRef(null);
  const selectedText = useRef(defaultInputValue || null);
  const appliedDefault = useRef(defaultInputValue);
  const adoptedValue = useRef(null);
  const latestOnSearch = useRef(onSearch);
  const debouncedTerm = useDebounce(term, delay);
  const list = Array.isArray(items) ? items : [];
  const query = term.trim();
  const canSearch = query.length >= minLength;
  const hasCurrentItems = query === lastSearched.current || query === selectedText.current;
  const isBusy = isSearching || canSearch && !hasCurrentItems;
  const visibleItems = canSearch && hasCurrentItems && !isSearching ? list : [];
  const renderItem = typeof children === "function" ? children : () => null;
  useEffect(() => {
    latestOnSearch.current = onSearch;
  });
  useEffect(() => {
    if (defaultInputValue === appliedDefault.current) return;
    const isUserEdited = term !== appliedDefault.current;
    appliedDefault.current = defaultInputValue;
    if (isUserEdited) return;
    selectedText.current = defaultInputValue || null;
    setTerm(defaultInputValue);
  }, [defaultInputValue, term]);
  useEffect(() => {
    if (defaultValue !== null && defaultValue !== void 0 && defaultValue !== "") return;
    if (adoptedValue.current === null) return;
    const isUserEdited = term !== selectedText.current;
    adoptedValue.current = null;
    if (isUserEdited) return;
    selectedText.current = null;
    lastSearched.current = null;
    setTerm("");
  }, [defaultValue, term]);
  useEffect(() => {
    if (defaultValue === null || defaultValue === void 0 || defaultValue === "") return;
    if (defaultInputValue) return;
    if (adoptedValue.current === defaultValue) return;
    if (lastSearched.current !== null) return;
    if (!Array.isArray(items)) return;
    const match = items.find((x3) => String(getItemValue(x3)) === String(defaultValue));
    if (!match) return;
    adoptedValue.current = defaultValue;
    const text = getItemText(match);
    selectedText.current = text;
    setTerm(text);
  }, [items, defaultValue, defaultInputValue, getItemValue, getItemText]);
  useEffect(() => {
    const pending = debouncedTerm.trim();
    if (pending.length < minLength) return;
    if (pending === selectedText.current) return;
    if (pending === lastSearched.current) return;
    lastSearched.current = pending;
    latestOnSearch.current(pending);
  }, [debouncedTerm, minLength]);
  const handleSelectionChange = (key) => {
    if (key === null || key === void 0) {
      selectedText.current = null;
      onSelect && onSelect(null, null);
      return;
    }
    const item = list.find((x3) => String(getItemValue(x3)) === String(key)) ?? null;
    selectedText.current = item ? getItemText(item) : null;
    onSelect && onSelect(item ? getItemValue(item) : key, item);
  };
  const handleClear = () => {
    setTerm("");
    lastSearched.current = null;
    adoptedValue.current = null;
    handleSelectionChange(null);
  };
  const renderEmptyState = () => {
    if (!canSearch)
      return /* @__PURE__ */ jsx("p", { className: "px-3 py-2 text-sm text-muted", children: formatMinLengthMessage(minLength) });
    if (isBusy) {
      return /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-center gap-2 px-3 py-2 text-sm text-muted", children: [
        /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
        searchingMessage
      ] });
    }
    return /* @__PURE__ */ jsx("p", { className: "px-3 py-2 text-sm text-muted", children: emptyMessage });
  };
  return /* @__PURE__ */ jsxs(
    ComboBox,
    {
      className,
      name,
      inputValue: term,
      onInputChange: (value) => setTerm(value ?? ""),
      onSelectionChange: handleSelectionChange,
      isDisabled,
      isRequired,
      isInvalid,
      autoFocus,
      fullWidth,
      menuTrigger: "input",
      allowsEmptyCollection: true,
      defaultFilter: () => true,
      "aria-label": label ? void 0 : "Search",
      variant: "secondary",
      children: [
        label && /* @__PURE__ */ jsx(Label, { children: label }),
        /* @__PURE__ */ jsxs(ComboBox.InputGroup, { children: [
          /* @__PURE__ */ jsx(Input, { placeholder, className: isClearable ? "pr-14" : void 0 }),
          isClearable && term && !isDisabled && /* @__PURE__ */ jsx(
            Button,
            {
              isIconOnly: true,
              size: "sm",
              variant: "ghost",
              "aria-label": "Clear",
              className: "absolute right-7 top-1/2 h-6 w-6 min-w-6 -translate-y-1/2 rounded-full",
              onPress: handleClear,
              children: /* @__PURE__ */ jsx("i", { className: "ri-close-line" })
            }
          ),
          isBusy ? /* @__PURE__ */ jsx(ComboBox.Trigger, { children: /* @__PURE__ */ jsx(Spinner, { size: "sm", color: "current" }) }) : /* @__PURE__ */ jsx(ComboBox.Trigger, {})
        ] }),
        /* @__PURE__ */ jsx(ComboBox.Popover, { children: /* @__PURE__ */ jsx(ListBox, { items: visibleItems, renderEmptyState, children: (item) => /* @__PURE__ */ jsxs(ListBox.Item, { id: getItemValue(item), textValue: getItemText(item), children: [
          renderItem(item),
          /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
        ] }) }) }),
        description && /* @__PURE__ */ jsx(Description, { children: description }),
        errorMessage && /* @__PURE__ */ jsx(FieldError, { children: errorMessage })
      ]
    }
  );
};
const defaultResultKey = (result, index) => result?.id ?? index;
const defaultFormatResultCount = (count) => `${count} result${count === 1 ? "" : "s"}`;
const SearchPanelInput = ({
  label,
  placeholder,
  value,
  onChange,
  onSubmit,
  isSearching,
  isDisabled,
  autoFocus,
  className
}) => /* @__PURE__ */ jsxs(
  SearchField,
  {
    className,
    value,
    onChange,
    onSubmit,
    isDisabled,
    autoFocus,
    "aria-label": label ? void 0 : "Search",
    fullWidth: true,
    children: [
      label && /* @__PURE__ */ jsx(Label, { children: label }),
      /* @__PURE__ */ jsxs(SearchField.Group, { children: [
        isSearching ? /* @__PURE__ */ jsx(SearchField.SearchIcon, { children: /* @__PURE__ */ jsx(Spinner, { size: "sm", color: "current" }) }) : /* @__PURE__ */ jsx(SearchField.SearchIcon, {}),
        /* @__PURE__ */ jsx(SearchField.Input, { placeholder }),
        /* @__PURE__ */ jsx(SearchField.ClearButton, {})
      ] })
    ]
  }
);
const SearchPanel = ({
  results = null,
  isSearching = false,
  onSearch,
  onClearResults,
  onResultSelect,
  children,
  label,
  placeholder = "Search...",
  defaultValue = "",
  minLength = 1,
  autoFocus = false,
  isDisabled = false,
  showButton = false,
  buttonLabel = "Search",
  buttonPlacement = "horizontal",
  buttonProps,
  resultsButtonPlacement = "left",
  modalTitle = "Search Results",
  modalSize = "lg",
  emptyMessage = "No results found.",
  promptMessage = "Enter a search term to begin.",
  formatResultCount = defaultFormatResultCount,
  getResultKey = defaultResultKey,
  className = ""
}) => {
  const [term, setTerm] = useState(defaultValue);
  const [isResultsOpen, setIsResultsOpen] = useState(false);
  const awaitingResults = useRef(null);
  const wasSearching = useRef(isSearching);
  const hasResults = Array.isArray(results);
  const resultCount = hasResults ? results.length : 0;
  const isVertical = buttonPlacement === "vertical";
  const isResultsInline = resultsButtonPlacement === "left" || resultsButtonPlacement === "right";
  const isResultsFirst = resultsButtonPlacement === "left" || resultsButtonPlacement === "top";
  const canSearch = !isDisabled && !isSearching && term.trim().length >= minLength;
  const renderResult = typeof children === "function" ? children : () => null;
  const search = useCallback(() => {
    if (!canSearch) return;
    awaitingResults.current = { previous: results };
    onSearch(term.trim());
  }, [canSearch, results, onSearch, term]);
  const handleChange = (value) => {
    setTerm(value);
    if (!value && term) {
      awaitingResults.current = null;
      onClearResults && onClearResults();
    }
  };
  useEffect(() => {
    const finishedSearching = wasSearching.current && !isSearching;
    wasSearching.current = isSearching;
    if (!awaitingResults.current || isSearching || !Array.isArray(results)) return;
    if (results === awaitingResults.current.previous && !finishedSearching) return;
    awaitingResults.current = null;
    setIsResultsOpen(true);
  }, [results, isSearching]);
  const searchButton = /* @__PURE__ */ jsx(
    Button,
    {
      isPending: isSearching,
      isDisabled: isDisabled || !isSearching && !canSearch,
      fullWidth: isVertical,
      onPress: search,
      ...buttonProps,
      children: ({ isPending }) => /* @__PURE__ */ jsxs(Fragment, { children: [
        isPending && /* @__PURE__ */ jsx(Spinner, { size: "sm", color: "current" }),
        buttonLabel
      ] })
    }
  );
  const searchInput = (inputProps = {}) => /* @__PURE__ */ jsx(
    SearchPanelInput,
    {
      label,
      placeholder,
      value: term,
      onChange: handleChange,
      onSubmit: search,
      isSearching,
      isDisabled,
      ...inputProps
    }
  );
  const searchArea = showButton ? /* @__PURE__ */ jsxs("div", { className: isVertical ? "flex w-full flex-col gap-2" : "flex w-full items-end gap-2", children: [
    searchInput({ autoFocus, className: isVertical ? void 0 : "flex-1" }),
    searchButton
  ] }) : searchInput({ autoFocus, className: "w-full" });
  const selectResult = (result, index) => {
    setIsResultsOpen(false);
    onResultSelect(result, index);
  };
  const resultsButton = !isResultsOpen && resultCount > 0 && /* @__PURE__ */ jsxs(
    Button,
    {
      variant: "tertiary",
      size: "sm",
      className: "shrink-0",
      onPress: () => setIsResultsOpen(true),
      children: [
        "View ",
        formatResultCount(resultCount)
      ]
    }
  );
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: classes({
        "flex gap-2": true,
        // Inline keeps the button beside the input, aligned to the input's baseline so a
        // labelled field does not push it out of the row.
        "flex-row items-end": isResultsInline,
        "flex-col items-start": !isResultsInline,
        [className]: !!className
      }),
      children: [
        isResultsFirst && resultsButton,
        /* @__PURE__ */ jsx("div", { className: isResultsInline ? "min-w-0 flex-1" : "w-full", children: searchArea }),
        !isResultsFirst && resultsButton,
        /* @__PURE__ */ jsx(Modal.Backdrop, { isOpen: isResultsOpen, onOpenChange: setIsResultsOpen, children: /* @__PURE__ */ jsx(Modal.Container, { size: modalSize, scroll: "inside", children: /* @__PURE__ */ jsxs(Modal.Dialog, { children: [
          /* @__PURE__ */ jsx(Modal.CloseTrigger, {}),
          /* @__PURE__ */ jsxs(Modal.Header, { className: "flex flex-col gap-3", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
              /* @__PURE__ */ jsx(Modal.Heading, { children: modalTitle }),
              hasResults && /* @__PURE__ */ jsx("span", { className: "text-sm text-muted", children: formatResultCount(resultCount) })
            ] }),
            searchInput()
          ] }),
          /* @__PURE__ */ jsxs(Modal.Body, { className: "flex flex-col gap-4", children: [
            isSearching && /* @__PURE__ */ jsx("div", { className: "flex justify-center py-2", children: /* @__PURE__ */ jsx(Spinner, {}) }),
            !isSearching && !hasResults && /* @__PURE__ */ jsx("p", { className: "py-6 text-center text-muted", children: promptMessage }),
            !isSearching && hasResults && resultCount === 0 && /* @__PURE__ */ jsx("p", { className: "py-6 text-center text-muted", children: emptyMessage }),
            resultCount > 0 && /* @__PURE__ */ jsx(
              "ul",
              {
                "aria-busy": isSearching,
                className: classes({
                  "flex flex-col divide-y divide-default": true,
                  "opacity-50": isSearching
                }),
                children: results.map((result, index) => /* @__PURE__ */ jsx("li", { className: "py-2 first:pt-0 last:pb-0", children: onResultSelect ? /* @__PURE__ */ jsx(
                  "button",
                  {
                    type: "button",
                    className: "w-full cursor-pointer rounded-md px-2 py-1 text-left hover:bg-default focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
                    onClick: () => selectResult(result, index),
                    children: renderResult(result, index)
                  }
                ) : renderResult(result, index) }, getResultKey(result, index)))
              }
            )
          ] })
        ] }) }) })
      ]
    }
  );
};
const GSM7_BASIC = `@£$¥èéùìòÇ
Øø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà`;
const GSM7_EXTENDED = "^{}\\[~]|€\f";
const GSM7 = /* @__PURE__ */ new Set([...GSM7_BASIC, ...GSM7_EXTENDED]);
const DEFAULT_SMS_COST_PER_SEGMENT = 83e-4;
function calculateSmsSegments(text, costPerSegment = DEFAULT_SMS_COST_PER_SEGMENT) {
  const value = text ?? "";
  const chars = [...value];
  const isGsm7 = chars.every((ch) => GSM7.has(ch));
  const length = isGsm7 ? chars.reduce((total, ch) => total + (GSM7_EXTENDED.includes(ch) ? 2 : 1), 0) : value.length;
  const [singleMax, multiMax] = isGsm7 ? [160, 153] : [70, 67];
  const segments = length === 0 ? 0 : length <= singleMax ? 1 : Math.ceil(length / multiMax);
  return {
    encoding: isGsm7 ? "GSM-7" : "UCS-2",
    length,
    segments,
    cost: segments * costPerSegment,
    singleMax,
    multiMax
  };
}
function substitutePlaceholders(text, values = {}) {
  return String(text ?? "").replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (_match, rawKey) => {
    const value = values[rawKey.trim()];
    return value != null ? String(value) : "";
  });
}
const SmsSegments = ({
  text = "",
  placeholderValues,
  costPerSegment = DEFAULT_SMS_COST_PER_SEGMENT,
  showCost = true,
  className = ""
}) => {
  const resolved = placeholderValues ? substitutePlaceholders(text, placeholderValues) : text;
  const { encoding, length, segments, cost } = calculateSmsSegments(resolved, costPerSegment);
  return /* @__PURE__ */ jsxs("div", { className: `text-muted flex flex-wrap items-center gap-x-2 gap-y-1 text-xs ${className}`, children: [
    /* @__PURE__ */ jsxs("span", { children: [
      length,
      " ",
      length === 1 ? "character" : "characters"
    ] }),
    /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: "·" }),
    /* @__PURE__ */ jsx("span", { children: encoding }),
    /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: "·" }),
    /* @__PURE__ */ jsxs("span", { children: [
      segments,
      " ",
      segments === 1 ? "segment" : "segments"
    ] }),
    showCost && /* @__PURE__ */ jsxs(Chip, { size: "sm", variant: "soft", color: "accent", children: [
      "~$",
      cost.toFixed(4)
    ] })
  ] });
};
const StackedField = ({ label, children, span }) => {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "border-b border-r border-[var(--border-subtle)] px-4 py-2.5",
      style: span ? { gridColumn: `span ${span} / span ${span}` } : void 0,
      children: [
        /* @__PURE__ */ jsx("p", { className: "mb-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[var(--fg-3)]", children: label }),
        /* @__PURE__ */ jsx("div", { className: "text-[13px] text-[var(--foreground)] truncate", children })
      ]
    }
  );
};
const STAT_TONE = {
  good: "text-[var(--pill-success-fg)]",
  warn: "text-[var(--pill-warning-fg)]",
  danger: "text-[var(--pill-danger-fg)]"
};
const StatCard = ({ label, value, note, tone, className = "" }) => /* @__PURE__ */ jsxs(
  "div",
  {
    className: `min-w-0 flex-1 rounded-[10px] border border-[var(--border-default-c)] bg-[var(--surface)] px-5 py-4 ${className}`,
    children: [
      /* @__PURE__ */ jsx("p", { className: "mb-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--fg-3)]", children: label }),
      /* @__PURE__ */ jsx(
        "p",
        {
          className: `text-[26px] font-bold leading-none tracking-tight tabular-nums ${STAT_TONE[tone] ?? "text-[var(--fg-1)]"}`,
          children: value
        }
      ),
      note && /* @__PURE__ */ jsx("p", { className: "mt-2 text-xs leading-snug text-[var(--fg-3)]", children: note })
    ]
  }
);
const TextWithCopy = ({ text, value, className = "" }) => /* @__PURE__ */ jsxs(
  "div",
  {
    className: classes({
      "flex flex-row items-center gap-2": true,
      ...classObject(className)
    }),
    children: [
      /* @__PURE__ */ jsx("span", { children: text }),
      /* @__PURE__ */ jsx(CopyToClipboard, { text: value })
    ]
  }
);
const SPAN_CLASSES = {
  2: "sm:col-span-2 md:col-span-2",
  3: "sm:col-span-2 md:col-span-3",
  4: "sm:col-span-2 md:col-span-4"
};
const WidgetField = ({ label, children, isLoading = false, span, className = "" }) => /* @__PURE__ */ jsxs("div", { className: `flex flex-col gap-1 min-w-0 ${SPAN_CLASSES[span] ?? ""} ${className}`, children: [
  /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: label }),
  isLoading ? /* @__PURE__ */ jsx(Skeleton$1, { className: "h-5 w-24 rounded-md" }) : /* @__PURE__ */ jsx("span", { className: "text-foreground text-sm font-medium truncate", children: children || children === 0 ? children : "—" })
] });
const openItem$1 = "flex gap-2 text-sm text-gray-400 hover:text-accent p-0";
const openHeader = "text-xs font-bold uppercase text-gray-400 p-0";
const closedItem$1 = "justify-center w-full " + openItem$1;
const closedHeader = "flex justify-center " + openHeader;
function AccordionMenu({ items, isHeader }) {
  const openCheck = useSidebarExpanded();
  const [expandedKeys, setExpandedKeys] = useState(
    new Set(isHeader && openCheck ? items.map((path) => path.label) : [])
  );
  useEffect(() => {
    setExpandedKeys(new Set(isHeader && openCheck ? items.map((path) => path.label) : []));
  }, [openCheck, setExpandedKeys, items, isHeader]);
  return /* @__PURE__ */ jsx(
    Accordion,
    {
      className: "p-0 flex flex-col gap-3",
      expandedKeys,
      onExpandedChange: setExpandedKeys,
      allowsMultipleExpanded: openCheck,
      children: items.map((path) => /* @__PURE__ */ jsxs(Accordion.Item, { id: path.label, "aria-label": path.label, children: [
        /* @__PURE__ */ jsx(Accordion.Heading, { children: /* @__PURE__ */ jsxs(
          Accordion.Trigger,
          {
            className: "p-0 " + (openCheck ? path.isHeader ? openHeader : openItem$1 : path.isHeader ? closedHeader : closedItem$1),
            children: [
              path.icon && openCheck && /* @__PURE__ */ jsx("i", { className: path.icon }),
              openCheck ? path.label : /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
                /* @__PURE__ */ jsx(Tooltip.Trigger, { children: /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(ClosedIcon$1, { item: path, hasIcon: path.icon }) }) }),
                /* @__PURE__ */ jsx(Tooltip.Content, { placement: "right", children: path.label })
              ] }),
              !(isHeader || !openCheck) && /* @__PURE__ */ jsx(Accordion.Indicator, {})
            ]
          }
        ) }),
        /* @__PURE__ */ jsx(Accordion.Panel, { children: /* @__PURE__ */ jsx(
          Accordion.Body,
          {
            className: "mt-3 " + (openCheck ? "ps-3" : "border-y-2 rounded-lg border-black/25 dark:border-white/25"),
            children: /* @__PURE__ */ jsx(Menu$1, { paths: path.subPaths })
          }
        ) })
      ] }, path.label))
    }
  );
}
function Menu$1({ paths }) {
  const openCheck = useSidebarExpanded();
  const accordionItems = paths.filter((path) => path.subPaths);
  const links = paths.filter((path) => path.href && !path.subPaths);
  return /* @__PURE__ */ jsxs(ListBox, { "aria-label": "Navigation", className: "p-0 gap-3", children: [
    accordionItems.length > 0 && /* @__PURE__ */ jsx(ListBox.Item, { textValue: "sub-path menu", className: "cursor-default p-0", children: /* @__PURE__ */ jsx(AccordionMenu, { items: accordionItems }) }),
    links.length > 0 && links.map((path) => /* @__PURE__ */ jsx(
      ListBox.Item,
      {
        textValue: path.label,
        id: path.label,
        className: "cursor-default p-0",
        children: openCheck ? /* @__PURE__ */ jsxs(Link$1, { to: path.href, className: "cursor-pointer " + openItem$1, children: [
          path.icon && /* @__PURE__ */ jsx("i", { className: path.icon }),
          path.label
        ] }) : /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
          /* @__PURE__ */ jsx(Tooltip.Trigger, { children: /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(Link$1, { to: path.href, className: "cursor-pointer " + closedItem$1, children: /* @__PURE__ */ jsx(ClosedIcon$1, { item: path, hasIcon: path.icon }) }) }) }),
          /* @__PURE__ */ jsx(Tooltip.Content, { placement: "right", children: path.label })
        ] })
      },
      path.label
    ))
  ] });
}
function ClosedIcon$1({ item, hasIcon }) {
  return hasIcon ? /* @__PURE__ */ jsx("i", { className: item.icon + " text-2xl" }) : /* @__PURE__ */ jsx(Avatar, { className: "w-6 h-6 my-2", size: "sm", children: /* @__PURE__ */ jsx(Avatar.Fallback, { className: "font-bold uppercase", children: item.label.slice(0, 1) }) });
}
const AppSwitcherContext = createContext();
const AppSwitcher = ({ children }) => {
  const [open, setOpen] = useState(false);
  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);
  return /* @__PURE__ */ jsxs(Popover, { isOpen: open, onOpenChange: (x3) => setOpen(x3), children: [
    /* @__PURE__ */ jsx(Button, { isIconOnly: true, size: "sm", variant: "ghost", className: "rounded-full", children: /* @__PURE__ */ jsx("i", { className: "ri-apps-2-line text-lg text-gray-400" }) }),
    /* @__PURE__ */ jsx(Popover.Content, { placement: "bottom", children: /* @__PURE__ */ jsxs(Popover.Dialog, { className: "p-3", children: [
      /* @__PURE__ */ jsx(Popover.Arrow, {}),
      /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-2 max-w-[20rem]", children: /* @__PURE__ */ jsx(AppSwitcherContext.Provider, { value: { closeSwitcher: handleClose }, children }) })
    ] }) })
  ] });
};
const ApplicationCard = ({
  id,
  title,
  iconClass,
  canFavorite,
  isFavorite,
  onFavorite,
  onFavoriteText,
  onPress,
  onUnfavoriteText
}) => {
  useContext(AppSwitcherContext);
  return /* @__PURE__ */ jsxs("div", { className: "relative", children: [
    /* @__PURE__ */ jsx(
      Card,
      {
        variant: "secondary",
        className: "hover:outline-1 outline-accent w-full rounded-sm justify-center py-2",
        children: /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-3 items-center", children: [
          /* @__PURE__ */ jsx("i", { className: `${iconClass} text-2xl text-accent justify-self-start ms-2` }),
          /* @__PURE__ */ jsx("span", { className: "text-center", children: title })
        ] })
      }
    ),
    canFavorite && /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
      /* @__PURE__ */ jsx(
        Button,
        {
          className: "absolute right-1 top-1/2 -translate-y-1/2 justify-self-end scale-75 data-[pressed=true]:scale-[.7] z-10 rounded-full",
          onPress: onFavorite,
          size: "sm",
          isIconOnly: true,
          variant: "tertiary",
          children: /* @__PURE__ */ jsx("i", { className: `text-xl text-warning ri-bookmark-3-${isFavorite ? "fill" : "line"}` })
        },
        id
      ),
      /* @__PURE__ */ jsx(Tooltip.Content, { placement: "start", children: isFavorite ? onUnfavoriteText : onFavoriteText })
    ] })
  ] });
};
AppSwitcher.App = ApplicationCard;
function Breadcrumb({ items = [], className = "", children }) {
  return /* @__PURE__ */ jsxs("div", { className: `flex flex-row justify-between px-4 pt-3 ${className}`, children: [
    /* @__PURE__ */ jsx(Breadcrumbs, { className: "content-center", children: items.map((item, i) => /* @__PURE__ */ jsx(
      BreadcrumbsItem,
      {
        isDisabled: item.disabled ?? (!item.onPress && i < items.length - 1),
        onPress: item.onPress,
        children: item.label
      },
      `${item.label ?? i}`
    )) }),
    children
  ] });
}
Breadcrumb.displayName = "Breadcrumb";
const DateRangePicker = ({ value, onChange, sourceTZ, destinationTZ, label, ...rest }) => {
  const dt = convertISOtoCalendarDateTime(value, [null, null], sourceTZ, destinationTZ);
  const handleChange = useCallback(
    (dateRange) => {
      onChange([dateRange.start.toString(), dateRange.end.toString()]);
    },
    [onChange]
  );
  return /* @__PURE__ */ jsxs(
    DateRangePicker$1,
    {
      ...rest,
      value: {
        start: dt[0],
        end: dt[1]
      },
      onChange: (range) => {
        const converted = convertCalendarDateRange(
          [range.start, range.end],
          sourceTZ,
          destinationTZ
        );
        handleChange({
          start: converted[0],
          end: converted[1]
        });
      },
      children: [
        label && /* @__PURE__ */ jsx(Label, { children: label }),
        /* @__PURE__ */ jsxs(DateField.Group, { children: [
          /* @__PURE__ */ jsxs(DateField.InputContainer, { children: [
            /* @__PURE__ */ jsx(DateField.Input, { slot: "start", children: (segment) => /* @__PURE__ */ jsx(DateField.Segment, { segment }) }),
            /* @__PURE__ */ jsx(DateRangePicker$1.RangeSeparator, {}),
            /* @__PURE__ */ jsx(DateField.Input, { slot: "end", children: (segment) => /* @__PURE__ */ jsx(DateField.Segment, { segment }) })
          ] }),
          /* @__PURE__ */ jsx(DateField.Suffix, { children: /* @__PURE__ */ jsx(DateRangePicker$1.Trigger, { children: /* @__PURE__ */ jsx(DateRangePicker$1.TriggerIndicator, {}) }) })
        ] }),
        /* @__PURE__ */ jsx(DateRangePicker$1.Popover, { children: /* @__PURE__ */ jsxs(RangeCalendar, { "aria-label": "Choose date range", children: [
          /* @__PURE__ */ jsxs(RangeCalendar.Header, { children: [
            /* @__PURE__ */ jsx(RangeCalendar.Heading, {}),
            /* @__PURE__ */ jsx(RangeCalendar.NavButton, { slot: "previous" }),
            /* @__PURE__ */ jsx(RangeCalendar.NavButton, { slot: "next" })
          ] }),
          /* @__PURE__ */ jsxs(RangeCalendar.Grid, { children: [
            /* @__PURE__ */ jsx(RangeCalendar.GridHeader, { children: (day) => /* @__PURE__ */ jsx(RangeCalendar.HeaderCell, { children: day }) }),
            /* @__PURE__ */ jsx(RangeCalendar.GridBody, { children: (date) => /* @__PURE__ */ jsx(RangeCalendar.Cell, { date }) })
          ] })
        ] }) })
      ]
    }
  );
};
const DateRangePreset = (props) => {
  const { children, ...rest } = props;
  return /* @__PURE__ */ jsx(
    Radio,
    {
      ...rest,
      className: cn(
        "flex-none m-0 h-8 bg-surface hover:bg-overlay items-center justify-between",
        "cursor-pointer rounded-full border-2 border-default/60",
        "data-[selected=true]:border-accent"
      ),
      children: /* @__PURE__ */ jsx(Radio.Content, { className: "px-1 m-0", children: /* @__PURE__ */ jsx(Label, { className: "text-xs text-muted", children }) })
    }
  );
};
const DateRangePresetsContainer = forwardRef(
  ({ onChange, from = DateTime.local(), sourceTZ, destinationTZ = "utc" }, ref) => {
    const [selectedPreset, setSelectedPreset] = useState("custom");
    useImperativeHandle(ref, () => ({
      reset: () => setSelectedPreset("custom")
    }));
    const handleOnChange = useCallback(
      (x3) => {
        const rangeType = x3 === "custom" ? DateRangeTypes.Today : parseInt(x3);
        const dateRange = buildDateRangeForType({
          type: rangeType,
          from,
          sourceTZ,
          destinationTZ
        });
        onChange({
          start: parseDateTime(dateRange[0]),
          end: parseDateTime(dateRange[1])
        });
      },
      [onChange, from, sourceTZ, destinationTZ]
    );
    return /* @__PURE__ */ jsx("div", { className: "w-0 min-w-full p-3", children: /* @__PURE__ */ jsxs(
      RadioGroup,
      {
        value: selectedPreset,
        orientation: "horizontal",
        onChange: (x3) => {
          setSelectedPreset(x3);
          handleOnChange(x3);
        },
        className: "overflow-x-scroll flex-nowrap",
        children: [
          /* @__PURE__ */ jsx(DateRangePreset, { value: "custom", children: "Custom" }),
          Object.entries(DateRangeTypeLabels).map(([key, value]) => /* @__PURE__ */ jsx(DateRangePreset, { value: key, children: value }, key))
        ]
      }
    ) });
  }
);
const DateRangePickerWithPresets = ({
  value,
  onChange,
  sourceTZ,
  destinationTZ,
  ...rest
}) => {
  const presets = useRef(null);
  const handleChange = useCallback(
    (dateRange) => {
      presets.current.reset();
      onChange(dateRange);
    },
    [onChange]
  );
  return /* @__PURE__ */ jsx(
    DateRangePicker,
    {
      ...rest,
      value,
      sourceTZ,
      destinationTZ,
      onChange: handleChange,
      CalendarBottomContent: /* @__PURE__ */ jsx(
        DateRangePresetsContainer,
        {
          ref: presets,
          onChange: (range) => onChange([range.start.toString(), range.end.toString()]),
          sourceTZ,
          destinationTZ
        }
      )
    }
  );
};
function Footer({ children, className = "" }) {
  return /* @__PURE__ */ jsx(
    "footer",
    {
      className: classes({
        "h-16 shrink-0 text-gray-400 bg-default-100": true,
        ...classObject(className)
      }),
      children
    }
  );
}
Footer.displayName = "Footer";
function DefaultFooter({ children }) {
  return /* @__PURE__ */ jsx(Footer, { children });
}
DefaultFooter.displayName = Footer.displayName;
function Header({ children, className = "" }) {
  return /* @__PURE__ */ jsx(
    "header",
    {
      className: classes({
        "h-18 shrink-0 text-gray-400": true,
        ...classObject(className)
      }),
      children
    }
  );
}
Header.displayName = "Header";
const ThemeContext = createContext(null);
const themeAssets = (resolvedTheme) => resolvedTheme === "dark" ? { logo: "logo_primary.png", icon: "logo_sqaure.png" } : { logo: "logo_primary.png", icon: "logo_square.png" };
const ThemeProvider = ({ children }) => {
  const { resolvedTheme, theme, setTheme } = useTheme("system");
  const { logo, icon } = themeAssets(resolvedTheme);
  const context = useMemo(
    () => ({
      theme,
      resolvedTheme,
      logo,
      icon,
      setTheme
    }),
    [theme, resolvedTheme, logo, icon, setTheme]
  );
  return /* @__PURE__ */ jsx(ThemeContext.Provider, { value: context, children });
};
const useOptionalThemeProvider = () => useContext(ThemeContext);
const useThemeProvider = () => {
  const context = useOptionalThemeProvider();
  if (context === null) throw new Error("useThemeProvider requires ThemeProvider");
  return context;
};
const ThemeToggle = () => {
  const { theme, setTheme } = useThemeProvider();
  return /* @__PURE__ */ jsx(
    Button,
    {
      isIconOnly: true,
      size: "sm",
      variant: "ghost",
      className: "rounded-full mx-4",
      onPress: () => setTheme(theme === "light" ? "dark" : "light"),
      children: /* @__PURE__ */ jsx(
        "i",
        {
          className: classes({
            "ri-moon-line": theme === "light",
            "ri-sun-line": theme === "dark",
            "text-base text-gray-400": true
          })
        }
      )
    }
  );
};
function DefaultHeader({
  hideLang,
  hideTheme,
  hideNotifs,
  hideOptions,
  optionsBadge,
  notifsBadge,
  children,
  langs = []
}) {
  const { lang, setLang } = useDefaultLayoutContext();
  const [modalOpen, setModalOpen] = useState(false);
  return /* @__PURE__ */ jsxs(Header, { className: "flex items-center w-full bg-background border-b", children: [
    /* @__PURE__ */ jsx("div", { className: "grow h-full w-full", children }),
    /* @__PURE__ */ jsx(ThemeToggle, {}),
    /* @__PURE__ */ jsx(
      Modal.Backdrop,
      {
        variant: "blur",
        isOpen: modalOpen,
        onOpenChange: (open) => !open && setModalOpen(false),
        children: /* @__PURE__ */ jsx(Modal.Container, { size: "xs", placement: "center", children: /* @__PURE__ */ jsxs(Modal.Dialog, { children: [
          /* @__PURE__ */ jsx(Modal.Header, { children: /* @__PURE__ */ jsx(Modal.Heading, { children: "Select Language" }) }),
          /* @__PURE__ */ jsx(Modal.Body, { children: /* @__PURE__ */ jsxs(
            Select,
            {
              value: lang,
              onChange: (key) => setLang(key),
              className: "rounded-sm",
              "aria-label": "Language",
              children: [
                /* @__PURE__ */ jsxs(Select.Trigger, { children: [
                  /* @__PURE__ */ jsx(Select.Value, {}),
                  /* @__PURE__ */ jsx(Select.Indicator, {})
                ] }),
                /* @__PURE__ */ jsx(Select.Popover, { children: /* @__PURE__ */ jsx(ListBox, { children: langs.map((lang2) => /* @__PURE__ */ jsxs(ListBox.Item, { id: lang2.code, textValue: lang2.displayName, children: [
                  lang2.displayName,
                  /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
                ] }, lang2.code)) }) })
              ]
            }
          ) }),
          /* @__PURE__ */ jsx(Modal.Footer, {})
        ] }) })
      }
    )
  ] });
}
DefaultHeader.displayName = Header.displayName;
function ContentHeader({ children, className = "" }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: classes({
        "flex flex-col": true,
        ...classObject(className)
      }),
      children
    }
  );
}
ContentHeader.displayName = "ContentHeader";
function DefaultContentHeader({ children, className = "" }) {
  return /* @__PURE__ */ jsx(
    ContentHeader,
    {
      className: classes({
        "bg-background border-b p-1": true,
        ...classObject(className)
      }),
      children
    }
  );
}
DefaultContentHeader.displayName = ContentHeader.displayName;
function SidebarHeader({ children, className }) {
  return /* @__PURE__ */ jsx("div", { className, children });
}
SidebarHeader.displayName = "SidebarHeader";
function DefaultSidebarHeader({
  logo,
  closedLogo = null,
  className = "",
  logoClassName = ""
}) {
  const {
    isOpen: desktopIsOpen,
    setIsOpen,
    mobileIsOpen,
    setMobileIsOpen
  } = useDefaultLayoutContext();
  const md = useMediaQuery("(min-width: 768px)");
  const mode = useSidebarMode();
  const isOpen = useSidebarExpanded();
  function handleClick() {
    if (md) {
      setIsOpen(!desktopIsOpen);
    } else {
      setMobileIsOpen(!mobileIsOpen);
    }
  }
  return /* @__PURE__ */ jsxs(
    SidebarHeader,
    {
      className: classes({
        "h-18 shrink-0 flex items-center p-4 border-b relative": true,
        "justify-between": isOpen,
        "justify-center": !isOpen,
        ...classObject(className)
      }),
      children: [
        /* @__PURE__ */ jsx(
          "img",
          {
            src: !isOpen && closedLogo ? closedLogo : logo,
            className: classes({
              "max-h-[2rem]": true,
              ...classObject(logoClassName)
            })
          }
        ),
        isOpen && (md || mode === "drawer") && /* @__PURE__ */ jsx(Button, { isIconOnly: true, size: "sm", variant: "ghost", onPress: handleClick, children: /* @__PURE__ */ jsx("i", { className: "ri-arrow-left-s-line text-lg text-gray-400" }) }),
        !isOpen && /* @__PURE__ */ jsx(
          Button,
          {
            isIconOnly: true,
            size: "lg",
            className: "transition-opacity duration-300 z-10 absolute opacity-0 hover:opacity-100 origin-center top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
            variant: "secondary",
            onPress: handleClick,
            children: /* @__PURE__ */ jsx("i", { className: "ri-arrow-right-s-line text-lg text-gray-400" })
          }
        )
      ]
    }
  );
}
DefaultSidebarHeader.displayName = SidebarHeader.displayName;
function SidebarMain({ children, className = "", dir = "ltr" }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      dir,
      className: classes({
        "grow overflow-y-auto overflow-x-hidden": true,
        ...classObject(className)
      }),
      children: /* @__PURE__ */ jsx("div", { dir: "ltr", children })
    }
  );
}
SidebarMain.displayName = "SidebarMain";
function DefaultSidebarMain({ paths, leftBar, children }) {
  if (paths.filter((e) => e.isHeader).find((e) => e.subPaths === void 0))
    throw new Error("invalid paths shape");
  return /* @__PURE__ */ jsx(SidebarMain, { dir: leftBar ? "rtl" : "ltr", className: "p-4", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col w-full items-center", children: [
    /* @__PURE__ */ jsx("div", { className: "w-full", children }),
    /* @__PURE__ */ jsx(AccordionMenu, { items: paths, isHeader: true })
  ] }) });
}
DefaultSidebarMain.displayName = SidebarMain.displayName;
function SidebarFooter({ children, className }) {
  return /* @__PURE__ */ jsx("div", { className, children });
}
SidebarFooter.displayName = "SidebarFooter";
function Profile({ name = "[name]", email: email2, status, children }) {
  const isExpanded = useSidebarExpanded();
  const tileClass = "flex h-full items-center gap-x-2 w-auto " + (isExpanded ? "px-3" : "px-0");
  return children ? /* @__PURE__ */ jsxs(Dropdown, { children: [
    /* @__PURE__ */ jsx(Dropdown.Trigger, { children: /* @__PURE__ */ jsx("div", { className: tileClass + " cursor-pointer", children: /* @__PURE__ */ jsx(ProfileTile, { name, email: email2, status }) }) }),
    /* @__PURE__ */ jsx(Dropdown.Popover, { placement: "end", children: /* @__PURE__ */ jsx(Dropdown.Menu, { "aria-label": "Profile dropdown", children }) })
  ] }) : /* @__PURE__ */ jsx("div", { className: tileClass + " cursor-default", children: /* @__PURE__ */ jsx(ProfileTile, { name, email: email2, status }) });
}
const getInitials = (name) => {
  if (name.includes(" ")) {
    const names = name.split(" ");
    return names[0].slice(0, 1) + names[names.length - 1].slice(0, 1);
  } else return name;
};
function ProfileTile({ name, email: email2, status }) {
  const isOpen = useSidebarExpanded();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(Badge.Anchor, { children: [
      /* @__PURE__ */ jsx(Avatar, { className: "rounded-full", children: /* @__PURE__ */ jsx(Avatar.Fallback, { children: getInitials(name) }) }),
      /* @__PURE__ */ jsx(
        Badge,
        {
          color: status === "active" && "success" || status === "away" && "warning" || status === "offline" && "default" || status === "busy" && "danger" || status === "available" && "accent" || "default",
          placement: "bottom-right",
          className: "border-gray-150 dark:border-gray-850 rounded-full",
          size: "sm"
        }
      )
    ] }),
    isOpen && /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1 overflow-hidden text-start", children: [
      /* @__PURE__ */ jsx("p", { children: name }),
      /* @__PURE__ */ jsx("p", { className: "text-xs", children: email2 })
    ] })
  ] });
}
Profile.displayName = "Profile";
function DefaultSidebarFooter({ children }) {
  const ProfileContainer = reactUtils.getElementByType(children, Profile);
  const isExpanded = useSidebarExpanded();
  return /* @__PURE__ */ jsx(
    SidebarFooter,
    {
      className: "flex h-16 shrink-0 items-start " + (isExpanded ? "justify-start ms-2" : "justify-center"),
      children: ProfileContainer
    }
  );
}
DefaultSidebarFooter.displayName = SidebarFooter.displayName;
DefaultSidebarFooter.Profile = Profile;
function Drawer({ isOpen, onClosed, side, size, children, className = "" }) {
  useEffect(() => {
    if (onClosed) !isOpen && onClosed();
  }, [isOpen, onClosed]);
  useEffect(() => {
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        onClosed();
      }
    });
    return () => document.removeEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        onClosed();
      }
    });
  }, [onClosed]);
  return /* @__PURE__ */ jsx(Fragment, { children: createPortal(
    /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          className: classes({
            "fixed top-0 left-0 bottom-0 right-0 z-20 backdrop-blur-sm transition-opacity duration-[0.3s]": true,
            "opacity-100": isOpen,
            "pointer-events-none opacity-0": !isOpen
          }),
          onClick: onClosed
        }
      ),
      /* @__PURE__ */ jsx(
        "div",
        {
          className: classes({
            "bg-gray-100 dark:bg-gray-800 fixed z-[200] overflow-hidden flex flex-col transition-[color,translate] duration-[0.3s] ease-in-out": true,
            "translate-x-0": (side === "left" || side === "right") && isOpen,
            "translate-y-0": (side === "top" || side === "bottom") && isOpen,
            "right-0": side !== "left",
            "left-0": side !== "right",
            "bottom-0": side !== "top",
            "top-0": side !== "bottom",
            "-translate-x-full": side === "left" && !isOpen,
            "translate-x-full": side === "right" && !isOpen,
            "-translate-y-full": side === "top" && !isOpen,
            "translate-y-full": side === "bottom" && !isOpen,
            "w-60": (side === "left" || side === "right") && size === "sm",
            "h-60": (side === "top" || side === "bottom") && size === "sm",
            "w-96": (side === "left" || side === "right") && size === "md",
            "h-80": (side === "top" || side === "bottom") && size === "md",
            "w-1/2": (side === "left" || side === "right") && size === "lg",
            "h-1/2": (side === "top" || side === "bottom") && size === "lg",
            ...classObject(className)
          }),
          children
        }
      )
    ] }),
    document.body
  ) });
}
function Sidebar$1({
  children,
  className = "",
  isOpen = true,
  onClosed,
  mobileIsOpen = false
}) {
  const HeaderContainer = reactUtils.getElementByType(children, SidebarHeader);
  const MainContainer = reactUtils.getElementByType(children, SidebarMain);
  const FooterContainer = reactUtils.getElementByType(children, SidebarFooter);
  const md = useMediaQuery("(min-width: 768px)");
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      "nav",
      {
        className: classes({
          "flex flex-col w-16 h-full shrink-0 text-gray-400 ease-in-out overflow-hidden shell-sidebar": true,
          "md:w-70": isOpen,
          "md:w-20": !isOpen,
          ...classObject(className)
        }),
        children: /* @__PURE__ */ jsxs(SidebarModeContext.Provider, { value: "rail", children: [
          HeaderContainer,
          MainContainer,
          FooterContainer
        ] })
      }
    ),
    /* @__PURE__ */ jsx(
      Drawer,
      {
        isOpen: md ? false : mobileIsOpen,
        side: "left",
        size: "sm",
        onClosed,
        className: "text-gray-400",
        children: /* @__PURE__ */ jsxs(SidebarModeContext.Provider, { value: "drawer", children: [
          HeaderContainer,
          MainContainer,
          FooterContainer
        ] })
      }
    )
  ] });
}
Sidebar$1.displayName = "Sidebar";
Sidebar$1.Header = SidebarHeader;
Sidebar$1.Main = SidebarMain;
Sidebar$1.Footer = SidebarFooter;
function DefaultSidebar({ children }) {
  const { isOpen, mobileIsOpen, setMobileIsOpen } = useDefaultLayoutContext();
  return /* @__PURE__ */ jsx(Sidebar$1, { className: "bg-background border-e", isOpen, mobileIsOpen, onClosed: () => setMobileIsOpen(false), children });
}
DefaultSidebar.displayName = Sidebar$1.displayName;
DefaultSidebar.Header = DefaultSidebarHeader;
DefaultSidebar.Main = DefaultSidebarMain;
DefaultSidebar.Footer = DefaultSidebarFooter;
function Main$2({ children }) {
  return /* @__PURE__ */ jsx("main", { className: "bg-background-secondary grow overflow-y-auto overflow-x-hidden", children });
}
Main$2.displayName = "Main";
function Skeleton({ children }) {
  const SidebarContainer = reactUtils.getElementByType(children, Sidebar$1);
  const HeaderContainer = reactUtils.getElementByType(children, Header);
  const ContentHeaderContainer = reactUtils.getElementByType(children, ContentHeader);
  const MainContainer = reactUtils.getElementByType(children, Main$2);
  const FooterContainer = reactUtils.getElementByType(children, Footer);
  return /* @__PURE__ */ jsxs("div", { className: "flex h-full text-black dark:text-white text-sm", children: [
    SidebarContainer,
    /* @__PURE__ */ jsxs("div", { className: "flex grow flex-col h-full overflow-x-hidden", children: [
      HeaderContainer,
      ContentHeaderContainer,
      MainContainer,
      FooterContainer
    ] })
  ] });
}
Skeleton.Sidebar = Sidebar$1;
Skeleton.Header = Header;
Skeleton.ContentHeader = ContentHeader;
Skeleton.Main = Main$2;
Skeleton.Footer = Footer;
const DefaultLayout = withProvider(
  ({
    children,
    onOpenChanged = () => {
    },
    onThemeChanged = () => {
    },
    onLangChanged = () => {
    },
    defaultTheme,
    defaultLang
  }) => {
    const { isOpen, lang, theme, setTheme } = useDefaultLayoutContext(defaultTheme);
    useEffect(() => {
      onOpenChanged();
    }, [isOpen, onOpenChanged]);
    useEffect(() => {
      theme && setTheme(theme, onThemeChanged);
    }, [theme, onThemeChanged]);
    useEffect(() => {
      lang && lang !== defaultLang && onLangChanged(lang);
    }, [lang, defaultLang, onLangChanged]);
    return /* @__PURE__ */ jsx(Skeleton, { children });
  }
);
DefaultLayout.Sidebar = DefaultSidebar;
DefaultLayout.Header = DefaultHeader;
DefaultLayout.ContentHeader = DefaultContentHeader;
DefaultLayout.Footer = DefaultFooter;
function ToggleButtons({
  buttons,
  initialValue,
  onChange,
  className,
  groupClassName = "justify-start"
}) {
  const [selected, setSelected] = useState(initialValue || "");
  const toggleSelected = useCallback(
    (val) => {
      setSelected(val);
      onChange && onChange(val);
    },
    // eslint-disable-next-line
    [setSelected]
  );
  useEffect(() => {
    toggleSelected(initialValue);
  }, [initialValue, toggleSelected]);
  return /* @__PURE__ */ jsx(ButtonGroup, { className: groupClassName, children: buttons.map((button, i) => /* @__PURE__ */ jsxs(
    Button,
    {
      variant: selected === button.key ? toButtonVariant(button.color) : "secondary",
      onPress: () => toggleSelected(button.key),
      className,
      children: [
        i > 0 && /* @__PURE__ */ jsx(ButtonGroup.Separator, {}),
        button.icon && /* @__PURE__ */ jsx("i", { className: button.icon }),
        button.label,
        button.activeIcon && selected === button.key && /* @__PURE__ */ jsx("i", { className: button.activeIcon })
      ]
    },
    button.key
  )) }, selected);
}
const EnabledToggle = ({ enabled, onChange }) => {
  const selected = enabled ? "yes" : "no";
  return /* @__PURE__ */ jsx(
    ToggleButtons,
    {
      initialValue: selected,
      onChange: (val) => onChange(val === "yes" ? true : false),
      buttons: [
        {
          key: "yes",
          label: selected === "yes" ? "Enabled" : "Enable",
          icon: "text-lg ri-pulse-fill",
          color: selected === "yes" ? "success" : "danger",
          activeIcon: "text-lg ri-checkbox-circle-line"
        },
        {
          key: "no",
          label: selected === "no" ? "Disabled" : "Disable",
          icon: "text-lg ri-zzz-fill",
          color: selected === "yes" ? "success" : "danger",
          activeIcon: "text-lg ri-checkbox-circle-line"
        }
      ]
    }
  );
};
const ErrorBoundaryContext = createContext(null);
const initialState = {
  didCatch: false,
  error: null
};
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.resetErrorBoundary = this.resetErrorBoundary.bind(this);
    this.state = initialState;
  }
  static getDerivedStateFromError(error) {
    return {
      didCatch: true,
      error
    };
  }
  resetErrorBoundary() {
    const {
      error
    } = this.state;
    if (error !== null) {
      var _this$props$onReset, _this$props;
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      (_this$props$onReset = (_this$props = this.props).onReset) === null || _this$props$onReset === void 0 ? void 0 : _this$props$onReset.call(_this$props, {
        args,
        reason: "imperative-api"
      });
      this.setState(initialState);
    }
  }
  componentDidCatch(error, info) {
    var _this$props$onError, _this$props2;
    (_this$props$onError = (_this$props2 = this.props).onError) === null || _this$props$onError === void 0 ? void 0 : _this$props$onError.call(_this$props2, error, info);
  }
  componentDidUpdate(prevProps, prevState) {
    const {
      didCatch
    } = this.state;
    const {
      resetKeys
    } = this.props;
    if (didCatch && prevState.error !== null && hasArrayChanged(prevProps.resetKeys, resetKeys)) {
      var _this$props$onReset2, _this$props3;
      (_this$props$onReset2 = (_this$props3 = this.props).onReset) === null || _this$props$onReset2 === void 0 ? void 0 : _this$props$onReset2.call(_this$props3, {
        next: resetKeys,
        prev: prevProps.resetKeys,
        reason: "keys"
      });
      this.setState(initialState);
    }
  }
  render() {
    const {
      children,
      fallbackRender,
      FallbackComponent,
      fallback
    } = this.props;
    const {
      didCatch,
      error
    } = this.state;
    let childToRender = children;
    if (didCatch) {
      const props = {
        error,
        resetErrorBoundary: this.resetErrorBoundary
      };
      if (typeof fallbackRender === "function") {
        childToRender = fallbackRender(props);
      } else if (FallbackComponent) {
        childToRender = createElement(FallbackComponent, props);
      } else if (fallback !== void 0) {
        childToRender = fallback;
      } else {
        throw error;
      }
    }
    return createElement(ErrorBoundaryContext.Provider, {
      value: {
        didCatch,
        error,
        resetErrorBoundary: this.resetErrorBoundary
      }
    }, childToRender);
  }
}
function hasArrayChanged() {
  let a = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : [];
  let b2 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : [];
  return a.length !== b2.length || a.some((item, index) => !Object.is(item, b2[index]));
}
const stages = [
  { stage: "connecting", icon: "ri-wifi-line", label: "Connecting" },
  { stage: "verifying", icon: "ri-shield-check-line", label: "Verifying" },
  { stage: "authorizing", icon: "ri-lock-unlock-line", label: "Authorizing" },
  { stage: "complete", icon: "ri-checkbox-circle-line", label: "Complete" }
];
function Authorizing({
  isVisible = true,
  message = "Logging you in...",
  defaultTheme = "light"
}) {
  const [currentStage, setCurrentStage] = useState(0);
  const [fadeIn, setFadeIn] = useState(false);
  const themeContext = useOptionalThemeProvider();
  const theme = themeContext?.resolvedTheme ?? defaultTheme;
  const { logo } = themeAssets(theme);
  useEffect(() => {
    if (!isVisible) {
      setCurrentStage(0);
      setFadeIn(false);
      return;
    }
    setFadeIn(true);
    const interval = setInterval(() => {
      setCurrentStage((prev) => {
        if (prev < stages.length - 2) return prev + 1;
        clearInterval(interval);
        return prev;
      });
    }, 900);
    return () => clearInterval(interval);
  }, [isVisible]);
  if (!isVisible) return null;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn({
        "fixed inset-0 z-50 flex items-center justify-center bg-surface": true,
        "dark": theme === "dark"
      }),
      style: {
        opacity: fadeIn ? 1 : 0,
        transition: "opacity 0.3s ease"
      },
      children: [
        /* @__PURE__ */ jsx("div", { className: "absolute inset-0 bg-black/60 backdrop-blur-sm" }),
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: "relative z-10 flex flex-col items-center gap-6 rounded-2xl p-10 shadow-2xl",
            style: {
              background: "var(--color-surface)",
              minWidth: 320,
              transform: fadeIn ? "translateY(0) scale(1)" : "translateY(12px) scale(0.97)",
              transition: "transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)"
            },
            children: [
              /* @__PURE__ */ jsx("img", { src: `https://cdn.lifebeacon.com/img/${logo}`, alt: "Alert1", className: "w-32 mb-2" }),
              /* @__PURE__ */ jsx("div", { className: "relative flex items-center justify-center", children: /* @__PURE__ */ jsx(Spinner, { size: "lg", color: "accent" }) }),
              /* @__PURE__ */ jsx("p", { className: "text-sm text-muted", children: message ?? "Authenticating your session..." }),
              /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-2 w-full", children: stages.slice(0, -1).map(({ stage, icon, label }, i) => {
                const isPast = i < currentStage;
                const isCurrent = i === currentStage;
                return /* @__PURE__ */ jsxs(
                  "div",
                  {
                    className: "flex items-center gap-3 text-xs",
                    style: {
                      opacity: isPast || isCurrent ? 1 : 0.3,
                      transition: "opacity 0.4s ease"
                    },
                    children: [
                      /* @__PURE__ */ jsx(
                        "i",
                        {
                          className: `${icon} text-base`,
                          style: {
                            color: isPast ? "var(--color-success)" : isCurrent ? "var(--color-accent)" : "var(--color-muted)",
                            transition: "color 0.4s ease"
                          }
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        "span",
                        {
                          style: {
                            color: isPast ? "var(--color-success)" : isCurrent ? "var(--color-foreground)" : "var(--color-muted)",
                            transition: "color 0.4s ease"
                          },
                          children: label
                        }
                      ),
                      isPast && /* @__PURE__ */ jsx(
                        "i",
                        {
                          className: "ri-check-line ml-auto text-base",
                          style: { color: "var(--color-success)" }
                        }
                      ),
                      isCurrent && /* @__PURE__ */ jsx(
                        "i",
                        {
                          className: "ri-loader-4-line ml-auto text-base animate-spin",
                          style: { color: "var(--color-accent)" }
                        }
                      )
                    ]
                  },
                  stage
                );
              }) })
            ]
          }
        )
      ]
    }
  );
}
const Loading = (props) => {
  return /* @__PURE__ */ jsx("div", { className: "flex w-full h-full justify-center items-center", children: /* @__PURE__ */ jsx(Spinner, { ...props }) });
};
const Error$1 = ({ stack, showStackTrace = true }) => {
  return /* @__PURE__ */ jsx("div", { className: "flex w-full h-full flex-1 items-center justify-center", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center justify-center gap-2", children: [
    /* @__PURE__ */ jsx("i", { className: "ri-robot-2-line text-5xl" }),
    /* @__PURE__ */ jsx("p", { children: "Beep beep, boop boop. Something went wrong." }),
    /* @__PURE__ */ jsx(Button, { size: "sm", variant: "primary", onPress: () => window.location.reload(), children: "Reload" }),
    showStackTrace && /* @__PURE__ */ jsx(Accordion, { variant: "surface", className: "mt-4", children: /* @__PURE__ */ jsxs(Accordion.Item, { id: "details", "aria-label": "Error Details", children: [
      /* @__PURE__ */ jsx(Accordion.Heading, { children: /* @__PURE__ */ jsxs(Accordion.Trigger, { children: [
        "Details",
        /* @__PURE__ */ jsx(Accordion.Indicator, {})
      ] }) }),
      /* @__PURE__ */ jsx(Accordion.Panel, { children: /* @__PURE__ */ jsx(Accordion.Body, { children: /* @__PURE__ */ jsx("code", { className: "text-xs bg-surface-secondary px-1.5 py-0.5 rounded-sm", children: /* @__PURE__ */ jsx("pre", { children: stack }) }) }) })
    ] }) })
  ] }) });
};
const Forbidden = () => {
  return /* @__PURE__ */ jsx("div", { className: "flex w-full h-full flex-1 items-center justify-center", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center justify-center gap-2", children: [
    /* @__PURE__ */ jsx("i", { className: "ri-prohibited-fill text-5xl text-danger" }),
    /* @__PURE__ */ jsx("p", { className: "text-5xl", children: "403" }),
    /* @__PURE__ */ jsx("p", { children: "You are not authorized to access this resource." })
  ] }) });
};
const NotFound = ({ resourceType, onBack }) => {
  return /* @__PURE__ */ jsx("div", { className: "flex w-full h-full flex-1 items-center justify-center", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center justify-center gap-2", children: [
    /* @__PURE__ */ jsx("i", { className: "ri-robot-fill text-5xl" }),
    /* @__PURE__ */ jsx("p", { className: "text-5xl", children: "404" }),
    /* @__PURE__ */ jsx("p", { children: "These aren't the droids you're looking for." }),
    resourceType && /* @__PURE__ */ jsx("p", { children: `Missing, that ${resourceType} is.` }),
    onBack && /* @__PURE__ */ jsx(Button, { size: "sm", variant: "primary", onPress: onBack, children: "Go back" })
  ] }) });
};
const ErrorHandler = ({ children }) => {
  const [stack, setStack] = useState("");
  return /* @__PURE__ */ jsx(
    ErrorBoundary,
    {
      fallbackRender: ({ error }) => /* @__PURE__ */ jsx(Error$1, { error, stack }),
      onError: (_2, info) => setStack(info.componentStack),
      children
    }
  );
};
function Main$1({ children, className = "" }) {
  return /* @__PURE__ */ jsx("div", { className: cn("min-h-0 flex-1 overflow-y-auto", className), children });
}
Main$1.displayName = "FlatCard.Main";
function FlatCard({ children, variant = "transparent", className = "", ...props }) {
  return /* @__PURE__ */ jsx(
    Card,
    {
      variant,
      className: cn("flex h-full min-h-0 flex-col overflow-hidden", className),
      ...props,
      children
    }
  );
}
FlatCard.displayName = "FlatCard";
FlatCard.Header = Card.Header;
FlatCard.Title = Card.Title;
FlatCard.Description = Card.Description;
FlatCard.Main = Main$1;
FlatCard.Footer = Card.Footer;
const HelpIcon = ({ content, ...rest }) => {
  return /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
    /* @__PURE__ */ jsx(Tooltip.Trigger, { children: /* @__PURE__ */ jsx(RiInformationLine, { className: "inline-block", ...rest }) }),
    /* @__PURE__ */ jsx(Tooltip.Content, { children: content })
  ] });
};
function LoadingBar({
  isLoading = true,
  placement = "top",
  label = "Loading",
  className = ""
}) {
  if (!isLoading) return null;
  return /* @__PURE__ */ jsx(
    ProgressBar,
    {
      isIndeterminate: true,
      "aria-label": label,
      color: "accent",
      size: "sm",
      className: cn(
        "absolute inset-x-0 z-50 h-1 w-full gap-0",
        placement === "bottom" ? "bottom-0" : "top-0",
        className
      ),
      children: /* @__PURE__ */ jsx(ProgressBar.Track, { children: /* @__PURE__ */ jsx(ProgressBar.Fill, {}) })
    }
  );
}
LoadingBar.displayName = "LoadingBar";
const openItem = "flex gap-1 text-sm text-grey-400 dark:text-grey-100 p-1";
const closedItem = "justify-center w-full " + openItem;
const combineUrl = (baseUrl, route) => {
  baseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  route = route.startsWith("/") ? route.slice(1) : route;
  return `${baseUrl}/${route}`;
};
function Menu({ baseUrl, items = [], onlyVisibleOnBaseUrl = false, isVisible = true }) {
  const openCheck = useSidebarExpanded();
  isVisible = isVisible && (onlyVisibleOnBaseUrl ? window.location.pathname.startsWith(baseUrl) : true);
  if (!isVisible) return null;
  return /* @__PURE__ */ jsx("div", { role: "navigation", "aria-label": "Navigation", className: "flex flex-col ms-2 gap-1 border-s-2", children: items.map((item) => {
    if (!(item.isVisible ?? true)) return null;
    if (!item.items) {
      const to = combineUrl(baseUrl, item.route);
      const active = window.location.pathname === to;
      const linkClass = active ? " text-accent" : "";
      return /* @__PURE__ */ jsx("div", { className: "p-1", children: openCheck ? /* @__PURE__ */ jsxs(
        Link$1,
        {
          to,
          "aria-current": active ? "page" : void 0,
          className: "cursor-pointer " + openItem + linkClass,
          children: [
            item.icon && /* @__PURE__ */ jsx("i", { className: item.icon }),
            item.title
          ]
        }
      ) : /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
        /* @__PURE__ */ jsx(Tooltip.Trigger, { children: /* @__PURE__ */ jsx(
          Link$1,
          {
            to,
            "aria-current": active ? "page" : void 0,
            className: "cursor-pointer " + closedItem + linkClass,
            children: /* @__PURE__ */ jsx(ClosedIcon, { title: item.title, icon: item.icon })
          }
        ) }),
        /* @__PURE__ */ jsx(Tooltip.Content, { placement: "right", children: item.title })
      ] }) }, to);
    }
    return /* @__PURE__ */ jsx("div", { className: "p-1", children: /* @__PURE__ */ jsx(Accordion, { className: "p-0 flex flex-col gap-3", children: /* @__PURE__ */ jsxs(Accordion.Item, { id: `${item.title}-more`, "aria-label": item.title, children: [
      /* @__PURE__ */ jsx(Accordion.Heading, { children: /* @__PURE__ */ jsxs(Accordion.Trigger, { className: "p-0 " + (openCheck ? openItem : closedItem), children: [
        item.icon && openCheck && /* @__PURE__ */ jsx("i", { className: item.icon }),
        openCheck ? /* @__PURE__ */ jsx("span", { className: "font-normal", children: item.title }) : /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
          /* @__PURE__ */ jsx(Tooltip.Trigger, { children: /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(ClosedIcon, { title: item.title, icon: item.icon }) }) }),
          /* @__PURE__ */ jsx(Tooltip.Content, { placement: "right", children: item.title })
        ] }),
        openCheck && /* @__PURE__ */ jsx(Accordion.Indicator, {})
      ] }) }),
      /* @__PURE__ */ jsx(Accordion.Panel, { children: /* @__PURE__ */ jsx(
        Accordion.Body,
        {
          className: "mt-3 " + (openCheck ? "ps-3" : "border-y-2 rounded-lg border-black/25 dark:border-white/25"),
          children: /* @__PURE__ */ jsx(Menu, { baseUrl, items: item.items, isVisible: item.isVisible })
        }
      ) })
    ] }) }) }, item.title);
  }) });
}
function ClosedIcon({ title, icon }) {
  return icon ? /* @__PURE__ */ jsx("i", { className: icon + " text-2xl" }) : /* @__PURE__ */ jsx(Avatar, { className: "w-6 h-6 my-2", size: "sm", children: /* @__PURE__ */ jsx(Avatar.Fallback, { className: "font-bold uppercase", children: title.slice(0, 1) }) });
}
function MainNavigation({ items, defaultExpandedKeys }) {
  const openCheck = useSidebarExpanded();
  items = items.filter((app) => app.navigation().isVisible).filter((app) => app.navigation().items.filter((x3) => x3.isVisible).length > 0);
  return openCheck ? /* @__PURE__ */ jsx(MainNavigationAccordion, { items, defaultExpandedKeys }) : /* @__PURE__ */ jsx(MainNavigationListbox, { items });
}
function MainNavigationAccordion({ items, defaultExpandedKeys }) {
  return /* @__PURE__ */ jsx(Accordion, { className: "mt-2", hideSeparator: true, defaultExpandedKeys, children: items.map((item) => /* @__PURE__ */ jsxs(Accordion.Item, { id: item.id, children: [
    /* @__PURE__ */ jsx(Accordion.Heading, { className: "h-12", children: /* @__PURE__ */ jsxs(Accordion.Trigger, { className: "text-md gap-2", children: [
      /* @__PURE__ */ jsx("i", { className: item.description.iconClass }),
      /* @__PURE__ */ jsx("span", { children: item.description.title }),
      /* @__PURE__ */ jsx(Accordion.Indicator, {})
    ] }) }),
    /* @__PURE__ */ jsx(Accordion.Panel, { children: /* @__PURE__ */ jsx(Accordion.Body, { children: /* @__PURE__ */ jsx(
      Menu,
      {
        baseUrl: item.baseUrl,
        items: item.navigation().items,
        isVisible: item.navigation().isVisible
      },
      item.id
    ) }) })
  ] }, item.id)) });
}
function MainNavigationListbox({ items }) {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsx(ListBox, { onAction: (i) => navigate(items[i].baseUrl + items[i].description.defaultRoute), children: items.map((item, i) => /* @__PURE__ */ jsx(ListBoxItem, { id: i, className: "justify-center px-0", children: /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
    /* @__PURE__ */ jsx(Tooltip.Trigger, { className: "flex h-8 w-full items-center justify-center text-xl", children: /* @__PURE__ */ jsx(
      "i",
      {
        className: `${item.description.iconClass} ${window.location.pathname.includes(item.baseUrl) ? "text-accent" : ""}`
      }
    ) }),
    /* @__PURE__ */ jsx(Tooltip.Content, { placement: "end", children: /* @__PURE__ */ jsx("span", { children: item.description.title }) })
  ] }) }, item.id)) });
}
const PasswordInput = ({ label, description, errorMessage, placeholder, ...rest }) => {
  const [isVisible, setIsVisible] = useState(false);
  return /* @__PURE__ */ jsxs(TextField, { type: isVisible ? "text" : "password", ...rest, children: [
    label && /* @__PURE__ */ jsx(Label, { children: label }),
    /* @__PURE__ */ jsxs(InputGroup, { children: [
      /* @__PURE__ */ jsx(InputGroup.Input, { className: "min-w-0", variant: "secondary", placeholder }),
      /* @__PURE__ */ jsx(InputGroup.Suffix, { children: /* @__PURE__ */ jsx(
        "i",
        {
          className: cn({
            "focus:outline-hidden": true,
            "ri-eye-off-line": isVisible,
            "ri-eye-line": !isVisible,
            "cursor-pointer": true
          }),
          onClick: () => setIsVisible((prev) => !prev)
        }
      ) })
    ] }),
    description && /* @__PURE__ */ jsx(Description, { children: description }),
    /* @__PURE__ */ jsx(FieldError, { children: errorMessage })
  ] });
};
var __assign = function() {
  __assign = Object.assign || function(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
        t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __rest = function(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
    t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function")
    for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
      if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
        t[p[i]] = s[p[i]];
    }
  return t;
};
var valueHasCapitalCharacters = function(value) {
  var i = 0;
  if (value.length === 0) {
    return false;
  }
  while (i < value.length) {
    var character = value.charAt(i);
    if (character == character.toLowerCase()) ;
    else if (character == character.toUpperCase()) {
      return true;
    }
    i++;
  }
  return false;
};
var valueHasLowercaseCharacters = function(value) {
  var i = 0;
  if (value.length === 0) {
    return false;
  }
  while (i < value.length) {
    var character = value.charAt(i);
    if (character == character.toUpperCase()) ;
    else if (character == character.toLowerCase()) {
      return true;
    }
    i++;
  }
  return false;
};
var ReactPasswordChecklist = function(_a) {
  var className = _a.className, style = _a.style, rules = _a.rules, value = _a.value, valueAgain = _a.valueAgain, minLength = _a.minLength, maxLength = _a.maxLength, rtl = _a.rtl, onChange = _a.onChange, _b = _a.specialCharsRegex, specialCharsRegex = _b === void 0 ? /[~`¿¡!#$%\^&*€£@+÷=\-\[\]\\';,/{}\(\)|\\":<>\?\.\_]/g : _b, _c = _a.messages, messages = _c === void 0 ? {} : _c, remainingProps = __rest(_a, ["className", "style", "rules", "value", "valueAgain", "minLength", "maxLength", "rtl", "onChange", "specialCharsRegex", "messages"]);
  var ruleDefinitions = {
    minLength: {
      valid: value.length >= (minLength || 100),
      message: messages.minLength || "Password has at least ".concat(minLength, " characters.")
    },
    specialChar: {
      valid: specialCharsRegex.test(value),
      message: messages.specialChar || "Password has special characters."
    },
    number: {
      valid: /\d/g.test(value),
      message: messages.number || "Password has a number."
    },
    match: {
      valid: value.length > 0 && value === valueAgain,
      message: messages.match || "Passwords match."
    },
    capital: {
      valid: valueHasCapitalCharacters(value),
      message: messages.capital || "Password has a capital letter."
    },
    lowercase: {
      valid: valueHasLowercaseCharacters(value),
      message: messages.lowercase || "Password has a lowercase letter."
    },
    capitalAndLowercase: {
      valid: valueHasCapitalCharacters(value) && valueHasLowercaseCharacters(value),
      message: messages.capitalAndLowercase || "Password contains at least one uppercase and lowercase characters."
    },
    letter: {
      valid: /[a-zA-Z]/g.test(value),
      message: messages.letter || "Password has a letter."
    },
    maxLength: {
      valid: value.length <= (maxLength || 16),
      message: messages.maxLength || "Password has no more than ".concat(maxLength, " characters.")
    },
    notEmpty: {
      valid: Boolean(value.length > 0 && valueAgain && valueAgain.length > 0),
      message: messages.notEmpty || "Password fields are not empty."
    },
    noSpaces: {
      valid: Boolean(value.length > 0 && !/\s/.test(value)),
      message: messages.noSpaces || "Password contains no spaces."
    }
  };
  var enabledRules = useMemo(function() {
    return rules.filter(function(rule) {
      return Boolean(ruleDefinitions[rule]);
    });
  }, [rules]);
  var isValid = enabledRules.every(function(rule) {
    return ruleDefinitions[rule].valid;
  });
  var failedRules = useMemo(function() {
    return enabledRules.filter(function(rule) {
      return !ruleDefinitions[rule].valid;
    });
  }, [value, valueAgain, enabledRules]);
  useEffect(function() {
    if (typeof onChange === "function") {
      onChange(isValid, failedRules);
    }
  }, [isValid, failedRules]);
  if (rtl) {
    className = className ? className + " rtl" : "rtl";
  }
  return u.createElement("ul", { className, style: __assign({ margin: 0, padding: 0 }, style) }, enabledRules.map(function(rule) {
    var _a2 = ruleDefinitions[rule], message = _a2.message, valid = _a2.valid;
    return u.createElement(Rule, __assign({ key: rule, valid, iconSize: 18, validColor: "#4BCA81", invalidColor: "#FF0033", rtl }, remainingProps), message);
  }));
};
var Rule = function(_a) {
  var valid = _a.valid, iconSize = _a.iconSize, validColor = _a.validColor, invalidColor = _a.invalidColor, validTextColor = _a.validTextColor, invalidTextColor = _a.invalidTextColor, iconComponents = _a.iconComponents, hideIcon = _a.hideIcon, rtl = _a.rtl, children = _a.children, itemClassName = _a.itemClassName;
  return u.createElement(
    "li",
    { className: "".concat(itemClassName, " ").concat(valid ? "valid" : "invalid"), style: {
      listStyleType: "none",
      display: "flex",
      alignItems: "center",
      margin: "2px 0"
    } },
    !hideIcon ? iconComponents ? valid ? iconComponents.ValidIcon : iconComponents.InvalidIcon : u.createElement(
      "svg",
      { className: "checklist-icon", version: "1.1", xmlns: "http://www.w3.org/2000/svg", width: iconSize, height: iconSize, viewBox: "0 0 512 512", style: { marginRight: rtl ? 0 : 5, marginLeft: rtl ? 5 : 0 } },
      u.createElement("path", { fill: valid ? validColor : invalidColor, d: valid ? "M432 64l-240 240-112-112-80 80 192 192 320-320z" : "M507.331 411.33c-0.002-0.002-0.004-0.004-0.006-0.005l-155.322-155.325 155.322-155.325c0.002-0.002 0.004-0.003 0.006-0.005 1.672-1.673 2.881-3.627 3.656-5.708 2.123-5.688 0.912-12.341-3.662-16.915l-73.373-73.373c-4.574-4.573-11.225-5.783-16.914-3.66-2.080 0.775-4.035 1.984-5.709 3.655 0 0.002-0.002 0.003-0.004 0.005l-155.324 155.326-155.324-155.325c-0.002-0.002-0.003-0.003-0.005-0.005-1.673-1.671-3.627-2.88-5.707-3.655-5.69-2.124-12.341-0.913-16.915 3.66l-73.374 73.374c-4.574 4.574-5.784 11.226-3.661 16.914 0.776 2.080 1.985 4.036 3.656 5.708 0.002 0.001 0.003 0.003 0.005 0.005l155.325 155.324-155.325 155.326c-0.001 0.002-0.003 0.003-0.004 0.005-1.671 1.673-2.88 3.627-3.657 5.707-2.124 5.688-0.913 12.341 3.661 16.915l73.374 73.373c4.575 4.574 11.226 5.784 16.915 3.661 2.080-0.776 4.035-1.985 5.708-3.656 0.001-0.002 0.003-0.003 0.005-0.005l155.324-155.325 155.324 155.325c0.002 0.001 0.004 0.003 0.006 0.004 1.674 1.672 3.627 2.881 5.707 3.657 5.689 2.123 12.342 0.913 16.914-3.661l73.373-73.374c4.574-4.574 5.785-11.227 3.662-16.915-0.776-2.080-1.985-4.034-3.657-5.707z" })
    ) : null,
    u.createElement("span", { style: {
      paddingTop: 2,
      opacity: valid ? 1 : !invalidTextColor ? 0.5 : void 0,
      flex: 1,
      color: valid ? validTextColor : invalidTextColor
    } }, children)
  );
};
const reducer$1 = (state, action) => {
  switch (action.type) {
    case "message":
      return { ...state, message: action.payload };
    case "password1":
      return { ...state, password1: action.payload };
    case "password2":
      return { ...state, password2: action.payload };
    case "valid":
      return { ...state, valid: action.payload };
    default:
      return state;
  }
};
const PasswordConfirmInput = ({ onChange, ...rest }) => {
  const [form, dispatch] = useReducer(reducer$1, {
    password1: "",
    password2: "",
    valid: false
  });
  useEffect(() => {
    if (form.valid && onChange) {
      onChange(form.password2);
    }
  }, [form.valid, form.password2]);
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
    /* @__PURE__ */ jsx(
      PasswordInput,
      {
        id: "password1",
        name: "password1",
        label: "Password",
        value: form.password1,
        onChange: (x3) => dispatch({ type: "password1", payload: x3 }),
        variant: "secondary",
        ...rest
      }
    ),
    /* @__PURE__ */ jsx(
      PasswordInput,
      {
        id: "password2",
        name: "password2",
        label: "Re-enter password",
        value: form.password2,
        onChange: (x3) => dispatch({ type: "password2", payload: x3 }),
        variant: "secondary",
        ...rest
      }
    ),
    form.password2 && /* @__PURE__ */ jsx(
      ReactPasswordChecklist,
      {
        rules: ["minLength", "specialChar", "number", "capital", "lowercase", "match"],
        minLength: 8,
        value: form.password1,
        valueAgain: form.password2,
        onChange: (isValid) => {
          if (form.valid !== isValid) {
            dispatch({ type: "valid", payload: isValid });
          }
        }
      }
    )
  ] });
};
const RoutingErrorHandler = ({ useRouteError, onBack, showStackTrace = true }) => {
  const error = useRouteError();
  if (isRouteErrorResponse(error) && error.status === 404) {
    return /* @__PURE__ */ jsx(NotFound, { onBack });
  }
  return /* @__PURE__ */ jsx(Error$1, { error, stack: error?.stack ?? "", showStackTrace });
};
function Sidebar({ children, className = "" }) {
  return /* @__PURE__ */ jsx("div", { className: cn("h-full min-w-0", className), children });
}
Sidebar.displayName = "SplitCard.Sidebar";
function Main({ children, className = "" }) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex h-full min-w-0 flex-col", className), children });
}
Main.displayName = "SplitCard.Main";
function Body({
  children,
  resizable = true,
  defaultSidebarWidth = "16rem",
  minSidebarWidth = "12rem",
  maxSidebarWidth = "24rem",
  autoSaveId,
  className = ""
}) {
  const sidebar = reactUtils.getElementByType(children, Sidebar);
  const main = reactUtils.getElementByType(children, Main);
  const content = resizable ? /* @__PURE__ */ jsxs(Resizable, { className: "h-full", orientation: "horizontal", autoSaveId, children: [
    /* @__PURE__ */ jsx(
      Resizable.Panel,
      {
        defaultSize: defaultSidebarWidth,
        minSize: minSidebarWidth,
        maxSize: maxSidebarWidth,
        groupResizeBehavior: "preserve-pixel-size",
        children: sidebar
      }
    ),
    /* @__PURE__ */ jsx(Resizable.Handle, { withIndicator: true }),
    /* @__PURE__ */ jsx(Resizable.Panel, { minSize: 30, children: main })
  ] }) : /* @__PURE__ */ jsxs("div", { className: "flex h-full", children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "border-default/60 h-full shrink-0 overflow-y-auto border-r",
        style: { width: defaultSidebarWidth },
        children: sidebar
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "h-full min-w-0 flex-1 overflow-y-auto", children: main })
  ] });
  return /* @__PURE__ */ jsx("div", { className: cn("min-h-0 flex-1", className), children: content });
}
Body.displayName = "SplitCard.Body";
function SplitCard({ children, variant, className = "", ...props }) {
  return /* @__PURE__ */ jsx(
    Card,
    {
      variant,
      className: cn("flex h-full min-h-0 flex-col overflow-hidden", className),
      ...props,
      children
    }
  );
}
SplitCard.displayName = "SplitCard";
SplitCard.Header = Card.Header;
SplitCard.Title = Card.Title;
SplitCard.Description = Card.Description;
SplitCard.Body = Body;
SplitCard.Sidebar = Sidebar;
SplitCard.Main = Main;
SplitCard.Footer = Card.Footer;
const TextFieldWithCopy = ({ label, placeholder, ...rest }) => {
  const inputRef = useRef();
  const defaultFeedback = {
    isOpen: false,
    message: "Copied to clipboard!",
    delay: 2e3
  };
  const [feedback, setFeedback] = useState(defaultFeedback);
  const copy = async () => {
    try {
      if (inputRef.current.value) {
        await navigator.clipboard.writeText(inputRef.current.value);
        setFeedback((x3) => ({ ...x3, isOpen: true }));
      }
    } catch (err) {
      setFeedback((x3) => ({ ...x3, isOpen: true, message: "Not copied!" }));
    }
  };
  useEffect(() => {
    if (inputRef.current) {
      const input = inputRef.current;
      const handler = () => input.select();
      input.addEventListener("focus", handler);
      return () => input.removeEventListener("focus", handler);
    }
  }, [inputRef]);
  return /* @__PURE__ */ jsxs(TextField, { ...rest, children: [
    label && /* @__PURE__ */ jsx(Label, { children: label }),
    /* @__PURE__ */ jsxs(InputGroup, { children: [
      /* @__PURE__ */ jsx(
        InputGroup.Input,
        {
          ref: inputRef,
          className: "min-w-0",
          variant: "secondary",
          placeholder
        }
      ),
      /* @__PURE__ */ jsx(InputGroup.Suffix, { children: /* @__PURE__ */ jsxs(
        Tooltip,
        {
          isOpen: feedback.isOpen,
          onOpenChange: (open) => !open && setFeedback(defaultFeedback),
          closeDelay: feedback.delay,
          children: [
            /* @__PURE__ */ jsx(Tooltip.Trigger, { children: /* @__PURE__ */ jsx(Link, { className: "cursor-pointer", onPress: copy, children: /* @__PURE__ */ jsx("i", { className: "ri-file-copy-line" }) }) }),
            /* @__PURE__ */ jsx(Tooltip.Content, { children: feedback.message })
          ]
        }
      ) })
    ] })
  ] });
};
const reducer = (state, action) => {
  switch (action.type) {
    case "weeks":
      return { ...state, weeks: action.payload };
    case "days":
      return { ...state, days: action.payload };
    case "hours":
      return { ...state, hours: action.payload };
    case "minutes":
      return { ...state, minutes: action.payload };
    case "seconds":
      return { ...state, seconds: action.payload };
    case "milliseconds":
      return { ...state, milliseconds: action.payload };
    default:
      return state;
  }
};
const useTimeCalculator = (defaultValue, units = "s") => {
  const [state, dispatch] = useReducer(reducer, convertToUnits(defaultValue, units));
  const total = convert(state.weeks, "w", units) + convert(state.days, "d", units) + convert(state.hours, "h", units) + convert(state.minutes, "m", units) + convert(state.seconds, "s", units) + convert(state.milliseconds, "ms", units);
  return {
    parts: convertToUnits(total, units),
    total,
    priority: TimeUnitPriority[units],
    setWeeks: (val) => dispatch({ type: "weeks", payload: val }),
    setDays: (val) => dispatch({ type: "days", payload: val }),
    setHours: (val) => dispatch({ type: "hours", payload: val }),
    setMinutes: (val) => dispatch({ type: "minutes", payload: val }),
    setSeconds: (val) => dispatch({ type: "seconds", payload: val }),
    setMilliseconds: (val) => dispatch({ types: "milliseconds", payload: val })
  };
};
const TimePartField = ({ name, label, value, onChange, isHidden }) => /* @__PURE__ */ jsxs(
  TextField,
  {
    name,
    type: "number",
    value,
    onChange,
    className: cn({ hidden: isHidden }),
    children: [
      /* @__PURE__ */ jsx(Label, { children: label }),
      /* @__PURE__ */ jsx(Input, { variant: "secondary" })
    ]
  }
);
const TimeCalculator = ({ defaultValue = 0, units = "s", onChange }) => {
  const time2 = useTimeCalculator(defaultValue, units);
  useEffect(() => {
    onChange && onChange(time2.total);
  }, [time2.total, onChange]);
  return /* @__PURE__ */ jsxs(Popover, { children: [
    /* @__PURE__ */ jsx(Button, { isIconOnly: true, size: "sm", variant: "ghost", children: /* @__PURE__ */ jsx("i", { className: "text-lg ri-calculator-line" }) }),
    /* @__PURE__ */ jsx(Popover.Content, { placement: "top", children: /* @__PURE__ */ jsxs(Popover.Dialog, { className: "flex flex-col gap-2 *:max-w-24 py-2", children: [
      /* @__PURE__ */ jsx(
        TimePartField,
        {
          name: "weeks",
          label: "Weeks",
          value: time2.parts.weeks,
          onChange: time2.setWeeks,
          isHidden: time2.priority < TimeUnitPriority.week
        }
      ),
      /* @__PURE__ */ jsx(
        TimePartField,
        {
          name: "days",
          label: "Days",
          value: time2.parts.days,
          onChange: time2.setDays,
          isHidden: time2.priority < TimeUnitPriority.day
        }
      ),
      /* @__PURE__ */ jsx(
        TimePartField,
        {
          name: "hours",
          label: "Hours",
          value: time2.parts.hours,
          onChange: time2.setHours,
          isHidden: time2.priority < TimeUnitPriority.hour
        }
      ),
      /* @__PURE__ */ jsx(
        TimePartField,
        {
          name: "minutes",
          label: "Minutes",
          value: time2.parts.minutes,
          onChange: time2.setMinutes,
          isHidden: time2.priority < TimeUnitPriority.minute
        }
      ),
      /* @__PURE__ */ jsx(
        TimePartField,
        {
          name: "seconds",
          label: "Seconds",
          value: time2.parts.seconds,
          onChange: time2.setSeconds,
          isHidden: time2.priority < TimeUnitPriority.second
        }
      ),
      /* @__PURE__ */ jsx(
        TimePartField,
        {
          name: "milliseconds",
          label: "Milliseconds",
          value: time2.parts.milliseconds,
          onChange: time2.setMilliseconds,
          isHidden: time2.priority < TimeUnitPriority.millisecond
        }
      )
    ] }) })
  ] });
};
const TimeCalculatorInput = ({ value, onChange, units, label, ...rest }) => {
  const [_value, setValue] = useState(value);
  useEffect(() => {
    onChange && onChange(_value);
  }, [_value]);
  return /* @__PURE__ */ jsxs(TextField, { ...rest, value: _value, children: [
    label && /* @__PURE__ */ jsx(Label, { children: label }),
    /* @__PURE__ */ jsxs(InputGroup, { children: [
      /* @__PURE__ */ jsx(InputGroup.Input, { className: "min-w-0" }),
      /* @__PURE__ */ jsx(InputGroup.Suffix, { className: "pr-0", children: /* @__PURE__ */ jsx(TimeCalculator, { defaultValue: value, units, onChange: setValue }) })
    ] })
  ] });
};
const Toggle = ({ isLoading, isDisabled, children, ...rest }) => {
  const thumbIconClass = (isSelected) => isLoading ? "ri-refresh-line animate-spin" : isSelected ? "ri-check-line" : "ri-circle-line";
  return /* @__PURE__ */ jsx(Switch, { isDisabled: isDisabled || isLoading, ...rest, children: ({ isSelected }) => /* @__PURE__ */ jsxs(Switch.Content, { children: [
    /* @__PURE__ */ jsx(Switch.Control, { children: /* @__PURE__ */ jsx(Switch.Thumb, { children: /* @__PURE__ */ jsx(Switch.Icon, { children: /* @__PURE__ */ jsx("i", { className: thumbIconClass(isSelected) }) }) }) }),
    children
  ] }) });
};
const SIZE_CLASSES$1 = {
  "xs": "min-w-xs",
  "sm": "min-w-sm",
  "md": "min-w-md",
  "lg": "min-w-lg",
  "xl": "min-w-xl",
  "2xl": "min-w-2xl",
  "3xl": "min-w-3xl",
  "4xl": "min-w-4xl",
  "5xl": "min-w-5xl",
  "full": "min-w-full"
};
const DrawerFooterContext = createContext(null);
const withDrawerController = (Component2) => {
  const Wrapped = ({
    isOpen = true,
    size = "5xl",
    placement = "right",
    isDismissable = true,
    isKeyboardDismissDisabled,
    hideCloseButton,
    headerContent = void 0,
    footerContent = void 0,
    onClose,
    classNames = {},
    ...props
  }) => {
    const finalHideCloseButton = hideCloseButton ?? !isDismissable;
    const finalKeyboardDismiss = isKeyboardDismissDisabled ?? !isDismissable;
    const close = () => onClose();
    const [slotUsers, setSlotUsers] = useState(0);
    const [slotElement, setSlotElement] = useState(null);
    const register = useCallback(() => {
      setSlotUsers((n) => n + 1);
      return () => setSlotUsers((n) => n - 1);
    }, []);
    const showFooter = Boolean(footerContent) || slotUsers > 0;
    return /* @__PURE__ */ jsx(
      Drawer$1.Backdrop,
      {
        isOpen,
        isDismissable,
        isKeyboardDismissDisabled: finalKeyboardDismiss,
        className: classNames.backdrop,
        onOpenChange: (isOpen2) => !isOpen2 && onClose(),
        children: /* @__PURE__ */ jsx(Drawer$1.Content, { placement, children: /* @__PURE__ */ jsxs(
          Drawer$1.Dialog,
          {
            className: ["rounded-none", SIZE_CLASSES$1[size], classNames.base].filter(Boolean).join(" "),
            children: [
              !finalHideCloseButton && /* @__PURE__ */ jsx(Drawer$1.CloseTrigger, {}),
              headerContent && /* @__PURE__ */ jsx(Drawer$1.Header, { className: classNames.header, children: /* @__PURE__ */ jsx(Drawer$1.Heading, { children: headerContent(close) }) }),
              /* @__PURE__ */ jsx(Drawer$1.Body, { className: classNames.body, children: /* @__PURE__ */ jsx(DrawerFooterContext.Provider, { value: { register, element: slotElement }, children: /* @__PURE__ */ jsx(Component2, { onClose, ...props }) }) }),
              showFooter && /* @__PURE__ */ jsxs(Drawer$1.Footer, { className: classNames.footer, children: [
                footerContent?.(close),
                /* @__PURE__ */ jsx("div", { ref: setSlotElement, className: "contents" })
              ] })
            ]
          }
        ) })
      }
    );
  };
  return Wrapped;
};
const useDrawerFooter = () => useContext(DrawerFooterContext);
const DrawerActions = ({ children, className = "" }) => {
  const slot = useDrawerFooter();
  const register = slot?.register;
  useEffect(() => register?.(), [register]);
  const row = /* @__PURE__ */ jsx("div", { className: `flex w-full items-center justify-end gap-2 ${className}`, children });
  if (!slot) return row;
  return slot.element ? createPortal(row, slot.element) : null;
};
const V3_MODAL_SIZES = ["xs", "sm", "md", "lg", "cover", "full"];
const SIZE_CLASSES = {
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl"
};
const withModalController = (Wrapped) => {
  const Wrapper = ({
    isOpen,
    item,
    title,
    onClose,
    onSuccess,
    isDismissable = true,
    hideCloseButton,
    isKeyboardDismissDisabled,
    size,
    scrollBehavior,
    ...props
  }) => {
    const finalHideCloseButton = hideCloseButton ?? !isDismissable;
    const finalKeyboardDismiss = isKeyboardDismissDisabled ?? !isDismissable;
    return /* @__PURE__ */ jsx(
      Modal.Backdrop,
      {
        isOpen,
        onOpenChange: (open) => onClose(open),
        isDismissable,
        isKeyboardDismissDisabled: finalKeyboardDismiss,
        children: /* @__PURE__ */ jsx(
          Modal.Container,
          {
            size: V3_MODAL_SIZES.includes(size) ? size : void 0,
            scroll: scrollBehavior === "normal" ? "inside" : scrollBehavior,
            children: /* @__PURE__ */ jsxs(Modal.Dialog, { className: SIZE_CLASSES[size], children: [
              !finalHideCloseButton && /* @__PURE__ */ jsx(Modal.CloseTrigger, {}),
              /* @__PURE__ */ jsx(Modal.Header, { className: "flex flex-col gap-1", children: /* @__PURE__ */ jsx(Modal.Heading, { children: title }) }),
              /* @__PURE__ */ jsx(Modal.Body, { children: /* @__PURE__ */ jsx(Wrapped, { ...props, item, onSuccess, onClose }) })
            ] })
          }
        )
      }
    );
  };
  return Wrapper;
};
const useModalController = (Component2, { title, onSuccess, onClose, isOpen, selected, scrollBehavior }) => {
  const [_isOpen, setIsOpen] = useState(isOpen);
  const [_selected, setSelected] = useState(selected);
  const onOpen = (item) => {
    setIsOpen(true);
    setSelected(item);
  };
  const handleOnClose = () => {
    setIsOpen(false);
    setSelected(null);
    onClose && onClose();
  };
  const handleOnSuccess = (params) => {
    setIsOpen(false);
    setSelected(null);
    onSuccess && onSuccess(params);
  };
  return [
    { open: (data) => onOpen(data) },
    (props) => /* @__PURE__ */ jsx(
      Component2,
      {
        ...props,
        title,
        isOpen: _isOpen,
        item: _selected,
        onClose: handleOnClose,
        onSuccess: handleOnSuccess,
        scrollBehavior
      }
    )
  ];
};
const useDrawerController = (Component2, { headerContent, footerContent, onSuccess, onClose, isOpen, selected, size } = {}) => {
  const [_isOpen, setIsOpen] = useState(isOpen);
  const [_selected, setSelected] = useState(selected);
  const onOpen = (item) => {
    setIsOpen(true);
    setSelected(item);
  };
  const handleOnClose = () => {
    setIsOpen(false);
    onClose && onClose();
  };
  const handleOnSuccess = (params) => {
    setIsOpen(false);
    onSuccess && onSuccess(params);
  };
  const latest = useRef(null);
  latest.current = {
    headerContent,
    footerContent,
    isOpen: _isOpen,
    item: _selected,
    size,
    onClose: handleOnClose,
    onSuccess: handleOnSuccess
  };
  const Wrapped = useMemo(
    () => function DrawerControllerWrapped(props) {
      return /* @__PURE__ */ jsx(Component2, { ...props, ...latest.current });
    },
    [Component2]
  );
  return [{ open: (data) => onOpen(data), close: handleOnClose }, Wrapped];
};
const buttonProperties = {
  confirm: {
    confirm: {
      label: "Confirm",
      color: "primary",
      variant: "solid"
    },
    cancel: {
      label: "Cancel",
      color: "default",
      variant: "light"
    }
  },
  delete: {
    confirm: {
      label: "Delete",
      color: "primary",
      variant: "solid"
    },
    cancel: {
      label: "Cancel",
      color: "default",
      variant: "light"
    }
  }
};
const renderMessage = (value, item) => {
  if (typeof value === "function") {
    return value(item ?? {});
  } else if (typeof value === "string") {
    return value;
  } else {
    return "";
  }
};
const useDialog = ({
  type = "confirm",
  title = "Confirmation",
  message,
  body,
  buttons,
  onSuccess,
  onClose
}) => {
  const _buttons = { ...buttonProperties[type], ...buttons };
  const [modal, ModalController] = useModalController(
    ({ onSuccess: onSuccess2, onClose: onClose2, isOpen, item = {} }) => {
      return /* @__PURE__ */ jsx(Modal.Backdrop, { isOpen, onOpenChange: (open) => !open && onClose2(), children: /* @__PURE__ */ jsx(Modal.Container, { children: /* @__PURE__ */ jsxs(Modal.Dialog, { children: [
        /* @__PURE__ */ jsx(Modal.Header, { className: "flex flex-col gap-1", children: /* @__PURE__ */ jsx(Modal.Heading, { children: title }) }),
        /* @__PURE__ */ jsx(Modal.Body, { children: /* @__PURE__ */ jsxs("div", { className: "mb-4", children: [
          renderMessage(message, item),
          body && body(item)
        ] }) }),
        /* @__PURE__ */ jsxs(Modal.Footer, { children: [
          /* @__PURE__ */ jsx(
            Button,
            {
              size: "sm",
              variant: toButtonVariant(_buttons.cancel.color, _buttons.cancel.variant),
              onPress: onClose2,
              children: _buttons.cancel.label
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              size: "sm",
              variant: toButtonVariant(_buttons.confirm.color, _buttons.confirm.variant),
              onPress: () => onSuccess2(item),
              children: _buttons.confirm.label
            }
          )
        ] })
      ] }) }) });
    },
    { onSuccess, onClose }
  );
  return [modal, ModalController];
};
const useDialogConfirmation = ({ title = "Confirmation", ...rest }) => useDialog({ type: "confirm", title, ...rest });
const useDialogDelete = ({ title = "Delete", ...rest }) => useDialog({ type: "delete", title, ...rest });
const ListController = ({ items, children }) => {
  return isArray(items) && isFunction(children) && items.map((item) => children(item));
};
const ScheduleFrequency = Object.freeze({
  Once: 1,
  Hour: 2,
  Day: 3,
  Week: 4,
  Month: 5
});
const DaysOfWeekFlags = Object.freeze({
  None: 0,
  Sunday: 1,
  Monday: 2,
  Tuesday: 4,
  Wednesday: 8,
  Thursday: 16,
  Friday: 32,
  Saturday: 64
});
const DayOfWeek = Object.freeze({
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6
});
const MissedRunPolicy = Object.freeze({
  SkipToNext: 1,
  CatchUp: 2
});
const ScheduleStatusType = Object.freeze({
  Active: 1,
  Claimed: 2,
  Paused: 3,
  Completed: 4,
  Cancelled: 5,
  Failed: 6
});
const MAX_INTERVAL_COUNT = 1e3;
const FREQUENCY_OPTIONS = Object.freeze([
  { id: ScheduleFrequency.Once, label: "Once" },
  { id: ScheduleFrequency.Hour, label: "Hourly" },
  { id: ScheduleFrequency.Day, label: "Daily" },
  { id: ScheduleFrequency.Week, label: "Weekly" },
  { id: ScheduleFrequency.Month, label: "Monthly" }
]);
const intervalUnitLabel = (frequency, count) => {
  const plural = count !== 1;
  switch (frequency) {
    case ScheduleFrequency.Hour:
      return plural ? "hours" : "hour";
    case ScheduleFrequency.Day:
      return plural ? "days" : "day";
    case ScheduleFrequency.Week:
      return plural ? "weeks" : "week";
    case ScheduleFrequency.Month:
      return plural ? "months" : "month";
    default:
      return "";
  }
};
const MISSED_RUN_POLICY_OPTIONS = Object.freeze([
  {
    id: MissedRunPolicy.SkipToNext,
    label: "Skip to next",
    description: "Fire once, then realign to the first occurrence after now."
  },
  {
    id: MissedRunPolicy.CatchUp,
    label: "Catch up",
    description: "Advance one interval at a time, so every missed occurrence gets its own event."
  }
]);
const WEEK_OF_MONTH_OPTIONS = Object.freeze([
  { id: 1, label: "first" },
  { id: 2, label: "second" },
  { id: 3, label: "third" },
  { id: 4, label: "fourth" },
  { id: 5, label: "last" }
]);
const DAY_OF_WEEK_OPTIONS = Object.freeze([
  { id: DayOfWeek.Sunday, label: "Sunday", short: "S" },
  { id: DayOfWeek.Monday, label: "Monday", short: "M" },
  { id: DayOfWeek.Tuesday, label: "Tuesday", short: "T" },
  { id: DayOfWeek.Wednesday, label: "Wednesday", short: "W" },
  { id: DayOfWeek.Thursday, label: "Thursday", short: "T" },
  { id: DayOfWeek.Friday, label: "Friday", short: "F" },
  { id: DayOfWeek.Saturday, label: "Saturday", short: "S" }
]);
const MONTH_NAMES = Object.freeze([
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
]);
const DAY_NAMES = Object.freeze([
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday"
]);
const ordinal = (n) => {
  const remainder100 = n % 100;
  const remainder10 = n % 10;
  let suffix = "th";
  if (remainder100 < 11 || remainder100 > 13) {
    if (remainder10 === 1) suffix = "st";
    else if (remainder10 === 2) suffix = "nd";
    else if (remainder10 === 3) suffix = "rd";
  }
  return `${n}${suffix}`;
};
const weekOfMonthLabel = (week) => week === 5 ? "last" : ordinal(week);
const dayFlag = (dayOfWeek) => 1 << dayOfWeek;
const hasDay = (flags, dayOfWeek) => ((flags ?? 0) & dayFlag(dayOfWeek)) !== 0;
const toggleDay = (flags, dayOfWeek) => (flags ?? 0) ^ dayFlag(dayOfWeek);
const dayNames = (flags) => DAY_NAMES.filter((_2, day) => hasDay(flags ?? DaysOfWeekFlags.None, day));
const offsetLabel = (offsetMinutes) => {
  if (!offsetMinutes) return "UTC";
  const sign = offsetMinutes < 0 ? "-" : "+";
  const absolute = Math.abs(offsetMinutes);
  const hours = Math.floor(absolute / 60);
  const minutes = absolute % 60;
  return `UTC${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};
const formatTimeOfDay = (time2) => {
  if (!time2) return "";
  const hour12 = time2.hour % 12 === 0 ? 12 : time2.hour % 12;
  const suffix = time2.hour < 12 ? "AM" : "PM";
  return `${hour12}:${String(time2.minute ?? 0).padStart(2, "0")} ${suffix}`;
};
const formatLongDate = (date) => date ? `${MONTH_NAMES[date.month - 1]} ${date.day}, ${date.year}` : "";
const MS_PER_DAY = 864e5;
const MIDNIGHT = { hour: 0, minute: 0, second: 0 };
const isKnownTimeZone = (timeZoneId) => {
  if (!timeZoneId || !timeZoneId.trim()) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timeZoneId });
    return true;
  } catch {
    return false;
  }
};
const localTimeZone = () => getLocalTimeZone();
const todayIn = (timeZoneId) => {
  const date = today(timeZoneId || "UTC");
  return { year: date.year, month: date.month, day: date.day };
};
const toUtcDate = (value) => {
  if (value === null || value === void 0 || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") return new Date(value);
  const text = String(value).trim();
  const hasZone = /[Zz]$/.test(text) || /[+-]\d{2}:?\d{2}$/.test(text);
  const date = new Date(hasZone || !text.includes("T") ? text : `${text}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};
const parseTimeOnly = (value) => {
  if (value === null || value === void 0 || value === "") return null;
  if (typeof value === "object") {
    return { hour: value.hour ?? 0, minute: value.minute ?? 0, second: value.second ?? 0 };
  }
  const parts = String(value).split(":");
  if (parts.length < 2) return null;
  return {
    hour: Number(parts[0]),
    minute: Number(parts[1]),
    second: Math.trunc(Number(parts[2] ?? 0)) || 0
  };
};
const formatTimeOnly = (time2) => {
  if (!time2) return null;
  return [time2.hour ?? 0, time2.minute ?? 0, time2.second ?? 0].map((part) => String(part).padStart(2, "0")).join(":");
};
const parseDateOnly = (value) => {
  if (!value) return null;
  if (typeof value === "object") return { year: value.year, month: value.month, day: value.day };
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return null;
  return { year, month, day };
};
const formatDateOnly = (date) => date ? `${String(date.year).padStart(4, "0")}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}` : null;
const formatLocalDateTime = (date, time2) => date ? `${formatDateOnly(date)}T${formatTimeOnly(time2 ?? MIDNIGHT)}` : null;
const parseLocalDateTime = (value) => {
  if (!value) return null;
  const [datePart, timePart] = String(value).trim().split("T");
  const date = parseDateOnly(datePart);
  if (!date) return null;
  return { date, time: parseTimeOnly(timePart) ?? { hour: 0, minute: 0, second: 0 } };
};
const epochDay = (date) => {
  const utc = /* @__PURE__ */ new Date(0);
  utc.setUTCFullYear(date.year, date.month - 1, date.day);
  utc.setUTCHours(0, 0, 0, 0);
  return Math.round(utc.getTime() / MS_PER_DAY);
};
const fromEpochDay = (days) => {
  const utc = new Date(days * MS_PER_DAY);
  return { year: utc.getUTCFullYear(), month: utc.getUTCMonth() + 1, day: utc.getUTCDate() };
};
const addDays = (date, days) => fromEpochDay(epochDay(date) + days);
const dayOfWeekOf = (date) => ((epochDay(date) + 4) % 7 + 7) % 7;
const daysInMonth = (year, month) => {
  const utc = /* @__PURE__ */ new Date(0);
  utc.setUTCFullYear(year, month, 0);
  return utc.getUTCDate();
};
const addMonths = (year, month, add) => {
  const zeroBased = month - 1 + add;
  return { year: year + Math.floor(zeroBased / 12), month: (zeroBased % 12 + 12) % 12 + 1 };
};
class ZoneClock {
  constructor(timeZoneId) {
    this.timeZone = timeZoneId && timeZoneId.trim() && isKnownTimeZone(timeZoneId) ? timeZoneId : "UTC";
  }
  /** A local calendar date plus an optional time of day (midnight when unset) as a UTC instant. */
  toUtc(localDate, timeOfDay) {
    const time2 = timeOfDay ?? MIDNIGHT;
    return new CalendarDateTime(
      localDate.year,
      localDate.month,
      localDate.day,
      time2.hour ?? 0,
      time2.minute ?? 0,
      time2.second ?? 0
    ).toDate(this.timeZone);
  }
  /** A UTC instant as local wall-clock parts. */
  toLocalParts(utc) {
    const zoned = fromDate(utc, this.timeZone);
    return {
      year: zoned.year,
      month: zoned.month,
      day: zoned.day,
      hour: zoned.hour,
      minute: zoned.minute,
      second: zoned.second
    };
  }
  /** The local calendar date a UTC instant falls on. */
  localDateOf(utc) {
    const { year, month, day } = this.toLocalParts(utc);
    return { year, month, day };
  }
  /** The local time of day a UTC instant falls on. */
  localTimeOf(utc) {
    const { hour, minute, second } = this.toLocalParts(utc);
    return { hour, minute, second };
  }
  /** Offset in minutes east of UTC at the given instant. */
  offsetAt(utc) {
    return fromDate(utc, this.timeZone).offset / 6e4;
  }
}
const MS_PER_HOUR = 36e5;
const MAX_BLOCKS = 520;
const MAX_MONTHS = 24;
const resolveOnceInstant = (schedule) => {
  const local = parseLocalDateTime(schedule.runOnLocal);
  if (local) return new ZoneClock(schedule.timeZoneId).toUtc(local.date, local.time);
  return toUtcDate(schedule.startsOnUtc);
};
const normalize = (schedule, nowUtc = /* @__PURE__ */ new Date()) => {
  const frequency = Number(schedule.frequency);
  return {
    frequency,
    intervalCount: Number(schedule.intervalCount ?? 1) || 1,
    daysOfWeek: schedule.daysOfWeek ?? null,
    weekOfMonth: schedule.weekOfMonth ?? null,
    dayOfWeek: schedule.dayOfWeek ?? null,
    dayOfMonth: schedule.dayOfMonth ?? null,
    timeOfDay: parseTimeOnly(schedule.timeOfDay),
    timeZoneId: schedule.timeZoneId ?? null,
    missedRunPolicy: schedule.missedRunPolicy ?? MissedRunPolicy.SkipToNext,
    startsOnUtc: frequency === ScheduleFrequency.Once ? resolveOnceInstant(schedule) : toUtcDate(schedule.startsOnUtc) ?? nowUtc,
    endsOnUtc: frequency === ScheduleFrequency.Once ? null : toUtcDate(schedule.endsOnUtc),
    nextRunOnUtc: toUtcDate(schedule.nextRunOnUtc)
  };
};
const once = {
  computeNextUtc: (schedule, afterUtc, inclusive) => inclusive ? schedule.startsOnUtc : null,
  describeRecurrence: (schedule) => {
    const clock = new ZoneClock(schedule.timeZoneId);
    const local = clock.toLocalParts(schedule.startsOnUtc);
    const offset = offsetLabel(clock.offsetAt(schedule.startsOnUtc));
    return `once on ${formatLongDate(local)} at ${formatTimeOfDay(local)} (${offset})`;
  }
};
const hourly = {
  computeNextUtc: (schedule, afterUtc, inclusive) => {
    const stepMs = schedule.intervalCount * MS_PER_HOUR;
    const start = schedule.startsOnUtc.getTime();
    const after = afterUtc.getTime();
    if (after <= start) return new Date(inclusive ? start : start + stepMs);
    const steps = Math.floor((after - start) / stepMs);
    let candidate = start + steps * stepMs;
    while (inclusive ? candidate < after : candidate <= after) candidate += stepMs;
    return new Date(candidate);
  },
  describeRecurrence: (schedule) => schedule.intervalCount === 1 ? "every hour" : `every ${schedule.intervalCount} hours`
};
const daily = {
  computeNextUtc: (schedule, afterUtc, inclusive) => {
    const clock = new ZoneClock(schedule.timeZoneId);
    const startDate = clock.localDateOf(schedule.startsOnUtc);
    const afterDate = clock.localDateOf(afterUtc);
    const daysSinceStart = epochDay(afterDate) - epochDay(startDate);
    const steps = daysSinceStart <= 0 ? 0 : Math.floor(daysSinceStart / schedule.intervalCount);
    let date = addDays(startDate, steps * schedule.intervalCount);
    let candidate = clock.toUtc(date, schedule.timeOfDay);
    while (inclusive ? candidate < afterUtc : candidate <= afterUtc) {
      date = addDays(date, schedule.intervalCount);
      candidate = clock.toUtc(date, schedule.timeOfDay);
    }
    return candidate;
  },
  describeRecurrence: (schedule) => schedule.intervalCount === 1 ? "every day" : `every ${schedule.intervalCount} days`
};
const weekly = {
  computeNextUtc: (schedule, afterUtc, inclusive) => {
    const days = schedule.daysOfWeek ?? DaysOfWeekFlags.None;
    if (days === DaysOfWeekFlags.None) return null;
    const clock = new ZoneClock(schedule.timeZoneId);
    const startDate = clock.localDateOf(schedule.startsOnUtc);
    const anchor = addDays(startDate, -dayOfWeekOf(startDate));
    const afterDate = clock.localDateOf(afterUtc);
    const weeksSinceAnchor = Math.max(0, Math.floor((epochDay(afterDate) - epochDay(anchor)) / 7));
    let blockStart = addDays(
      anchor,
      Math.floor(weeksSinceAnchor / schedule.intervalCount) * schedule.intervalCount * 7
    );
    for (let block = 0; block < MAX_BLOCKS; block += 1) {
      for (let offset = 0; offset < 7; offset += 1) {
        const date = addDays(blockStart, offset);
        if (!hasDay(days, dayOfWeekOf(date))) continue;
        const candidate = clock.toUtc(date, schedule.timeOfDay);
        if (inclusive ? candidate >= afterUtc : candidate > afterUtc) return candidate;
      }
      blockStart = addDays(blockStart, schedule.intervalCount * 7);
    }
    return null;
  },
  describeRecurrence: (schedule) => {
    const every = schedule.intervalCount === 1 ? "every week" : `every ${schedule.intervalCount} weeks`;
    const days = dayNames(schedule.daysOfWeek ?? DaysOfWeekFlags.None).join(", ");
    return days ? `${every} on ${days}` : every;
  }
};
const nthWeekday = (year, month, week, day) => {
  const first = { year, month, day: 1 };
  const toFirstMatch = (day - dayOfWeekOf(first) + 7) % 7;
  let dayOfMonth = 1 + toFirstMatch + (week - 1) * 7;
  if (dayOfMonth > daysInMonth(year, month)) dayOfMonth -= 7;
  return { year, month, day: dayOfMonth };
};
const monthlyDate = (schedule, year, month) => schedule.dayOfMonth !== null && schedule.dayOfMonth !== void 0 ? { year, month, day: Math.min(schedule.dayOfMonth, daysInMonth(year, month)) } : nthWeekday(year, month, schedule.weekOfMonth ?? 1, schedule.dayOfWeek ?? 0);
const monthly = {
  computeNextUtc: (schedule, afterUtc, inclusive) => {
    const clock = new ZoneClock(schedule.timeZoneId);
    const start = clock.localDateOf(schedule.startsOnUtc);
    const after = clock.localDateOf(afterUtc);
    const monthsSinceStart = (after.year - start.year) * 12 + (after.month - start.month);
    const blocks = monthsSinceStart <= 0 ? 0 : Math.floor(monthsSinceStart / schedule.intervalCount);
    let { year, month } = addMonths(start.year, start.month, blocks * schedule.intervalCount);
    for (let step = 0; step < MAX_MONTHS; step += 1) {
      const candidate = clock.toUtc(monthlyDate(schedule, year, month), schedule.timeOfDay);
      if (inclusive ? candidate >= afterUtc : candidate > afterUtc) return candidate;
      ({ year, month } = addMonths(year, month, schedule.intervalCount));
    }
    return null;
  },
  describeRecurrence: (schedule) => {
    const every = schedule.intervalCount === 1 ? "every month" : `every ${schedule.intervalCount} months`;
    if (schedule.dayOfMonth !== null && schedule.dayOfMonth !== void 0) {
      return `${every} on the ${ordinal(schedule.dayOfMonth)}`;
    }
    const week = weekOfMonthLabel(schedule.weekOfMonth ?? 1);
    return `${every} on the ${week} ${DAY_NAMES[schedule.dayOfWeek ?? 0]}`;
  }
};
const STRATEGIES = {
  [ScheduleFrequency.Once]: once,
  [ScheduleFrequency.Hour]: hourly,
  [ScheduleFrequency.Day]: daily,
  [ScheduleFrequency.Week]: weekly,
  [ScheduleFrequency.Month]: monthly
};
const withinRange = (schedule, candidate) => !candidate || schedule.endsOnUtc && candidate > schedule.endsOnUtc ? null : candidate;
const firstRun = (schedule) => {
  const strategy = STRATEGIES[schedule.frequency];
  if (!strategy || !schedule.startsOnUtc) return null;
  return withinRange(schedule, strategy.computeNextUtc(schedule, schedule.startsOnUtc, true));
};
const nextRun = (schedule, afterUtc) => {
  const strategy = STRATEGIES[schedule.frequency];
  if (!strategy || !schedule.startsOnUtc) return null;
  if (afterUtc < schedule.startsOnUtc) return firstRun(schedule);
  return withinRange(schedule, strategy.computeNextUtc(schedule, afterUtc, false));
};
const calculateFirstRunUtc = (schedule) => firstRun(normalize(schedule));
const calculateNextRunUtc = (schedule, afterUtc) => nextRun(normalize(schedule), toUtcDate(afterUtc) ?? /* @__PURE__ */ new Date());
const calculateNextRuns = (schedule, count = 3) => {
  const normalized = normalize(schedule);
  const runs = [];
  let run = firstRun(normalized);
  while (run && runs.length < count) {
    runs.push(run);
    run = nextRun(normalized, run);
  }
  return runs;
};
const describeSchedule = (schedule) => {
  const normalized = normalize(schedule);
  const strategy = STRATEGIES[normalized.frequency];
  if (!strategy || !normalized.startsOnUtc) return "";
  let text = `Occurs ${strategy.describeRecurrence(normalized)}`;
  if (normalized.frequency !== ScheduleFrequency.Once && normalized.timeOfDay) {
    const clock = new ZoneClock(normalized.timeZoneId);
    const at = normalized.nextRunOnUtc ?? firstRun(normalized) ?? normalized.startsOnUtc;
    text += ` at ${formatTimeOfDay(normalized.timeOfDay)} (${offsetLabel(clock.offsetAt(at))})`;
  }
  return text;
};
const MonthlyMode = Object.freeze({
  DayOfMonth: "dayOfMonth",
  Weekday: "weekday"
});
const createScheduleState = ({ timeZoneId, frequency = ScheduleFrequency.Day } = {}) => {
  const zone = timeZoneId || localTimeZone();
  const today2 = todayIn(zone);
  return {
    frequency,
    intervalCount: 1,
    daysOfWeek: dayFlag(dayOfWeekOf(today2)),
    monthlyMode: MonthlyMode.DayOfMonth,
    dayOfMonth: today2.day,
    weekOfMonth: Math.min(5, Math.ceil(today2.day / 7)),
    dayOfWeek: dayOfWeekOf(today2),
    timeOfDay: "09:00:00",
    timeZoneId: zone,
    startDate: null,
    startTime: "09:00:00",
    endDate: null,
    missedRunPolicy: MissedRunPolicy.SkipToNext,
    runOnDate: formatDateOnly(today2),
    runOnTime: "09:00:00"
  };
};
const startInstant = (state) => {
  const date = parseDateOnly(state.startDate);
  if (!date) return null;
  const time2 = state.frequency === ScheduleFrequency.Hour ? parseTimeOnly(state.startTime) : null;
  return new ZoneClock(state.timeZoneId).toUtc(date, time2);
};
const endInstant = (state) => {
  const date = parseDateOnly(state.endDate);
  if (!date) return null;
  return new ZoneClock(state.timeZoneId).toUtc(date, { hour: 23, minute: 59, second: 59 });
};
const clampInterval = (value) => {
  const count = Math.trunc(Number(value));
  return Number.isFinite(count) ? count : 0;
};
const buildScheduleRequest = (state) => {
  const isOnce = state.frequency === ScheduleFrequency.Once;
  const isWeekly = state.frequency === ScheduleFrequency.Week;
  const isMonthly = state.frequency === ScheduleFrequency.Month;
  const byWeekday = isMonthly && state.monthlyMode === MonthlyMode.Weekday;
  const byDayOfMonth = isMonthly && state.monthlyMode === MonthlyMode.DayOfMonth;
  const start = isOnce ? null : startInstant(state);
  const end = isOnce ? null : endInstant(state);
  return {
    frequency: state.frequency,
    intervalCount: isOnce ? 1 : clampInterval(state.intervalCount),
    daysOfWeek: isWeekly ? state.daysOfWeek ?? 0 : null,
    weekOfMonth: byWeekday ? state.weekOfMonth : null,
    dayOfWeek: byWeekday ? state.dayOfWeek : null,
    dayOfMonth: byDayOfMonth ? state.dayOfMonth : null,
    // Hourly carries its time in the interval itself, and Once in RunOnLocal; the validator
    // rejects a TimeOfDay on either.
    timeOfDay: isOnce || state.frequency === ScheduleFrequency.Hour ? null : formatTimeOnly(parseTimeOnly(state.timeOfDay)),
    timeZoneId: state.timeZoneId || null,
    // A one-time schedule cannot set a policy, because every advance returns null anyway.
    missedRunPolicy: isOnce ? MissedRunPolicy.SkipToNext : state.missedRunPolicy,
    // Null lets the server default the start to now.
    startsOnUtc: start ? start.toISOString() : null,
    endsOnUtc: end ? end.toISOString() : null,
    // Local wall clock, resolved server-side so DST gaps and ambiguity stay in one place.
    runOnLocal: isOnce ? formatLocalDateTime(parseDateOnly(state.runOnDate), parseTimeOnly(state.runOnTime)) : null
  };
};
const validateScheduleRequest = (request) => {
  const fieldErrors = {};
  const errors = [];
  const fail = (field, message) => {
    fieldErrors[field] = fieldErrors[field] ?? message;
    errors.push(message);
  };
  const isOnce = request.frequency === ScheduleFrequency.Once;
  if (!isOnce && (request.intervalCount < 1 || request.intervalCount > MAX_INTERVAL_COUNT)) {
    fail("intervalCount", `The interval must be between 1 and ${MAX_INTERVAL_COUNT}.`);
  }
  if (request.timeOfDay && !request.timeZoneId) {
    fail("timeZoneId", "A time zone is required when a time of day is set.");
  }
  if (request.timeZoneId && !isKnownTimeZone(request.timeZoneId)) {
    fail(
      "timeZoneId",
      `Unknown time zone '${request.timeZoneId}'. Use an IANA id, e.g. 'America/Denver'.`
    );
  }
  const start = toUtcDate(request.startsOnUtc);
  const end = toUtcDate(request.endsOnUtc);
  if (end && start && end < start) {
    fail("endDate", "The end date cannot be before the start date.");
  }
  if (isOnce && !parseLocalDateTime(request.runOnLocal) && !start) {
    fail("runOn", "Choose the date and time to run.");
  }
  if (request.frequency === ScheduleFrequency.Week && !request.daysOfWeek) {
    fail("daysOfWeek", "Choose at least one day of the week.");
  }
  if (request.frequency === ScheduleFrequency.Month) {
    const byDay = request.dayOfMonth !== null && request.dayOfMonth !== void 0;
    const byWeekday = request.weekOfMonth !== null && request.weekOfMonth !== void 0;
    if (!byDay && !byWeekday) fail("monthly", "Choose a day of the month or a weekday.");
    if (byDay && (request.dayOfMonth < 1 || request.dayOfMonth > 31)) {
      fail("dayOfMonth", "The day of the month must be between 1 and 31.");
    }
    if (byWeekday && (request.weekOfMonth < 1 || request.weekOfMonth > 5)) {
      fail("weekOfMonth", "The week of the month must be between 1 and 5, where 5 means the last.");
    }
  }
  if (errors.length === 0 && !calculateFirstRunUtc(request)) {
    fail("range", "This schedule produces no run date within its date range.");
  }
  return { isValid: errors.length === 0, errors, fieldErrors };
};
const fromScheduleRequest = (source, { timeZoneId } = {}) => {
  const zone = source?.timeZoneId || timeZoneId || localTimeZone();
  const state = createScheduleState({ timeZoneId: zone, frequency: source?.frequency });
  if (!source) return state;
  const clock = new ZoneClock(zone);
  const start = toUtcDate(source.startsOnUtc);
  const end = toUtcDate(source.endsOnUtc);
  const timeOfDay = parseTimeOnly(source.timeOfDay);
  if (source.frequency === ScheduleFrequency.Once) {
    const local = parseLocalDateTime(source.runOnLocal);
    const runOn = local ?? (start ? { date: clock.localDateOf(start), time: clock.localTimeOf(start) } : null);
    return {
      ...state,
      timeZoneId: zone,
      runOnDate: runOn ? formatDateOnly(runOn.date) : state.runOnDate,
      runOnTime: runOn ? formatTimeOnly(runOn.time) : state.runOnTime
    };
  }
  const hasDayOfMonth = source.dayOfMonth !== null && source.dayOfMonth !== void 0;
  return {
    ...state,
    intervalCount: clampInterval(source.intervalCount ?? 1),
    daysOfWeek: source.daysOfWeek ?? state.daysOfWeek,
    monthlyMode: hasDayOfMonth ? MonthlyMode.DayOfMonth : MonthlyMode.Weekday,
    dayOfMonth: hasDayOfMonth ? source.dayOfMonth : state.dayOfMonth,
    weekOfMonth: source.weekOfMonth ?? state.weekOfMonth,
    dayOfWeek: source.dayOfWeek ?? state.dayOfWeek,
    timeOfDay: timeOfDay ? formatTimeOnly(timeOfDay) : state.timeOfDay,
    timeZoneId: zone,
    startDate: start ? formatDateOnly(clock.localDateOf(start)) : null,
    startTime: start ? formatTimeOnly(clock.localTimeOf(start)) : state.startTime,
    endDate: end ? formatDateOnly(clock.localDateOf(end)) : null,
    missedRunPolicy: source.missedRunPolicy ?? MissedRunPolicy.SkipToNext
  };
};
const toCalendarDate = (value) => {
  if (!value) return null;
  try {
    return parseDate(value);
  } catch {
    return null;
  }
};
const toTime = (value) => {
  if (!value) return null;
  try {
    return parseTime(value);
  } catch {
    return null;
  }
};
const ScheduleSelect = ({
  label,
  value,
  onChange,
  options,
  isDisabled,
  isInvalid,
  errorMessage,
  className,
  ...rest
}) => /* @__PURE__ */ jsxs(
  Select,
  {
    selectedKey: value ?? null,
    onSelectionChange: (key) => onChange(key),
    isDisabled,
    isInvalid,
    className,
    ...rest,
    children: [
      label && /* @__PURE__ */ jsx(Label, { children: label }),
      /* @__PURE__ */ jsxs(Select.Trigger, { children: [
        /* @__PURE__ */ jsx(Select.Value, {}),
        /* @__PURE__ */ jsx(Select.Indicator, {})
      ] }),
      /* @__PURE__ */ jsx(Select.Popover, { children: /* @__PURE__ */ jsx(ListBox, { items: options, children: (option) => /* @__PURE__ */ jsxs(ListBox.Item, { id: option.id, textValue: option.label, children: [
        option.label,
        /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
      ] }) }) }),
      isInvalid && errorMessage && /* @__PURE__ */ jsx(FieldError, { children: errorMessage })
    ]
  }
);
const ScheduleDateField = ({
  label,
  value,
  onChange,
  description,
  isDisabled,
  isInvalid,
  errorMessage,
  className,
  ...rest
}) => /* @__PURE__ */ jsxs(
  DatePicker,
  {
    value: toCalendarDate(value),
    onChange: (date) => onChange(date ? formatDateOnly(date) : null),
    isDisabled,
    isInvalid,
    className,
    ...rest,
    children: [
      label && /* @__PURE__ */ jsx(Label, { children: label }),
      /* @__PURE__ */ jsxs(DateField.Group, { variant: "secondary", children: [
        /* @__PURE__ */ jsx(DateField.Input, { children: (segment) => /* @__PURE__ */ jsx(DateField.Segment, { segment }) }),
        /* @__PURE__ */ jsx(DateField.Suffix, { children: /* @__PURE__ */ jsx(DatePicker.Trigger, { children: /* @__PURE__ */ jsx(DatePicker.TriggerIndicator, {}) }) })
      ] }),
      description && /* @__PURE__ */ jsx(Description, { children: description }),
      isInvalid && errorMessage && /* @__PURE__ */ jsx(FieldError, { children: errorMessage }),
      /* @__PURE__ */ jsx(DatePicker.Popover, { children: /* @__PURE__ */ jsxs(Calendar, { children: [
        /* @__PURE__ */ jsxs(Calendar.Header, { children: [
          /* @__PURE__ */ jsxs(Calendar.YearPickerTrigger, { children: [
            /* @__PURE__ */ jsx(Calendar.YearPickerTriggerHeading, {}),
            /* @__PURE__ */ jsx(Calendar.YearPickerTriggerIndicator, {})
          ] }),
          /* @__PURE__ */ jsx(Calendar.NavButton, { slot: "previous" }),
          /* @__PURE__ */ jsx(Calendar.NavButton, { slot: "next" })
        ] }),
        /* @__PURE__ */ jsxs(Calendar.Grid, { children: [
          /* @__PURE__ */ jsx(Calendar.GridHeader, { children: (day) => /* @__PURE__ */ jsx(Calendar.HeaderCell, { children: day }) }),
          /* @__PURE__ */ jsx(Calendar.GridBody, { children: (date) => /* @__PURE__ */ jsx(Calendar.Cell, { date }) })
        ] }),
        /* @__PURE__ */ jsx(Calendar.YearPickerGrid, { children: /* @__PURE__ */ jsx(Calendar.YearPickerGridBody, { children: (cell) => /* @__PURE__ */ jsx(Calendar.YearPickerCell, { year: cell.year }) }) })
      ] }) })
    ]
  }
);
const ScheduleTimeField = ({
  label,
  value,
  onChange,
  isDisabled,
  isInvalid,
  errorMessage,
  className,
  ...rest
}) => /* @__PURE__ */ jsxs(
  TimeField,
  {
    value: toTime(value),
    onChange: (time2) => time2 && onChange(formatTimeOnly(time2)),
    isDisabled,
    isInvalid,
    className,
    ...rest,
    children: [
      label && /* @__PURE__ */ jsx(Label, { children: label }),
      /* @__PURE__ */ jsx(TimeField.Group, { variant: "secondary", children: /* @__PURE__ */ jsx(TimeField.Input, { children: (segment) => /* @__PURE__ */ jsx(TimeField.Segment, { segment }) }) }),
      isInvalid && errorMessage && /* @__PURE__ */ jsx(FieldError, { children: errorMessage })
    ]
  }
);
const RadioLabel = ({ value, children }) => /* @__PURE__ */ jsx(Radio, { value, className: "mt-0!", children: /* @__PURE__ */ jsxs(Radio.Content, { children: [
  /* @__PURE__ */ jsx(Radio.Control, { children: /* @__PURE__ */ jsx(Radio.Indicator, {}) }),
  children
] }) });
const MonthlyAnchorFields = ({
  mode,
  dayOfMonth,
  weekOfMonth,
  dayOfWeek,
  onChange,
  isDisabled
}) => {
  const byDayOfMonth = mode === MonthlyMode.DayOfMonth;
  return /* @__PURE__ */ jsxs(
    RadioGroup,
    {
      value: mode,
      onChange: (value) => onChange("monthlyMode", value),
      isDisabled,
      "aria-label": "Monthly anchor",
      variant: "secondary",
      className: "gap-3",
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [
          /* @__PURE__ */ jsx(RadioLabel, { value: MonthlyMode.DayOfMonth, children: "On day" }),
          /* @__PURE__ */ jsx(
            NumberField,
            {
              value: dayOfMonth,
              onChange: (value) => onChange("dayOfMonth", value),
              minValue: 1,
              maxValue: 31,
              isDisabled: isDisabled || !byDayOfMonth,
              "aria-label": "Day of the month",
              variant: "secondary",
              className: "w-36",
              children: /* @__PURE__ */ jsxs(NumberField.Group, { children: [
                /* @__PURE__ */ jsx(NumberField.DecrementButton, {}),
                /* @__PURE__ */ jsx(NumberField.Input, {}),
                /* @__PURE__ */ jsx(NumberField.IncrementButton, {})
              ] })
            }
          ),
          byDayOfMonth && dayOfMonth > 28 && /* @__PURE__ */ jsx("span", { className: "text-xs text-muted", children: "Shorter months run on their last day." })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [
          /* @__PURE__ */ jsx(RadioLabel, { value: MonthlyMode.Weekday, children: "On the" }),
          /* @__PURE__ */ jsx(
            ScheduleSelect,
            {
              value: weekOfMonth,
              options: WEEK_OF_MONTH_OPTIONS,
              onChange: (value) => onChange("weekOfMonth", Number(value)),
              isDisabled: isDisabled || byDayOfMonth,
              "aria-label": "Week of the month",
              variant: "secondary",
              className: "w-40"
            }
          ),
          /* @__PURE__ */ jsx(
            ScheduleSelect,
            {
              value: dayOfWeek,
              options: DAY_OF_WEEK_OPTIONS,
              onChange: (value) => onChange("dayOfWeek", Number(value)),
              isDisabled: isDisabled || byDayOfMonth,
              "aria-label": "Day of the week",
              variant: "secondary",
              className: "w-44"
            }
          )
        ] })
      ]
    }
  );
};
const useScheduleBuilder = ({
  value,
  defaultValue,
  onChange,
  timeZoneId,
  previewCount = 3
} = {}) => {
  const zone = timeZoneId || value?.timeZoneId || defaultValue?.timeZoneId || localTimeZone();
  const seed = useCallback(
    (source) => source ? fromScheduleRequest(source, { timeZoneId: zone }) : createScheduleState({ timeZoneId: zone }),
    [zone]
  );
  const [state, setState] = useState(() => seed(defaultValue ?? value));
  const request = useMemo(() => buildScheduleRequest(state), [state]);
  const validation = useMemo(() => validateScheduleRequest(request), [request]);
  const description = useMemo(
    () => validation.isValid ? describeSchedule(request) : "",
    [request, validation.isValid]
  );
  const nextRuns = useMemo(
    () => validation.isValid ? calculateNextRuns(request, previewCount) : [],
    [request, validation.isValid, previewCount]
  );
  const emitted = useRef(null);
  const changeHandler = useRef(onChange);
  changeHandler.current = onChange;
  useEffect(() => {
    const serialized = JSON.stringify(request);
    if (emitted.current === serialized) return;
    emitted.current = serialized;
    changeHandler.current?.(request, {
      isValid: validation.isValid,
      errors: validation.errors,
      fieldErrors: validation.fieldErrors,
      description,
      nextRuns
    });
  }, [request, validation, description, nextRuns]);
  useEffect(() => {
    if (!value) return;
    const adopted = seed(value);
    const serialized = JSON.stringify(buildScheduleRequest(adopted));
    if (serialized === emitted.current) return;
    emitted.current = serialized;
    setState(adopted);
  }, [value, seed]);
  const setField = useCallback(
    (field, fieldValue) => setState((previous) => ({ ...previous, [field]: fieldValue })),
    []
  );
  const setFrequency = useCallback(
    (frequency) => setState((previous) => ({ ...previous, frequency: Number(frequency) })),
    []
  );
  const toggleDayOfWeek = useCallback(
    (dayOfWeek) => setState((previous) => ({
      ...previous,
      daysOfWeek: toggleDay(previous.daysOfWeek, dayOfWeek)
    })),
    []
  );
  const reset = useCallback((source) => setState(seed(source)), [seed]);
  return {
    state,
    request,
    isValid: validation.isValid,
    errors: validation.errors,
    fieldErrors: validation.fieldErrors,
    description,
    nextRuns,
    timeZoneId: zone,
    setField,
    setFrequency,
    toggleDayOfWeek,
    reset
  };
};
const runFormatter = (timeZoneId) => new Intl.DateTimeFormat(void 0, {
  weekday: "short",
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
  timeZone: timeZoneId || "UTC"
});
const SectionTitle = ({ children }) => /* @__PURE__ */ jsx("p", { className: "text-[11px] font-semibold uppercase tracking-[0.08em] text-muted", children });
const SchedulePreview = ({
  description,
  nextRuns = [],
  errors = [],
  timeZoneId,
  className
}) => {
  const formatter = useMemo(() => runFormatter(timeZoneId), [timeZoneId]);
  if (errors.length > 0) {
    return /* @__PURE__ */ jsxs(Alert, { status: "danger", className, children: [
      /* @__PURE__ */ jsx(Alert.Indicator, {}),
      /* @__PURE__ */ jsxs(Alert.Content, { children: [
        /* @__PURE__ */ jsx(Alert.Title, { children: "This schedule is not ready" }),
        /* @__PURE__ */ jsx(Alert.Description, { children: /* @__PURE__ */ jsx("ul", { className: "list-inside list-disc", children: errors.map((error) => /* @__PURE__ */ jsx("li", { children: error }, error)) }) })
      ] })
    ] });
  }
  return /* @__PURE__ */ jsx(Card, { variant: "secondary", className: cn("w-full", className), children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-col gap-4", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
      /* @__PURE__ */ jsx(SectionTitle, { children: "Summary" }),
      /* @__PURE__ */ jsx("p", { className: "text-sm", children: description })
    ] }),
    nextRuns.length > 0 && /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
      /* @__PURE__ */ jsx(SectionTitle, { children: nextRuns.length === 1 ? "Runs" : "Next runs" }),
      /* @__PURE__ */ jsx("ul", { className: "flex flex-col gap-1 text-sm tabular-nums", children: nextRuns.map((run) => /* @__PURE__ */ jsx("li", { children: formatter.format(run) }, run.toISOString())) })
    ] })
  ] }) });
};
const WeekdayPicker = ({ value, onChange, isDisabled, className }) => {
  const selectedKeys = new Set(
    DAY_OF_WEEK_OPTIONS.filter((day) => hasDay(value, day.id)).map((day) => String(day.id))
  );
  const handleChange = (keys) => onChange([...keys].reduce((mask, key) => mask | dayFlag(Number(key)), 0));
  return /* @__PURE__ */ jsx(
    ToggleButtonGroup,
    {
      selectionMode: "multiple",
      selectedKeys,
      onSelectionChange: handleChange,
      isDisabled,
      className,
      "aria-label": "Days of the week",
      children: DAY_OF_WEEK_OPTIONS.map((day, index) => /* @__PURE__ */ jsxs(ToggleButton, { id: String(day.id), "aria-label": day.label, children: [
        index > 0 && /* @__PURE__ */ jsx(ToggleButtonGroup.Separator, {}),
        day.short
      ] }, day.id))
    }
  );
};
const Section = ({ title, description, children }) => /* @__PURE__ */ jsxs("section", { className: "flex flex-col gap-3", children: [
  /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-0.5", children: [
    /* @__PURE__ */ jsx("h3", { className: "text-[11px] font-semibold uppercase tracking-[0.08em] text-muted", children: title }),
    description && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted", children: description })
  ] }),
  children
] });
const ScheduleBuilder = ({
  value,
  defaultValue,
  onChange,
  timeZoneId,
  zones = COMMON_TIME_ZONES,
  frequencies,
  previewCount = 3,
  showPreview = true,
  showTimeZone = timeZoneId === void 0 || timeZoneId === null,
  showDateRange = true,
  showEndDate = true,
  showMissedRunPolicy = true,
  isDisabled,
  className,
  ...rest
}) => {
  const { state, setField, setFrequency, fieldErrors, errors, description, nextRuns } = useScheduleBuilder({ value, defaultValue, onChange, timeZoneId, previewCount });
  const frequencyOptions = useMemo(
    () => frequencies?.length ? FREQUENCY_OPTIONS.filter((option) => frequencies.includes(option.id)) : FREQUENCY_OPTIONS,
    [frequencies]
  );
  const isOnce = state.frequency === ScheduleFrequency.Once;
  const isHourly = state.frequency === ScheduleFrequency.Hour;
  const isWeekly = state.frequency === ScheduleFrequency.Week;
  const isMonthly = state.frequency === ScheduleFrequency.Month;
  const missedRunPolicy = MISSED_RUN_POLICY_OPTIONS.find(
    (option) => option.id === state.missedRunPolicy
  );
  return /* @__PURE__ */ jsxs("div", { className: cn("flex w-full flex-col gap-6", className), ...rest, children: [
    /* @__PURE__ */ jsxs(Section, { title: "Repeats", children: [
      /* @__PURE__ */ jsx(
        ToggleButtonGroup,
        {
          selectionMode: "single",
          disallowEmptySelection: true,
          selectedKeys: /* @__PURE__ */ new Set([String(state.frequency)]),
          onSelectionChange: (keys) => {
            const [key] = [...keys];
            if (key) setFrequency(key);
          },
          isDisabled,
          "aria-label": "Frequency",
          children: frequencyOptions.map((option, index) => /* @__PURE__ */ jsxs(ToggleButton, { id: String(option.id), children: [
            index > 0 && /* @__PURE__ */ jsx(ToggleButtonGroup.Separator, {}),
            option.label
          ] }, option.id))
        }
      ),
      !isOnce && /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [
        /* @__PURE__ */ jsx("span", { className: "text-sm", children: "Every" }),
        /* @__PURE__ */ jsx(
          NumberField,
          {
            value: state.intervalCount,
            onChange: (interval) => setField("intervalCount", interval),
            minValue: 1,
            maxValue: MAX_INTERVAL_COUNT,
            isDisabled,
            isInvalid: Boolean(fieldErrors.intervalCount),
            "aria-label": "Interval",
            variant: "secondary",
            className: "w-36",
            children: /* @__PURE__ */ jsxs(NumberField.Group, { children: [
              /* @__PURE__ */ jsx(NumberField.DecrementButton, {}),
              /* @__PURE__ */ jsx(NumberField.Input, {}),
              /* @__PURE__ */ jsx(NumberField.IncrementButton, {})
            ] })
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "text-sm", children: intervalUnitLabel(state.frequency, state.intervalCount) })
      ] }),
      isWeekly && /* @__PURE__ */ jsx(
        WeekdayPicker,
        {
          value: state.daysOfWeek,
          onChange: (days) => setField("daysOfWeek", days),
          isDisabled
        }
      ),
      isMonthly && /* @__PURE__ */ jsx(
        MonthlyAnchorFields,
        {
          mode: state.monthlyMode,
          dayOfMonth: state.dayOfMonth,
          weekOfMonth: state.weekOfMonth,
          dayOfWeek: state.dayOfWeek,
          onChange: setField,
          isDisabled
        }
      )
    ] }),
    /* @__PURE__ */ jsx(Section, { title: isOnce ? "Runs on" : "Time of day", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-start gap-3", children: [
      isOnce && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          ScheduleDateField,
          {
            label: "Date",
            value: state.runOnDate,
            onChange: (date) => setField("runOnDate", date),
            isDisabled,
            isInvalid: Boolean(fieldErrors.runOn),
            errorMessage: fieldErrors.runOn
          }
        ),
        /* @__PURE__ */ jsx(
          ScheduleTimeField,
          {
            label: "Time",
            value: state.runOnTime,
            onChange: (time2) => setField("runOnTime", time2),
            isDisabled
          }
        )
      ] }),
      isHourly && /* @__PURE__ */ jsx("p", { className: "text-sm text-muted", children: "An hourly schedule keeps an even spacing from its start, so it has no time of day." }),
      !isOnce && !isHourly && /* @__PURE__ */ jsx(
        ScheduleTimeField,
        {
          label: "At",
          value: state.timeOfDay,
          onChange: (time2) => setField("timeOfDay", time2),
          isDisabled,
          className: "w-44"
        }
      ),
      showTimeZone && /* @__PURE__ */ jsx(
        TimeZoneComboBox,
        {
          value: state.timeZoneId,
          zones,
          onChange: (zone) => setField("timeZoneId", zone),
          isDisabled,
          isInvalid: Boolean(fieldErrors.timeZoneId),
          errorMessage: fieldErrors.timeZoneId,
          className: "w-72"
        }
      )
    ] }) }),
    !isOnce && showDateRange && /* @__PURE__ */ jsx(Section, { title: "Range", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-start gap-3", children: [
      /* @__PURE__ */ jsx(
        ScheduleDateField,
        {
          label: "Starts on",
          description: "Leave empty to start now.",
          value: state.startDate,
          onChange: (date) => setField("startDate", date),
          isDisabled
        }
      ),
      isHourly && /* @__PURE__ */ jsx(
        ScheduleTimeField,
        {
          label: "Starting at",
          value: state.startTime,
          onChange: (time2) => setField("startTime", time2),
          isDisabled: isDisabled || !state.startDate
        }
      ),
      showEndDate && /* @__PURE__ */ jsx(
        ScheduleDateField,
        {
          label: "Ends on",
          description: "Leave empty to run indefinitely.",
          value: state.endDate,
          onChange: (date) => setField("endDate", date),
          isDisabled,
          isInvalid: Boolean(fieldErrors.endDate),
          errorMessage: fieldErrors.endDate
        }
      )
    ] }) }),
    !isOnce && showMissedRunPolicy && /* @__PURE__ */ jsx(
      Section,
      {
        title: "Missed runs",
        description: "What to do when the scheduler comes back after unplanned downtime.",
        children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
          /* @__PURE__ */ jsx(
            ScheduleSelect,
            {
              value: state.missedRunPolicy,
              options: MISSED_RUN_POLICY_OPTIONS,
              onChange: (policy) => setField("missedRunPolicy", Number(policy)),
              isDisabled,
              "aria-label": "Missed run policy",
              className: "w-72"
            }
          ),
          missedRunPolicy && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted", children: missedRunPolicy.description })
        ] })
      }
    ),
    showPreview && /* @__PURE__ */ jsx(
      SchedulePreview,
      {
        description,
        nextRuns,
        errors,
        timeZoneId: state.timeZoneId
      }
    )
  ] });
};
export {
  AccordionMenu,
  AppSwitcher,
  ArchiveButton,
  ArrayDynamicField,
  Attachments,
  Authorizing,
  Autocomplete,
  BankAccountInput,
  Breadcrumb,
  COMMON_TIME_ZONES,
  COUNTRIES,
  CardInput,
  CheckboxCard,
  ConfirmButton,
  ConfirmableButton,
  ContentHeader,
  CopyToClipboard,
  CreateItemButton,
  DAY_NAMES,
  DAY_OF_WEEK_OPTIONS,
  DEFAULT_COUNTRY,
  DEFAULT_SMS_COST_PER_SEGMENT,
  DateRangePicker,
  DateRangePickerWithPresets,
  DateRangeTypeLabels,
  DateRangeTypes,
  DayOfWeek,
  DaysOfWeekFlags,
  DefaultContentHeader,
  DefaultFooter,
  DefaultHeader,
  DefaultLayout,
  DefaultLayoutContext,
  DefaultSidebar,
  DefaultSidebarFooter,
  DefaultSidebarHeader,
  DefaultSidebarMain,
  DeleteButton,
  DetailCard,
  DictionaryDynamicField,
  Drawer,
  DrawerActions,
  DrawerFooterContext,
  EnabledToggle,
  Error$1 as Error,
  ErrorHandler,
  ErrorListChip,
  FREQUENCY_OPTIONS,
  FieldRow,
  FlatCard,
  Footer,
  Forbidden,
  GoogleAutocomplete,
  GoogleMap,
  GoogleMapCircle,
  GoogleMapMarker,
  Grid,
  Header,
  HelpIcon,
  IconButton,
  ImageInput,
  KeyDisplay,
  ListController,
  Loading,
  LoadingBar,
  LockedValue,
  MAX_INTERVAL_COUNT,
  MISSED_RUN_POLICY_OPTIONS,
  MONTH_NAMES,
  Main$2 as Main,
  MainNavigation,
  Menu,
  MissedRunPolicy,
  ModifyButton,
  MonthlyAnchorFields,
  MonthlyMode,
  MultiActionButton,
  NewButton,
  NotFound,
  PasswordConfirmInput,
  PasswordInput,
  PhoneInput,
  Pill,
  Profile,
  ReloadButton,
  ResourceActions,
  RoutingErrorHandler,
  SaveButton,
  ScheduleBuilder,
  ScheduleDateField,
  ScheduleFrequency,
  SchedulePreview,
  ScheduleSelect,
  ScheduleStatusType,
  ScheduleTimeField,
  SearchComboBox,
  SearchPanel,
  Sidebar$1 as Sidebar,
  SidebarFooter,
  SidebarHeader,
  SidebarMain,
  SidebarModeContext,
  Skeleton,
  SmsSegments,
  SplitCard,
  StackedField,
  StatCard,
  TagsField,
  TextFieldWithCopy,
  TextWithCopy,
  ThemeProvider,
  ThemeToggle,
  TimeCalculator,
  TimeCalculatorInput,
  TimeUnitPriority,
  TimeZoneComboBox,
  Toggle,
  ToggleButtons,
  USTimeZoneComboBox,
  WEEK_OF_MONTH_OPTIONS,
  WeekdayPicker,
  WidgetField,
  ZoneClock,
  addDays,
  addMonths,
  allTimeZones,
  browserTimeZone,
  browserTimeZoneIn,
  buildDateRangeForType,
  buildScheduleRequest,
  calculateFirstRunUtc,
  calculateNextRunUtc,
  calculateNextRuns,
  calculateSmsSegments,
  canvasPreview,
  classObject,
  classes,
  convertCalendarDateRange,
  convertISOtoCalendarDateTime,
  convertToUnits,
  convertWithRemainder,
  createScheduleState,
  dayFlag,
  dayNames,
  dayOfWeekOf,
  daysInMonth,
  describeSchedule,
  echo,
  epochDay,
  findCountry,
  findCountryByDigits,
  flagEmoji,
  formatDateOnly,
  formatLocalDateTime,
  formatLongDate,
  formatPhone,
  formatTimeOfDay,
  formatTimeOnly,
  fromEpochDay,
  fromScheduleRequest,
  getBase64,
  hasDay,
  iconByType,
  intervalUnitLabel,
  isKnownTimeZone,
  keyPressedHandler,
  localTimeZone,
  masks,
  noop,
  offsetLabel,
  ordinal,
  parseDateOnly,
  parseLocalDateTime,
  parseTimeOnly,
  reactUtils,
  startsWith,
  substitutePlaceholders,
  themeAssets,
  time,
  toButtonVariant,
  toChipColor,
  toChipVariant,
  toUtcDate,
  todayIn,
  toggleDay,
  useDefaultLayoutContext,
  useDialog,
  useDialogConfirmation,
  useDialogDelete,
  useDrawerController,
  useDrawerFooter,
  useModalController,
  useOptionalThemeProvider,
  useScheduleBuilder,
  useSidebarExpanded,
  useSidebarMode,
  useThemeProvider,
  validateScheduleRequest,
  validators,
  weekOfMonthLabel,
  withDrawerController,
  withModalController,
  withProvider
};
//# sourceMappingURL=index.js.map
